/**
 * SpaceModule — the JTL-style space sector (world kind 'space').
 *
 * Activates when the client adopts the space world (Y at a city spaceport →
 * `enterSpace` → atlas:world-adopted). While active it owns the rig the same
 * way the admin fly camera does (atlas.spaceFlight gates the avatar).
 *
 * The flight model + HUD follow Star Wars Galaxies: Jump to Lightspeed:
 *   flight    THROTTLE-based (W/S set 0–100%, the ship holds it), mouse
 *             steers pitch+yaw, A/D (and Q/E) ROLL — rolling into a turn
 *             tightens it exactly as in JTL — R levels the wings, SHIFT
 *             boosts on a booster reservoir, F matches the target's speed.
 *   combat    SPACE/click fires twin bolts off the weapon CAPACITOR (firing
 *             drains it, it recharges); TAB cycles targets; the HUD projects
 *             target BRACKETS, a LEAD reticle (aim there, not at the ship),
 *             and an off-screen direction arrow. Shields split FRONT/REAR —
 *             hits drain the facing arc before the hull. Hull runs out →
 *             emergency descent (exitSpace).
 *   sector    deterministic layout (lib/space_sector): the Commons planet
 *             under the launch ring, two sibling worlds, a waystation,
 *             spinning asteroid belts, a junkyard — and pirate dens.
 *   landing   X at the waystation or your descent beacon → `exitSpace` lands
 *             you back at the origin spaceport.
 *
 * Server contract unchanged: spaceMove (5 Hz, ≤1500 m per write), spaceKill
 * (bounty through the ledger), exitSpace. Remote pilots render at their TRUE
 * 3D position (playerState.posY streams through spaceMove) with a motion-
 * derived pitch — never glued to the y=0 plane.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { pinStaticMotionVectors } from 'enginex/render/batching/staticMotionVectors.js';
import { activePreset, activeTier, SPACE_TIER_FLAGS } from '../lib/quality_tiers.js';
import { nextFrame } from '../lib/frame_clock.js';
import { createNebulaMaterial } from 'enginex/render/sky/effects/NebulaMaterial.js';
import { stage4Stats } from '../lib/stage4_fx.js';
import { civPatrolPosts, postsInWindow, pickQuarry, boltHits, PATROL_LIVE_MAX, PATROL_RESPAWN_MS, type PatrolPost } from '../lib/space_patrols.js';
import { contactColor, haloColor, wingMarkerColor } from '../lib/space_iff.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { reportThreat } from '../lib/audio/threat.js';
import { GROUND_FAR_PLANE_M } from '../lib/terrain_far_tier.js';
import { cloudTunnel } from '../lib/cloud_tunnel.js';
import { cloudsAllowed } from '../lib/space_clouds.js';
import { afterSubmitWindow, disposeLater as freeLater } from '../lib/gpu_dispose.js';
import { sfx, sfxAt, sfxLoop, stopSfxScope, type SfxAtOpts } from '../lib/sfx.js';
import { bus } from '../bus.js';
import { arrivalPoint } from '../lib/ports.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { ASSET_STORE_BASE } from '../config.js';
import { prefabAssetId } from '../lib/player_models.js';
import { SpaceHud, type RadarContact } from '../lib/space_hud.js';
import {
  PLANETS, POIS, entrySlot, pirateSpawn, asteroidField, nebulaPuffs, navPoints,
  TIER_STATS, SECTOR_EDGE_M, HYPER_SPEED, PIRATE_RESPAWN_S, FREIGHT_LANES,
  worldPlanetOf, worldPlanetEligible, worldWarpArrival, WORLD_LAND_MULT, WORLD_PLANET_POOL,
  patrolOrbit, TOTAL_ENEMIES, CONVOY_ROUTES, CONVOY_SPEED, CONVOY_AMBUSH,
  FACTION_WINGS, wingPos, VOID_CELL_M, VOID_RAIDER_P, type WingSpec,
  type PoiSpec, type NavPoint, type FreightLane, type WorldPlanet, type PatrolOrbit,
} from '../lib/space_sector.js';
import { tableStamp } from '../lib/fast_find.js';
/** The war's forts — POIS is static, so the filter the battle / wreck windows ran several times a tick is done once. */
let WAR_FORTS: PoiSpec[] | null = null;
const warForts = (): PoiSpec[] => (WAR_FORTS ??= POIS.filter((p) => p.kind === 'fortress' || p.kind === 'megastation'));
import { SpaceMap } from '../lib/space_map.js';
import { GalaxyMap } from '../lib/galaxy_map.js';
import { padSnap, padHeld, padLook, padXrActive } from '../lib/gamepad.js';
// space campaign L (2026-10-01, gap 27): the headset in the sector — its cockpit, its flight map, its comfort.
import { comfort as xrComfort } from '../lib/xr_comfort.js';
import {
  spaceComfortOn, sectorEffects, prefersReducedMotion, desktopSpaceComfort, headsetBank, rollRateScale, levelMode,
  assistSlerpT, sectorMeter, type SectorEffects,
} from '../lib/space_comfort.js';
import { spaceXrFlight, spaceXrLook } from '../lib/space_xr_flight.js';
import { SpaceVrCockpit, type SectorVrView } from './vr/SpaceVrCockpit.js';
import { claimToast } from '../lib/space_claims.js';
import { iffTag } from '../lib/space_iff.js';
const NO_VR_VIEW: SectorVrView = { status: null, prompt: null, toast: null, target: null, rollDeg: 0, chassis: '', sovereignCabin: null, sovereignEye: { y: 0, z: 0 }, rollMode: '' };
import { keyHeldIn, keyIs, keyHint, keyActionsFor, type KeyActionName, type KeyFrameName } from '../lib/keybinds.js';
// space campaign B (2026-09-30): who owns a key, squadron orders, THE FRONT, the match law.
import { sectorKeyOwner, isComposingKey, beamMode, beamOn, type KeyOwnerFacts, type ReleaseCause } from '../lib/space_input.js';
import { squadOffer, matchSpeedThrottle, type MateFacts } from '../lib/space_squad_nav.js';
import { frontLabels, systemLabel, type FrontPress } from '../lib/space_front.js';
import { stationByKey } from '@shared/space_stations.js';
import { sidedHullLabel } from '../lib/space_iff.js';
import { flightModel, AERO, turnAuthority, capTurn, smoothRates, turnLoad, bankTarget, stepBank, commandedSpeedScale, type AeroRates, type FlightModel } from '../lib/aero_flight.js';
import { prebuiltSystems, userSystem, systemSector, systemNavPoints, galacticDistance, COURSE_S_PER_LH, type StarSystem } from '../lib/galaxy.js';
import { CHASSIS, DEFAULT_CHASSIS, shipStats, NO_SKILLS, type ShipStats, type SkillLevels } from '../lib/fleet.js';
import { stationsOf, PVP_HIT_MAX, CREW_BOARD_RANGE_M, CREW_TURRET_SEAT } from '@shared/space_stations.js';
import { Fn, vec4, attribute, cameraProjectionMatrix, modelViewMatrix, positionGeometry, screenSize, uv, float, length } from 'three/tsl';
import { shipmentPosAt, ESCORT_RANGE_M, ESCORT_REWARD_SOV, SYS0_BELTS, beltsOf, depositReach, patrolReadiness, patrolShipsFor, readinessPostFor, firmIdentityBits } from '@shared/space_economy.js';
import { parseIntel, intelFreshness, newestPerSystem, ageLabel, scoutPays, SCOUT_PAY_SOV, BOOKMARK_KINDS } from '@shared/space_intel.js';
import { discovery } from '../lib/discovery_client.js'; // space campaign F: shared-stock sites, the metered beam, the mystery's beacons
import { surveyDesk } from '../lib/discovery_panel.js'; // space campaign F: the survey desk window
import { slotOf, LOD_NAME, SYS0_DENS, DEN_HIT_MAX, denPatrolPoint, DEN_HULL_STREAM_MS, DEN_HULL_STALE_S, type Lod } from '@shared/space_lod.js';
import { OPERATION_LABEL, decisionCycle, type CivOperation } from '@shared/space_ops.js';
import { WAR_FRIGATES, WAR_CORVETTES, ESCORT_KEY, CAMPAIGN_POINTS as WAR_POINTS, hullKind as warHullKind } from '@shared/space_campaign.js';
import { onLethal, onClaimResult, tickPending, pendingOf, setPending, pendingList, pendingHud, installWarContractProbe, campaignOverride } from '../lib/space_war_contract.js'; // space campaign slice C
import { lateWindow, LATE_NOTICE_S } from '@shared/campaign_contract.js'; // space campaign slice C
import { renderCampaignRecord } from '../lib/space_aar.js'; // space campaign slice C
import { tickSpaceOps, seatExtractions } from '../lib/space_ops_client.js'; // space campaign slice H: operations
import { renderOperationsBoard } from '../lib/space_ops_board.js'; // space campaign slice H: the operations board
import { warCapSeats, warCapAnchor, capKinStep, warLineD as warLineDOf, warTimeS, MASS_LOCK_M, type V3 as WarV3 } from '@shared/war_authority.js'; // space campaign slice D: ONE seat formula, client and server
import { warPinned, warLifeRow, lifeEnded, noteFired, firedFrom, sendWarShot, massLocked, salvoShooters, authorityHud, authorityTelemetry, lastMark, msSinceIntent, kinOthers } from '../lib/space_war_authority.js'; // space campaign slice D
import { Identity } from 'spacetimedb';
import { FlightCombat } from '../lib/space_flight_combat.js'; // space campaign I: the true lead, two fire groups, sensor interference
import { cockpitEye, CockpitOverlay } from '../lib/cockpit.js';
import { zoomCamera, zoomStep, chaseFor, ZOOM_CHASE, ZOOM_MIN } from '../lib/cockpit_zoom.js';
import { buildSovereignCockpit, SOVEREIGN_EYE } from '../lib/sovereign_cockpit.js';
import { interiorMaterial } from '../lib/interior_textures.js';
import { FleetDock } from '../lib/fleet_dock.js';
import { stationServicesOnClient, bindRepairSink } from '../lib/station_client.js'; // space campaign K
import { bindFlightPools } from '../lib/station_panel.js';
import { weaknessScore, strikeHold, BOUNTY_TIERS, type WeaknessMetrics } from '../lib/bounty_logic.js';
import { settlementSites, type SettlementSite } from '@shared/settlements.js';
import {
  SPACE_ORES, oresForTier, MINE_CYCLE_S, SALVAGE_CYCLE_S, sysTierOf,
  sigsOf, scanEpoch, wormholesOf, incursionsOf, incursionEpoch, whAttrsOf, systemEffectOf,
  INCURSION_SPAWN_MULT, controlFromScore, HOLD_UNITS_PER_CARGO, WRECK_STRIP_RANGE_M, type SpaceSig,
  whHiddenKey, SIG_SCAN_CYCLES, SCAN_COOLDOWN_S, type Wormhole,
} from '@shared/space_industry.js';
import type { AssetModule } from './AssetModule.js';
// ── The 2026-09-25 realism wave (report §9): the sector's picture — pure rules in lib/space/space_look.ts ──
import { setSkyBakePaused, setSpaceBackground, nightSkyAssets } from './SkyModule.js';
import {
  SYS0_SUN_DIR, SYS0_SUN_ANG_R, SUN_DISC_EMISSION, eclipseVisible, angularRadius, kelvinToRgb, kelvinOfHex, sunIntensityAt,
  starStreak, fovKickDeg, shakeAmp, TRAUMA_DECAY_PER_S, blastTrauma, dustAlpha, SPACE_KEY_INTENSITY,
} from '../lib/space/space_look.js';
import { loadSpaceSkyAssets, spaceBackgroundNode, setSpaceSkySystem, spaceEnvironment, spaceStreakLayer, setSpaceStreaks, spaceSkyState, setSpaceSkyGains } from '../lib/space/space_sky.js';
import { sunRig, placeSun, setSunLook, sunState, sunBodies, setStarQuadMode, setStarGlowEdge } from '../lib/space/space_sun.js';
import { spaceGlare, spacePostState } from '../lib/space/space_post.js';
import { motionDust, driveDust, holdDust, dustState } from '../lib/space/motion_dust.js';
// hullShellBox: the hull's SHELL box — its effect children (plumes, shield, nav quads) never widen it (THE STERN LAW).
import { attachPlumes, drivePlumes, plumeState, hullShellBox, type HullPlumes } from '../lib/space/plume.js';
import { lightWarHull, navPointSet, navMesh, hullLookState, setNavGain } from '../lib/space/hull_look.js';
import { navPointCount, fxBudget } from '../lib/space/space_look.js';
import { createBeamMaterial } from 'enginex/vfx/BeamMaterial.js';
import { SpaceFx, spaceFxState } from '../lib/space/space_fx.js';
import { planetMaterial, planetRimMaterial, legacyRimMaterial, buildAtmosphereShell, cityLightsTexture, uPlanetSun, planetLookState, type PlanetLook } from '../lib/space/planet_look.js';
// shader gaps R4 — SPACE LIGHT: receiver-side eclipses, planetary rings, the Commons' orbital cloud shell (lib/space/space_light_look.ts)
import { SpaceLight, EclipseSet, OrbitalCloudBaker, buildRing, buildCloudShell, pixelAngle as slPixelAngle, ringProfileCpu, hookOrigin } from '../lib/space/space_light_look.js';
import { lookAtLocal, seatHemisphereUp } from '../lib/local_origin.js'; // THE SCENE SHIFT: the two three.js reads that take WORLD space
import { sys0RingFor, derivedRingFor, RING_R_IN, RING_R_OUT } from '../lib/space/rings.js';
import { currentTod as slTod } from './SkyModule.js'; // shader gaps R4: the orbital clouds read the ground's own clock
import { pickWeatherOverride as slWeatherOverride } from '../lib/weather.js'; // shader gaps R4
import { TIERS as SL_TIERS, type TierPreset as SlTierPreset } from '../lib/quality_tiers.js'; // shader gaps R4
import { BlackHoleController } from '../lib/space/black_hole_look.js'; // shader gaps: black hole — the lens fed with this sky
import { NebulaController } from '../lib/space/nebula_volume.js'; // space realism: nebulae — baked volumes, one per site
import { nebulaSitesFor, nebulaLevel, nebulaModeOf } from '../lib/space/nebula_look.js';
import { NaturalController, naturalOn } from '../lib/space/natural/natural_controller.js'; // natural objects: star kinds, typed worlds, moons, small bodies, the system's sky
import type { NaturalEnv } from '../lib/space/natural/natural_api.js';
import { showpieceHosts } from '../lib/space/natural/system_makeup.js';
import {
  blackHoleFlag, BLACK_HOLE_KILL_KEY, type BlackHolePlaceInput,
  debugViewPlacement, bodyBlocker, sphereBlocker, hullPointBlockers, viewBearingText, debugViewLeftS, schwarzschildRadiusM, sunBlocksHoleView, PLACE_AHEAD_RS,
  BLACK_HOLE_DEFAULT_MASS, DEBUG_VIEW_MS, DEBUG_VIEW_SUN_MIN_RAD, type ViewBlocker, type DebugViewPlacement, bodyBehindCap, bodyBeforeCap, farBodyAnchorM,
} from '../lib/space/black_hole.js'; // shader gaps: black hole (+ the debug menu's one-minute view)
import { HullShield, shieldState } from '../lib/space/shield_look.js';
import { RockField, rockFieldState } from '../lib/space/asteroids.js';
import { impactDecals } from '../lib/impact_decals.js';
import { shipEngineering } from '../lib/ship_engineering_client.js'; // space campaign G: subsystems, the field patch, crew leases
import { engineeringPanel } from '../lib/engineering_panel.js'; // space campaign G: the HUD strip + the engineer overlay
import { ENGINEER_SEAT } from '@shared/ship_engineering.js'; // space campaign G: the engineer station's seat on a Sovereign
import { RemoteTrack, makeRemotePose } from '../lib/remote_motion.js'; // physics follow-up item 5: one smoothing model for remote movers
import { BREAKUP, h01 as breakupH, chunkLife, ventRate, fadeProgress, initialSpin, stepTumble, qrot, type Tumble, type Q4 } from '../lib/space/breakup.js'; // item 7
import { buildBreakupModel, disposeBreakupModel, type BreakupModel, type ChunkModel } from '../lib/space/breakup_mesh.js';
import { applyDissolve } from 'enginex/vfx/DissolveModifier.js';
/** THE RAIDER LAW: a fight on the lane. */
type AmbushRow = { ambushId: bigint; shipmentId: bigint; denKey: string; raiders: number; raidersAlive: number; x: number; y: number; z: number; state: string };
/** Space Wave 6: a shared den row. */
type DenRow = { stateId: bigint; systemId: string; denKey: string; alive: number; capacity: number; tier: number; authorityEpoch: bigint };
/** THE DEN SLOT LAW: an engaged den hull's shared hp and the pilot it hunts. */
type SlotRow = { slotId: bigint; systemId: string; denKey: string; slot: number; hp: number; hpMax: number; target: Identity; updatedAt: { microsSinceUnixEpoch: bigint } };
/** THE HIDDEN-POSITION LAW: what I have learned of a hidden site (a signature or a wormhole throat). */
type RevealRow = { revealId: bigint; identity: Identity; sigKey: string; systemId: string; epoch: number; kind: string; round: number; bearingDeg: number; rangeM: number; x: number; y: number; z: number };
/** Space Wave 5: the intel and bookmark rows the chart and the ops panel read. */
type IntelRow = { intelId: bigint; observer: Identity; civ: string; systemId: string; sensor: string; observedAt: { microsSinceUnixEpoch: bigint }; payload: string };
type BookmarkRow = { bookmarkId: bigint; owner: Identity; civ: string; systemId: string; x: number; y: number; z: number; label: string; kind: string };
/** Space Wave 4: the campaign rows the war renderer reads. */
type CampaignRow = { campaignId: bigint; cyc: bigint; fortKey: string; targetSystem: string; state: string; openedAt: { microsSinceUnixEpoch: bigint }; endsAt: { microsSinceUnixEpoch: bigint }; winner: string; retreatSide: number; rounds: number };
type CampaignSideRow = { campaignId: bigint; side: number; civ: string; capsCommitted: number; capsAlive: number; skirmCommitted: number; skirmAlive: number; skLost: number; capLost: number; points: number };
/** THE ROSTER LAW (2026-09-22): one row per committed capital — the server names WHICH hull died and to whom. */
type CampaignCapRow = { campaignId: bigint; side: number; slot: number; alive: boolean; killedBy: string; round: number; killedAt: { microsSinceUnixEpoch: bigint } };
/** THE ESCORT LAW (2026-09-22): one row per committed frigate or corvette — the same identity contract as a capital. */
type CampaignEscortRow = { campaignId: bigint; side: number; cls: string; slot: number; alive: boolean; killedBy: string; round: number; killedAt: { microsSinceUnixEpoch: bigint } };
type HullClass = 'fighter' | 'corvette' | 'frigate';
/** THE ESCORT LAW: the three war hull classes below the capitals, as multipliers over the gunship FSM's tier stats —
 *  a CORVETTE (the small ship above the fighter) and a FRIGATE (the medium ship) fly the same machine, slower,
 *  tougher, harder-hitting, with a longer reach and a hitbox the size of their hull. `len` is the drawn length. */
const HULL_CLASS: Record<HullClass, { len: number; hp: number; speed: number; dmg: number; fireCd: number; hitR: number; range: number; halo: number }> = {
  fighter:  { len: 0,   hp: 1,  speed: 1,    dmg: 1, fireCd: 1,   hitR: 9,  range: 1,   halo: 2.6 },
  corvette: { len: 34,  hp: 4,  speed: 0.72, dmg: 2, fireCd: 1.1, hitR: 18, range: 1.6, halo: 5 },
  frigate:  { len: 110, hp: 16, speed: 0.32, dmg: 4, fireCd: 1.6, hitR: 55, range: 3,   halo: 14 },
};
/** Space Wave 3: the shape of a space_shipment row the lane renderer reads. */
type TransitRow = { shipmentId: bigint; firmId: bigint; fromKey: string; toKey: string; qty: number; state: string; departAt: { microsSinceUnixEpoch: bigint }; arriveAt: { microsSinceUnixEpoch: bigint }; ambushAt: { microsSinceUnixEpoch: bigint }; ambushRolled: boolean; escortedBy: Identity };

const MAX_SPEED = 105;        // m/s at 100% throttle
const BOOST_MULT = 2.5;       // booster speed multiplier (burst)
// OVERCRUISE: hold full throttle, hands steady, and the engines wind up to
// ×10 (1,050 m/s) over ~21 s — steering hard, firing, or taking a hit spools
// it back down fast. (The "x10 faster but it takes a while" law.)
const CRUISE_MAX = 10;
const CRUISE_RAMP = 0.42;     // /s toward ×10
const CRUISE_DECAY = 3.5;     // /s back toward ×1
const REPAIR_COST_SOV = 25;   // mirror of server REPAIR_COST_SOV
const FREIGHTER_SPEED = 240;  // m/s along a lane
const ACCEL_K = 2.2;          // /s exponential chase toward the throttle's commanded velocity
const THROTTLE_RATE = 0.6;    // throttle change /s while W or S is held
const ROLL_RATE = 1.9;        // rad/s on A/D
const LEVEL_RATE = 3.0;       // /s wing-leveling slerp while R is held
const TURN = 0.0021;          // rad per mouse px
const SEND_HZ = 5;
const BOLT_SPEED = 420;
const BOLT_LIFE_S = 1.6;
const FIRE_COOLDOWN_S = 0.16;
const BEAM_PICK_CONE = Math.PI / 4;  // the beam prefers a wreck inside 45° of the nose (item: choose among wrecks)
const PIRATE_AGGRO_M = 900;   // hostiles commit when you can SEE them (feedback: attacked from nowhere)
const PIRATE_FIRE_M = 380;
// JTL-pattern AI timing (the FreeSpace attack-fatigue / TIE break rhythm).
const ATTACK_BREAK_M = 160;   // bore-in ends here — break off, never ram
const ATTACK_MAX_S = 5.5;     // attack-run fatigue: break off after this long
const BREAK_S = 2.4;
const EVADE_S = 1.9;
const FLEE_S = 10;            // a fleeing pirate that survives this long ESCAPES
// GLB hull yaw normalization: the cooked ships are modeled NOSE +X — without
// this the fuselage flies SIDEWAYS (the wave-1 bug: the camera saw the flank,
// not the tail). Rotate −π/2 to put the nose on −Z (flight forward).
const SHIP_MODEL_YAW = -Math.PI / 2;
/** Bodies farther than this render as scale-down impostors on the view ray
 *  (f32 jitter begins near ~150 km offsets; the Commons nadir sits at ~86 km
 *  and stays REAL). */
const FAR_ANCHOR_M = 100_000;
const PLAYER_HULL = 100;
const SHIELD_ARC = 40;        // front and rear each
const CAP_MAX = 100;
const CAP_VOLLEY = 9;         // twin-bolt volley cost
const CAP_REGEN = 16;         // /s
const BOOSTER_MAX = 100;
const BOOSTER_DRAIN = 30;     // /s while boosting
const BOOSTER_REGEN = 10;     // /s while not
const RADAR_RANGE_M = 40_000;
const UP = new THREE.Vector3(0, 1, 0);
/** The key light (the realism wave keeps the old 3.2 — the terminator now comes from losing the fill, not a brighter key). */
const SPACE_SUN_INTENSITY = SPACE_KEY_INTENSITY; // space_look: the bloom threshold is set above what this key can light
/** The starlight floor on the old 1.3 ambient slot (its 0x223044 is dim in linear: ≈ (0.016, 0.029, 0.058)). */
const SPACE_STARLIGHT = 0.5;
/** Planetshine gain on the hemisphere slot: × the planet's lit albedo × its share of the sky. */
const PLANETSHINE_K = 1.6;
/** R4: each sys0 body's atmosphere thickness (wraps the terminator, weights the in-scatter; 0 = airless). */
const PLANET_AIR: Record<string, number> = { 'The Commons': 1.0, Cindral: 0.55, Vorune: 1.4, Palewater: 0.45, Ashfall: 0 };
/** R7: the eased FOV kick's rate (1/s) and the shake's peak rotation (rad) and translation (m). */
const FOV_EASE = 2.5;
const SHAKE_ROT = 0.012;
const SHAKE_POS = 0.08;

type PirateAi = 'patrol' | 'intercept' | 'attack' | 'break' | 'evade' | 'circle' | 'flee';

interface Pirate {
  key: string;
  poiKey: string;         // den — pack aggro + attack-run tokens share it
  tier: 1 | 2 | 3;
  root: THREE.Object3D;   // position in OFFSET space (floating origin)
  hp: number;
  deadUntil: number;      // performance.now() ms when it respawns (0 = alive)
  home: THREE.Vector3;    // ABSOLUTE coords
  fireAt: number;
  vel: THREE.Vector3;     // tracked per tick — feeds the HUD lead solution
  prevPos: THREE.Vector3;
  ai: PirateAi;
  aiUntil: number;        // ms — current state's deadline
  jinkSeed: number;       // per-ship corkscrew phase
  evadeBrake: boolean;    // this evade is the TIE "hit the brakes" variant
  escaped: boolean;       // fled successfully (no bounty, quiet respawn)
  slowUntil: number;      // ms — engines subsystem knocked out (component targeting)
  jamUntil: number;       // ms — weapons subsystem knocked out
  /** Realism wave R5: this hull's engine plumes (attached lazily inside plume range; a skin swap re-attaches). */
  plumes?: HullPlumes;
  /** War side this gunship fights for (undefined = ordinary den pirate). */
  warSide?: 0 | 1;
  /** THE ESCORT LAW: the war hull's class (undefined / 'fighter' = the gunship; corvette / frigate = an escort). */
  cls?: HullClass;
  /** A WINGMAN on the player's side: green halo, hunts hostiles, no bounty. */
  ally?: boolean;
  /** A hired BOUNTY HUNTER: server-owned hp (bounty_contract), never flees. */
  bountyId?: bigint;
  /** THE DEN SLOT LAW: the server judged my last blow the killing one (paid there — never spaceKill again). */
  serverKill?: boolean;
  /** THE DEN POSITION LAW: the seat another pilot streams for this hull (they are fighting it; I follow it). */
  seat?: { x: number; y: number; z: number; yaw: number; at: number; stamp: bigint };
  /** The streamed seats, played back on their server stamps (lib/remote_motion.ts) — the old lerp(dt·4) stepped. */
  seatTrack?: RemoteTrack;
  /** Is this hull's current quarry the player (set each AI tick by quarryOf)? The seeker rule reads it. */
  quarryIsPlayer?: boolean;
  /** The engine halo's civ/threat colour at spawn (space_iff recolours a friend green). */
  haloHex?: number;
  /** The halo's last applied colour (recolour only on change). */
  haloShown?: number;
}
interface Bolt { sprite: THREE.Object3D; vel: THREE.Vector3; ttl: number; hostile: boolean; dmg?: number; fromAlly?: boolean; side?: 0 | 1 }
/** A tracking missile: player→pirate or pirate→player. `dumb` = lock broken
 *  (countermeasures / target died) — it flies straight and burns out. */
interface Missile { sprite: THREE.Object3D; vel: THREE.Vector3; ttl: number; target: Pirate | 'player' | null; dumb: boolean; mine?: boolean }
type PowerPreset = 'balanced' | 'weapons' | 'shields' | 'engines';
type SubTarget = 'hull' | 'engines' | 'weapons';
const MISSILE_SPEED = 300;
const MISSILE_TURN = 2.6;     // rad/s homing authority
const MISSILE_DMG_PLAYER = 22;
const MISSILE_COOL_S = 2.5;
const SUBSYS_OUT_S = 6;       // engines/weapons disable window
/** ACES: named warlords holding the fortress dens — slot-0 pirates with
 *  2.5× hull and a half-again hull scale. Killing one is the same server
 *  spaceKill (their dens are exactly the strike-contract targets). */
const ACE_NAMES: Record<string, string> = {
  poi9: 'Dread Warden Khorag', poi18: 'Pale Queen Ilsyth',
  poi20: 'Iron-Judge Bren Mor', poi27: 'The Ash Baron',
};
const ACE_HP_MULT = 2.5;
/** Faction fleet battles: every HOUR (shared wall clock) the two versona
 *  civs WAR for 30 min over a rotating fortress — two full battle lines
 *  (capitals + fighter screens) warp in on the shared clock, so every
 *  client sees the identical war. Strike contracts overlap the dens, and
 *  the war itself promotes REAL hostiles onto anyone it reads as an enemy
 *  (an unaligned hull is fair game to BOTH sides). THE ENGAGEMENT LAW
 *  (2026-09-17): the server's campaign retreats no earlier than 15 min in —
 *  these two MUST equal the mirror's BATTLE_PERIOD_S / BATTLE_LEN_S. */
const BATTLE_PERIOD_S = 3600;
const BATTLE_LEN_S = 1800; // half the cycle — reachable after the toast (feedback: never saw one)
const BATTLE_CAPS = 10;       // capital ships per side (feedback: "at least 10 huge ships")
const WAR_SKIRMISHERS = 30;   // REAL FSM gunships per side (2026-08-23: the instanced swarm is gone)
const SKIRMISH_RESPAWN_MS = 5000; // a kill launches a replacement from a living capital after this
// (BATTLE_WARPIN_S 22 was declared and never read — arrivals are hashed over 0–8 s; removed 2026-09-30, slice C.)
const BATTLE_VIS_M = 60_000;
/** THE FLEET WAR'S SCORE (2026-09-21, "play fleet_battle.mp3 when you are in an area with a deep space fleet battle"):
 *  inside this radius of a LIVE war's centre — the 26 km theater and its approaches, with the 4.5 km hulls filling the
 *  sky — the sector reports a `fleet` threat and the music machine loops `fleet_battle` (lib/audio/threat.ts). */
const FLEET_MUSIC_M = 40_000;
const WAR_CAP_HP = 520;       // a capital eats ~40 bolts (missiles chew faster)
const WAR_CAP_LEN = 4500;     // 4.5 km dreadnoughts (feedback: ×15 — they should DWARF you)
const WAR_CAP_HIT_R = 800;    // fat hull hitbox
const WAR_LINE_M = 16_000;    // wall stand-off from the center — a real theater
const WAR_ZONE_M = 26_000;    // player fire counts anywhere inside the theater
const WAR_CAP_SPEED = 48;     // m/s — a 4.5 km hull CRUISES, it never darts
const WAR_CAP_SEP = 5600;     // separation bubble between hull centers
const WAR_CAP_AGGRO = 10_000; // hostile capitals DETECT and engage you from here
const WAR_PD_RANGE = 2600;    // point-defense envelope
const WAR_ALLIES_MAX = 6;     // sworn pilots fly with an escort screen
/** THE FINALE: the window's last BATTLE_END_S seconds (2026-09-09: "have a few of the capital ships on both sides
 *  explode … then all the capital ships warp out" — the scripted explosions were RETIRED on 2026-09-22 by THE FINALE
 *  CONTRACT below: they destroyed capitals the server's roster had alive; the real deaths carry the same VFX). */
const BATTLE_END_S = 32;
/** THE FINALE CONTRACT (2026-09-22): the finale destroys NOTHING — every capital death is the server roster's. The
 *  LOSER (the retreating side, else the side with fewer capitals) warps out first from END_WARP_FROM_S; the WINNER
 *  holds the field and withdraws from END_WARP_WINNER_S; a draw sends both from END_WARP_FROM_S. Skirmishers follow
 *  their side. Everything is gone before the window's last second (te ≤ 31 on a forced window). */
const END_WARP_FROM_S = 11;
const END_WARP_WINNER_S = 20;
/** A capital is a warp signature, not a hull, for its first WARP_STRETCH_S seconds: no collider, no damage, no torpedo lock. */
const WARP_STRETCH_S = 0.7;

/** Deterministic [0,1) hash for war choreography (fnv-1a). */
function warH(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0) / 4294967296;
}

/** One capital's battle-frame seat (sorted by warp-in arrival). */
interface WarCap { arr: number; dx: number; by: number; bz: number; sway: number }
/** One fighter's furball orbit (sorted by warp-in arrival). */

/** Hyper-warp: a 30 s crossing inside a visible warp tunnel. */
const SYSJUMP_S = 30;
/** A refused arrival is asked again this often, for a little longer than the server's own HYPER_GRACE_S (120 s) sweep. */
const ARRIVAL_RETRY_MS = 3000;
const ARRIVAL_RETRY_MAX_MS = 15_000;
const ARRIVAL_RETRY_FOR_MS = 135_000;

/** What the debug menu's black hole button reports back (the text is the player-facing line). */
export interface BlackHoleViewResult {
  ok: boolean;
  reason: '' | 'not-in-space' | 'graphics-off' | 'no-camera' | 'failed';
  text: string;
  leftS: number;
  placement: DebugViewPlacement | null;
}

export class SpaceModule implements IModule {
  readonly name = 'SpaceModule';
  private app: App | null = null;
  private assets: AssetModule | null = null;
  private active = false;
  private root: THREE.Group | null = null;
  private shipMesh: THREE.Object3D | null = null;
  /** First-person cockpit view (Y): a canopy parented to the camera; the rig
   *  flies identically either way — only the camera's rig-local offset moves. */
  private cockpitView = false;
  private readonly cockpitOverlay = new CockpitOverlay();
  /** Scroll-wheel camera distance (rig-local back offset): the cockpit engages
   *  when it drops into the ZOOM_COCKPIT band, and pulls the chase far out for a
   *  wide situational view when raised. */
  private camBack = ZOOM_CHASE;
  /** The Sovereign's 3-D captain's-chair cockpit (built lazily, rig-attached). */
  private sovereignCockpit: THREE.Group | null = null;
  private engineGlows: THREE.Sprite[] = [];
  private pirateMaster: THREE.Object3D | null = null;
  /** Remote pilots in this system: their hull, the smoothing track (lib/remote_motion.ts — buffered interpolation on the
   *  rows' server stamps; the old chase `lerp(row, dt·6)` stepped tens to hundreds of metres every row at km/s), the
   *  drawn velocity (an AI's lead) and the pitch derived from it. `target` stays the newest SERVER row in offset space —
   *  what the rules read (space_flight_combat's serverPos / its lead estimate). */
  private remoteShips = new Map<string, { obj: THREE.Object3D; target: THREE.Vector3; track: RemoteTrack; vel: THREE.Vector3; pitch: number }>();
  /** Every remote pilot's track, fed by the player_state row events while the sector is active (arrival + stamp). */
  private remoteTracks = new Map<string, RemoteTrack>();
  /** C3: pilot hex → the system the server places them in, kept by the spaceLocation row callbacks (was a full table scan with a
   *  toHexString per row EVERY frame of a sector). No row = the Commons Reach ('sys0'), as before. */
  private sysOfMap = new Map<string, string>();
  private sysOfConn: unknown = null;
  private remoteFeedConn: unknown = null;
  private readonly _rPose = makeRemotePose();
  private pirates: Pirate[] = [];
  private bolts: Bolt[] = [];
  private planets: Array<{ mesh: THREE.Mesh; spin: number; abs?: THREE.Vector3; radius?: number; name?: string; reach?: number }> = []; // reach: the ring's outer edge in radii (the debug black hole keeps clear of it)
  private planetLodAcc = 0;
  private lodRuns = 0; // diag: planet-LOD executions
  /** Far-planet IMPOSTOR anchors: bodies beyond FAR_ANCHOR_M render at a
   *  fixed safe distance along the true view ray, scaled to the identical
   *  angular size — f32 vertex math at 300+ km offsets otherwise jitters
   *  the triangles (the far-planet flicker report, 2026-07-30). */
  private planetAnchors: Array<{ group: THREE.Group; abs: THREE.Vector3; reachM?: number; back?: boolean }> = []; // reachM / back: THE BACK SEAT (a body beyond a drawn black hole)
  private backSeated = 0; // diag: far bodies standing behind a hole's cap this frame
  private readonly _capH = { x: 0, y: 0, z: 0 };
  private readonly _capB = { x: 0, y: 0, z: 0 };
  /** Natural objects (2026-10-07): what this system is made of and the views that draw it (lib/space/natural). */
  private natural: NaturalController | null = null;
  /** PUBLIC user-world planets (worldId → scene objects + derived spec) —
   *  synced from the replicated world table every ~2 s, so new public
   *  worlds surface as planets MID-FLIGHT (the galactic-map wave). */
  private worldPlanets = new Map<string, { group: THREE.Group; mesh: THREE.Mesh; spin: number; name: string; worldId: bigint; spec: WorldPlanet }>();
  private worldSyncAcc = 0;
  private spinners: Array<{ obj: THREE.Object3D; spin: number }> = [];
  /** Non-sector lights hidden while in space (saved visibility, restored on exit). */
  private hiddenLights: Array<{ obj: THREE.Object3D; visible: boolean }> = [];
  /** True across a system→system hyperwarp swap: deactivate/activate skip
   *  the fog + light-set flips (each one recompiles EVERY material in the
   *  scene — the double storm behind the hyperwarp destroyed-buffer flood:
   *  deferred submits encoded pre-dispose land post-dispose under compile
   *  pressure, 2026-08-23). */
  private systemSwapping = false;
  /** Rig meshes hidden for flight — SAVED states, restored exactly on landing
   *  (a blanket `visible = true` un-hid meshes other systems keep hidden). */
  private hiddenRigMeshes: Array<{ obj: THREE.Object3D; visible: boolean }> = [];
  /** Commons volumetric-cloud density, zeroed in space (restored on exit). */
  private savedCloudDensity: number | null = null;
  private savedFog: THREE.Scene['fog'] = null;
  private savedCam: { pos: THREE.Vector3; rot: THREE.Euler } | null = null;
  // ── The realism wave (2026-09-25, report §9): one sun, a vacuum's post chain, a real sky ──
  /** The key light + its TARGET (seated under the root: the light moves with the camera along the sun
   *  direction, so its world direction IS that direction — it aimed at the world origin before and drifted
   *  with every rebase). */
  private sunLight: THREE.DirectionalLight | null = null;
  private sunTarget: THREE.Object3D | null = null;
  /** The starlight floor (the old 1.3 "ambient" slot, same kind — the light-set identity law). */
  private starlight: THREE.AmbientLight | null = null;
  /** Planetshine: the old hemisphere slot, its "sky" pointed at the nearest big planet, coloured by its lit albedo. */
  private planetshine: THREE.HemisphereLight | null = null;
  /** The legacy void shell + two star layers (kept only until the real sky lands, or under `atlas:space-sky = legacy`). */
  private legacySky: THREE.Object3D[] = [];
  private streaks: THREE.Mesh | null = null;
  private savedEnv: THREE.Scene['environment'] | undefined = undefined;
  /** The sun as seen from the ship this frame (world unit direction, angular radius, eclipse visibility). */
  private readonly sunDirNow = new THREE.Vector3().set(SYS0_SUN_DIR.x, SYS0_SUN_DIR.y, SYS0_SUN_DIR.z);
  private sunAngRNow = SYS0_SUN_ANG_R;
  /** The star's distance from the ship (m); Infinity in the Commons Reach, whose sun is a sky direction. */
  private sunDistNow = Infinity;
  /** natural objects: the DRAWN radius of this system's star (0 = the layout's footprint) — the eclipse set's source size. */
  private starRNow = 0;
  /** A star view gave a frame this visit: when it stops (the view faulted) the layout's own sun look is put back once. */
  private starFrameSeen = false;
  private sunVisible = 1;
  private readonly envDir = new THREE.Vector3(0, 0, 0);
  private sunRgb: [number, number, number] = [1, 0.95, 0.88];
  /** R7: camera feel — the base FOV the kick eases from, the eased kick, trauma. */
  private baseFov = 0;
  private fovKick = 0;
  private trauma = 0;
  private readonly camBasePos = new THREE.Vector3();
  private readonly camBaseRot = new THREE.Euler();
  private camBaseSet = false;
  private readonly _lookQ = new THREE.Quaternion();
  private readonly _lookV = new THREE.Vector3();
  private readonly _lookV2 = new THREE.Vector3();

  // Flight state — this.pos is ABSOLUTE (f64 JS math); the scene renders in
  // OFFSET space (floating origin `off`, rebased every 25 km) so f32 GPU
  // transforms never see sector-scale magnitudes up close.
  private vel = new THREE.Vector3();
  private quat = new THREE.Quaternion();
  private pos = new THREE.Vector3();
  private off = new THREE.Vector3();
  /** Objects that follow the ship every frame (starfield, void shell, sun glow). */
  private followers: Array<{ obj: THREE.Object3D; offset: THREE.Vector3 | null }> = [];
  // Hyperdrive nav (N cycles, J jumps) + map autopilot.
  private navIdx = -1;
  private nav: NavPoint[] = [];
  private hyper: '' | 'align' | 'spool' | 'jump' = '';
  private hyperT = 0;
  /** Sub-hyper autopilot (map click on a CLOSE target): steer + full throttle. */
  private autoCruise = false;
  /** Overcruise wind-up factor (1..CRUISE_MAX). */
  private cruise = 1;
  private map: SpaceMap | null = null;
  private mapAcc = 0;
  private freighters = new Map<string, { obj: THREE.Object3D; flightS: number }>();

  // ── Space COLLISION (client-side spheres/capsules in ABSOLUTE coords) ─────
  /** Per-POI collider group: coarse reach test, then member tests. Members:
   *  sphere (r) or vertical capsule (halfH along y, r). */
  private colliderGroups: Array<{
    x: number; y: number; z: number; reach: number;
    list: Array<{ x: number; y: number; z: number; r: number; halfH: number }>;
  }> = [];

  // ── The ×50 PATROL population (instanced hulls, promote-on-approach) ──────
  private patrolMesh: THREE.InstancedMesh | null = null;
  /** The patrol pool's material carries the static motion-vector pin (session 3 — `look().patrols`). */
  private patrolPinned = false;
  private patrols: Array<{
    poiIdx: number; orbit: PatrolOrbit; key: string;
    /** wall-clock s until this hull respawns after its promoted twin died. */
    deadUntil: number;
    /** promoted into the full-AI pirates array (hidden as an instance). */
    promoted: boolean;
  }> = [];
  private readonly promotedKeys = new Set<string>();

  // ── Cargo convoys (freighter + escorts + ambushing raiders) ───────────────
  /** Space Wave 3: the firms' runs (space_shipment rows) drawn as convoys, keyed by shipment id. */
  private shipments = new Map<string, { freighter: THREE.Object3D; escorts: THREE.Object3D[]; raiders: THREE.Object3D[]; bolts: Array<{ s: THREE.Sprite; from: THREE.Object3D; to: THREE.Object3D; t0: number }> }>();
  private convoys = new Map<string, {
    freighter: THREE.Object3D;
    escorts: THREE.Object3D[];
    raiders: THREE.Object3D[];
    bolts: Array<{ s: THREE.Sprite; from: THREE.Object3D; to: THREE.Object3D; t0: number }>;
  }>();
  private throttle = 0;
  private boost = false;
  private matchSpeed = false;
  private keys = new Set<string>();
  private hull = PLAYER_HULL;
  // Bounty hunter (the hired stalker in the void — see BountyHunterModule).
  private bountyGhost: THREE.Object3D | null = null;
  private bountyGhostAbs = new THREE.Vector3();
  /** The stalking rework (2026-09-18): the contact keeps a PHYSICAL track — a velocity, a
   *  flee vector once it is caught, and an absence with a return — never a re-seat. */
  private bountyGhostVel = new THREE.Vector3();
  private bountyGhostFlee = false;
  private bountyGhostHiddenUntil = 0;
  private bountyGhostResolved = false;
  private bountyEvalAcc = 0;
  private bountyHoldS = 0;
  private bountyShootCd = 0;
  private bountyLastHp = 0;
  private shieldF = SHIELD_ARC;
  private shieldR = SHIELD_ARC;
  private cap = CAP_MAX;
  private boosterE = BOOSTER_MAX;
  /** Active-chassis derived stats (lib/fleet.ts) — Lancer base until the
   *  player_ship / ship_component rows mirror in. */
  private stats: ShipStats = shipStats(DEFAULT_CHASSIS, []);
  private chassisKey = DEFAULT_CHASSIS;
  private fleetTick = 0;
  private dock: FleetDock | null = null;
  /** Energy routing (1-4): weapons/shields/engines trade regen + speed. */
  private power: PowerPreset = 'balanced';
  /** Component targeting (T): where the next hits land on the locked ship. */
  private subTarget: SubTarget = 'hull';
  private missileAmmo = 0;
  private counterAmmo = 0;
  private missileCool = 0;
  private droidCoolUntil = 0;
  private missiles: Missile[] = [];
  /** The live faction battle (wings + tracers), built when the window opens
   *  within visual range; torn down when it closes or the player leaves. */
  private battle: {
    cyc: number; poiKey: string; group: THREE.Group;
    frame: { c: THREE.Vector3; ax: THREE.Vector3; az: THREE.Vector3 };
    /** Capitals are INDIVIDUAL master clones (the merged-instancing path
     *  dropped cooked hulls to invisible — clones are the proven renderer;
     *  20 objects is nothing). Slots fill as each capital warps in. */
    capObjs: [Array<THREE.Object3D | null>, Array<THREE.Object3D | null>];
    capP: [WarCap[], WarCap[]];
    capN: [number, number];  // arrived capitals (count-packed warp-in)
    /** Skirmisher launch queue per side: timestamps at which a LIVING
     *  arrived capital releases the next gunship (initial staggered wave +
     *  one entry per kill, +5 s). live + queued ≤ WAR_SKIRMISHERS. */
    skRespawn: [number[], number[]];
    skSerial: number;
    // ── war REALISM: the ships are solid, damageable, and fight back ──
    capHp: [number[], number[]];        // per-capital hull pool
    capDead: [boolean[], boolean[]];
    /** Live kinematic state per capital (seek-the-anchor + separation —
     *  dreadnoughts CRUISE, they don't teleport with the seat math). */
    capKin: [Array<{ p: THREE.Vector3; v: THREE.Vector3; q: THREE.Quaternion } | null>, Array<{ p: THREE.Vector3; v: THREE.Vector3; q: THREE.Quaternion } | null>];
    flakAcc: number; torpAcc: number; pdAcc: number;
    warCols: { x: number; y: number; z: number; reach: number; list: Array<{ x: number; y: number; z: number; r: number; halfH: number }> };
    capFireAcc: number;                 // hostile capitals volley the player
    capGeo: THREE.BufferGeometry;       // procedural silhouette (fallback hulls)
    capGlows: THREE.Sprite[];
    beamPool: THREE.Mesh[];
    beams: Array<{ m: THREE.Mesh; ttl: number }>;
    flashes: Array<{ s: THREE.Sprite; ttl: number; grow: number }>;
    /** Tumbling hull-chunk debris (shared geos + ONE material, pooled). */
    debris: Array<{ m: THREE.Mesh; vel: THREE.Vector3; spin: THREE.Vector3; ttl: number; life: number }>;
    /** The finale (see BATTLE_END_S): planned once at te = 0. */
    endPlanned: boolean;
    endBooms: Array<{ side: 0 | 1; k: number; at: number; done: boolean }>;
    capLeave: [number[], number[]];     // war-time te at which each capital jumps
    capGone: [boolean[], boolean[]];    // warped out (hidden, no collider, no glow)
    skLeave: Map<string, number>;       // skirmisher key → te of its jump
    endStats: { te: number; booms: number; capsWarped: number; skWarped: number };
    endOutcome: number;                 // the finale's loser side (0|1), −1 a draw, −2 not planned yet
    rosterMode: boolean;                // the server roster names the dead (no local terminal authority)
    capClaimSent: [boolean[], boolean[]]; // a campaignKill request already out for this slot
    lastLoss: { side: number; k: number; by: string; at: number } | null; // the HUD's last cause
    escortDead: Set<string>;            // THE ESCORT LAW: an escort that died here never re-forms (a roster, not a pool)
    debrisGeos: THREE.BufferGeometry[];
    debrisMat: THREE.Material;
    beamAcc: number; rumbleAcc: number; markAcc: number;
    hostileKeys: Set<string>; promoN: number; warnedCyc: number;
    /** Space Wave 4: the campaign row's ceilings (capitals that warp in, gunships that launch) and the last sync. */
    capMax: [number, number]; skMax: [number, number]; syncAt: number; escortSyncAt: number;
    own: Array<{ dispose(): void }>;
  } | null = null;
  private battleToastCyc = -1;
  /** Dev/probe override: a forced battle window anchored at this epoch-second. */
  private battleForce = 0;
  // ── Space industry (mining/salvage/exploration/sov/incursions/ops) ───────
  private beam: THREE.Mesh | null = null;
  /** WALL-clock next-fire stamp — never sim dt (headless sim starvation
   *  stretched 4 s of accumulated fixed dt into minutes; the server's own
   *  cooldown is the real pace-keeper, so wall pacing cannot over-fire). */
  private beamNextAt = 0;
  private beamBusy = false;               // a claim is in flight — don't double-fire
  private wreckField: {
    cyc: number; group: THREE.Group; inst: THREE.InstancedMesh; cap: number;
    wrecks: Array<{ key: string; pos: THREE.Vector3; cls: 'c' | 'f'; appearAt: number }>;
    embers: THREE.Sprite[]; own: Array<{ dispose(): void }>;
  } | null = null;
  private sigBeacons = new Map<string, THREE.Object3D>();  // resolved sites (round 3 of MY reveals) → beacon
  private surveyNote = { text: '', at: 0 };                    // space campaign F: the last survey refusal toasted (rate-limited)
  private scanCooldownUntil = 0;
  private wormholeObjs: Array<{ key: string; to: string; pos: THREE.Vector3; obj: THREE.Object3D }> = [];
  private whJumpAt = 0;
  private presencePingAt = 0;
  /** THE LOCATION LAW (Space Wave 1): the last space_location seq we acted
   *  on (the watcher follows the row when it changes); the hyper order behind
   *  the running tunnel; the last hull that hit us (the obituary's attacker);
   *  retry pacing for course completion and the safety undock. */
  private locSeq = 0;
  private travelId: bigint | null = null;
  private lastHitBy: { kind: 'pirate' | 'war' | 'patrol' | 'hunter' | 'player'; key: string; side?: string; at: number } | null = null;
  private courseCompleting = false;
  private courseRetryAt = 0;
  private undockRetryAt = 0;
  private structObjs = new Map<bigint, THREE.Object3D>();
  private opsPanel: HTMLDivElement | null = null;
  private opsTimer = 0;
  private promoSide = new Map<string, 0 | 1>();       // battle hostiles → their side
  private incursionKills = 0;
  private incursionSeenEpoch = -1;
  private incBossKey: string | null = null;
  private industryToastAcc = 0;
  /** Cooked war-ship masters: fighters (promoted-gunship skins + swarm
   *  parts) and capitals (cloned per wall seat) — self-heal like chassis. */
  private warShipM: [THREE.Object3D | null, THREE.Object3D | null] = [null, null];
  /** THE ESCORT LAW: the cooked corvette / frigate hulls per civ (`prop:war_corvette_a` …); the fighter hull stands in until they land. */
  private warEscortM: Record<'corvette' | 'frigate', [THREE.Object3D | null, THREE.Object3D | null]> = { corvette: [null, null], frigate: [null, null] };
  private warCapM: [THREE.Object3D | null, THREE.Object3D | null] = [null, null];
  private warAssets: {
    ftrParts: Array<Array<{ geo: THREE.BufferGeometry; mat: THREE.Material }> | null>;
  } | null = null;
  /** Unlit-bake materials minted for war clones — disposed at DEACTIVATE
   *  (never at battle teardown: promoted hulls outlive the window). */
  private warBakedMats: THREE.Material[] = [];
  /** Realism wave R6: lit war-hull materials shared across clones of one master (keyed by source material, tint, scale). */
  private readonly warLitCache = new Map<string, THREE.Material>();
  /** A CAPITAL THAT DIES SLOWLY (physics follow-up item 7): the hull's chunk models, cut once per hull model and variant
   *  (lib/space/breakup_mesh.ts — geometries OURS, freed at deactivate), and the live breakups (their roots live in the
   *  battle group — gone with the war; their dissolve clones are freed when a chunk ends or the war tears down). */
  private readonly breakupModels = new Map<string, BreakupModel | null>();
  private breakups: Array<{ cyc: number; side: number; k: number; blastAcc: number; chunks: Array<{
    root: THREE.Group; model: ChunkModel; tumble: Tumble; pos: THREE.Vector3; vel: THREE.Vector3; life: number; age: number;
    mats: THREE.Material[]; handles: Array<{ progress: { value: number } }>; ventAcc: number; key: string;
  }> }> = [];
  private readonly breakupStats = { started: 0, chunks: 0, ended: 0, built: 0, buildMs: 0, prebuilt: 0, skipped: '' };
  private breakupPrebuild = false;
  /** Realism wave R5: the batched vacuum FX (flashes, fireballs, sparks, shrapnel, planar shocks, the light pool). */
  private fx: SpaceFx | null = null;
  /** R4: the Commons' planet material (its night lights arrive once the settlement sites are known). */
  private commonsLook: PlanetLook | null = null;
  // shader gaps R4 — SPACE LIGHT: the per-sector controller (null when every space-light flag is off), the Commons' cloud
  // shell (its night glow takes the city lights with the planet), the IBL sun lobe's last visibility step, the receivers sweep
  private spaceLight: SpaceLight | null = null;
  private blackHole: BlackHoleController | null = null; // shader gaps: black hole — ONE placed hole per sector entry (`__blackHole`)
  private nebulae: NebulaController | null = null; // space realism: nebulae — the system's volumes (`__nebula`)
  private blackHoleViewUntil = 0; // debug menu: performance.now() at which the one-minute view hole leaves (0 = none)
  private commonsCloudShell: { mesh: THREE.Mesh; setCities(t: THREE.Texture): void } | null = null;
  private slEnvVis = 1;
  private slRecvAt = 0;
  /** space P3 (2026-09-27): the Commons' atmosphere shell's group — `__space.commonsAtmo()` swaps the shell in place (the A/B). */
  private commonsAtmo: { group: THREE.Group; radius: number; hue: number } | null = null;
  /** R8: the player's visible shield arcs (a hull ellipsoid child of shipMesh, re-seated after a skin swap). */
  private shield: HullShield | null = null;
  private ventAcc = 0;
  /** R10: the instanced asteroid fields (one per POI / alt field). */
  private rockFields: RockField[] = [];
  /** Realism wave R5: the player's plumes and the capitals' (per hull object). */
  private playerPlumes: HullPlumes | null = null;
  private readonly capPlumes = new Map<THREE.Object3D, HullPlumes>();
  // ── Combat FX (research-informed): flash→sparks→smoke impacts; staged
  // secondaries→core→SHOCKWAVE RING ship deaths, scaled to hull length. ──
  private ringTex: THREE.CanvasTexture | null = null;
  private fxLive: Array<{ s: THREE.Sprite; vel: THREE.Vector3 | null; ttl: number; life: number; grow: number; from: number }> = [];
  private fxQueue: Array<{ at: number; fn: () => void }> = [];
  private fxStats = { impacts: 0, deaths: 0 };
  // ── Capital kinematics + war weapons + allies ──
  private capCloud: [Array<{ x: number; y: number; z: number; r: number }> | null, Array<{ x: number; y: number; z: number; r: number }> | null] = [null, null];
  private torpedoes: Array<{ s: THREE.Sprite; from: THREE.Vector3; tgtSide: number; tgtK: number; speed: number; trailAcc: number }> = [];
  private allyRespawnAt = 0;
  /** The galactic layer: which SOLAR SYSTEM this sector renders (sys0 = the
   *  Commons Reach — the only system with the full POI/civ machinery). */
  private system: StarSystem = prebuiltSystems()[0];
  private galaxyMap: GalaxyMap | null = null;
  /** Warp tunnel in flight (wall-clock deadline + streak sprites). */
  private sysJump: {
    target: StarSystem; until: number; streaks: THREE.Sprite[];
    tunnel: THREE.Group;
    tubes: Array<{ m: THREE.Mesh; tex: THREE.Texture; spin: number; rush: number }>;
    mouth: THREE.Sprite;
    completing: boolean; // the arrival has been asked of the server (completeTravel in flight)
    retrying?: boolean; // the server refused the arrival without a verdict — the tunnel is held and the ask repeated
    retryMs?: number; // the wait before the next ask (doubles to ARRIVAL_RETRY_MAX_MS: a faulting reducer is not hammered)
    giveUpAt?: number; // wall clock: past this a held tunnel drops out (the sweep has landed us by then)
    own: Array<{ dispose(): void }>;
  } | null = null;
  /** Manual interstellar course (wall clock, survives logout via localStorage). */
  /** The standing sublight course = my transit `course` travel_order (server
   *  truth — the old localStorage course is gone; a course now runs while you
   *  are logged out because the SWEEP completes it). */
  private get course(): { targetId: string; arriveAt: number; travelId: bigint } | null {
    const conn = atlas.conn;
    if (!conn?.identity) return null;
    try {
      for (const o of conn.db.travelOrder.iter()) {
        if (o.identity.isEqual(conn.identity) && o.state === 'transit' && o.mode === 'course') {
          return { targetId: o.toSystem, arriveAt: Number(o.arriveAt.microsSinceUnixEpoch / 1000n), travelId: o.travelId };
        }
      }
    } catch { /* not subscribed yet */ }
    return null;
  }
  /** My space_location row (null before the first subscription lands). */
  private myLocation(): { systemId: string; mode: string; stationKey: string; seq: number } | null {
    const conn = atlas.conn;
    if (!conn?.identity) return null;
    try { return conn.db.spaceLocation.identity.find(conn.identity) ?? null; } catch { return null; }
  }
  /** CIV PATROLS (2026-09-08): both civs keep a post at every point of
   *  interest; posts inside PATROL_SPAWN_M of the player field full-AI hulls
   *  (the void-raider law), culled past PATROL_CULL_M. Keyed by post. */
  private readonly patrolPosts: PatrolPost[] = civPatrolPosts(POIS);
  private patrolLive = new Map<string, Pirate[]>();
  private patrolScanAcc = 0;
  /** Distance-scaled contact markers — ships read at ANY range (feedback:
   *  hulls vanish past a few hundred meters; fights were fought blind). */
  private contactMarkers: THREE.Sprite[] = [];
  /** Per-charted-world collider groups (removed with their planet). */
  private worldClusterCols = new Map<string, { x: number; y: number; z: number; reach: number; list: Array<{ x: number; y: number; z: number; r: number; halfH: number }> }>();
  /** The pilot's civ ('' unaligned) — mirrored by refreshFleet. */
  private faction = '';
  /** Faction wings cruising the lanes; a ship slot is null after it PROMOTES
   *  into a full hostile (opposing civ) or dies with its wing cycle. */
  private wings: Array<{ spec: WingSpec; ships: Array<THREE.Object3D | null>; hostileCyc: number; allyFireAt: number }> = [];
  private wingToastAt = 0;
  /** Void raiders: sparse deterministic ambushers hashed from 22 km cells —
   *  the empty space between dens is no longer empty. */
  private voidRaiders = new Map<string, Pirate>();
  private voidScanAcc = 0;
  /** Walkable interior (B = station · I = Sovereign deck): the ship parks and
   *  the pilot walks a multi-zone volume staged far below the flight lane —
   *  docking bay → heavy-door corridor → promenade (the arrival theater). */
  private interior: {
    kind: 'station' | 'deck';
    group: THREE.Group;
    origin: THREE.Vector3;      // local-space room origin
    wx: number; wz: number;     // walk position in room space
    yaw: number; pitch: number;
    /** Walkable floor as a UNION of rects (they overlap at doorways). */
    rects: Array<{ x0: number; x1: number; z0: number; z1: number }>;
    /** Rolling doors that slide open on approach. */
    doors: Array<{ mesh: THREE.Mesh; x: number; z: number; open: number }>;
    terminal: { x: number; z: number } | null;
    /** Deck only: viewport count + the player-furnishing layer. */
    windows?: number;
    shipId?: bigint;
    furn?: THREE.Group;
    furnMap?: Map<bigint, THREE.Object3D>;
    furnAcc?: number;
  } | null = null;
  private furnPanel: HTMLDivElement | null = null;
  private furnTimer = 0;
  private deckSubbed = false;
  private spaceSubbed = false;
  /** Activation generation: async texture loads landing after a
   *  deactivate belong to a DEAD sector — they dispose themselves instead
   *  of leaking a decoded 4K texture per quick eject (the pod-lag tail). */
  private actGen = 0;
  /** The planet faces and reliefs THIS activation loaded into a PlanetLook: they live in TSL texture nodes (not in
   *  `material.map`), so the root's sweep never saw them and every entry or jump left ~450 MB to the collector. */
  private lookTex: THREE.Texture[] = [];
  /** THE SCENE SHIFT: where the sector root's origin stands in WORLD space (the FP64 rebase holds the scene at −origin). */
  private rootShift(): THREE.Vector3 { return this.root ? this.root.getWorldPosition(this._shiftV) : this._shiftV.set(0, 0, 0); }
  private readonly _shiftV = new THREE.Vector3();
  private lastHit = 0;
  private fireCool = 0;
  private sendAcc = 0;
  private bounty = 0;
  private target: Pirate | null = null;
  /** Smoothed mouse-input rates — drive the cosmetic bank/pitch of the hull. */
  private yawRate = 0;
  private pitchRate = 0;
  // THE AERO LAW (2026-09-22): the look accumulates here (rad) and the FIXED tick consumes it — capped at the hull's
  // rate at this speed under the `aero` model; the `space` model keeps the old chassis-scaled sensitivity.
  private steerX = 0;
  private steerY = 0;
  private flightModelNow: FlightModel = 'aero';
  private aeroRates: AeroRates = { yaw: 0, pitch: 0 };
  private aeroAuth = 1;
  private aeroLoad = 0;
  private aeroBank = 0; // the picture's coordinated bank (rad), on the rig — the flight quaternion never carries it
  // ── Space campaign L (2026-10-01, gap 27): the headset in the sector (lib/space_comfort, modules/vr/SpaceVrCockpit) ──
  private readonly vrCockpit = new SpaceVrCockpit();
  /** A headset presented last frame (the cockpit / zoom re-seat on the edge). */
  private vrWas = false;
  /** Seconds since the last roll input (the headset's auto-level waits for a still hand). */
  private rollIdleS = 0;
  /** The secondary effects in force this frame (shake, streaks, FOV kick, dust, tunnel spin, own warp flash). */
  private fxScale: SectorEffects = sectorEffects({ on: true, presenting: false, vrEffects: 'reduced', desktopMotion: 'full', prefersReduced: false });
  private readonly vrScratch = new THREE.Vector3();
  /** The pad snapshot whose press EDGES were dispatched (fixed() may run several times in one polled frame). */
  private xrEdgeSnap: object | null = null;
  /** The pad's boost (LT / the headset's left trigger) last tick — `this.boost` follows its edges. */
  private padBoostWas = false;
  private landingNow = false;
  /** Tick-driven landing completion (see land()): −1 idle, else counting down. */
  private landingTicks = -1;
  private landingArrival: { worldId: bigint; x: number; z: number } | null = null;
  /** Cached site list (settlementSites is an EXPENSIVE derivation — computed
   *  once off the landing path; land() must never grind the main thread). */
  private sites: SettlementSite[] = [];
  private sitesSeed = '';

  // HUD
  private hud: SpaceHud | null = null;
  private hudAcc = 0;
  private shieldWasUp = true; // edge detector for the shield-collapse cue
  private spaceBoostPrev = false; // afterburner ignition edge
  private radarAcc = 0;
  private remoteAcc = 0;
  /** Remote pilots' TRUE 3D positions, refreshed at 5 Hz from playerState. */
  private remoteRows = new Map<string, { x: number; y: number; z: number; yaw: number }>();

  // Scratch (no per-frame allocations)
  /** Ship position in OFFSET space — refreshed at the top of fixed()/frame(). */
  private readonly _ship = new THREE.Vector3();
  /** Pirate-AI to-ship direction (own scratch — steering reuses _v1.._v3). */
  private readonly _aim = new THREE.Vector3();
  private readonly _v1 = new THREE.Vector3();
  private readonly _v2 = new THREE.Vector3();
  private readonly _v3 = new THREE.Vector3();
  private readonly _q1 = new THREE.Quaternion();
  private readonly _q2 = new THREE.Quaternion();
  private readonly _e1 = new THREE.Euler();
  private readonly _m1 = new THREE.Matrix4();
  private readonly _v4 = new THREE.Vector3();
  private readonly _sfxV = new THREE.Vector3();
  private readonly _v5 = new THREE.Vector3();
  private readonly _m2 = new THREE.Matrix4();
  private readonly _c1 = new THREE.Color();
  private readonly _c2 = new THREE.Color();
  /** One shared glow texture — the old per-sprite CanvasTexture leaked a GPU
   *  texture per SHOT (bolt sprites were removed but never disposed). */
  private glowTex: THREE.CanvasTexture | null = null;

  /** Planet texture load states (name → 'loading'|'ok'|'failed') for __space. */
  private readonly texState = new Map<string, string>();
  /** Activation stage for __space diagnostics ('' | spawn | ready | ready+…). */
  private stage = '';
  /** Last reducer/flow error (surfaced in __space.state for probes). */
  private lastError = '';

  init(app: App): void {
    this.app = app;
    this.assets = app.getModule('AssetModule') as AssetModule | null;
    bus.on('atlas:world-entered', ({ worldId }) => {
      const conn = atlas.conn;
      if (!conn) return;
      let isSpace = false;
      for (const w of conn.db.world.iter()) if (w.worldId === worldId && w.kind === 'space') isSpace = true;
      if (isSpace) void this.activate();
      else if (this.active) this.deactivate();
    });
    bus.on('atlas:world-cleared', () => { if (this.active) this.deactivate(); });
    // A mid-game swap from space: tear the sector DOWN FIRST, behind the
    // transition cover, so its deferred GPU disposals never overlap the
    // destination world's render (the space→ground swap flood).
    bus.on('atlas:world-swapping', ({ fromSpace }) => { if (fromSpace && this.active) this.deactivate(); });

    // THE SECTOR'S KEYS (space campaign B, 2026-09-30): one action vocabulary (lib/keybinds.ts), one owner per key
    // (lib/space_input.ts). Text entry and a composing IME own their keys; the Esc Options menu pauses the sector; a
    // focused control keeps Tab / Space / Enter / the arrows; otherwise the key acts in the sector's FRAME — flying,
    // walking a station / the deck, or docked — and only that frame's actions answer, so a docked pilot's W never
    // flies, a walker's R never fires, and no key means two things at once (R was a missile AND level-out, E a roll
    // AND an escort, G a repair AND the chart). Every binding is the player's: keyIs / keyActionsFor, never a literal.
    window.addEventListener('keydown', (e) => {
      if (!this.active) return;
      const own = sectorKeyOwner(this.keyFacts(e), e.code);
      if (own.owner !== 'sector') {
        if (own.owner === 'ui' && own.ui === 'options') this.releaseHeld('ui'); // the modal took the keys
        return;
      }
      this.setKeyFrame(own.frame);
      if (own.frame !== 'sectorDocked') this.keys.add(e.code); // docked: nothing is held — the ship sits in the clamps
      if (own.frame === 'sector') {
        if (keyIs(e.code, 'boost')) this.boost = true;
        if (keyIs(e.code, 'fire') || keyIs(e.code, 'spaceTarget')) e.preventDefault(); // Space would scroll, Tab move focus
      }
      if (e.repeat) return; // a HELD key presses once (a held K used to flicker the ops panel open and shut)
      for (const a of keyActionsFor(e.code, own.frame)) this.sectorAction(a);
    });
    window.addEventListener('keyup', (e) => {
      this.keys.delete(e.code); // a release always lands, whoever owned the press
      if (keyIs(e.code, 'boost')) this.boost = false;
    });
    // The page stops hearing key-ups: release everything held (alt-tab kept a held Space firing, a held A rolling,
    // a held Shift boosting, a held U beaming — the sector had no release at all).
    window.addEventListener('blur', () => this.releaseHeld('blur'));
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.releaseHeld('hidden'); });
    document.addEventListener('pointerlockchange', () => { if (this.active && document.pointerLockElement === null) this.releaseHeld('pointer-unlock'); });
    // Probe lever (read-only): what the sector's keys REALLY did — the frame, the held set, each action's presses, the
    // stick's state and the last toasts (space campaign B's key census).
    (window as unknown as Record<string, unknown>).__spaceKeys = () => ({
      active: this.active, frame: this.keyFrame, held: [...this.keys], boost: this.boost,
      census: Object.fromEntries(this.keyCensus), beam: this.beamHeld(), beamMode: beamMode(),
      matchSpeed: this.matchSpeed, matchCapped: this.matchCapped, throttle: this.throttle,
      missileAmmo: this.missileAmmo, myMissiles: this.missiles.filter((m) => m.mine).length,
      worldId: String(atlas.currentWorldId), interior: this.interior?.kind ?? null, docked: !!this.dock,
      toasts: [...this.recentToasts],
    });
    window.addEventListener('mousemove', (e) => {
      if (!this.active || document.pointerLockElement === null) return;
      if (this.interior) { // head-look while walking
        this.interior.yaw -= e.movementX * 0.0023;
        this.interior.pitch = THREE.MathUtils.clamp(this.interior.pitch - e.movementY * 0.0023, -1.2, 1.2);
        return;
      }
      if (this.hyper === 'jump') return; // the drive owns the ship
      if (Math.abs(e.movementX) + Math.abs(e.movementY) > 4) this.autoCruise = false; // pilot takes the stick back

      // THE AERO LAW: the sky's constant for every hull (the chassis caps the RATE in the fixed tick, not the
      // sensitivity); the old `space` model keeps its chassis-scaled mouse. Either way the tick consumes it.
      const turn = this.flightModelNow === 'space' ? TURN * this.stats.turnRate : AERO.MOUSE_TURN;
      this.steerX += -e.movementX * turn;
      this.steerY += -e.movementY * turn;
      // Smoothed input rates feed the cosmetic lean (decay in frame()).
      this.yawRate = THREE.MathUtils.clamp(this.yawRate - e.movementX * 0.06, -1.6, 1.6);
      this.pitchRate = THREE.MathUtils.clamp(this.pitchRate - e.movementY * 0.06, -1.6, 1.6);
    });
    window.addEventListener('mousedown', (e) => {
      if (this.active && e.button === 0 && document.pointerLockElement !== null) this.flightCombat.primaryClick(); // space campaign I: the active fire group's primary (bolts by default — one volley a click, as ever)
    });
    // Scroll wheel = continuous camera zoom: in past a threshold → first-person
    // cockpit; out → a wide chase for situational awareness across the sector.
    window.addEventListener('wheel', (e) => {
      if (!this.active || this.interior) return;
      if (document.getElementById('space-map') || document.getElementById('galaxy-map')) return; // charts own the wheel
      e.preventDefault();
      this.camBack = zoomStep(this.camBack, e.deltaY);
      this.applyCamZoom();
    }, { passive: false });

    // `simScale` is a dev-gated PROBE lever (2026-10-01, THE SIM-TIME LAW): under SwiftShader a frame takes seconds and the
    // engine clamps it to five fixed steps, so a minute of wall time is a few sim-seconds — a probe runs k steps per step.
    app.world.addSystem(makeSystem('SpaceFlight', Phase.FixedSim, 41, () => { for (let i = 0; i < this.simScale; i++) { this.fixed(1 / 60); this.simTicks++; } }));
    app.world.addSystem(makeSystem('SpaceRender', Phase.Animation, 37, (dt) => this.frame(dt)));
    this.installDebug();
  }

  // ── Debug surface (probes + live inspection) ─────────────────────────────
  // Telemetry is always readable; ACTIONS are dev-gated (the debug-console
  // rule: localhost or the 'atlas:debug' flag). Headless probes drive the
  // sector through this — swiftshader can render the sphere-and-sprite space
  // scene, so unlike city assembly it verifies VISUALLY too.
  private devGated(): boolean {
    try {
      return location.hostname === 'localhost' || location.hostname === '127.0.0.1'
        || localStorage.getItem('atlas:debug') === '1';
    } catch { return false; }
  }

  private installDebug(): void {
    const mod = this;
    (window as unknown as { __space: object }).__space = {
      // Probe door: run the planet distance-LOD once. In headless SwiftShader the
      // space render loop can stall, so frame() (which normally drives the LOD at
      // ~2 Hz) may not tick — this lets the gate verify the fade math directly.
      forceLod(): void { mod.updatePlanetLod(); },
      // space P3 (2026-09-27): the Commons' shell in place — 'scatter' (§7.1, its own sky) | 'oneil' (the old tint fit); returns the kind now worn.
      commonsAtmo(mode?: 'scatter' | 'oneil'): string | null {
        const c = mod.commonsAtmo; if (!c) return null;
        const worn = (): string => c.group.children.some((o) => o.name === 'planet-atmosphere-scatter') ? 'scatter' : c.group.children.some((o) => o.name === 'planet-atmosphere') ? 'oneil' : 'none';
        if (mode === 'scatter' || mode === 'oneil') {
          for (const o of [...c.group.children]) if (o.name === 'planet-atmosphere' || o.name === 'planet-atmosphere-scatter') { c.group.remove(o); freeLater((o as THREE.Mesh).geometry); freeLater((o as THREE.Mesh).material as THREE.Material); } // the shell was drawn this frame: free after the submit window (THE WARP-TEARDOWN LAW)
          let prev: string | null = null; try { prev = localStorage.getItem('atlas:planet-atmo'); localStorage.setItem('atlas:planet-atmo', mode === 'oneil' ? 'oneil' : 'scatter'); } catch { /* storage blocked */ }
          mod.attachAtmosphere(c.group, c.radius, c.hue, SpaceModule.SUN_DIR, true);
          try { if (prev === null) localStorage.removeItem('atlas:planet-atmo'); else localStorage.setItem('atlas:planet-atmo', prev); } catch { /* storage blocked */ }
        }
        return worn();
      },
      state(): object {
        const nearestPoi = POIS
          .map((p) => ({ key: p.key, name: p.name, dist: Math.round(mod.pos.distanceTo(new THREE.Vector3(...p.pos))) }))
          .sort((a, b) => a.dist - b.dist);
        return {
          active: mod.active,
          stage: mod.stage,
          lastError: mod.lastError,
          pos: [Math.round(mod.pos.x), Math.round(mod.pos.y), Math.round(mod.pos.z)],
          speed: Math.round(mod.vel.length()),
          // THE LOCATION LAW (Space Wave 1): the server's row as the client caches it.
          loc: (() => { const l = mod.myLocation(); return l ? { systemId: l.systemId, mode: l.mode, stationKey: l.stationKey } : null; })(),
          throttle: Math.round(mod.throttle * 100) / 100,
          // THE AERO LAW: the sector's flight model and its felt state.
          flight: { model: mod.flightModelNow, bankDeg: Math.round(mod.aeroBank * 57.2958), authority: Math.round(mod.aeroAuth * 100) / 100, load: Math.round(mod.aeroLoad * 100) / 100, yawRate: Math.round(mod.aeroRates.yaw * 100) / 100, turnRate: mod.stats.turnRate },
          hull: Math.round(mod.hull),
          shield: Math.round(mod.shieldF + mod.shieldR), // legacy probe key — the arcs' sum
          shieldF: Math.round(mod.shieldF),
          shieldR: Math.round(mod.shieldR),
          cap: Math.round(mod.cap),
          booster: Math.round(mod.boosterE),
          bounty: mod.bounty,
          chassis: mod.chassisKey,
          cockpit: mod.cockpitView,
          fleet: {
            hpMax: mod.stats.hp, shieldArc: mod.arcMax(), capMax: mod.stats.capacitor,
            speedMax: Math.round(mod.stats.maxSpeed), weaponMult: Math.round(mod.stats.weaponMult * 100) / 100,
            missiles: mod.stats.missiles, counters: mod.stats.counters, droid: mod.stats.droid,
            dock: mod.dock !== null,
          },
          power: mod.power,
          subTarget: mod.subTarget,
          missileAmmo: mod.missileAmmo,
          counterAmmo: mod.counterAmmo,
          missilesLive: mod.missiles.length,
          battle: (() => {
            const w = mod.battleWindow();
            if (!w) return null;
            const b = mod.battle;
            return {
              poi: w.poi.key, t: Math.round(w.t), built: b !== null,
              caps: b ? [b.capN[0], b.capN[1]] : [0, 0],
              fighters: b ? [mod.warSkirmisherCount(0), mod.warSkirmisherCount(1)] : [0, 0],
              sides: mod.faction === '' ? 2 : 1, // hostile sides from the player's alignment
              hostiles: b ? mod.pirates.filter((p) => p.deadUntil === 0 && b.hostileKeys.has(p.key)).length : 0,
              // war REALISM diag
              capCols: b ? b.warCols.list.length : 0,
              capAlive: b ? [b.capDead[0].filter((d) => !d).length, b.capDead[1].filter((d) => !d).length] : [0, 0],
              capHpMin: b ? Math.min(...b.capHp[0], ...b.capHp[1]) : 0,
              realized: b ? mod.warSkirmisherCount(0) + mod.warSkirmisherCount(1) : 0, // every skirmisher IS a real FSM ship now
              cooked: mod.warCapM[0] !== null || mod.warCapM[1] !== null || mod.warAssets !== null,
              capObjsLive: b ? b.capObjs[0].filter(Boolean).length + b.capObjs[1].filter(Boolean).length : 0,
              cloud: (mod.capCloud[0]?.length ?? 0) + (mod.capCloud[1]?.length ?? 0),
              allies: mod.pirates.filter((q2) => q2.ally && q2.deadUntil === 0).length,
              patrols: { posts: mod.patrolPosts.length, fielded: mod.patrolLive.size,
                live: [0, 1].map((sd) => mod.pirates.filter((q2) => q2.key.startsWith('patrol') && q2.warSide === sd && q2.deadUntil === 0).length),
                engaged: mod.pirates.filter((q2) => q2.key.startsWith('patrol') && q2.deadUntil === 0 && q2.ai !== 'patrol').length },
              torpedoes: mod.torpedoes.length,
              fx: { impacts: mod.fxStats.impacts, deaths: mod.fxStats.deaths },
              ending: b ? { ...b.endStats, gone: [b.capGone[0].filter(Boolean).length, b.capGone[1].filter(Boolean).length], planned: b.endPlanned, booms: b.endBooms.filter((x) => x.done).length, outcome: b.endOutcome } : null,
              escorts: b ? [0, 1].map((sd) => (['frigate', 'corvette'] as const).map((c) => mod.pirates.filter((q2) => q2.warSide === sd && q2.cls === c && q2.deadUntil === 0).length)) : null,
              breakup: {
                live: mod.breakups.length, liveChunks: mod.breakups.reduce((n, bk) => n + bk.chunks.length, 0), ...mod.breakupStats,
                ages: mod.breakups.map((bk) => bk.chunks.map((c) => +c.age.toFixed(1))),
                spin: mod.breakups.flatMap((bk) => bk.chunks.map((c) => { const L = c.tumble.L, I = c.tumble.I; return +Math.hypot(L[0] / I[0], L[1] / I[1], L[2] / I[2]).toFixed(4); })),
                drift: mod.breakups.flatMap((bk) => bk.chunks.map((c) => Math.round(c.vel.length()))),
              },
              roster: b ? { mode: b.rosterMode, rows: mod.campaignRow() ? mod.campaignRoster(mod.campaignRow()!.campaignId).length : 0, dead: [b.capDead[0].filter(Boolean).length, b.capDead[1].filter(Boolean).length], claims: b.capClaimSent[0].filter(Boolean).length + b.capClaimSent[1].filter(Boolean).length, lastLoss: b.lastLoss } : null,
            };
          })(),
          aces: Object.keys(ACE_NAMES).length,
          pvp: { targets: mod.warTargets.size, taken: mod.pvpHitsTaken },
          crew: { captain: mod.crew?.captainName ?? null, manned: mod.manned },
          denHulls: { seated: mod.pirates.filter((p) => p.seat !== undefined).length, streamed: mod.denHullsStreamed },
          system: { id: mod.system.id, name: mod.system.name, tier: mod.system.tier },
          galaxy: mod.galaxySystems().length,
          course: mod.course ? { target: mod.course.targetId, remS: Math.round((mod.course.arriveAt - Date.now()) / 1000) } : null,
          sysJump: mod.sysJump !== null,
          sysJumpLeft: mod.sysJump ? Math.round((mod.sysJump.until - performance.now()) / 1000) : 0,
          tunnel: mod.sysJump !== null && mod.sysJump.tunnel.children.length > 0,
          industry: {
            beamTarget: (() => { const t = mod.beamTarget(); return t ? { mode: t.mode, key: t.key } : null; })(),
            cargo: mod.cargoUnits(),
            wrecks: mod.wreckField ? { total: mod.wreckField.wrecks.length, up: mod.wreckField.inst.count } : null,
            sigs: mod.liveSigs().length,
            sigBeacons: mod.sigBeacons.size,
            wormholes: mod.wormholeObjs.length,
            incursion: mod.incursionHere() ? mod.incursionInfluence() : null,
            control: mod.controllerOf(mod.system.id),
            structures: mod.structObjs.size,
            ops: mod.opsPanel !== null,
          },
          interior: mod.interior ? {
            kind: mod.interior.kind,
            wx: Math.round(mod.interior.wx * 10) / 10,
            wz: Math.round(mod.interior.wz * 10) / 10,
            zone: mod.interior.kind === 'deck' ? 'deck' : mod.interior.wx > 35.4 ? 'bay' : mod.interior.wx > 21.4 ? 'corridor' : 'promenade',
            doors: mod.interior.doors.map((d) => Math.round(d.open * 100) / 100),
            props: mod.interior.group.children.length,
            windows: mod.interior.windows ?? 0,
            furn: mod.interior.furnMap?.size ?? 0,
            furnPanel: mod.furnPanel !== null,
          } : null,
          target: mod.target?.key ?? null,
          shipLoaded: mod.shipMesh !== null,
          lodRuns: mod.lodRuns,
          shipPos: [Math.round(mod.pos.x), Math.round(mod.pos.y), Math.round(mod.pos.z)],
          planets: mod.planets.map((pl) => {
            const m = pl.mesh.material as THREE.MeshStandardMaterial;
            const ud = pl.mesh.userData as { rimMat?: THREE.MeshBasicMaterial };
            const ang = (pl.radius ?? 1) / Math.max(1, mod.pos.distanceTo(pl.abs ?? mod.pos));
            return {
              ang: Math.round(ang * 1000) / 1000,
              pos: pl.abs ? [pl.abs.x, pl.abs.y, pl.abs.z] : null, radius: pl.radius ?? null, // probe: park near a planet
              normalScale: m?.isMeshStandardMaterial ? Math.round((m.normalScale?.x ?? 0) * 100) / 100 : null,
              hasNormal: !!(m?.isMeshStandardMaterial && m.normalMap),
              rimOpacity: ud.rimMat ? Math.round(ud.rimMat.opacity * 100) / 100 : null,
            };
          }),
          pirates: (() => {
            const sl = mod.shipLocal().clone();
            const ai: Record<string, number> = {};
            for (const p of mod.pirates) if (p.deadUntil === 0) ai[p.ai] = (ai[p.ai] ?? 0) + 1;
            return {
              alive: mod.pirates.filter((p) => p.deadUntil === 0).length,
              dead: mod.pirates.filter((p) => p.deadUntil > 0).length,
              ai,
              nearest: mod.pirates.filter((p) => p.deadUntil === 0)
                .map((p) => ({
                  key: p.key, tier: p.tier, ai: p.ai,
                  dist: Math.round(p.root.position.distanceTo(sl)),
                  pos: [Math.round(p.root.position.x + mod.off.x), Math.round(p.root.position.y + mod.off.y), Math.round(p.root.position.z + mod.off.z)],
                }))
                .sort((a, b) => a.dist - b.dist)[0] ?? null,
            };
          })(),
          nav: mod.navIdx >= 0 ? mod.nav[mod.navIdx].name : null,
          hyper: mod.hyper,
          cruise: Math.round(mod.cruise * 10) / 10,
          autoCruise: mod.autoCruise,
          freighters: mod.freighters.size,
          convoys: mod.convoys.size,
          patrols: mod.patrols.length,
          patrolsPromoted: mod.patrols.filter((p) => p.promoted).length,
          wings: mod.wings.length,
          wingShipsLive: mod.wings.reduce((n, w) => n + w.ships.filter(Boolean).length, 0),
          voidRaiders: mod.voidRaiders.size,
          faction: mod.faction,
          totalEnemies: TOTAL_ENEMIES,
          colliderGroups: mod.colliderGroups.length,
          off: [Math.round(mod.off.x), Math.round(mod.off.y), Math.round(mod.off.z)],
          bolts: mod.bolts.length,
          remotes: mod.remoteShips.size,
          remoteSmooth: (() => {
            const ts = [...mod.remoteShips.values()].map((r) => r.track);
            const denT = mod.pirates.map((q) => q.seatTrack).filter((t): t is RemoteTrack => !!t);
            const all = [...ts, ...denT];
            return {
              pilots: ts.length, dens: denT.length,
              delayMs: all.length ? Math.round((all.reduce((a, t) => a + t.delayS, 0) / all.length) * 1000) : 0,
              snaps: all.reduce((a, t) => a + t.stats.snaps, 0), extrapFrames: all.reduce((a, t) => a + t.stats.extrapFrames, 0),
              restamped: all.reduce((a, t) => a + t.stats.restamped, 0), handbacks: all.reduce((a, t) => a + t.stats.handbacks, 0),
            };
          })(),
          nearestPoi,
          landing: mod.active ? mod.nearLanding() : null,
          // The galactic layer (probe surface): charted worlds + planet meshes.
          galactic: mod.eligibleWorldRows().map((w) => {
            const spec = worldPlanetOf(w.worldId);
            return {
              worldId: w.worldId.toString(), name: w.name,
              dist: Math.round(Math.hypot(spec.x - mod.pos.x, spec.y - mod.pos.y, spec.z - mod.pos.z)),
              built: mod.worldPlanets.has(w.worldId.toString()),
            };
          }),
        };
      },
      /** Probe door: hyperwarp to a charted world by id (the map-click path). */
      warpWorld(worldIdStr: string): boolean {
        if (!mod.devGated() || !mod.active) return false;
        const w = mod.eligibleWorldRows().find((x) => x.worldId.toString() === worldIdStr);
        if (!w) return false;
        mod.warpToWorld(w.worldId, w.name);
        return true;
      },
      /** Teleport the ship (client-side; the next spaceMove streams it). */
      /** Pose the ship `dist` m from a target, facing it (loading-shot framing). */
      frameShot(tx: number, ty: number, tz: number, dist: number): boolean {
        if (!mod.devGated() || !mod.active) return false;
        const target = new THREE.Vector3(tx, ty, tz);
        const dir = new THREE.Vector3(0.45, 0.18, 1).normalize();
        mod.pos.copy(target).addScaledVector(dir, dist); // pos only — the frame loop rebases the floating origin (setting off directly whites the render)
        const m = new THREE.Matrix4().lookAt(mod.pos, target, new THREE.Vector3(0, 1, 0));
        mod.quat.setFromRotationMatrix(m);
        mod.vel.set(0, 0, 0); mod.throttle = 0;
        return true;
      },
      /** Realism wave (2026-09-25): the picture's telemetry — sky, sun, post profile, exposure, glare, dust, lights. */
      look(): object { return mod.lookState(); },
      /** Probe diagnostic: every mesh whose bounds come within `r` m of the camera (what fills the frame). */
      lookNear(r = 200): object[] {
        const cam = mod.cam();
        if (!cam || !mod.app) return [];
        mod.app.world.scene.updateMatrixWorld(true);
        const cp = cam.getWorldPosition(new THREE.Vector3());
        const out: Array<{ name: string; type: string; mat: string; parent: string; dist: number; radius: number }> = [];
        const sph = new THREE.Sphere();
        mod.app.world.scene.traverse((o) => {
          const m = o as THREE.Mesh;
          if (!m.isMesh || !m.visible || !m.geometry) return;
          if (!m.geometry.boundingSphere) m.geometry.computeBoundingSphere();
          const bs = (m as unknown as { boundingSphere?: THREE.Sphere | null }).boundingSphere ?? m.geometry.boundingSphere;
          if (!bs) return;
          sph.copy(bs).applyMatrix4(m.matrixWorld);
          const d = sph.center.distanceTo(cp) - sph.radius;
          if (d < r) out.push({ name: m.name, type: m.type, mat: (m.material as THREE.Material)?.name || (m.material as THREE.Material)?.type, parent: m.parent?.name ?? '', dist: Math.round(d), radius: Math.round(sph.radius) });
        });
        return out.sort((a, b) => a.dist - b.dist).slice(0, 25);
      },
      /** Aim the nose along a WORLD direction (probe framing — the rig slerps there); speed zeroed. */
      aim(dx: number, dy: number, dz: number): boolean {
        if (!mod.devGated() || !mod.active) return false;
        const d = new THREE.Vector3(dx, dy, dz).normalize();
        const up = Math.abs(d.y) > 0.95 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0);
        mod.quat.setFromRotationMatrix(new THREE.Matrix4().lookAt(new THREE.Vector3(), d, up));
        mod.vel.set(0, 0, 0); mod.throttle = 0; mod.autoCruise = false;
        return true;
      },
      /** Probe levers for the look: star / bg gain, trauma, a held velocity (dust + streaks without flying). */
      lookLever(k: string, v: number, v2 = 0, v3 = 0): boolean {
        if (!mod.devGated() || !mod.active) return false;
        if (k === 'starGain') setSpaceSkyGains(v, undefined);
        else if (k === 'bgGain') setSpaceSkyGains(undefined, v);
        else if (k === 'trauma') mod.trauma = v;
        else if (k === 'vel') mod.vel.set(v, v2, v3);
        else if (k === 'speed') mod.vel.set(0, 0, -1).applyQuaternion(mod.quat).multiplyScalar(v); // a held speed along the nose
        // the dust alone, HELD at fade `v` and `v2` m/s along the nose while the ship stays parked (v < 0 lets go) — a
        // probe reads one still view with the dust and without it. Held, not set for one frame: the scene pass is drawn
        // once per animation frame, so a grab in the lever's own task shows the frame before it.
        else if (k === 'dust') holdDust(v < 0 ? null : v, new THREE.Vector3(0, 0, -1).applyQuaternion(mod.quat).multiplyScalar(v2));
        else if (k === 'nav') setNavGain(v);
        else if (k === 'sunQuads') setStarQuadMode(v > 0 ? 'auto' : 'flat'); // THE WIDE-QUAD LAW's A/B: 0 = every star's quads in the image plane
        else if (k === 'sunGlowEdge') setStarGlowEdge(v > 0);                // THE WIDE STAR'S EDGE's A/B: 0 = the old edge fade
        else if (k === 'boom') {
          // A test detonation `v` m ahead of the nose, hull length v2 (default 900 m) — the vacuum anatomy on demand.
          const ahead = new THREE.Vector3(0, 0, -1).applyQuaternion(mod.quat).multiplyScalar(v);
          mod.fxShipDeath(mod.shipLocal().clone().add(ahead), v2 || 900, 0xffd8a0);
        } else return false;
        return true;
      },
      warp(x: number, y: number, z: number): boolean {
        if (!mod.devGated() || !mod.active) return false;
        mod.pos.set(x, y, z);
        mod.vel.set(0, 0, 0);
        return true;
      },
      fire(): boolean { if (!mod.devGated() || !mod.active) return false; mod.fireCool = 0; mod.cap = mod.stats.capacitor; mod.fire(); return true; },
      /** Run the full client kill path on a pirate slot (explosion → spaceKill). */
      killPirate(key: string): boolean {
        if (!mod.devGated() || !mod.active) return false;
        const p = mod.pirates.find((x) => x.key === key && x.deadUntil === 0);
        if (!p) return false;
        mod.killPirate(p);
        return true;
      },
      setHull(v: number): boolean { if (!mod.devGated() || !mod.active) return false; mod.hull = v; return true; },
      /** Space-kit probe: take a hit from a direction (the shield's ripple; scorch and venting once that arc is down). */
      wound(amount = 6, dx = 0, dy = 0, dz = -1): { shieldF: number; shieldR: number; hull: number } | false {
        if (!mod.devGated() || !mod.active) return false;
        mod.damagePlayer(amount, new THREE.Vector3(dx, dy, dz));
        return { shieldF: mod.shieldF, shieldR: mod.shieldR, hull: mod.hull };
      },
      /** Space-kit probe: where this system's un-pictured things stand (absolute metres). */
      kitPlaces(): object {
        const wid = mod.system.worldId;
        let world: { x: number; y: number; z: number; radius: number; built: boolean } | null = null;
        if (wid) { try { const sp = worldPlanetOf(BigInt(wid)); world = { x: sp.x, y: sp.y, z: sp.z, radius: sp.radius, built: mod.worldPlanets.has(wid) }; } catch { /* not a world id */ } }
        return {
          system: mod.system.id, pos: { x: mod.pos.x, y: mod.pos.y, z: mod.pos.z },
          belts: mod.system.id === 'sys0' ? SYS0_BELTS.map((b) => ({ key: b.key, x: b.pos[0], y: b.pos[1], z: b.pos[2], reach: mod.colliderGroups.find((c) => c.x === b.pos[0] && c.y === b.pos[1] && c.z === b.pos[2])?.reach ?? 0 })) : [],
          station: mod.altStation ? { ...mod.altStation } : null,
          world,
          rocks: mod.system.id === 'sys0' ? (mod.colliderGroups.find((c) => SYS0_BELTS.some((b) => b.pos[0] === c.x && b.pos[1] === c.y && b.pos[2] === c.z))?.list ?? []).slice().sort((p, q) => q.r - p.r).slice(0, 5).map((r) => ({ x: r.x, y: r.y, z: r.z, r: r.r })) : [],
          planets: mod.planets.map((p) => ({ name: p.name ?? '', x: p.abs?.x ?? 0, y: p.abs?.y ?? 0, z: p.abs?.z ?? 0, radius: p.radius ?? 0 })),
          anchors: mod.planetAnchors.length,
        };
      },
      board(kind: 'station' | 'deck'): boolean { if (!mod.devGated() || !mod.active) return false; mod.toggleInterior(kind); return mod.interior !== null; },
      walkTo(x: number, z: number): boolean { if (!mod.devGated() || !mod.interior) return false; mod.interior.wx = x; mod.interior.wz = z; return true; },
      lookInterior(wx: number, wz: number, yaw: number, pitch: number): boolean { if (!mod.devGated() || !mod.interior) return false; mod.interior.wx = wx; mod.interior.wz = wz; mod.interior.yaw = yaw; mod.interior.pitch = pitch; return true; },
      diagInterior(): object | null {
        if (!mod.interior || !mod.app) return null;
        const cam = atlas.localMesh?.children.find((c) => (c as THREE.Camera).isCamera) as THREE.PerspectiveCamera | undefined;
        const camW = cam?.getWorldPosition(new THREE.Vector3());
        let meshes = 0, lights = 0, firstSet = false;
        const first = new THREE.Vector3();
        mod.interior.group.traverse((o) => {
          if ((o as THREE.Mesh).isMesh) { meshes++; if (!firstSet) { firstSet = true; o.getWorldPosition(first); } }
          if ((o as THREE.Light).isLight) lights++;
        });
        let p: THREE.Object3D | null = mod.interior.group, inScene = false;
        while (p) { if (p === mod.app.world.scene) { inScene = true; break; } p = p.parent; }
        return {
          cam: camW ? [Math.round(camW.x), Math.round(camW.y), Math.round(camW.z)] : null,
          near: cam?.near, far: cam?.far,
          group: mod.interior.group.getWorldPosition(new THREE.Vector3()).toArray().map((v) => Math.round(v)),
          firstMesh: first.toArray().map((v) => Math.round(v)),
          meshes, lights, inScene,
          fog: mod.app.world.scene.fog
            ? { type: (mod.app.world.scene.fog as THREE.FogExp2).isFogExp2 ? 'exp2' : 'linear', d: (mod.app.world.scene.fog as THREE.FogExp2).density ?? null }
            : null,
          fogNode: !!(mod.app.world.scene as unknown as { fogNode?: unknown }).fogNode,
          envNode: !!(mod.app.world.scene as unknown as { environmentNode?: unknown }).environmentNode,
          bg: !!mod.app.world.scene.background || !!(mod.app.world.scene as unknown as { backgroundNode?: unknown }).backgroundNode,
        };
      },
      warpSystem(id: string): boolean {
        if (!mod.devGated() || !mod.active) return false;
        const sy = mod.galaxySystems().find((x) => x.id === id);
        if (!sy || sy.id === mod.system.id) return false;
        mod.switchSystem(sy); // dev: skip the tunnel
        // THE LOCATION LAW: move the SERVER row with the skip — the watcher
        // rebuilds to the row on its next seq bump (an undock landing a moment
        // after the swap pulled the probe straight back to the Commons).
        (atlas.conn?.reducers as unknown as { debugSpaceTeleport(a: { systemId: string; x: number; y: number; z: number }): Promise<void> } | undefined)
          ?.debugSpaceTeleport({ systemId: sy.id, x: mod.pos.x, y: mod.pos.y, z: mod.pos.z }).catch(() => { /* not an admin — the stream walks the row over */ });
        return true;
      },
      /** Probe path: the REAL hyper-warp (30 s tunnel), not the dev skip.
       *  THE TRAVEL LAW: the request goes to the server first; the tunnel
       *  builds on acceptance — poll `state().sysJump` for it. */
      warpTunnel(id: string): boolean {
        if (!mod.devGated() || !mod.active || mod.sysJump || id === mod.system.id) return false;
        mod.warpToSystem(id);
        return true;
      },
      /** Probe path: anchor a forced battle window so harnesses never wait
       *  out the 20-min wall-clock cycle. */
      forceBattle(on: boolean): boolean {
        if (!mod.devGated()) return false;
        mod.battleForce = on ? Date.now() / 1000 : 0;
        if (!on) mod.teardownBattle();
        return true;
      },
      /** Probe path: jump the forced battle's clock to 2 s before the finale. */
      forceBattleEnd(): boolean {
        if (!mod.devGated() || mod.battleForce <= 0) return false;
        mod.battleForce = Date.now() / 1000 - (BATTLE_LEN_S - BATTLE_END_S - 2);
        return true;
      },
      /** Probe path: park just off the current battle line. */
      warpBattle(): boolean {
        if (!mod.devGated() || !mod.active) return false;
        const w = mod.battleWindow();
        if (!w) return false;
        mod.hyper = ''; mod.hyperT = 0;
        mod.vel.set(0, 0, 0);
        mod.pos.set(w.poi.pos[0] + w.poi.radius * 1.6 + 900, w.poi.pos[1] + 400, w.poi.pos[2] + 600);
        return true;
      },
      /** Realism-wave probe framing: park `dist` m off a BUILT war's centre, broadside to the axis, nose on the centre. */
      lookBattle(dist = 20000): boolean {
        if (!mod.devGated() || !mod.active || !mod.battle) return false;
        const f = mod.battle.frame;
        mod.hyper = ''; mod.hyperT = 0; mod.vel.set(0, 0, 0); mod.throttle = 0;
        mod.pos.copy(f.c).addScaledVector(f.az, dist).add(new THREE.Vector3(0, dist * 0.18, 0));
        const d = new THREE.Vector3().copy(f.c).sub(mod.pos).normalize();
        mod.quat.setFromRotationMatrix(new THREE.Matrix4().lookAt(new THREE.Vector3(), d, new THREE.Vector3(0, 1, 0)));
        return true;
      },
      /**
       * Item 7 probe framing: break the first living, drawn capital of `side` apart (the local death path — a forced
       * window has no roster) and park `dist` m off it, broadside, nose on it. Dev-gated.
       */
      breakupView(side: 0 | 1 = 0, dist = 7000): object | null {
        if (!mod.devGated() || !mod.active || !mod.battle) return null;
        const b = mod.battle, win = mod.battleWindow();
        if (!win) return null;
        let k = -1;
        for (let i = 0; i < b.capN[side]; i++) if (!b.capDead[side][i] && !b.capGone[side][i] && b.capObjs[side][i] && b.capKin[side][i]) { k = i; break; }
        if (k < 0) return null;
        const p = b.capKin[side][k]!.p.clone();
        mod.hyper = ''; mod.hyperT = 0; mod.vel.set(0, 0, 0); mod.throttle = 0;
        mod.pos.copy(p).addScaledVector(b.frame.az, dist).add(new THREE.Vector3(0, dist * 0.22, 0));
        const d = new THREE.Vector3().copy(p).sub(mod.pos).normalize();
        mod.quat.setFromRotationMatrix(new THREE.Matrix4().lookAt(new THREE.Vector3(), d, new THREE.Vector3(0, 1, 0)));
        mod.warCapitalKilled(side, k, win, false, '☄ (probe) a capital breaks apart');
        return { side, k, at: [Math.round(p.x), Math.round(p.y), Math.round(p.z)], chunks: mod.breakups.at(-1)?.chunks.length ?? 0 };
      },
      /**
       * Item 7 probe lever: advance every live breakup by `seconds` of sim time NOW (the same fixed step, 1/60 s), so a
       * picture of a 20 s tumble does not wait out SwiftShader's multi-second frames and its liveness reload. Dev-gated.
       */
      breakupAdvance(seconds: number): number[] {
        if (!mod.devGated() || !mod.battle) return [];
        const n = Math.max(0, Math.min(60 * 60, Math.round(seconds * 60)));
        for (let i = 0; i < n; i++) mod.stepBreakups(1 / 60);
        return mod.breakups.flatMap((bk) => bk.chunks.map((c) => Math.round(c.age * 10) / 10));
      },
      /** Realism-wave probe framing: park at 2.2 radii from the first nebula site, nose on it. */
      lookNebula(): object | null {
        if (!mod.devGated() || !mod.active || mod.system.id !== 'sys0') return null;
        const n = POIS.find((p) => p.kind === 'nebula');
        if (!n) return null;
        mod.hyper = ''; mod.hyperT = 0; mod.vel.set(0, 0, 0); mod.throttle = 0;
        mod.pos.set(n.pos[0] + n.radius * 2.2, n.pos[1] + n.radius * 0.4, n.pos[2]);
        const d = new THREE.Vector3(n.pos[0], n.pos[1], n.pos[2]).sub(mod.pos).normalize();
        mod.quat.setFromRotationMatrix(new THREE.Matrix4().lookAt(new THREE.Vector3(), d, new THREE.Vector3(0, 1, 0)));
        return { key: n.key, radius: n.radius, volume: activePreset().nebulae };
      },
      /** space realism: park `radii` nebula radii from a nebula site (its key, its kind, or the first), nose on it; `side` picks the view. */
      lookNebulaSite(which?: string, radii = 2.2, side = 0): object | null {
        if (!mod.devGated() || !mod.active) return null;
        const sites = nebulaSitesFor(mod.system.id, mod.system.id === 'sys0' ? POIS : [], mod.natural?.makeup.skyNebula);
        const n = sites.find((x) => x.key === which || x.kind === which) ?? sites[0];
        if (!n) return null;
        const dirs: Array<[number, number, number]> = [[0.15, 0.2, 1], [1, 0.15, -0.2], [-0.4, -0.3, -1]];
        const from = new THREE.Vector3(...dirs[Math.abs(Math.round(side)) % dirs.length]).normalize();
        const site = new THREE.Vector3(n.pos[0], n.pos[1], n.pos[2]);
        mod.hyper = ''; mod.hyperT = 0; mod.vel.set(0, 0, 0); mod.throttle = 0;
        let seat = 'here';   // radii ≤ 0: turn to face it from where the ship is (a landmark seen from the lanes)
        if (radii > 0) {
          seat = 'side';
          mod.pos.copy(site).addScaledVector(from, n.radius * radii);
          // THE SECTOR EDGE: the integrator clamps the ship inside SECTOR_EDGE_M, and a sky landmark stands outside it —
          // a seat beyond the edge was pulled back and the ship left facing the system's planets (the first pictures of
          // The Ember Cradle, 2026-10-07). The only side a pilot can see a landmark from is the system's.
          const lim = SECTOR_EDGE_M * 0.98;
          if (mod.pos.length() > lim) {
            seat = 'system-side';
            mod.pos.copy(site).addScaledVector(site.clone().normalize(), -Math.min(n.radius * radii, site.length()));
            if (mod.pos.length() > lim) { seat = 'edge'; mod.pos.copy(site).normalize().multiplyScalar(lim); }
          }
          (atlas.conn?.reducers as unknown as { debugSpaceTeleport(a: { systemId: string; x: number; y: number; z: number }): Promise<void> } | undefined)
            ?.debugSpaceTeleport({ systemId: mod.system.id, x: mod.pos.x, y: mod.pos.y, z: mod.pos.z }).catch(() => { /* not an admin — the stream walks the row over */ });
        }
        const d = site.clone().sub(mod.pos);
        const distance = d.length();
        if (distance > 1) mod.quat.setFromRotationMatrix(new THREE.Matrix4().lookAt(new THREE.Vector3(), d.normalize(), new THREE.Vector3(0, 1, 0)));
        return { key: n.key, name: n.name, kind: n.kind, radius: n.radius, distance, seat, acrossDeg: distance > n.radius ? (2 * Math.asin(n.radius / distance) * 180) / Math.PI : 360, state: mod.nebulae?.state() ?? null };
      },
      /** Probe path: park inside the nearest asteroid field. */
      warpField(): boolean {
        if (!mod.devGated() || !mod.active || !mod.colliderGroups.length) return false;
        // A Commons BELT, never a world cluster's rocks: the seam is metered against SYS0_BELTS
        // (world-cluster colliders come first in the list and grow with every new account's planet).
        const g = mod.colliderGroups.find((c) => SYS0_BELTS.some((b) => b.pos[0] === c.x && b.pos[1] === c.y && b.pos[2] === c.z)) ?? mod.colliderGroups[0];
        mod.hyper = ''; mod.hyperT = 0;
        mod.vel.set(0, 0, 0);
        mod.pos.set(g.x + g.reach * 0.5, g.y, g.z);
        // THE LOCATION LAW: the row follows the dev jump (the seam is metered at the STREAMED position, 1.5 km a write).
        (atlas.conn?.reducers as unknown as { debugSpaceTeleport(a: { systemId: string; x: number; y: number; z: number }): Promise<void> } | undefined)
          ?.debugSpaceTeleport({ systemId: mod.system.id, x: mod.pos.x, y: mod.pos.y, z: mod.pos.z }).catch(() => { /* not an admin — the stream walks the row over */ });
        return true;
      },
      /** Probe path: press-and-hold U without a keyboard. */
      holdBeam(on: boolean): boolean {
        if (!mod.devGated()) return false;
        mod.beamProbeHold = on; // space campaign B: the lever holds the beam itself — a rebound beam key still works
        return true;
      },
      scanPing(): boolean { if (!mod.devGated() || !mod.active) return false; mod.scanCooldownUntil = 0; mod.scanPing(); return true; },
      toggleOps(): boolean { if (!mod.devGated() || !mod.active) return false; mod.toggleOpsPanel(); return true; },
      /** Probe path: absolute teleport (screenshot framing). */
      warpTo(x: number, y: number, z: number): boolean {
        if (!mod.devGated() || !mod.active) return false;
        mod.hyper = ''; mod.hyperT = 0;
        mod.vel.set(0, 0, 0);
        mod.pos.set(x, y, z);
        return true;
      },
      /** Probe path: a living capital's current ABSOLUTE seat. */
      capSeat(side: number, k: number): [number, number, number] | null {
        if (!mod.devGated() || !mod.battle) return null;
        const win = mod.battleWindow();
        if (!win) return null;
        if (!mod.battle.capP[side]?.[k]) return null;
        const v = mod.capPosNow(side, k, win.t, mod._v2);
        return [v.x, v.y, v.z];
      },
      /** Probe path (slice D): park `gap` m off the capital's outermost LATERAL hull point (its mesh-true sphere cloud in
       *  its live pose), nose on that point, the SERVER row moved with the ship (the record's origin check sees where the
       *  bolts really leave from). Returns the aim point (absolute) — the bolt meets the hull there. */
      standOff(side: number, k: number, gap: number): [number, number, number] | null {
        if (!mod.devGated() || !mod.active || !mod.battle) return null;
        const win = mod.battleWindow();
        if (!win || !mod.battle.capP[side]?.[k]) return null;
        const c = mod.capPosNow(side, k, win.t, new THREE.Vector3());
        const az = mod.battle.frame.az;
        const w = new THREE.Vector3();
        const cloud = mod.capCloud[side], kin = mod.battle.capKin[side][k];
        let best = -Infinity;
        if (cloud && kin) for (const s of cloud) { const v = new THREE.Vector3(s.x, s.y, s.z).applyQuaternion(kin.q); const e = v.x * az.x + v.z * az.z + s.r; if (e > best) { best = e; w.copy(v); } }
        const tgt = c.clone().add(w);
        const out = Math.max(0, best - (w.x * az.x + w.z * az.z)) + gap;
        mod.hyper = ''; mod.hyperT = 0; mod.vel.set(0, 0, 0); mod.throttle = 0; mod.autoCruise = false;
        mod.pos.set(tgt.x + az.x * out, tgt.y, tgt.z + az.z * out);
        mod.quat.setFromRotationMatrix(new THREE.Matrix4().lookAt(new THREE.Vector3(), new THREE.Vector3(-az.x, 0, -az.z), new THREE.Vector3(0, 1, 0)));
        (atlas.conn?.reducers as unknown as { debugSpaceTeleport(a: { systemId: string; x: number; y: number; z: number }): Promise<void> } | undefined)
          ?.debugSpaceTeleport({ systemId: mod.system.id, x: mod.pos.x, y: mod.pos.y, z: mod.pos.z }).catch(() => { /* not an admin */ });
        return [tgt.x, tgt.y, tgt.z];
      },
      /** Probe path (slice D): validated-fire telemetry + every capital's hull here vs on the record. */
      warAuthority(): object | null {
        if (!mod.devGated()) return null;
        const camp = mod.campaignRow();
        const b = mod.battle;
        const caps: Array<{ side: number; k: number; here: number; record: number | null; life: number | null; dead: boolean }> = [];
        if (camp && b) for (const side of [0, 1] as const) for (let k = 0; k < b.capN[side]; k++) {
          const r = warLifeRow(camp.campaignId, 'capital', side, k);
          caps.push({ side, k, here: b.capHp[side][k], record: r ? r.hp : null, life: r ? r.life : null, dead: b.capDead[side][k] });
        }
        return { campaignId: camp ? String(camp.campaignId) : null, cyc: camp ? String(camp.cyc) : null, pinned: camp ? warPinned(camp.campaignId) : false, line: camp ? mod.warAuthorityLine(camp) : '', telemetry: authorityTelemetry(), caps, cycDrawn: b?.cyc ?? null, salvos: mod.salvoCount, salvoBeams: b ? b.beams.filter((x) => x.m.scale.x >= 50).length : 0, capHits: mod.warCapHits, myBolts: mod.bolts.filter((x) => !x.hostile && !x.fromAlly).length };
      },
      /** Probe path: land one bolt-equivalent on the nearest living capital. */
      strikeCapital(dmg: number): boolean {
        if (!mod.devGated() || !mod.active || !mod.battle) return false;
        const win = mod.battleWindow();
        if (!win) return false;
        const b = mod.battle;
        for (let side = 0; side < 2; side++) {
          for (let k = 0; k < b.capN[side]; k++) {
            if (b.capDead[side][k] || b.capGone[side][k]) continue;
            const p = mod.capPosNow(side, k, win.t, mod._v2);
            return mod.warBoltHit(mod.local(p.x, p.y, p.z, mod._v3), dmg || 12);
          }
        }
        return false;
      },
      /** Probe path: land a hit on the nearest living skirmisher gunship. */
      strikeFighter(): boolean {
        if (!mod.devGated() || !mod.active || !mod.battle) return false;
        for (const q of mod.pirates) {
          if (q.ally || q.deadUntil > 0 || q.warSide === undefined || !q.key.startsWith('war')) continue;
          q.hp -= 12;
          if (q.hp <= 0) mod.killPirate(q, false);
          return true;
        }
        return false;
      },
      /** Probe path (the three-way fight, 2026-10-01): every living hull — its side, its family, its FSM state and what
       *  it hunts NOW (a hull key, 'player', or null) through the one target rule — and the bolt tally (`reset` first). */
      melee(reset = false): object {
        const t = mod.meleeTally;
        if (reset) { for (const r of [...t.hits, ...t.kills]) r.fill(0); t.onPlayer.fill(0); t.byPlayer.fill(0); t.byPlayerWar = 0; t.ownSide = 0; }
        const family = (k: string): string => k.startsWith('patrol') ? 'patrol' : k.startsWith('war') ? (/:fri/.test(k) ? 'frigate' : /:cor/.test(k) ? 'corvette' : 'war') : k.startsWith('probe:') ? 'raider' : k.startsWith('raid') ? 'lane' : k.startsWith('wing') ? 'wing' : k.startsWith('bounty') ? 'hunter' : 'den';
        const live = mod.pirates.filter((p) => p.deadUntil === 0);
        const hulls = live.map((p) => {
          const q = mod.quarryOf(p);
          let quarry: string | null = null, qSide: number | null = null;
          if (q.live) {
            if (q.player) quarry = 'player';
            else {
              const o = live.find((x) => x !== p && x.root.position.distanceToSquared(q.pos) < 1e-6);
              quarry = o ? o.key : 'remote'; qSide = o ? mod.sideOf(o) : null;
            }
          }
          return { key: p.key, side: mod.sideOf(p), family: family(p.key), ai: p.ai, hp: Math.round(p.hp), dist: Math.round(p.root.position.distanceTo(mod._ship)), quarry, qSide, qd: q.live ? Math.round(p.root.position.distanceTo(q.pos)) : -1,
            pos: [Math.round(p.root.position.x + mod.off.x), Math.round(p.root.position.y + mod.off.y), Math.round(p.root.position.z + mod.off.z)] };
        });
        const bolts = { total: mod.bolts.length, war: mod.bolts.filter((x) => x.side !== undefined).length, hostile: mod.bolts.filter((x) => x.hostile).length };
        return { hulls, bolts, tally: JSON.parse(JSON.stringify(t)), faction: mod.faction, simTicks: mod.simTicks, simScale: mod.simScale, ship: [Math.round(mod.pos.x), Math.round(mod.pos.y), Math.round(mod.pos.z)] };
      },
      /** Probe path (THE SIM-TIME LAW): run `k` (1–12) fixed steps per FixedSim step; returns the scale now in force. */
      simScale(k: number): number {
        if (!mod.devGated()) return mod.simScale;
        mod.simScale = Math.max(1, Math.min(12, Math.round(Number.isFinite(k) ? k : 1)));
        return mod.simScale;
      },
      /** Probe path: a pirate raider (no den row, a local respawn) at ABSOLUTE (x, y, z); its key, or null. */
      spawnRaider(x: number, y: number, z: number, tier: 1 | 2 | 3 = 2): string | null {
        if (!mod.devGated() || !mod.active) return null;
        const key = `probe:raider${mod.probeRaiders++}`;
        return mod.spawnHostile(key, tier, x, y, z) ? key : null;
      },
      warpToPoi(key: string): boolean {
        if (!mod.devGated() || !mod.active) return false;
        const p = POIS.find((x) => x.key === key);
        if (!p) return false;
        mod.hyper = ''; mod.hyperT = 0;
        mod.vel.set(0, 0, 0);
        mod.pos.set(p.pos[0] + p.radius + 500, p.pos[1], p.pos[2]);
        // THE LOCATION LAW: spaceMove caps a write at 1.5 km, so a dev jump
        // never reaches the server row and docking (judged on the STREAMED
        // position) is refused hundreds of km out — move the row with the ship.
        (atlas.conn?.reducers as unknown as { debugSpaceTeleport(a: { systemId: string; x: number; y: number; z: number }): Promise<void> } | undefined)
          ?.debugSpaceTeleport({ systemId: mod.system.id, x: mod.pos.x, y: mod.pos.y, z: mod.pos.z }).catch(() => { /* not an admin — the stream catches up in flight */ });
        return true;
      },
      eject(): boolean { if (!mod.devGated() || !mod.active) return false; mod.eject(); return true; },
      /** THE CREW LAW levers: take the nearest Sovereign's turret (or a named captain's), give it up. */
      boardTurret(hex?: string): boolean {
        if (!mod.devGated() || !mod.active) return false;
        if (!hex) { mod.toggleTurretSeat(); return true; }
        (atlas.conn?.reducers as unknown as { boardTurret(a: { captain: Identity }): Promise<void> } | undefined)?.boardTurret({ captain: new Identity(hex) }).catch(() => { /* refused */ });
        return true;
      },
      leaveTurret(): boolean { if (!mod.devGated() || !mod.active || !mod.crew) return false; mod.toggleTurretSeat(); return true; },
      /** The lit-material triage: 'basic' | 'emissive' | 'standard' | 'clear' — a 30 m cube 70 m ahead. */
      litProbe(kind: 'basic' | 'emissive' | 'standard' | 'clear'): boolean { if (!mod.devGated() || !mod.active) return false; return mod.litProbe(kind); },
      setPower(p: PowerPreset): boolean { if (!mod.devGated() || !mod.active) return false; mod.setPower(p); return true; },
      setThrottle(v: number): boolean { if (!mod.devGated() || !mod.active) return false; mod.throttle = THREE.MathUtils.clamp(v, 0, 1); return true; },
      cycleTarget(): string | null { if (!mod.devGated() || !mod.active) return null; mod.cycleTarget(); return mod.target?.key ?? null; },
      land(): boolean { if (!mod.devGated() || !mod.active) return false; mod.land(); return true; },
      /** Probe door: ADMIN server launch + client adoption in one call — lets
       *  a headless probe reach the sector WITHOUT streaming the commons
       *  (the full-world boot OOMs swiftshader; the sector alone is light). */
      launch(): boolean {
        if (!mod.devGated() || mod.active) return false;
        const conn = atlas.conn;
        if (!conn) return false;
        void (async () => {
          try { await conn.reducers.debugEnterSpace({}); } catch (e) { mod.lastError = `launch: ${String((e as Error).message ?? e)}`; return; }
          let spaceId: bigint | null = null;
          for (let i = 0; i < 40 && spaceId === null; i++) {
            for (const w of conn.db.world.iter()) if (w.kind === 'space') { spaceId = w.worldId; break; }
            if (spaceId === null) await new Promise((r) => setTimeout(r, 250));
          }
          if (spaceId === null) { mod.lastError = 'launch: no space world replicated'; return; }
          const me = conn.identity ? conn.db.playerState.identity.find(conn.identity) : null;
          bus.emit('atlas:world-adopted', { worldId: spaceId, x: me?.posX ?? 0, z: me?.posZ ?? 0 });
        })();
        return true;
      },
      /** Cycle nav + engage the hyperdrive (probe path for the 100× sector). */
      setChassis(key: string): boolean { if (!mod.devGated() || !mod.active || !CHASSIS[key]) return false; const changed = key !== mod.chassisKey; mod.chassisKey = key; mod.stats = shipStats(key, []); if (changed) mod.swapPlayerHull(); mod.applyCamZoom(); return true; },
      cockpit(on?: boolean): boolean { if (!mod.devGated() || !mod.active) return false; const want = on ?? !mod.cockpitView; mod.camBack = want ? ZOOM_MIN + 0.5 : chaseFor(CHASSIS[mod.chassisKey]); mod.applyCamZoom(); return mod.cockpitView; },
      /** Scroll-zoom probe: set the camera back-distance directly + read the state. */
      zoom(back?: number): { camBack: number; cockpit: boolean; chassis: string; sovereign3d: boolean; camZ: number } {
        if (typeof back === 'number' && mod.active && !mod.interior) { mod.camBack = Math.max(ZOOM_MIN, Math.min(170, back)); mod.applyCamZoom(); }
        const cam = mod.cam();
        return { camBack: mod.camBack, cockpit: mod.cockpitView, chassis: mod.chassisKey, sovereign3d: !!mod.sovereignCockpit?.visible, camZ: cam ? Math.round(cam.position.z * 10) / 10 : NaN };
      },
      navTo(name: string): boolean {
        if (!mod.devGated() || !mod.active) return false;
        if (mod.nav.length === 0) mod.nav = mod.navFor();
        const idx = mod.nav.findIndex((n) => n.name === name);
        if (idx < 0) return false;
        mod.navIdx = idx;
        mod.toggleHyper();
        return true;
      },
      /** Orientation-QA camera: park the chase cam at an offset looking at the
       *  ship (a clean three-quarter view instead of a backlit silhouette).
       *  Pass 0,0,0 to restore the chase position. */
      qaCam(dx: number, dy: number, dz: number): boolean {
        if (!mod.devGated() || !mod.active) return false;
        const rig = atlas.localMesh;
        const cam = rig?.children.find((c) => (c as THREE.Camera).isCamera) as THREE.PerspectiveCamera | undefined;
        if (!cam) return false;
        if (dx === 0 && dy === 0 && dz === 0) {
          mod.applyCamZoom(); // the hull's own chase (chaseFor) — it was a fixed (0, 4.2, 15)
        } else {
          cam.position.set(dx, dy, dz);
          cam.lookAt(rig!.getWorldPosition(new THREE.Vector3()));
        }
        return true;
      },
      /** Live model-yaw tuner: rotate every ship hull group (orientation QA). */
      setModelYaw(rad: number): boolean {
        if (!mod.devGated() || !mod.active) return false;
        const setYaw = (group: THREE.Object3D | null): void => {
          const hull = group?.children[0];
          if (hull && (hull as THREE.Group).isGroup) hull.rotation.y = rad;
        };
        setYaw(mod.shipMesh);
        for (const p of mod.pirates) setYaw(p.root);
        return true;
      },
    };
  }

  // ── Activation ───────────────────────────────────────────────────────────
  private async activate(): Promise<void> {
    if (this.active || !this.app) return;
    this.active = true;
    this.bindRemoteFeed();
    atlas.spaceFlight = true;
    const groundHint = document.getElementById('hint');
    if (groundHint) groundHint.style.display = 'none'; // WASD/ground controls — not this cockpit's (ladder v2)
    const scene = this.app.world.scene;

    // Space is fogless and black — save the atmosphere's fog object (the
    // AtmosphereModule MUTATES one owned FogExp2; restoring the same object on
    // exit keeps its law intact — the WorldScriptRuntime pattern).
    // A system swap keeps space's null fog (deactivate skipped the restore) —
    // saving again here captured NULL and the Commons fog was gone for the
    // session after the first hyperwarp (2026-09-12).
    if (!this.systemSwapping) this.savedFog = scene.fog;
    scene.fog = null;
    // ── THE VACUUM (realism wave 2026-09-25, report §9 R1): the post chain drops its atmosphere (aerial, the
    // height fog that sat at its densest after every rebase, god rays aimed at the GROUND sun, AO/SSGI/SSR/DoF),
    // bloom at 1.0, flares at 4, clamped eye adaptation, a dither; the ground sky stops re-baking LUTs / PMREM
    // a Commons nobody sees; the ground's IBL is parked (updateSun installs the space one). Real entry only —
    // a hyperwarp swap stays in space (never flip global render state on a swap).
    if (!this.systemSwapping) {
      (this.app.getModule('Post') as { setSpaceProfile?(on: boolean): void } | null)?.setSpaceProfile?.(true);
      setSkyBakePaused(true);
      this.savedEnv = scene.environment;
    }

    this.root = new THREE.Group();
    this.root.name = 'space-sector';
    scene.add(this.root);

    // Floating origin FIRST: the offset anchors on the spawn point so every
    // build below can place itself in offset-local coordinates.
    // THE LOCATION LAW: render the system the server says we are in. A
    // client that remembers another sky (a reload mid-course, a sweep that
    // landed us while we were away) rebuilds to the row.
    if (!this.systemSwapping) {
      const loc = this.myLocation();
      if (loc && loc.systemId !== this.system.id) {
        const sy = this.galaxySystems().find((s) => s.id === loc.systemId);
        if (sy) { this.system = sy; this.toast(`Navigation resynced — ${sy.name}`); }
      }
      if (loc) this.locSeq = loc.seq;
    }
    const me0 = atlas.conn?.identity ? atlas.conn.db.playerState.identity.find(atlas.conn.identity) : null;
    this.pos.set(me0?.posX ?? 0, me0?.posY ?? 0, me0?.posZ ?? 0);
    if (this.system.id !== 'sys0') {
      // Alt-system entry: outside the star's corona, or on a charted world's
      // approach (the server is system-blind — the stream walks the row over).
      const spec2 = systemSector(this.system);
      if (this.system.worldId) {
        const wp = worldPlanetOf(BigInt(this.system.worldId));
        this.pos.set(wp.x + wp.radius * 2.4, wp.y, wp.z);
      } else {
        this.pos.set(spec2.star.radius * 2.4, 0, 0);
      }
    }
    this.off.copy(this.pos);
    this.followers = [];

    // ── THE SKY (realism wave 2026-09-25, report §9 R2): the ground's own Milky Way photo + 114 k-star catalogue
    // as the BACKGROUND node, rotated per system, no scintillation, HDR stars that bloom (lib/space/space_sky.ts).
    // The legacy void shell + two star layers stand only until those assets land (or under `atlas:space-sky =
    // legacy`, the fallback lever). The background is handed over at the real entry and kept across a swap.
    setSpaceSkySystem(this.system.id);
    const legacySkyOnly = (() => { try { return localStorage.getItem('atlas:space-sky') === 'legacy'; } catch { return false; } })() || !this.spaceFlag('spaceSky');
    const skyNode = legacySkyOnly ? null : spaceBackgroundNode();
    this.legacySky = [];
    if (!skyNode) this.buildLegacySky();
    this.lookLog(`activate gen=${this.actGen} swap=${this.systemSwapping} sky=${skyNode ? 'node' : legacySkyOnly ? 'legacy-only' : 'legacy-until-load'}`);
    if (skyNode) { setSpaceBackground(scene as never, skyNode); this.mountStreaks(); }
    else if (!legacySkyOnly) {
      const genS = this.actGen;
      void loadSpaceSkyAssets(this.app.renderer.native, nightSkyAssets(), activePreset().memoryBudgetMb >= 256 ? 64 : 32).then(() => {
        if (genS !== this.actGen || !this.active || !this.app) return; // left space (or swapped) while the sky loaded
        const node = spaceBackgroundNode();
        if (!node) return;
        setSpaceBackground(this.app.world.scene as never, node);
        this.lookLog(`bg-set (late) gen=${this.actGen}`);
        for (const o of this.legacySky) o.visible = false; // meshes, not lights — a visibility flip is free
        this.mountStreaks();
      });
    }

    // ── ONE SUN (report §9 R1/R3 — the sun fix): the key light's TARGET is seated under the root and the light
    // rides the camera along the sun direction (updateSun), so its world direction is exactly the direction the
    // disc, the O'Neil shells and the planets use; before, the target sat at the world origin and every sector
    // and FP64 rebase swung the terminator away from the glow. The 1.3 "ambient" slot becomes a starlight floor
    // and the hemisphere slot becomes PLANETSHINE (same light kinds — the light-set identity law). The visible
    // sun is a star now: an HDR limb-darkened disc, a corona and an eclipse-gated glare (lib/space/space_sun.ts).
    const sun = new THREE.DirectionalLight(0xfff2dc, SPACE_SUN_INTENSITY);
    const sunTarget = new THREE.Object3D();
    sunTarget.name = 'space-sun-target';
    sun.target = sunTarget;
    // shader gaps R4 — SPACE LIGHT: one controller per sector entry; with eclipses on, the key light carries THE HOOK — a
    // custom `shadow.shadowNode` (three r185 multiplies the light by it for every receiving mesh and renders NO shadow map):
    // each lit hull, station and rock takes its own receiver-side sun visibility. Set before the first frame (castShadow is
    // part of the lights' cache key — the light-set law: never flipped later).
    this.spaceLight = this.spaceUnlit() || !(this.slFlag('eclipseShadows') || this.slFlag('planetRings') || this.slFlag('orbitalClouds')) ? null : new SpaceLight();
    this.commonsCloudShell = null; this.slEnvVis = 1; this.slRecvAt = 0;
    if (this.spaceLight && (this.slFlag('eclipseShadows') || this.slFlag('planetRings'))) { // a ring shades hulls too
      sun.castShadow = true;
      (sun.shadow as unknown as { shadowNode: unknown }).shadowNode = this.spaceLight.hookNode();
    }
    this.installSpaceLightLevers();
    this.installBlackHoleLever(); // shader gaps: black hole
    this.sunLight = sun;
    this.sunTarget = sunTarget;
    this.starlight = new THREE.AmbientLight(0x223044, SPACE_STARLIGHT);
    this.planetshine = new THREE.HemisphereLight(0x8fb3dd, 0x05070b, 0);
    this.root.add(sun, sunTarget, this.starlight, this.planetshine);
    this.root.add(sunRig().group);
    this.envDir.set(0, 0, 0); // force the IBL lobe onto this system's sun at the first updateSun
    this.applySunLook();
    // R5: the batched vacuum FX + its pooled explosion lights (created NOW with the sector's light set — the
    // light-set identity law: parked at intensity 0, never flipped). 0 on minimal, 2 to med, 4 on high / ultra.
    // The live budget grows with the tier (768 / 1024 / 1536) and a full batch evicts by significance
    // (space_look.fxBudget / fxEvictIndex): a forced 10 v 10 dropped 148 spawns, the newest first, at a flat 768.
    this.fx = new SpaceFx(activeTier() === 'minimal' ? 0 : activeTier() === 'high' || activeTier() === 'ultra' ? 4 : 2, fxBudget(activeTier()));
    this.root.add(this.fx.group);
    // R7: velocity dust — a few hundred world-fixed motes in a 60 m box wrapped around the ship (session-shared).
    const dust = motionDust(activeTier() === 'minimal' || activeTier() === 'potato' ? 150 : 300);
    this.root.add(dust);
    this.followers.push({ obj: dust, offset: null });

    // The sector's sun OWNS the lighting. The commons day/night rig (sun,
    // hemisphere, ambient, street lamps) stays in the scene and competes for
    // the engine's light slots — launch at commons NIGHT and every standard
    // material in space rendered BLACK (probe finding 2026-07-27: lit planet
    // dark, own ship invisible). Hide every non-sector light, restore on exit.
    if (!this.systemSwapping) {
      // (On a swap the lights are ALREADY hidden and their saved
      // visibilities must survive for the eventual real exit — no re-scan,
      // no double light-set recompile.)
      this.hiddenLights = [];
      scene.traverse((o) => {
        if (!(o as THREE.Light).isLight) return;
        let p: THREE.Object3D | null = o;
        let ours = false;
        while (p) { if (p === this.root) { ours = true; break; } p = p.parent; }
        if (!ours) { this.hiddenLights.push({ obj: o, visible: o.visible }); o.visible = false; }
      });
    }

    // The commons volumetric cloud deck sits right at launch altitude (probe
    // 2026-07-27: white cloud banks filled the sector). Coverage is re-driven
    // per tick by the weather provider, so zero the DENSITY knob instead —
    // zero density short-circuits the whole cloud march.
    const clouds = this.app.getModule('Clouds') as
      | {
          renderer?: { setDensityScale(v: number): void; uniforms?: { densityScale?: { value: number } } };
          lowRenderer?: { setDensityScale(v: number): void } | null;
          setMeshFieldEnabled?(on: boolean): void;
        }
      | null;
    if (clouds?.renderer) {
      this.savedCloudDensity = clouds.renderer.uniforms?.densityScale?.value ?? 1;
      clouds.renderer.setDensityScale(0);
    }
    // NO CLOUDS IN SPACE (2026-09-10): the MESH cloud field (the primary
    // renderer when configured) and the low deck are separate objects from
    // the density knob above — they kept drawing puffs over the sector, and
    // a graphics-menu toggle re-armed them mid-flight. Off here; PostModule
    // applies the same `cloudsAllowed` rule on every rebuild; deactivate
    // restores whatever the TIER wants (the player may have toggled aloft).
    clouds?.lowRenderer?.setDensityScale(0);
    clouds?.setMeshFieldEnabled?.(false);

    // Planets: Higgsfield equirect textures on lit spheres + additive rim
    // atmosphere shells (fresnel-read at a fraction of the shader cost).
    const texLoader = new THREE.TextureLoader();
    const legacyPlanets = this.spaceUnlit();
    for (const p of (this.system.id === 'sys0' ? PLANETS : [])) {
      // Realism wave R4 (phases 1, 2, 4, 5): a planet lights ITSELF from its own sun direction — the wrapped
      // terminator, the ocean glint, the Commons' night lights, the in-scatter veil in its own hue — and its rim
      // glows on the day limb only (lib/space/planet_look.ts). The legacy standard material behind the lever.
      // shader gaps R4 — the planet as a RECEIVER (its eclipse set, Vorune's ring, the Commons' cloud shell)
      const slSet = this.spaceLight && !legacyPlanets ? new EclipseSet(p.name) : null;
      const slRing = slSet && this.slFlag('planetRings') ? sys0RingFor(p.name) : null;
      const slClouds = slSet && p.name === 'The Commons' && this.slFlag('orbitalClouds') ? (this.spaceLight!.clouds ??= new OrbitalCloudBaker()) : null;
      const slLight = slSet ? { set: slSet, R: p.radius, ring: !!slRing, clouds: slClouds } : null;
      const look: PlanetLook | null = legacyPlanets ? null : planetMaterial({
        rim: p.rim, atmosphere: PLANET_AIR[p.name] ?? 0.6, ocean: /ocean|ice/.test(p.tex), sunDir: SpaceModule.SUN_DIR, light: slLight,
      });
      const mat = look ? look.material : new THREE.MeshStandardMaterial({ color: 0x6a7488, roughness: 0.92, metalness: 0 });
      this.texState.set(p.name, 'loading');
      const gen0 = this.actGen;
      texLoader.load(`${ASSET_STORE_BASE}/${p.tex}`, (t) => {
        if (gen0 !== this.actGen) { t.dispose(); return; } // sector died mid-flight
        t.colorSpace = THREE.SRGBColorSpace;
        t.anisotropy = 8; // 4K equirect on a huge sphere — keep the limb crisp
        if (look) { look.setMap(t); this.lookTex.push(t); } // a node-held face is this activation's to free (the root's sweep reads classic slots only)
        else { const sm = mat as THREE.MeshStandardMaterial; sm.map = t; sm.color.set(0xffffff); sm.needsUpdate = true; }
        this.texState.set(p.name, 'ok');
      }, undefined, () => this.texState.set(p.name, 'failed'));
      // Bump pass: the derived normal map (linear space). Missing = flat — fine.
      if (p.normal) {
        texLoader.load(`${ASSET_STORE_BASE}/${p.normal}`, (t) => {
          if (gen0 !== this.actGen) { t.dispose(); return; }
          if (look) { look.setNormal(t, p.bump); this.lookTex.push(t); } // (as the face: freed with the activation)
          else { const sm = mat as THREE.MeshStandardMaterial; sm.normalMap = t; sm.normalScale = new THREE.Vector2(p.bump, p.bump); sm.needsUpdate = true; }
          this.texState.set(p.name, `${this.texState.get(p.name) ?? 'ok'}+n`);
        }, undefined, () => { /* flat is a legal fallback */ });
      }
      const mesh = new THREE.Mesh(new THREE.SphereGeometry(p.radius, 96, 64), mat);
      if (look) { mesh.userData.planetLook = look; if (p.name === 'The Commons') this.commonsLook = look; }
      const rim = new THREE.Mesh(
        new THREE.SphereGeometry(p.radius * 1.035, 64, 48),
        look ? planetRimMaterial(p.rim, look.sunDir, 0.16, slLight) // shader gaps R4: the day limb only where the sun reaches it
          : legacyRimMaterial(p.rim, 0.16), // a node material on its body layer (THE BODY LAYER LAW, space_light_look.ts)
      );
      const pg = new THREE.Group();
      pg.add(mesh, rim);
      const atmoHue = new THREE.Color(p.rim).getHSL({ h: 0, s: 0, l: 0 }).h;
      this.attachAtmosphere(pg, p.radius, atmoHue, SpaceModule.SUN_DIR, p.name === 'The Commons', slSet); // the planet's own hue (R4.5); the Commons wears its own sky (§7.1)
      if (slSet && this.spaceLight) this.attachSpaceLight(p.name, p.pos, p.radius, pg, mesh, rim, slSet, slRing, slClouds); // shader gaps R4
      if (p.name === 'The Commons') this.commonsAtmo = { group: pg, radius: p.radius, hue: atmoHue }; // space P3 (2026-09-27): the shell A/B lever
      mesh.userData.baseBump = p.bump;                 // for the distance normal-map fade (anti-speckle)
      mesh.userData.rimMat = rim.material;             // for the distance rim fade (anti-jagged-halo)
      mesh.userData.rimBase = 0.16;
      pg.position.copy(this.local(p.pos[0], p.pos[1], p.pos[2], this._v1));
      this.root.add(pg);
      this.planets.push({ mesh, spin: p.spin, abs: new THREE.Vector3(p.pos[0], p.pos[1], p.pos[2]), radius: p.radius, name: p.name, reach: slRing?.rOut });
      this.planetAnchors.push({ group: pg, abs: new THREE.Vector3(p.pos[0], p.pos[1], p.pos[2]) });
    }

    // Charted user worlds hang as planets too (synced live in frame()).
    if (this.system.id === 'sys0') this.syncWorldPlanets();

    // natural objects: what this system is made of (its star's kind, typed planets, moons, small bodies, its sky) and the
    // views that draw it; `atlas:natural` = 'off' keeps the sector as it was.
    this.natural?.dispose(); this.natural = null;
    if (naturalOn() && !this.spaceUnlit()) {
      try { this.natural = new NaturalController(this.naturalEnv(), this.system); } catch (e) { console.warn('[natural] the layer failed to start', e); this.natural = null; }
    }
    (window as unknown as Record<string, unknown>).__natural = Object.assign(() => this.natural?.state() ?? null, { hosts: () => Object.fromEntries(showpieceHosts()), makeup: () => this.natural?.makeup ?? null, faces: () => ({ asked: [...this.planetFaces.asked], loaded: [...this.planetFaces.loaded], fellBack: [...this.planetFaces.fellBack] }) });
    // space realism: nebulae — every nebula of this system (its nebula POIs as the kinds they are + the sky's distant
    // landmarks) is a baked volume the controller bakes on idle frames and fades in; `atlas:nebula` = 'legacy' keeps the
    // old glow volume and sprite puffs, 'off' draws none.
    const nebulaMode = nebulaModeOf((() => { try { return localStorage.getItem('atlas:nebula'); } catch { return null; } })());
    this.nebulae?.dispose(); this.nebulae = null;
    if (nebulaMode === 'volume') {
      this.nebulae = new NebulaController({
        parent: () => this.root,
        scene: () => (this.app?.world.scene as unknown as THREE.Scene | undefined) ?? null,
        renderer: () => this.app?.renderer.native ?? null,
        camera: () => this.holeCamera()?.cam ?? null,
        heightPx: () => (this.app?.renderer.native as unknown as { domElement?: HTMLCanvasElement } | undefined)?.domElement?.height ?? 1080,
        shipAbs: () => [this.pos.x, this.pos.y, this.pos.z],
        anchor: (group, abs) => { this.planetAnchors.push({ group, abs }); },
        unanchor: (group) => { this.planetAnchors = this.planetAnchors.filter((a) => a.group !== group); },
      });
      const nebulaSites = nebulaSitesFor(this.system.id, this.system.id === 'sys0' ? POIS : [], this.natural?.makeup.skyNebula); // natural objects: a showpiece system names its own nebula's kind
      // (a headset marches two eyes at its own resolution: the low level while a session presents)
      const inHeadset = (this.app?.renderer.native as unknown as { xr?: { isPresenting?: boolean } } | undefined)?.xr?.isPresenting === true;
      this.nebulae.build(nebulaSites, nebulaLevel(activePreset().nebulae === true && !inHeadset, activeTier() === 'ultra'));
      stage4Stats.nebulae += nebulaSites.length;
      (window as unknown as Record<string, unknown>).__nebula = Object.assign(() => this.nebulae?.state() ?? null, { finishNow: () => { this.nebulae?.finishNow(); return this.nebulae?.state() ?? null; }, showNow: () => { this.nebulae?.showNow(); return this.nebulae?.state() ?? null; }, setVisible: (on: boolean) => { this.nebulae?.setVisible(on); return on; } });
    }
    // Immediate sprite-only POIs (no decode wait): the legacy nebulae + the comet.
    for (const poi of (this.system.id === 'sys0' ? POIS : [])) {
      if (poi.kind === 'nebula') {
        if (nebulaMode !== 'legacy') continue;
        // Stage 4: a raymarched emissive volume over the site (anchored like a planet so it follows the re-basing).
        if (activePreset().nebulae) {
          try {
            // Realism wave R9: dark ridged DUST lanes that dim the stars behind, lit by an embedded star (v2 — engine NebulaMaterial `dust`).
            const { material } = createNebulaMaterial({ steps: 20, coreColor: [1.0, 0.55, 0.4], envelopeColor: [0.3, 0.45, 1.0], density: 0.9, absorption: 1.6, scale: 2.2, intensity: 0.9, ...(this.spaceUnlit() ? {} : { dust: 0.9, starColor: [2.6, 2.2, 1.8] as [number, number, number] }) });
            const vol = new THREE.Mesh(new THREE.SphereGeometry(1, 32, 24), material);
            vol.scale.setScalar(poi.radius * 1.15);
            vol.name = `nebula-volume:${poi.key}`;
            vol.renderOrder = -2;
            const ng = new THREE.Group();
            ng.add(vol);
            ng.position.copy(this.local(poi.pos[0], poi.pos[1], poi.pos[2], this._v1));
            this.root.add(ng);
            this.planetAnchors.push({ group: ng, abs: new THREE.Vector3(poi.pos[0], poi.pos[1], poi.pos[2]) });
            stage4Stats.nebulae++;
          } catch { /* best-effort */ }
        }
        for (const puff of nebulaPuffs(poi)) {
          const hueColor = new THREE.Color().setHSL(0.62 + puff.hue * 0.22, 0.65, 0.55); // teal→violet
          const sp = this.spriteGlow(hueColor.getHex(), puff.scale);
          (sp.material as THREE.SpriteMaterial).opacity = 0.12;
          sp.position.copy(this.local(puff.pos[0], puff.pos[1], puff.pos[2], this._v1));
          this.root.add(sp);
        }
      } else if (poi.kind === 'comet' && !this.natural?.drawsComets) { // natural objects: a real comet (nucleus, coma, two tails) replaces the sprite train
        const core = new THREE.Mesh(
          new THREE.IcosahedronGeometry(120, 1),
          new THREE.MeshStandardMaterial({ color: 0xcfe6f2, roughness: 0.6, metalness: 0.05 }),
        );
        core.position.copy(this.local(poi.pos[0], poi.pos[1], poi.pos[2], this._v1));
        this.root.add(core);
        this.spinners.push({ obj: core, spin: 0.15 });
        // The tail: a fading train of glow sprites streaming anti-sunward.
        const tailDir = new THREE.Vector3(-0.55, -0.8, -0.3).normalize();
        for (let t = 1; t <= 5; t++) {
          const seg = this.spriteGlow(0xaee6ff, 420 * t);
          (seg.material as THREE.SpriteMaterial).opacity = 0.16 / t;
          seg.position.copy(core.position).addScaledVector(tailDir, 600 * t * t);
          this.root.add(seg);
        }
      }
    }

    // ── Critical state FIRST (probe finding 2026-07-24): the serial GLB
    // awaits below can take 30+ s on slow decode paths — flight, landing
    // (session sub!), HUD and PIRATES must not wait on cosmetics.
    this.stage = 'spawn';
    // (pos + off were anchored at the top of activate — before any build.)
    this.vel.set(0, 0, 0);
    this.quat.identity();
    this.throttle = 0;
    this.navIdx = -1;
    this.nav = this.navFor();
    this.hyper = '';
    this.hyperT = 0;
    this.refreshFleet();
    this.hull = this.stats.hp;
    this.shieldF = this.arcMax();
    this.shieldR = this.arcMax();
    this.cap = this.stats.capacitor;
    this.boosterE = BOOSTER_MAX;
    this.missileAmmo = this.stats.missiles;
    this.counterAmmo = this.stats.counters;
    this.power = 'balanced';
    this.subTarget = 'hull';
    this.bounty = 0;
    this.target = null;
    this.matchSpeed = false;
    // A deactivate() mid-landing (world-cleared) must not wedge the next run.
    this.landingNow = false;
    this.landingTicks = -1;
    this.landingArrival = null;
    try {
      if (!this.spaceSubbed) {
      this.spaceSubbed = true;
      atlas.conn?.subscriptionBuilder().subscribe([
        'SELECT * FROM space_session', 'SELECT * FROM player_ship',
        'SELECT * FROM ship_component', 'SELECT * FROM pilot_state', 'SELECT * FROM player_item',
        'SELECT * FROM pilot_mission', // the Duty tab reads open contracts
        // The industry wave: sov ledger, deployed platforms, my stock + my
        // claim history (greys out wrecks/signatures already stripped).
        'SELECT * FROM sector_control', 'SELECT * FROM space_structure',
        'SELECT * FROM player_resource',
        'SELECT * FROM space_claim WHERE identity = :sender',
        // Tier-1 wave: the order book, haulage board, kill records, hull
        // insurance, the local roster, and the living-wormhole mass ledgers.
        'SELECT * FROM market_order', 'SELECT * FROM courier_contract', 'SELECT * FROM kill_record',
        'SELECT * FROM ship_insurance', 'SELECT * FROM space_presence', 'SELECT * FROM wormhole_flux',
        // Tier-2: wars, sieges, tackle, freelance, skills, abyss, ESS, loyalty.
        'SELECT * FROM guild_war', 'SELECT * FROM structure_siege', 'SELECT * FROM space_tackle',
        'SELECT * FROM freelance_project', 'SELECT * FROM pilot_skill', 'SELECT * FROM pilot_training',
        // Space Wave 3: the lane — firms, belt deposits, runs in transit, shortage prices.
        'SELECT * FROM space_firm', 'SELECT * FROM space_deposit', "SELECT * FROM space_shipment WHERE state = 'transit'", 'SELECT * FROM space_price_mod',
        // The planetary port (2026-09-17): the Commons ↔ space ore bridge — the ports, their runs, the exchange's depth.
        'SELECT * FROM planetary_port', "SELECT * FROM port_haul WHERE state = 'offered'", "SELECT * FROM port_haul WHERE state = 'aboard'", 'SELECT * FROM space_market_depth',
        // Space Wave 4: the campaign — the server's war, its sides, my receipts, the reserves, the shared incursion grip.
        'SELECT * FROM space_campaign', 'SELECT * FROM campaign_side', 'SELECT * FROM campaign_capital', 'SELECT * FROM campaign_escort', 'SELECT * FROM campaign_receipt WHERE identity = :sender', 'SELECT * FROM civ_reserve', 'SELECT * FROM incursion_state',
        // Space campaign slice D: the pin (a war that judges every named hit) and the named lives' CANONICAL hp.
        'SELECT * FROM war_authority', 'SELECT * FROM war_life',
        // Space Wave 5: my own observations and bookmarks; the civ's arrive through subscribeIntel() once the banner is known.
        'SELECT * FROM space_intel WHERE observer = :sender', 'SELECT * FROM space_bookmark WHERE owner = :sender',
        // Space Wave 6: every system's resolution and the shared den populations.
        'SELECT * FROM sim_lod', 'SELECT * FROM den_state',
        // The den slot law + the hidden-position law: engaged hulls' shared hp, and MY reveals only.
        'SELECT * FROM den_slot', 'SELECT * FROM sig_reveal WHERE identity = :sender',
        // The den position law + the PvP law: streamed seats of engaged hulls, and the blows landed on ME.
        'SELECT * FROM den_hull', 'SELECT * FROM space_pvp_hit WHERE target = :sender',
        // THE CREW LAW: every turret seat taken (the captain's screen hides its crew's hull).
        'SELECT * FROM space_crew',
        // Space Wave 7: the councils' decisions, the treaties, the advice, the firms' policies, the fleet stances.
        'SELECT * FROM civ_operation', 'SELECT * FROM civ_treaty', 'SELECT * FROM space_advice', 'SELECT * FROM firm_policy', 'SELECT * FROM campaign_stance',
        // THE RAIDER LAW: the fights on the lane (real hulls drawn from the dens).
        "SELECT * FROM space_ambush WHERE state = 'fighting'",
        'SELECT * FROM abyssal_run', 'SELECT * FROM ess_bank', 'SELECT * FROM pilot_loyalty',
        'SELECT * FROM guild', 'SELECT * FROM guild_member',
        // Space Wave 1: THE LOCATION LAW — where every pilot is, and my travel orders.
        'SELECT * FROM space_location', 'SELECT * FROM travel_order WHERE identity = :sender',
        // Space Wave 2: THE CUSTODY LAW — my lots (hold/warehouses/manifests), every wreck, the abyss hull.
        'SELECT * FROM cargo_lot WHERE owner = :sender', 'SELECT * FROM ship_wreck', 'SELECT * FROM abyssal_hull',
      ]);
      }
    } catch { /* fine */ }

    // The rig: hide the walking body, hang a FALLBACK ship immediately (the
    // cooked fighter swaps in when its GLB lands), take the chase camera.
    const rig = atlas.localMesh;
    if (rig) {
      this.hiddenRigMeshes = [];
      rig.traverse((o) => {
        if ((o as THREE.Mesh).isMesh) {
          this.hiddenRigMeshes.push({ obj: o, visible: o.visible });
          o.visible = false;
        }
      });
      // shipMesh = the COSMETIC group (bank/pitch animation only). Model-axis
      // normalization lives on the inner hull group so the bank never fights
      // the yaw fix (the wave-1 sideways-ship bug).
      const ship = new THREE.Group();
      const hull0 = new THREE.Group();
      hull0.add(this.fallbackShip(0x4f8cf0)); // fallback noses −Z natively
      ship.add(hull0);
      this.shipMesh = ship;
      rig.add(ship);
      // Engine exhaust: since the realism wave (R5) real PLUMES sized from the hull's own stern (updateLookFx
      // attaches them to shipMesh and re-attaches after every skin swap); the two fixed 1.4 m glow dots
      // stand only under the legacy lever `atlas:space-unlit = 1`.
      this.engineGlows = [];
      if (!this.spaceFlag('spacePlumes')) for (const side of [-0.85, 0.85]) {
        const eg = this.spriteGlow(0x66aaff, 1.4);
        eg.position.set(side, -0.1, 4.6);
        (eg.material as THREE.SpriteMaterial).opacity = 0;
        rig.add(eg);
        this.engineGlows.push(eg);
      }
      const cam = rig.children.find((c) => (c as THREE.Camera).isCamera) as THREE.PerspectiveCamera | undefined;
      if (cam) {
        this.savedCam = { pos: cam.position.clone(), rot: cam.rotation.clone() };
        cam.position.set(0, 4.2, 15);
        cam.rotation.set(-0.07, 0, 0);
        cam.far = 3_600_000; // the gas giant sits 2,000 km out and must render
        this.baseFov = cam.fov; // R7: the kick eases from the player's own FOV
        cam.updateProjectionMatrix();
      }
      this.camBack = chaseFor(CHASSIS[this.chassisKey]); // each flight starts at the hull's default chase distance
    }

    // Pirates spawn NOW with fallback hulls — combat never waits on a decode.
    for (const poi of (this.system.id === 'sys0' ? POIS : [])) {
      const tier = poi.tier;
      for (let i = 0; i < poi.pirates; i++) {
        const root = new THREE.Group();
        const hull = new THREE.Group();
        hull.add(this.fallbackShip(tier === 3 ? 0xd02020 : tier === 2 ? 0xb03030 : 0x9a4040));
        root.add(hull);
        const isAce = i === 0 && ACE_NAMES[poi.key] !== undefined;
        if (isAce) root.scale.setScalar(1.5); // the warlord's hull reads at a glance
        const halo = this.spriteGlow(isAce ? 0xff4444 : 0xff7a5c, isAce ? 3.6 : 2.4);
        (halo.material as THREE.SpriteMaterial).opacity = 0.5;
        root.add(halo);
        const home = new THREE.Vector3(...pirateSpawn(poi, i));
        root.position.copy(this.local(home.x, home.y, home.z, this._v1));
        this.root.add(root);
        this.pirates.push({
          key: `${poi.key}:pirate${i}`, poiKey: poi.key, tier, root,
          hp: TIER_STATS[tier].hp * (isAce ? ACE_HP_MULT : 1), deadUntil: 0, home, fireAt: 0,
          vel: new THREE.Vector3(), prevPos: root.position.clone(),
          ai: 'patrol', aiUntil: 0, jinkSeed: i * 7 + poi.key.length, evadeBrake: false, escaped: false, slowUntil: 0, jamUntil: 0,
        });
      }
    }

    this.hud = new SpaceHud();
    this.hud.mount();
    this.toast(`Entered the sector — ${keyHint('spaceLand')} lands at the waystation or your descent beacon`);
    this.stage = 'ready';

    // ── Cosmetic asset population, ASYNC (never blocks flight/combat) ──
    void (async () => {
      try {
        this.stage = 'ready+loading-models';
        // Warm the site cache here (off the landing hot path).
        const conn2 = atlas.conn;
        if (conn2) {
          for (const w of conn2.db.world.iter()) {
            if (w.kind === 'commons' && this.sitesSeed !== w.terrainSeed) {
              this.sitesSeed = w.terrainSeed;
              this.sites = settlementSites(w.terrainSeed);
            }
          }
        }
        // R4.4: the Commons' night side glows where its settlements are (the real sites, baked once per seed).
        if (this.commonsLook && this.sites && this.active) this.commonsLook.setCities(cityLightsTexture(this.sitesSeed, this.sites));
        if (this.commonsCloudShell && this.sites && this.active) this.commonsCloudShell.setCities(cityLightsTexture(this.sitesSeed, this.sites)); // shader gaps R4: the clouds glow over the lights
        const gen = this.actGen;
        const [station, rockMaster, pirate, fighter] = await Promise.all([
          this.master('prop:space_station'), this.master('prop:asteroid_a'),
          this.master('prop:ship_pirate'), this.master('prop:ship_fighter'),
        ]);
        if (!this.active || !this.root || gen !== this.actGen) return; // left space mid-load, or a system swap re-activated (the old closure must not build into the new root)
        this.pirateMaster = pirate;
        // Rocks always exist: a gray icosahedron stands in when the cooked
        // asteroid never decodes (headless probes still see real fields).
        const rock = rockMaster ?? new THREE.Mesh(
          new THREE.IcosahedronGeometry(1, 1),
          new THREE.MeshStandardMaterial({ color: 0x6a6f78, roughness: 0.95 }),
        );
        const iceMat = new THREE.MeshStandardMaterial({ color: 0xbfe8ff, roughness: 0.25, metalness: 0.05, emissive: 0x11314a });
        // Collision groups build alongside the props (ABSOLUTE coords — the
        // flight step tests this.pos directly, immune to origin rebasing).
        // World-cluster colliders may already exist (syncWorldPlanets runs
        // before this async build) — carry them through the reset.
        this.colliderGroups = [...this.worldClusterCols.values()];
        const groupOf = new Map<string, { x: number; y: number; z: number; reach: number; list: Array<{ x: number; y: number; z: number; r: number; halfH: number }> }>();
        const grpFor = (poi: PoiSpec) => {
          let g = groupOf.get(poi.key);
          if (!g) { g = { x: poi.pos[0], y: poi.pos[1], z: poi.pos[2], reach: poi.radius * 1.15 + 900, list: [] }; groupOf.set(poi.key, g); this.colliderGroups.push(g); }
          return g;
        };
        const placeRocks = (poi: PoiSpec, count: number, mat?: THREE.Material): void => {
          const grp = grpFor(poi);
          if (!this.spaceUnlit()) {
            // Realism wave R10: ONE instanced field per POI — non-uniform scales, hashed tumbles, per-rock shade.
            const items = asteroidField(poi, count);
            const field = new RockField(rock, items.map((a) => ({ pos: a.pos, scale: a.scale })), poi.key, this.off, mat);
            this.root!.add(field.group);
            this.rockFields.push(field);
            for (const a of items) grp.list.push({ x: a.pos[0], y: a.pos[1], z: a.pos[2], r: a.scale * 1.05, halfH: 0 });
            return;
          }
          for (const a of asteroidField(poi, count)) {
            const r = rock.clone();
            if (mat) r.traverse((o) => { if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).material = mat; });
            r.position.copy(this.local(a.pos[0], a.pos[1], a.pos[2], this._v1));
            r.scale.setScalar(a.scale);
            r.rotation.set(a.spin * 10, a.spin * 20, a.spin * 5);
            this.root!.add(r);
            this.spinners.push({ obj: r, spin: a.spin });
            grp.list.push({ x: a.pos[0], y: a.pos[1], z: a.pos[2], r: a.scale * 1.05, halfH: 0 });
          }
        };
        const placeStation = (poi: PoiSpec, height: number, spin: number, tint?: number): void => {
          if (!station) return;
          const st = station.clone();
          st.position.copy(this.local(poi.pos[0], poi.pos[1], poi.pos[2], this._v1));
          const box = new THREE.Box3().setFromObject(st);
          st.scale.setScalar(height / Math.max(1, box.max.y - box.min.y));
          if (this.spaceFlag('spaceLitHulls')) {
            // Realism wave R11: the station lit under the one sun with the window layer, port/starboard nav
            // lights, 1 Hz strobes and a docking corridor of chasing lights (sampled once on the master).
            this.dressWarHull(st, 1, true);
            const mb = new THREE.Box3().setFromObject(station);
            const mh = Math.max(1, mb.max.y - mb.min.y);
            const g = new THREE.Group(); g.add(station.clone());
            const set = navPointSet(station, g, 150, mh * 0.004, { axis: new THREE.Vector3(1, 0, 0), from: mh * 0.35, to: mh * 1.5, n: 14 });
            if (set) st.add(navMesh(set));
          }
          this.root!.add(st);
          this.spinners.push({ obj: st, spin });
          // A vertical capsule hugs the spindle far better than one huge sphere.
          grpFor(poi).list.push({ x: poi.pos[0], y: poi.pos[1], z: poi.pos[2], r: height * 0.17, halfH: height * 0.45 });
          if (tint !== undefined) {
            const beacon = this.spriteGlow(tint, height * 1.4, true);
            (beacon.material as THREE.SpriteMaterial).opacity = 0.35;
            beacon.position.copy(st.position);
            this.root!.add(beacon);
          }
        };
        if (this.system.id !== 'sys0') {
          this.buildAltSector(rock, station, iceMat);
        } else {
        // Station heights carry the ×2 wave.
        for (const poi of POIS) {
          switch (poi.kind) {
            case 'station': placeStation(poi, 880, 0.02); break;
            case 'megastation':
              // The MASSIVE anchor: a 2.8 km spindle with traffic beacons.
              placeStation(poi, 5_600, 0.006, 0x9fd7ff);
              placeRocks(poi, 6);
              break;
            case 'asteroids': placeRocks(poi, 42); break;
            case 'junkyard': placeRocks(poi, 26); break;
            case 'ice': placeRocks(poi, 34, iceMat); break;
            case 'mine': placeStation(poi, 360, 0.01, 0xffd97e); placeRocks(poi, 14); break;
            case 'fortress': placeStation(poi, 1_200, 0.008, 0xff4444); placeRocks(poi, 8); break;
            case 'derelict': {
              // A dead capital hull: the pirate ship blown up to wreck scale,
              // unlit-dark, listing on its axis, ringed by debris.
              if (pirate) {
                const hulk = pirate.clone();
                hulk.traverse((o) => {
                  const m = (o as THREE.Mesh);
                  if (m.isMesh) m.material = new THREE.MeshStandardMaterial({ color: 0x3a4048, roughness: 0.9, metalness: 0.3 });
                });
                hulk.position.copy(this.local(poi.pos[0], poi.pos[1], poi.pos[2], this._v1));
                const box = new THREE.Box3().setFromObject(hulk);
                hulk.scale.setScalar(680 / Math.max(1, box.max.x - box.min.x));
                hulk.rotation.set(0.4, 1.2, 0.7);
                this.root!.add(hulk);
                this.spinners.push({ obj: hulk, spin: 0.004 });
                grpFor(poi).list.push({ x: poi.pos[0], y: poi.pos[1], z: poi.pos[2], r: 340, halfH: 0 });
              }
              placeRocks(poi, 12);
              break;
            }
            // nebula + comet were built sprite-side at activation.
            case 'nebula': case 'comet': break;
          }
        }
        this.buildPatrols(pirate);
        this.buildWings();
        } // sys0 content
        this.natural?.build(rock); // natural objects: comets, belts, discs, landmarks — once the sector's own bodies stand
        if (pirate) {
          for (const p of this.pirates) {
            p.root.clear();
            p.root.add(this.wrapHull(pirate.clone(), 11 + p.tier * 2));
          }
        }
        // Player hull: the active chassis prefab, stock fighter until it cooks.
        if (this.shipMesh) {
          const spec = CHASSIS[this.chassisKey] ?? CHASSIS[DEFAULT_CHASSIS];
          const skin = (await this.master(spec.prefab)) ?? fighter;
          if (!this.active || !this.root || gen !== this.actGen) return; // WHY: only the first await re-checked — a landing or a system swap during this load let the dead closure go on into the new sector
          if (skin && spec === (CHASSIS[this.chassisKey] ?? CHASSIS[DEFAULT_CHASSIS])) { // WHY: a hull swapped during the load has its own re-skin; this older skin must not land over it
            this.shipMesh.clear();
            this.shipMesh.add(this.wrapHull(skin.clone(), spec.hullSize));
            if (this.cockpitView) this.applyCockpitView(); // re-seat + re-style for the new chassis
          }
        }
        // War-ship hulls (Higgsfield): promoted-gunship skins + merged
        // instanced geometry for the swarm and the capital walls. The live
        // battle tears down so the next tick rebuilds wearing them.
        const [wfa, wfb, wca, wcb, wcoa, wcob, wfra, wfrb] = await Promise.all([
          this.master('prop:war_fighter_a'), this.master('prop:war_fighter_b'),
          this.master('prop:war_capital_a'), this.master('prop:war_capital_b'),
          this.master('prop:war_corvette_a'), this.master('prop:war_corvette_b'),
          this.master('prop:war_frigate_a'), this.master('prop:war_frigate_b'),
        ]);
        this.warShipM = [wfa, wfb];
        this.warCapM = [wca, wcb];
        this.warEscortM = { corvette: [wcoa, wcob], frigate: [wfra, wfrb] };
        if (!this.active || !this.root || gen !== this.actGen) return; // WHY: the masters above are the session's; the teardown and the stage below are the LIVE sector's — a dead closure tore down the new sector's battle (wingmen too)
        // 8.5 m fighters: at battle stand-off ranges the 5.6 m hulls shrank
        // to unreadable specks next to the 4.5 km capitals.
        const ftrA = wfa ? this.shipPartSets(wfa, 8.5) : null;
        const ftrB = wfb ? this.shipPartSets(wfb, 8.5) : null;
        if (ftrA || ftrB || wca || wcb || wcoa || wcob || wfra || wfrb) {
          this.warAssets = { ftrParts: [ftrA, ftrB] };
          this.teardownBattle(); // the next tick rebuilds wearing the hulls
        }
        this.stage = 'ready+models';
      } catch (e) {
        this.stage = `ready+model-error:${String((e as Error).message ?? e).slice(0, 60)}`;
      }
    })();
  }

  private deactivate(): void {
    this.active = false;
    // THE SECTOR'S loops only (2026-09-20). This used to be stopAllSfxLoops(): leaving space also stopped the rain, the
    // fauna, the bounty hunter's drone and your low-HP heartbeat — every loop in the game shared one un-owned map.
    stopSfxScope('space');
    // The 28 contact sprites lived under the old root: `place()` kept mutating
    // orphans and the markers vanished for the session after the first landing
    // or hyperwarp (2026-09-12). The ops-panel subscription likewise outlived its panel.
    this.contactMarkers = [];
    try { this.opsSub?.unsubscribe(); } catch { /* gone */ }
    this.opsSub = null;
    if (!this.systemSwapping) { // leaving space: release the roster channel (a jump re-lays it on the next heartbeat)
      try { this.sysChatSub?.unsubscribe(); } catch { /* gone */ }
      this.sysChatSub = null; this.sysChatSystem = '';
    }
    this.actGen++; // orphan in-flight texture loads (they self-dispose)
    this.stage = '';
    atlas.spaceFlight = false;
    atlas.combatSpace = false;
    const groundHint = document.getElementById('hint');
    if (groundHint) groundHint.style.display = '';
    const scene = this.app?.world.scene;
    if (scene) {
      if (this.root) {
        // DIAG toggle (__sweepOff.root): the hyperwarp-flood bisection probe
        // disables sweep categories per warp to isolate which disposal kills
        // a still-rendered resource. Leak-only, dev-gated by usage.
        if (!(globalThis as unknown as { __sweepOff?: { root?: boolean } }).__sweepOff?.root) {
          this.disposeSectorRoot(this.root); // free the sector's GPU footprint (the pod-lag leak)
          for (const t of this.lookTex) this.disposeLater(t); // the planets' node-held faces and reliefs: the sweep above reads classic map slots only, so these were never freed
        }
        this.lookTex = [];
        scene.remove(this.root);
      }
      if (!this.systemSwapping) scene.fog = this.savedFog; // a swap keeps space's null fog — no global recompile
      if (!this.systemSwapping) {
        // THE VACUUM ends (realism wave 2026-09-25): the ground's post chain, sky bakes, IBL and background return.
        (this.app?.getModule('Post') as { setSpaceProfile?(on: boolean): void } | null)?.setSpaceProfile?.(false);
        setSkyBakePaused(false);
        if (this.savedEnv !== undefined) { scene.environment = this.savedEnv; this.savedEnv = undefined; }
        setSpaceBackground(scene as never, null);
        this.lookLog(`deactivate: ground background restored gen=${this.actGen}`);
      }
    }
    this.sunLight = null; this.sunTarget = null; this.starlight = null; this.planetshine = null;
    this.legacySky = []; this.streaks = null;
    this.trauma = 0; this.fovKick = 0; this.camBaseSet = false; this.altStarR = 0; this.starRNow = 0; this.starFrameSeen = false;
    this.fx?.clear(); this.fx = null; // its group (the session's batch + rings, this sector's lights) left with the root
    this.commonsLook = null; this.commonsAtmo = null; this.shield = null; this.rockFields = [];
    this.spaceLight?.clouds?.dispose(); this.spaceLight = null; this.commonsCloudShell = null; // shader gaps R4 (the ring / shell meshes left with the root)
    this.blackHole?.dispose(); this.blackHole = null; this.blackHoleViewUntil = 0; // shader gaps: black hole (its background swap stands down once !active)
    this.nebulae?.dispose(); this.nebulae = null; // space realism: nebulae (the baked volumes stay cached for the session)
    this.natural?.dispose(); this.natural = null; // natural objects (its meshes left with the root; the views free what they own)
    if (!this.systemSwapping) {
      for (const h of this.hiddenLights) h.obj.visible = h.visible;
      this.hiddenLights = [];
    }
    if (this.savedCloudDensity !== null) {
      const clouds = this.app?.getModule('Clouds') as
        | { renderer?: { setDensityScale(v: number): void }; lowRenderer?: { setDensityScale(v: number): void } | null; setMeshFieldEnabled?(on: boolean): void }
        | null;
      clouds?.renderer?.setDensityScale(this.savedCloudDensity);
      clouds?.lowRenderer?.setDensityScale(this.savedCloudDensity);
      // Back on the ground: the tier decides again (cloudsAllowed with the
      // sector down) — never the value saved on the way up.
      clouds?.setMeshFieldEnabled?.(cloudsAllowed(!!activePreset().cloudsEnabled, false));
      this.savedCloudDensity = null;
    }
    this.root = null;
    this.pirates = [];
    this.patrolLive.clear(); // the hulls lived under root
    this.bolts = [];
    this.missiles = []; // sprites lived under root — removed with the scene root
    this.teardownBattle(); // battle group lived under root; its procedural geos/mats are ours to free
    this.teardownWrecks();
    // Industry scene objects lived under root (gone with it) — free our
    // procedural materials and drop the maps so the next system rebuilds.
    if (this.beam) {
      this.disposeLater(this.beam.geometry);
      this.disposeLater(this.beam.material as THREE.Material);
      this.beam = null;
    }
    for (const [, obj] of this.sigBeacons) {
      for (const c of obj.children) {
        this.disposeLater((c as THREE.Mesh).material as THREE.Material | undefined);
        this.disposeLater((c as THREE.Mesh).geometry as THREE.BufferGeometry | undefined);
      }
    }
    this.sigBeacons.clear();
    for (const o of this.wormholeObjs) {
      for (const c of o.obj.children) this.disposeLater((c as THREE.Sprite).material as THREE.SpriteMaterial | undefined);
    }
    this.wormholeObjs = [];
    for (const [, obj] of this.structObjs) {
      for (const c of obj.children) {
        this.disposeLater((c as THREE.Mesh).material as THREE.Material | undefined);
        this.disposeLater((c as THREE.Mesh).geometry as THREE.BufferGeometry | undefined);
      }
    }
    this.structObjs.clear();
    this.promoSide.clear();
    this.incBossKey = null;
    if (this.opsPanel) { window.clearInterval(this.opsTimer); this.opsPanel.remove(); this.opsPanel = null; }
    // War hull cache: part geos/mats are OURS (built from master buffers);
    // masters themselves belong to the asset cache and stay untouched.
    const sweepOff = (globalThis as unknown as { __sweepOff?: { bake?: boolean } }).__sweepOff;
    if (this.warAssets) {
      if (!sweepOff?.bake) {
        for (const set of this.warAssets.ftrParts) {
          for (const e of set ?? []) { this.disposeLater(e.geo); this.disposeLater(e.mat); }
        }
      }
      this.warAssets = null;
    }
    this.warShipM = [null, null];
    this.warCapM = [null, null];
    this.warEscortM = { corvette: [null, null], frigate: [null, null] };
    if (!sweepOff?.bake) for (const m of this.warBakedMats) this.disposeLater(m);
    this.warBakedMats = [];
    this.warLitCache.clear(); // its materials were in warBakedMats (disposed above)
    for (const m of this.breakupModels.values()) if (m) disposeBreakupModel(m, (d) => this.disposeLater(d)); // item 7: ours
    this.breakupModels.clear();
    this.playerPlumes = null; this.capPlumes.clear(); // children of the root / rig groups — gone with them
    for (const f of this.fxLive) this.disposeLater(f.s.material as THREE.SpriteMaterial);
    this.fxLive = [];
    this.fxQueue = [];
    this.capCloud = [null, null];
    this.wings = [];    // wing ships lived under root
    this.voidRaiders.clear();
    this.planets = [];
    this.planetAnchors = [];
    this.worldPlanets.clear(); // scene died with root — a stale map would block the next activate's rebuild
    this.worldSyncAcc = 0;
    this.spinners = [];
    this.followers = [];
    this.hyper = '';
    this.hyperT = 0;
    this.navIdx = -1;
    this.autoCruise = false;
    this.cruise = 1;
    this.off.set(0, 0, 0);
    this.map?.unmount();
    this.map = null;
    this.dock?.close();
    this.dock = null;
    this.galaxyMap?.unmount();
    this.galaxyMap = null;
    if (this.sysJump) {
      // Streaks + tunnel lived under root (gone with the scene), but their
      // procedural materials/geos/textures are ours to free.
      for (const st of this.sysJump.streaks) this.disposeLater(st.material as THREE.SpriteMaterial);
      for (const d of this.sysJump.own) this.disposeLater(d);
      this.sysJump = null;
    }
    this.altStation = null;
    if (this.furnPanel) { window.clearInterval(this.furnTimer); this.furnPanel.remove(); this.furnPanel = null; }
    this.interior = null; atlas.spaceInterior = false; // room lived under root; camera restored below with savedCam
    this.freighters.clear();
    this.convoys.clear();
    this.shipments.clear();
    this.patrols = [];
    this.patrolMesh = null; // lived under root — removed with the scene root
    this.promotedKeys.clear();
    this.colliderGroups = [];
    this.worldClusterCols.clear();
    this.remoteShips.clear();
    this.remoteTracks.clear();
    this.remoteRows.clear();
    this.playerWrecks.clear(); // the root died with the scene; the map must not hold ghosts (the cleanup law)
    this.target = null;
    const rig = atlas.localMesh;
    if (rig) {
      if (this.shipMesh) rig.remove(this.shipMesh);
      if (this.sovereignCockpit) {
        rig.remove(this.sovereignCockpit);
        // Free the cockpit's unique geometries + materials — but DEFERRED past the
        // submit window (warp-teardown law), never inline during the swap frame.
        this.sovereignCockpit.traverse((o) => {
          const m = o as THREE.Mesh;
          if (m.isMesh) { this.disposeLater(m.geometry); this.disposeLater(m.material as THREE.Material); }
        });
        this.sovereignCockpit = null;
      }
      for (const eg of this.engineGlows) rig.remove(eg);
      // Restore the SAVED visibility — a blanket `true` un-hid rig meshes
      // their owners keep hidden (post-landing ghost geometry at the camera).
      for (const h of this.hiddenRigMeshes) h.obj.visible = h.visible;
      this.hiddenRigMeshes = [];
      rig.quaternion.identity();
      const cam = rig.children.find((c) => (c as THREE.Camera).isCamera) as THREE.PerspectiveCamera | undefined;
      if (cam && this.savedCam) {
        cam.position.copy(this.savedCam.pos);
        cam.rotation.copy(this.savedCam.rot);
        cam.far = GROUND_FAR_PLANE_M; // ground far plane: the terrain far tier reaches ±98 km
        if (this.baseFov > 0) { cam.fov = this.baseFov; this.baseFov = 0; } // R7: the FOV kick never outlives the flight
        cam.updateProjectionMatrix();
      }
    }
    this.cockpitOverlay.dispose();
    this.vrCockpit.detach(); this.vrWas = false; sectorMeter().reset(); // space campaign L: the headset's cockpit leaves with the rig's flight pose
    this.cockpitView = false;
    this.camBack = ZOOM_CHASE;
    this.shipMesh = null;
    this.engineGlows = [];
    this.hud?.unmount();
    this.hud = null;
    this.flightCombat.unmount(); // space campaign I: the aiming / EW layer leaves with the HUD
    // One deferred batch for the whole sector: everything above left the
    // scene this tick; the GPU handles free after in-flight frames flush.
    this.flushDisposeSoon();
  }

  // ── Assets ───────────────────────────────────────────────────────────────
  /** Tag every geometry/material/texture in a subtree as SHARED — the
   *  deactivate sweep must never free them. Clones share these exact
   *  resource objects, so tagging the MASTER covers every clone forever. */
  private markShared(obj: THREE.Object3D): void {
    obj.traverse((o) => {
      const m = o as THREE.Mesh;
      const g = m.geometry as THREE.BufferGeometry | undefined;
      if (g) g.userData.shared = true;
      const mm = m.material as THREE.Material | THREE.Material[] | undefined;
      for (const mat of Array.isArray(mm) ? mm : mm ? [mm] : []) {
        mat.userData.shared = true;
        const sm = mat as THREE.MeshStandardMaterial;
        for (const tex of [sm.map, sm.normalMap, sm.emissiveMap, sm.roughnessMap, sm.aoMap]) {
          if (tex) tex.userData.shared = true;
        }
      }
    });
  }

  private async master(key: string): Promise<THREE.Object3D | null> {
    const conn = atlas.conn;
    if (!conn || !this.assets) return null;
    const id = prefabAssetId(conn, key);
    if (id === null) return null;
    try {
      const obj = await this.assets.load(id);
      if (obj) this.markShared(obj); // clones inherit the tag via shared resources
      return obj;
    } catch { return null; }
  }

  /** THE POD-LAG FIX: every activation builds a full sector (starfields,
   *  planets with freshly loaded 4K textures, nebulae, procedural hulls) —
   *  removing the root WITHOUT disposing leaked all of it into the
   *  renderer's caches, and each space trip made the whole game slower
   *  (measured: −fps + ~60 MB heap per eject cycle). Sweep everything
   *  UNSHARED; master-derived resources carry the shared tag and survive. */
  /** Deferred GPU teardown (the leave-a-battle hyper-warp flood): r185's
   *  WebGPU submit is async — disposing a resource the SAME tick it leaves
   *  the scene can race an in-flight command buffer ("[Buffer] used in
   *  submit while destroyed", flooding every later frame on real-GPU frame
   *  pacing; headless swiftshader never reproduces the race). Objects leave
   *  the scene NOW; their GPU handles free after in-flight frames flush. */
  private pendingDispose: Array<{ dispose(): void }> = [];

  private disposeLater(d: { dispose(): void } | null | undefined): void {
    if (d) this.pendingDispose.push(d);
  }

  private flushDisposeSoon(): void {
    if (this.pendingDispose.length === 0) return;
    if ((globalThis as { __noFree?: boolean }).__noFree) { this.pendingDispose = []; return; } // DIAG (see gpu_dispose)
    const batch = this.pendingDispose;
    this.pendingDispose = [];
    // frames+time gate: the 350 ms wall-clock timer fired INSIDE a multi-
    // second world-swap frame whose command buffer still referenced these
    // buffers ("used in submit while destroyed" ×500 — descent field
    // report 2026-08-23). afterSubmitWindow waits for real rendered frames.
    // 12 frames + 3 s: sector swaps run pipeline-compile storms in which
    // r185 defers submits — command buffers ENCODED while a resource lived
    // can be SUBMITTED frames later; the window must outlast the storm.
    afterSubmitWindow(() => {
      for (const d of batch) { try { d.dispose(); } catch { /* already freed */ } }
    }, 12, 3000);
  }

  private disposeSectorRoot(root: THREE.Group): void {
    const seenG = new Set<THREE.BufferGeometry>();
    const seenM = new Set<THREE.Material>();
    root.traverse((o) => {
      const m = o as THREE.Mesh;
      const g = m.geometry as THREE.BufferGeometry | undefined;
      // Every three Sprite shares ONE module-level geometry: disposing it here
      // (per skirmisher death, every deactivate) tore the buffers from under
      // every live sprite in the scene (2026-09-12).
      if (g && !g.userData.shared && !(o as THREE.Sprite).isSprite && !seenG.has(g)) { seenG.add(g); this.disposeLater(g); }
      const mm = m.material as THREE.Material | THREE.Material[] | undefined;
      for (const mat of Array.isArray(mm) ? mm : mm ? [mm] : []) {
        if (mat.userData.shared || seenM.has(mat)) continue;
        seenM.add(mat);
        const sm = mat as THREE.MeshStandardMaterial & THREE.SpriteMaterial;
        for (const tex of [sm.map, sm.normalMap, sm.emissiveMap, sm.roughnessMap, sm.aoMap]) {
          if (tex && !tex.userData.shared) this.disposeLater(tex);
        }
        this.disposeLater(mat);
      }
    });
  }

  /** Ship-skin normalization: box-fit to `size` m inside a group carrying
   *  SHIP_MODEL_YAW (modeled nose → −Z — the sideways-ship fix). */
  private wrapHull(skin: THREE.Object3D, size: number): THREE.Group {
    const box = new THREE.Box3().setFromObject(skin);
    const longest = Math.max(1, box.max.x - box.min.x, box.max.y - box.min.y, box.max.z - box.min.z);
    skin.scale.setScalar(size / longest);
    const hull = new THREE.Group();
    hull.rotation.y = SHIP_MODEL_YAW;
    hull.add(skin);
    return hull;
  }

  /** Shift a wrapped hull so its bbox centre sits at the local origin —
   *  the EXACT normalization hullSphereCloud applies, so collider cloud and
   *  rendered mesh occupy the same space. */
  private recenterHull(hull: THREE.Object3D): void {
    hull.updateMatrixWorld(true);
    const c = new THREE.Box3().setFromObject(hull).getCenter(new THREE.Vector3()); // build-time only
    for (const child of hull.children) child.position.sub(c);
  }

  private fallbackShip(color: number): THREE.Object3D {
    const g = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.5, metalness: 0.4 });
    const body = new THREE.Mesh(new THREE.ConeGeometry(1.4, 6, 8), mat);
    body.rotation.x = -Math.PI / 2;
    const wing = new THREE.Mesh(new THREE.BoxGeometry(7, 0.22, 2.2), mat);
    wing.position.z = 1;
    g.add(body, wing);
    return g;
  }

  private glowTexture(): THREE.CanvasTexture {
    if (!this.glowTex) {
      const c = document.createElement('canvas');
      c.width = c.height = 64;
      const g2 = c.getContext('2d')!;
      const grad = g2.createRadialGradient(32, 32, 2, 32, 32, 31);
      grad.addColorStop(0, 'rgba(255,255,255,1)');
      grad.addColorStop(0.35, 'rgba(255,255,255,0.5)');
      grad.addColorStop(1, 'rgba(255,255,255,0)');
      g2.fillStyle = grad;
      g2.fillRect(0, 0, 64, 64);
      this.glowTex = new THREE.CanvasTexture(c);
      this.glowTex.userData.shared = true; // session cache — never swept
    }
    return this.glowTex;
  }

  private softGlowTex: THREE.CanvasTexture | null = null;
  /**
   * The glow for LARGE, still sprites (a station's beacon, a platform's halo). The shared glow's tail runs straight to
   * zero in LINEAR light, which the display's curve turns into a visible rim at the sprite's edge — measured on the
   * mine's beacon (the space-kit probe, 2026-10-08): 39/255 falling to 0 over the last 8 px of a 100-px disc. This tail
   * is (1 − u)²: zero slope at the edge, a fade that is close to linear for the eye. The inner third is the shared
   * texture's. Small sprites (bolts, engine halos, markers) keep the shared one — their rim is under a pixel.
   */
  private softGlowTexture(): THREE.CanvasTexture {
    if (!this.softGlowTex) {
      const c = document.createElement('canvas');
      c.width = c.height = 128;
      const g2 = c.getContext('2d')!;
      const grad = g2.createRadialGradient(64, 64, 4, 64, 64, 63);
      grad.addColorStop(0, 'rgba(255,255,255,1)');
      grad.addColorStop(0.35, 'rgba(255,255,255,0.5)');
      for (let i = 1; i <= 10; i++) { const u = i / 10; grad.addColorStop(0.35 + 0.65 * u, `rgba(255,255,255,${(0.5 * (1 - u) * (1 - u)).toFixed(4)})`); }
      g2.fillStyle = grad;
      g2.fillRect(0, 0, 128, 128);
      this.softGlowTex = new THREE.CanvasTexture(c);
      this.softGlowTex.userData.shared = true; // session cache — never swept
    }
    return this.softGlowTex;
  }
  private spriteGlow(color: number, size: number, soft = false): THREE.Sprite {
    const sp = new THREE.Sprite(markMrtOverlay(new THREE.SpriteMaterial({
      map: soft ? this.softGlowTexture() : this.glowTexture(), color, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true,
    })));
    sp.scale.setScalar(size);
    return sp;
  }

  // ── Floating origin ──────────────────────────────────────────────────────
  /** Rebase the offset origin onto the ship whenever it drifts 25 km out —
   *  every root child shifts once; render coordinates stay small forever. */
  private maybeRebase(): void {
    const dx = this.pos.x - this.off.x, dy = this.pos.y - this.off.y, dz = this.pos.z - this.off.z;
    if (dx * dx + dy * dy + dz * dz < 25_000 * 25_000) return;
    const shift = this._v1.set(dx, dy, dz);
    if (this.root) for (const c of this.root.children) c.position.sub(shift);
    // Every TRACKED kinematic snapshot shifts too — a rebase that moves the
    // scene but not prevPos reads as a 750 km/frame velocity and the AI lerp
    // FLINGS pirates across the sector (probe finding 2026-07-29).
    for (const p of this.pirates) p.prevPos.sub(shift);
    // (the smoothing tracks hold ABSOLUTE coordinates — a rebase moves nothing they keep; the rules' server row shifts)
    for (const rs of this.remoteShips.values()) rs.target.sub(shift);
    this.off.copy(this.pos);
  }

  /** Absolute → offset-local (render) coords. */
  /** Stage 4: the sector sun's direction (world) — the atmosphere shells' light. */
  private static readonly SUN_DIR = new THREE.Vector3(0.55, 0.8, 0.3).normalize();

  /** Stage 4: an O'Neil atmosphere shell over a planet group (radius in the planet's units). */
  private attachAtmosphere(group: THREE.Group, radius: number, hue: number, sunDir: THREE.Vector3 = SpaceModule.SUN_DIR, homeSky = false, slSet: EclipseSet | null = null, extra?: Record<string, unknown>): void {
    if (!activePreset().planetAtmosphere) return;
    // Realism wave (2026-09-25, report §9 R4.5): an AIRLESS body (a 14 km moon) carries no limb at all.
    if (radius < 20_000) return;
    // THE HOME WORLD FROM ORBIT (space P3, 2026-09-27 — §7.1): the Commons wears the SAME physical atmosphere its ground
    // sky is made of (ATLAS_ATMOSPHERE: the aerosol column, ozone, Cornette-Shanks), marched per fragment with the sun's
    // transmittance — a blue day limb that reddens and thins across the terminator and vanishes over the night side;
    // O'Neil's tint fit stays for the other bodies. The shell is O'Neil's 2.5 % (a physically-scaled 1.6 % line on a
    // 78 km world vanishes past ~1,000 km); lit by π × the planet surface's own sun. `atlas:planet-atmo = 'oneil'` A/B.
    let oneil = false; try { oneil = localStorage.getItem('atlas:planet-atmo') === 'oneil'; } catch { oneil = false; }
    try {
      // The shell itself is planet_look.buildAtmosphereShell (shared with the picture bed): the home world's scatter
      // shell or O'Neil's, an MRT overlay at renderOrder 2, seated BEHIND its planet (THE BODY LAYER LAW — at the
      // far-body anchor the depth buffer cannot tell a shell's back faces from the planet's front; a leak drew white specks).
      group.add(buildAtmosphereShell({ ...(extra ?? {}), radius, hue, sunDir, homeSky: homeSky && !oneil, set: slSet } as Parameters<typeof buildAtmosphereShell>[0]).mesh); // (natural objects: a planet kind's extra shell options ride in `extra`)
      stage4Stats.atmospheres++;
    } catch { /* best-effort */ }
  }

  // ── shader gaps R4 — SPACE LIGHT (receiver eclipses, rings, the orbital cloud shell; lib/space/space_light_look.ts) ──
  /** A space-light switch: the tier's flag (a saved profile without the key takes its tier's) under its kill switch. */
  private slFlag(k: 'eclipseShadows' | 'planetRings' | 'orbitalClouds'): boolean {
    const kill = { eclipseShadows: 'atlas:eclipse', planetRings: 'atlas:rings', orbitalClouds: 'atlas:orbital-clouds' }[k];
    try { if (localStorage.getItem(kill) === 'off') return false; } catch { /* no storage */ }
    const v = activePreset()[k];
    return v ?? (SL_TIERS[activeTier()] as SlTierPreset)[k] ?? false;
  }
  /**
   * Seat a planet's space light: its ring (two halves in the planet GROUP — the back half before the shells, the front
   * after: rings.RING_ORDER), its cloud shell (a child of the SPINNING mesh — the night lights' uv), the rim after the
   * ring's back half, and the body registered with the controller (the occluder and receiver lists are picked per frame).
   */
  private attachSpaceLight(name: string, pos: readonly [number, number, number] | number[], R: number, pg: THREE.Group, mesh: THREE.Mesh, rim: THREE.Mesh | null, set: EclipseSet, ring: { rIn: number; rOut: number; normal: { x: number; y: number; z: number }; tauScale: number } | null, clouds: OrbitalCloudBaker | null): void {
    const sl = this.spaceLight;
    if (!sl) return;
    const rb = ring ? buildRing({ R, rIn: ring.rIn, rOut: ring.rOut, normal: ring.normal, set, sunColor: uPlanetSun, uPixel: sl.uPixel }) : null;
    if (rb) pg.add(rb.back, rb.front);
    if (rim) rim.renderOrder = 2; // after the ring's back half (1) and the cloud shell (1.5); additive — its order among the shells is free
    if (clouds) {
      const shell = buildCloudShell({ R, set, sunColor: uPlanetSun, clouds });
      mesh.add(shell.mesh);
      this.commonsCloudShell = shell;
    }
    sl.addBody({ key: name, abs: { x: pos[0], y: pos[1], z: pos[2] }, radius: R, set, ring, ringBuild: rb });
  }
  /**
   * Per frame, inside updateSun (after the camera's glare eclipse): the sun in every frame of reference, the occluders
   * (the registered planets + the charted worlds + the war's living capitals), the hook's render-space set, the sun seen
   * through a ring from the camera, the IBL's sun lobe by the ship's own visibility, the receivers sweep (1 Hz) and the
   * orbital cloud bake.
   */
  private driveSpaceLight(camX: number, camY: number, camZ: number): void {
    const sl = this.spaceLight;
    if (!sl || !this.app) return;
    sl.flags.eclipse = this.slFlag('eclipseShadows');
    sl.flags.rings = this.slFlag('planetRings');
    sl.flags.clouds = this.slFlag('orbitalClouds');
    if (this.system.id === 'sys0') sl.setSun({ kind: 'dir', dir: SYS0_SUN_DIR, angR: SYS0_SUN_ANG_R });
    // natural objects: a penumbra is as wide as its SOURCE — a white dwarf's eclipse is knife-edged, a supergiant's soft
    else sl.setSun({ kind: 'point', pos: { x: 0, y: 0, z: 0 }, radius: this.starRNow || this.altStarR || systemSector(this.system).star.radius });
    const extra: Array<{ x: number; y: number; z: number; r: number; key: string }> = [];
    for (const [k, wp] of this.worldPlanets) extra.push({ x: wp.spec.x, y: wp.spec.y, z: wp.spec.z, r: wp.spec.radius, key: `world:${k}` });
    const b = this.battle;
    if (b) for (let s = 0; s < 2; s++) for (let k = 0; k < b.capN[s]; k++) {
      const kin = b.capKin[s][k];
      if (!kin || b.capDead[s][k] || b.capGone[s][k]) continue;
      extra.push({ x: kin.p.x, y: kin.p.y, z: kin.p.z, r: WAR_CAP_LEN * 0.18, key: `cap${s}:${k}` }); // the hull's thick middle, as the glare's
    }
    const cam = this.cam();
    const hPx = (this.app.renderer.native as unknown as { domElement?: HTMLCanvasElement }).domElement?.height ?? 1080;
    // THE HOOK'S FRAME: the hook reads positionWorld (offset-local + the scene's shift), so its origin is off − shift; about `off` alone no hull behind a planet was eclipsed and one beside the shadow was
    sl.update({ off: hookOrigin(this.off, this.rootShift()), ship: this.pos, cam: { x: camX, y: camY, z: camZ }, extra, pixelAngle: slPixelAngle(cam?.fov ?? 60, hPx) });
    this.sunVisible *= sl.camRingT;
    const q = Math.round(sl.shipVis * 20) / 20;
    if (q !== this.slEnvVis) { this.slEnvVis = q; this.envDir.set(0, 0, 0); } // the IBL's sun lobe re-bakes (a 64×32 texture) at 5 % steps
    const now = performance.now();
    if (this.sunLight?.castShadow && now - this.slRecvAt > 1000) { // the hook is installed (eclipses or rings)
      this.slRecvAt = now;
      const skip = new Set<THREE.Object3D>(this.planetAnchors.map((a) => a.group)); // impostors: their render position is not where they are
      for (const wp of this.worldPlanets.values()) skip.add(wp.group);
      sl.markReceivers([this.root, this.shipMesh], skip);
    }
    const clouds = sl.clouds;
    if (clouds && sl.flags.clouds) {
      if (!clouds.state.seed) { const seed = this.sitesSeed || this.slCommonsSeed(); if (seed) clouds.start(seed); }
      clouds.tick(Date.now() / 1000, slTod(), this.slOverride());
    }
    if (this.commonsCloudShell) this.commonsCloudShell.mesh.visible = sl.flags.clouds; // the kill switch hides it live
  }
  private slCommonsSeed(): string {
    const conn = atlas.conn;
    if (!conn) return '';
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') return w.terrainSeed;
    return '';
  }
  /** The weather pin the ground resolves with (the debug Local override over the live-ops Commons one). */
  private slOverride(): string | undefined {
    const conn = atlas.conn;
    const wm = this.app?.getModule('Weather') as { debugWeather?: string | null } | undefined;
    let commons: string | undefined;
    const id = atlas.commonsWorldId;
    if (conn && id !== null) { const wx = conn.db.skyState.worldId.find(id)?.weather; commons = wx && wx !== 'clear' ? wx : undefined; }
    return slWeatherOverride(wm?.debugWeather ?? null, commons);
  }
  /** `__eclipse()` / `__rings()` / `__orbitalClouds()` — state + levers (the kill switches are localStorage, next entry to build). */
  private installSpaceLightLevers(): void {
    const w = window as unknown as Record<string, unknown>;
    const kill = (key: string, on: boolean): void => { try { if (on) localStorage.removeItem(key); else localStorage.setItem(key, 'off'); } catch { /* no storage */ } };
    const eclipse = (): Record<string, unknown> => ({ built: !!this.spaceLight, castShadow: !!this.sunLight?.castShadow, sunVisibleCam: +this.sunVisible.toFixed(4), envVis: this.slEnvVis, ...(this.spaceLight?.state() ?? {}) });
    Object.assign(eclipse, {
      set: (on: boolean) => { kill('atlas:eclipse', on); return this.slFlag('eclipseShadows'); },
      /** The receiver visibility at an ABSOLUTE point (the CPU mirror of the hook — in the hook's own frame, `hookOrigin`: about `off` alone it answered for a point a scene shift away). */
      at: (x: number, y: number, z: number) => { const o = hookOrigin(this.off, this.rootShift()); return this.spaceLight ? this.spaceLight.hook.visibilityAt({ x: x - o.x, y: y - o.y, z: z - o.z }) : 1; },
    });
    w.__eclipse = eclipse;
    const rings = (): Record<string, unknown> => {
      const sl = this.spaceLight;
      const out = (sl?.bodies ?? []).filter((bd) => bd.ring).map((bd) => {
        const n = bd.ring!.normal, L = this.sunDirNow;
        const d = { x: this.pos.x - bd.abs.x, y: this.pos.y - bd.abs.y, z: this.pos.z - bd.abs.z }; const dl = Math.hypot(d.x, d.y, d.z) || 1;
        return { planet: bd.key, R: bd.radius, rIn: bd.ring!.rIn, rOut: bd.ring!.rOut, tauScale: bd.ring!.tauScale, sunElevDeg: +(Math.asin(n.x * L.x + n.y * L.y + n.z * L.z) * 180 / Math.PI).toFixed(2), viewMu: +((n.x * d.x + n.y * d.y + n.z * d.z) / dl).toFixed(4), visible: bd.ringBuild ? bd.ringBuild.front.visible : false, order: bd.ringBuild ? [bd.ringBuild.back.renderOrder, bd.ringBuild.front.renderOrder] : null };
      });
      return { flag: this.slFlag('planetRings'), rings: out, profileTexels: ringProfileCpu().w, profileSpan: [RING_R_IN, RING_R_OUT] };
    };
    Object.assign(rings, { set: (on: boolean) => { kill('atlas:rings', on); return this.slFlag('planetRings'); } });
    w.__rings = rings;
    const clouds = (): Record<string, unknown> => ({ flag: this.slFlag('orbitalClouds'), built: !!this.spaceLight?.clouds, shell: !!this.commonsCloudShell, ...(this.spaceLight?.clouds?.state ?? {}) });
    Object.assign(clouds, { set: (on: boolean) => { kill('atlas:orbital-clouds', on); return this.slFlag('orbitalClouds'); }, bakeNow: () => { this.spaceLight?.clouds?.bakeNow(); } });
    w.__orbitalClouds = clouds;
  }

  // ── shader gaps: black hole — ONE hole placed on demand in this system (no destination yet; a future one calls place) ──
  /** `__blackHole({ at?, mass?, disc? })` places (or moves) it, `.remove()`, `.state()`, `.set(on)` the kill switch. */
  /** A far body's reach in its own unscaled frame: the largest bounding sphere among its meshes (shells and ring included). */
  private anchorReachM(group: THREE.Group): number {
    let reach = 0;
    const inv = 1 / Math.max(1e-9, group.scale.x);
    group.updateWorldMatrix(true, true);
    const c = new THREE.Vector3(), g0 = new THREE.Vector3().setFromMatrixPosition(group.matrixWorld);
    group.traverse((o) => {
      const geo = (o as THREE.Mesh).geometry as THREE.BufferGeometry | undefined;
      if (!geo || !(o as THREE.Mesh).isMesh) return;
      if (!geo.boundingSphere) { try { geo.computeBoundingSphere(); } catch { return; } }
      const bs = geo.boundingSphere;
      if (!bs || !Number.isFinite(bs.radius)) return;
      c.copy(bs.center).applyMatrix4(o.matrixWorld);
      const r = (c.distanceTo(g0) + bs.radius * o.matrixWorld.getMaxScaleOnAxis()) * inv;
      if (Number.isFinite(r) && r > reach) reach = r;
    });
    return reach;
  }
  /**
   * THE BACK SEAT's second half: a body seated behind the cap takes its 'over' layers (a cloud shell, a ring's front
   * half, a tail's near half) out of the picture — their pull is measured from the body's own depth and would carry
   * them in front of the cap again. Layers, not `visible`: their owners write that. Restored when the body leaves.
   */
  private seatOverLayers(group: THREE.Group, back: boolean): void {
    group.traverse((o) => {
      if (back) {
        const m = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
        const over = Array.isArray(m) ? m.some((x) => x?.userData?.bodyLayer === 'over') : m?.userData?.bodyLayer === 'over';
        if (over && o.userData.backSeatMask === undefined && o.layers.mask !== 0) { o.userData.backSeatMask = o.layers.mask; o.layers.mask = 0; }
      } else if (o.userData.backSeatMask !== undefined) {
        o.layers.mask = o.userData.backSeatMask as number;
        delete o.userData.backSeatMask;
      }
    });
  }

  private installBlackHoleLever(): void {
    this.blackHole?.dispose();
    const tierFlag = (): 'off' | 'low' | 'high' | undefined => activePreset().blackHole ?? (SL_TIERS[activeTier()] as SlTierPreset).blackHole;
    const killed = (): boolean => { try { return localStorage.getItem(BLACK_HOLE_KILL_KEY) === 'off'; } catch { return false; } };
    const ctl = new BlackHoleController({
      parent: () => this.root,
      scene: () => (this.app?.world.scene as unknown as THREE.Scene | undefined) ?? null,
      renderer: () => this.app?.renderer.native ?? null,
      camera: () => this.holeCamera(),
      origin: () => ({ x: this.off.x, y: this.off.y, z: this.off.z }),
      heightPx: () => (this.app?.renderer.native as unknown as { domElement?: HTMLCanvasElement } | undefined)?.domElement?.height ?? 1080,
      flag: () => blackHoleFlag(tierFlag(), killed()),
      assets: () => loadSpaceSkyAssets(this.app?.renderer.native, nightSkyAssets(), activePreset().memoryBudgetMb >= 256 ? 64 : 32),
      systemId: () => this.system.id,
      // a sky-pass swap only while in the sector; null = the plain space background again
      setBackground: (node) => { if (this.active && this.app) setSpaceBackground(this.app.world.scene as never, node ?? spaceBackgroundNode()); },
      // space look (2026-10-08): the lens bends the sector's stars — the rig, a pair's second star — while its sky pass is the sky
      stars: () => sunBodies(),
      skyIsLensed: () => !this.legacySky.some((o) => o.visible), // the legacy void shell covers the background pass: the quads keep the sun there
    });
    this.blackHole = ctl;
    this.blackHoleViewUntil = 0; // a new sector entry: the old controller (and any view hole) went with the old one
    const w = window as unknown as Record<string, unknown>;
    const lever = (opts?: BlackHolePlaceInput) => { this.blackHoleViewUntil = 0; return ctl.place(opts ?? {}); }; // a hand-placed hole stays
    Object.assign(lever, {
      remove: () => { this.blackHoleViewUntil = 0; ctl.remove(); return ctl.state(); },
      state: () => ctl.state(),
      view: (ms?: number) => this.debugViewBlackHole(ms), // the debug menu's button: placed to be seen, gone after a minute
      viewState: () => this.debugBlackHoleView(),
      backSeat: (on?: boolean) => { if (typeof on === 'boolean') this.blackHole?.setBackSeat(on); const cf = this.blackHole?.capFrame() ?? null; return ({ on: !!cf, capDeg: cf ? (cf.coreRad * 180) / Math.PI : 0, backSeatKm: cf ? cf.backSeatM / 1000 : 0, capForward: !!cf?.forward, seated: this.backSeated, bodies: this.planetAnchors.map((a) => ({ back: a.back === true, reachM: a.reachM ?? null, scale: a.group.scale.x, distM: Math.hypot(a.abs.x - this.pos.x, a.abs.y - this.pos.y, a.abs.z - this.pos.z) })) }); },
      set: (on: boolean) => { try { if (on) localStorage.removeItem(BLACK_HOLE_KILL_KEY); else localStorage.setItem(BLACK_HOLE_KILL_KEY, 'off'); } catch { /* no storage */ } return blackHoleFlag(tierFlag(), killed()); },
    });
    w.__blackHole = lever;
  }

  /** The frame's camera for the lens: the camera object, its render-space position and world orientation (rig × camera). */
  private holeCamera(): { cam: THREE.PerspectiveCamera; pos: THREE.Vector3; quat: THREE.Quaternion } | null {
    const rig = atlas.localMesh, cam = this.cam();
    if (!rig || !cam) return null;
    return { cam, pos: cam.position.clone().applyQuaternion(rig.quaternion).add(rig.position), quat: rig.quaternion.clone().multiply(cam.quaternion) };
  }

  /** Natural objects: the sector as the layer's views see it (lib/space/natural/natural_api.ts). */
  private naturalEnv(): NaturalEnv {
    const tier = activeTier();
    const native = this.app?.renderer.native as unknown as { xr?: { isPresenting?: boolean }; domElement?: HTMLCanvasElement } | undefined;
    const headset = native?.xr?.isPresenting === true;
    return {
      root: () => this.root,
      scene: () => (this.app?.world.scene as unknown as THREE.Scene | undefined) ?? null,
      renderer: () => this.app?.renderer.native ?? null,
      camera: () => this.holeCamera(),
      heightPx: () => (this.app?.renderer.native as unknown as { domElement?: HTMLCanvasElement } | undefined)?.domElement?.height ?? 1080,
      origin: () => this.off,
      shipAbs: () => this.pos,
      sunAt: (abs, out) => {
        if (this.system.id === 'sys0') { out.set(SYS0_SUN_DIR.x, SYS0_SUN_DIR.y, SYS0_SUN_DIR.z); return Infinity; }
        const d = Math.hypot(abs.x, abs.y, abs.z);
        if (d < 1) { out.set(0, 1, 0); return 0; }
        out.set(-abs.x / d, -abs.y / d, -abs.z / d);
        return d;
      },
      anchor: (group, abs) => { this.planetAnchors.push({ group, abs }); },
      unanchor: (group) => { this.planetAnchors = this.planetAnchors.filter((a) => a.group !== group); },
      follow: (obj) => { if (!this.root) return; this.root.add(obj); this.followers.push({ obj, offset: null }); },
      addBody: (b) => {
        const rec = { mesh: b.mesh, spin: b.spin ?? 0, abs: b.abs, radius: b.radius, name: b.name, reach: b.reach };
        this.planets.push(rec);
        return () => { this.planets = this.planets.filter((p) => p !== rec); };
      },
      addCollider: (g) => { this.colliderGroups.push(g); return () => { this.colliderGroups = this.colliderGroups.filter((c) => c !== g); }; },
      nowS: () => Date.now() / 1000,
      level: headset ? 'low' : tier === 'ultra' ? 'ultra' : tier === 'med' || tier === 'high' ? 'high' : 'low',
      particleCap: activePreset().particleCap ?? 0,
      flags: { atmosphere: activePreset().planetAtmosphere === true, eclipses: !!this.spaceLight && this.slFlag('eclipseShadows'), rings: this.slFlag('planetRings'), unlit: this.spaceUnlit(), headset },
      toast: (t) => this.toast(t),
    };
  }

  /** What a view hole must not be drawn across, seen from the camera: every planet (out to its shells or rings) and the sun. */
  private holeViewBlockers(camAbs: { x: number; y: number; z: number }): ViewBlocker[] {
    const out: ViewBlocker[] = [];
    for (const p of this.planets) {
      if (!p.abs || !p.radius) continue;
      const b = bodyBlocker(camAbs, p.abs, p.radius, p.reach, p.name || 'a planet');
      if (b) out.push(b);
    }
    for (const wp of this.worldPlanets.values()) {
      const b = bodyBlocker(camAbs, wp.spec, wp.spec.radius, null, wp.name || 'a world');
      if (b) out.push(b);
    }
    // space realism: a nebula is drawn over the sky and is not lensed either — the hole keeps off its bright body
    if (this.nebulae) for (const n of nebulaSitesFor(this.system.id, this.system.id === 'sys0' ? POIS : [], this.natural?.makeup.skyNebula)) {
      const b = sphereBlocker(camAbs, { x: n.pos[0], y: n.pos[1], z: n.pos[2] }, n.radius * 0.7, n.name);
      if (b && b.angR < Math.PI / 2) out.push(b); // (inside one it is all around: nothing to steer clear of)
    }
    // …and so are the things round a star the lens does not draw (space look, 2026-10-08): a star KIND's own parts
    // (its disc, streams, jets, shells — volumes about the star, cut by the hole's cap) and a distant companion sun
    // (a sprite on the sky). The star BODIES are bent; the view keeps the hole off what would be cut.
    if (this.natural && this.system.id !== 'sys0') {
      const reach = this.natural.starPartsReachM;
      const b = reach > 0 ? sphereBlocker(camAbs, { x: 0, y: 0, z: 0 }, reach, 'the star\'s disc and jets') : null;
      if (b && b.angR < Math.PI / 2) out.push(b);
      const far = this.natural.makeup.distant;
      if (far) {
        const dx = far.pos[0] - camAbs.x, dy = far.pos[1] - camAbs.y, dz = far.pos[2] - camAbs.z, d = Math.hypot(dx, dy, dz);
        if (d > 0 && Number.isFinite(d)) out.push({ dir: { x: dx / d, y: dy / d, z: dz / d }, angR: DEBUG_VIEW_SUN_MIN_RAD, name: 'the distant companion sun' });
      }
    }
    // The sun: since 2026-10-08 the lens BENDS a star that stands beyond the hole (the Commons Reach's sun is a sky direction —
    // a hole across it is the Einstein ring), so the view keeps off it only where the star would stand NEARER than the hole
    // (a derived system's star inside the view's 50 r_s: nothing bends there, the star would cover the hole) — and under the
    // legacy sky, where the lens is only its cap and cannot draw a star at all.
    const L = this.sunDirNow; // from the ship toward the star (a direction in sys0; the camera rides within metres of the ship)
    const holeDist = schwarzschildRadiusM(BLACK_HOLE_DEFAULT_MASS) * PLACE_AHEAD_RS;
    if (sunBlocksHoleView(this.sunDistNow, holeDist) || this.legacySky.some((o) => o.visible)) out.push({ dir: { x: L.x, y: L.y, z: L.z }, angR: Math.max(this.sunAngRNow, DEBUG_VIEW_SUN_MIN_RAD), name: 'the sun' });
    out.push(...this.ownHullBlockers());
    return out;
  }

  /** The player's own hull as the chase camera sees it: a 3×3×3 lattice over its box (nothing from the cockpit, where the hull is hidden or holds the eye). */
  private ownHullBlockers(): ViewBlocker[] {
    const ship = this.shipMesh, cam = this.cam();
    if (!ship || !cam) return [];
    for (let o: THREE.Object3D | null = ship; o; o = o.parent) if (!o.visible) return [];
    ship.updateWorldMatrix(true, false);
    cam.updateWorldMatrix(true, false);
    const box = hullShellBox(ship); // ship-local
    if (box.isEmpty()) return [];
    const eye = cam.getWorldPosition(new THREE.Vector3());
    if (box.containsPoint(ship.worldToLocal(eye.clone()))) return [];
    const pts: Array<{ x: number; y: number; z: number }> = [];
    const p = new THREE.Vector3();
    for (let i = 0; i < 27; i++) {
      p.set(
        box.min.x + ((i % 3) / 2) * (box.max.x - box.min.x),
        box.min.y + ((Math.floor(i / 3) % 3) / 2) * (box.max.y - box.min.y),
        box.min.z + (Math.floor(i / 9) / 2) * (box.max.z - box.min.z),
      );
      ship.localToWorld(p).sub(eye); // world − eye: a direction, whatever the scene's shift
      pts.push({ x: p.x, y: p.y, z: p.z });
    }
    return hullPointBlockers(pts);
  }

  /**
   * Debug menu → Admin → Space: stand a black hole where it can be SEEN — the default hole, 50 r_s out on the direction
   * nearest the view's centre that is clear of every planet and the sun — for `ms` (one minute). It is this client's
   * picture only (no row, no gravity) and leaves with the sector too: the exit, a system swap, a sign-out's reload.
   */
  async debugViewBlackHole(ms: number = DEBUG_VIEW_MS): Promise<BlackHoleViewResult> {
    const fail = (reason: BlackHoleViewResult['reason'], text: string): BlackHoleViewResult => ({ ok: false, reason, text, leftS: 0, placement: null });
    const ctl = this.blackHole;
    if (!this.active || !ctl) return fail('not-in-space', 'You are not in space — launch to the sector first.');
    if (ctl.state().flag === 'off') return fail('graphics-off', 'Black hole lensing is off on this graphics profile — Options → Graphics → "Black hole lensing".');
    const c = this.holeCamera();
    if (!c) return fail('no-camera', 'No camera yet — try again in a moment.');
    const camAbs = { x: c.pos.x + this.off.x, y: c.pos.y + this.off.y, z: c.pos.z + this.off.z };
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(c.quat), up = new THREE.Vector3(0, 1, 0).applyQuaternion(c.quat);
    const placement = debugViewPlacement(camAbs, fwd, up, this.holeViewBlockers(camAbs), schwarzschildRadiusM(BLACK_HOLE_DEFAULT_MASS));
    this.blackHoleViewUntil = 0; // re-armed below, once it stands
    const st = await ctl.place({ at: placement.at });
    if (this.blackHole !== ctl || !this.active) return fail('not-in-space', 'You left the sector before it formed.');
    if (!st.placed || st.error) { this.debugRemoveBlackHole(); return fail('failed', `The black hole did not form: ${st.error || 'unknown'}.`); } // never an earlier hole left standing with no clock
    const life = Math.min(DEBUG_VIEW_MS, Math.max(1000, Number.isFinite(ms) ? ms : DEBUG_VIEW_MS));
    this.blackHoleViewUntil = performance.now() + life;
    const where = viewBearingText(placement);
    const km = Math.round(placement.distanceM / 1000).toLocaleString('en-US');
    const crowded = placement.clear ? '' : ` No clear sky from here — it overlaps ${placement.nearest || 'a body'}.`;
    return {
      ok: true, reason: '', leftS: debugViewLeftS(this.blackHoleViewUntil, performance.now()), placement,
      text: `Black hole ${km} km out, ${where}.${crowded} It leaves in ${Math.round(life / 1000)} s, or when you leave the sector or sign out.`,
    };
  }

  /** Take the debug view's hole away now (also the timer's own path). */
  debugRemoveBlackHole(): void {
    this.blackHoleViewUntil = 0;
    this.blackHole?.remove();
  }

  /** The debug view's state for the menu's countdown: whether a view hole stands and the whole seconds it has left. */
  debugBlackHoleView(): { inSpace: boolean; standing: boolean; shown: boolean; leftS: number } {
    const leftS = debugViewLeftS(this.blackHoleViewUntil, performance.now());
    const st = this.blackHole?.state();
    return { inSpace: this.active, standing: leftS > 0 && !!st?.placed, shown: !!st?.shown, leftS };
  }

  // ── The realism wave (2026-09-25, report §9): the sky, the sun, the camera's feel ─────────────────────────
  /**
   * The LEGACY sky (the pre-2026-09-25 void): a black inward shell masking the engine sky + two instanced
   * star layers (clip-space quads, per-star pixel sizes, per-layer seeds). It stands until the real sky's
   * assets land, and forever under `localStorage atlas:space-sky = legacy`.
   */
  private buildLegacySky(): void {
    if (!this.root) return;
    const voidShell = new THREE.Mesh(
      new THREE.SphereGeometry(2_600_000, 24, 16),
      new THREE.MeshBasicMaterial({ color: 0x01030a, side: THREE.BackSide, depthWrite: false }),
    );
    voidShell.renderOrder = -10;
    this.root.add(voidShell);
    this.followers.push({ obj: voidShell, offset: null });
    this.legacySky.push(voidShell);
    for (const [count, radius, sizePx, color, seed] of [
      [2600, 120_000, 2.6, 0xffffff, 2166136261],
      [1400, 90_000, 1.9, 0xbfd4ff, 0x9e3779b9],
    ] as const) {
      const centers = new Float32Array(count * 3), sizes = new Float32Array(count);
      let h: number = seed; // PER-LAYER SEED (2026-09-14): one shared seed put both layers on the same directions
      const rnd = (): number => { h ^= h << 13; h ^= h >>> 17; h ^= h << 5; return ((h >>> 0) / 4294967296); };
      for (let i = 0; i < count; i++) {
        const t = rnd() * Math.PI * 2, p = Math.acos(rnd() * 2 - 1);
        centers[i * 3] = radius * Math.sin(p) * Math.cos(t);
        centers[i * 3 + 1] = radius * Math.cos(p);
        centers[i * 3 + 2] = radius * Math.sin(p) * Math.sin(t);
        sizes[i] = sizePx * (0.7 + rnd() * 0.8);
      }
      const stars = this.buildStarLayer(centers, sizes, color);
      this.root.add(stars);
      this.followers.push({ obj: stars, offset: null });
      this.legacySky.push(stars);
    }
  }

  /** R7: the star-streak layer (session-shared) rides the ship under the inverse sky rotation. */
  private mountStreaks(): void {
    if (!this.root) return;
    const m = spaceStreakLayer();
    if (!m) return;
    const q = setSpaceSkySystem(this.system.id);
    m.quaternion.copy(q).invert(); // the background samples R·d; the layer's stars sit at Rᵀ·s
    m.visible = false;
    this.root.add(m);
    this.followers.push({ obj: m, offset: null });
    this.streaks = m;
  }

  /** The star's colour + key intensity for this system (sys0: a 5,800 K sun at infinity; alt: its star's hue). */
  private applySunLook(): void {
    const alt = this.system.id !== 'sys0';
    const kelvin = alt ? kelvinOfHex(this.system.starColor) : 5800;
    const rgb = kelvinToRgb(kelvin);
    const lin = new THREE.Color().setRGB(rgb[0], rgb[1], rgb[2], THREE.SRGBColorSpace);
    this.sunRgb = [lin.r, lin.g, lin.b];
    this.sunLight?.color.copy(lin);
    setSunLook(this.sunRgb, SUN_DISC_EMISSION, 1);
  }

  /**
   * One sun, every frame (Animation/37, after the followers): the direction from the ship (sys0: the fixed
   * sector sun at infinity; an alt system: its star at the origin), the key light seated ON the ship along
   * it (target under the root — an exact world direction), the analytic eclipse by every nearer planet and
   * capital, the disc/corona/glare quads, the glare inputs of the space exposure, planetshine from the
   * nearest big planet, and the IBL lobe re-aimed past 3°.
   */
  /**
   * The player's own hull as an eclipse occluder (m): the geometric mean of its two SMALLER half-extents, measured
   * in the hull's own frame (never a world AABB — a banked hull's box inflates), ×0.9 — a conservative disc that
   * hides the sun only when the fuselage truly crosses it. Plumes, glows and instanced children are not hull.
   * Re-measured every 120 frames (a skin swap changes it).
   */
  private ownHullOccR(): number {
    const ship = this.shipMesh;
    if (!ship) return 0;
    if (this.hullOccFrame-- > 0) return this.hullOccR;
    this.hullOccFrame = 120;
    const inv = this._hullInv.copy(ship.matrixWorld).invert(), box = this._hullBox.makeEmpty();
    ship.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || (m as unknown as THREE.InstancedMesh).isInstancedMesh || !m.geometry || !m.visible) return;
      const mat = m.material as THREE.Material | THREE.Material[];
      if (Array.isArray(mat) ? mat.every((x) => x.transparent) : mat?.transparent) return;
      if (!m.geometry.boundingBox) m.geometry.computeBoundingBox();
      const bb = m.geometry.boundingBox;
      if (bb) box.union(this._hullBox2.copy(bb).applyMatrix4(this._hullM.multiplyMatrices(inv, m.matrixWorld)));
    });
    if (box.isEmpty()) { this.hullOccR = 0; return 0; }
    const e = box.getSize(this._hullSize).multiplyScalar(0.5);
    const h = [e.x, e.y, e.z].sort((a, b) => a - b);
    this.hullOccR = Math.sqrt(h[0] * h[1]) * 0.9;
    return this.hullOccR;
  }
  private hullOccR = 0;
  private hullOccFrame = 0;
  private readonly _hullInv = new THREE.Matrix4();
  private readonly _hullM = new THREE.Matrix4();
  private readonly _hullBox = new THREE.Box3();
  private readonly _hullBox2 = new THREE.Box3();
  private readonly _hullSize = new THREE.Vector3();

  /** THE STERN LAW's telemetry: the player's nozzle seats (x, r) against the hull shell's span (the probe's check). */
  private playerSeatLook(): { seats?: number[][]; span?: number } {
    const hp = this.playerPlumes, ship = this.shipMesh;
    if (!hp || !ship) return {};
    const b = hullShellBox(ship);
    return { seats: hp.seats.map((q) => [Math.round(q.x * 100) / 100, Math.round(q.r * 100) / 100]), span: Math.round((b.max.x - b.min.x) * 100) / 100 };
  }

  private updateSun(dt = 0): void {
    const sun = this.sunLight, tgt = this.sunTarget;
    if (!sun || !tgt || !this.app) return;
    const dir = this.sunDirNow;
    let starDist = Infinity;
    if (this.system.id !== 'sys0') {
      const footprint = this.altStarR || (this.altStarR = systemSector(this.system).star.radius);
      // natural objects: the star's KIND gives the disc its drawn size and colour and the key light its variability; the
      // footprint (the layout's star radius) stays the light's reference and the lanes' measure.
      const sf = this.natural?.starFrame(dt) ?? null;
      // a star view that faulted mid-visit left the key light, `sunRgb` and the rig in its last colour and emission — the layout's own look comes back, once
      if (sf) this.starFrameSeen = true; else if (this.starFrameSeen) { this.starFrameSeen = false; this.applySunLook(); }
      const starR = sf ? sf.radiusM : footprint;
      this.starRNow = sf ? sf.radiusM : 0;
      dir.set(-this.pos.x, -this.pos.y, -this.pos.z);
      starDist = Math.max(1, dir.length());
      dir.divideScalar(starDist);
      this.sunAngRNow = angularRadius(starR, starDist);
      sun.intensity = sunIntensityAt(starDist, footprint * 2.4, SPACE_SUN_INTENSITY, 0.45) * (sf ? Math.max(0, sf.lightScale) : 1);
      if (sf) {
        this.sunRgb = sf.rgb;
        sun.color.setRGB(sf.rgb[0], sf.rgb[1], sf.rgb[2]);
        setSunLook(sf.rgb, SUN_DISC_EMISSION * Math.max(0, sf.emission), Math.max(0, sf.corona));
      }
    } else {
      dir.set(SYS0_SUN_DIR.x, SYS0_SUN_DIR.y, SYS0_SUN_DIR.z);
      this.sunAngRNow = SYS0_SUN_ANG_R;
      sun.intensity = SPACE_SUN_INTENSITY;
    }
    const ship = this.shipLocal();
    tgt.position.copy(ship);
    sun.position.copy(ship).addScaledVector(dir, 1000);
    // R4: the planets' own light — the same colour × intensity as the key (their DIRECTIONS are per planet).
    (uPlanetSun as unknown as { value: THREE.Color }).value.setRGB(this.sunRgb[0] * sun.intensity, this.sunRgb[1] * sun.intensity, this.sunRgb[2] * sun.intensity);

    // The eclipse: every body NEARER than the star, as a disc seen from the CAMERA (session 2: a chase camera
    // sits up to 170 m off the ship's centre, and the player's own hull is an occluder from there — the glare is a
    // depthTest:false lens effect, so a hull across the disc was veiled by the flare it should have hidden). The
    // ship stands in until the camera exists.
    const rig = atlas.localMesh, cam = this.cam();
    let vx = this.pos.x, vy = this.pos.y, vz = this.pos.z;
    const camQ = rig && cam ? this._lookQ.copy(rig.quaternion).multiply(cam.quaternion) : null;
    const camL = rig && cam ? this._lookV.copy(cam.position).applyQuaternion(rig.quaternion).add(rig.position) : null;
    if (camL) { vx = camL.x + this.off.x; vy = camL.y + this.off.y; vz = camL.z + this.off.z; }
    const occ = this.eclipseOcc;
    occ.length = 0;
    const push = (x: number, y: number, z: number, r: number): void => {
      const dx = x - vx, dy = y - vy, dz = z - vz;
      const d = Math.hypot(dx, dy, dz);
      if (d < 1 || d > starDist) return;
      occ.push({ dir: { x: dx / d, y: dy / d, z: dz / d }, angR: angularRadius(r, d) });
    };
    const hullR = camL && !this.cockpitView && this.shipMesh?.visible ? this.ownHullOccR() : 0;
    if (hullR > 0) push(this.pos.x, this.pos.y, this.pos.z, hullR);
    for (const pl of this.planets) if (pl.abs && pl.radius) push(pl.abs.x, pl.abs.y, pl.abs.z, pl.radius);
    for (const wp of this.worldPlanets.values()) push(wp.spec.x, wp.spec.y, wp.spec.z, wp.spec.radius);
    const b = this.battle;
    if (b) {
      for (let s = 0; s < 2; s++) for (let k = 0; k < b.capN[s]; k++) {
        const kin = b.capKin[s][k];
        if (!kin || b.capDead[s][k] || b.capGone[s][k]) continue;
        push(kin.p.x, kin.p.y, kin.p.z, WAR_CAP_LEN * 0.18); // the hull's thick middle, not its full length
      }
    }
    this.sunVisible = eclipseVisible(dir, this.sunAngRNow, occ);
    this.sunDistNow = starDist;
    this.driveSpaceLight(vx, vy, vz); // shader gaps R4: the receivers' sun (and the camera's through a ring — sunVisible × T)

    // The camera: its root-frame position and world orientation (rig × camera — no updateMatrixWorld).
    if (cam && camQ && camL) {
      placeSun(camL, camQ, dir, this.sunAngRNow, this.sunVisible, starDist); // the distance: a black-hole lens bends a star by where it stands
      // natural objects: everything else stellar (a companion, a stream, a disc, jets) is seated beside the rig
      if (this.natural?.hasStar) this.natural.placeStar({ camLocal: camL, camQuat: camQ, cam, camAbs: { x: vx, y: vy, z: vz }, dir, starDist, visible: this.sunVisible, heightPx: (this.app.renderer.native as unknown as { domElement?: HTMLCanvasElement } | undefined)?.domElement?.height ?? 1080 });
      // The glare inputs of the space exposure: where the sun sits on screen.
      const v = this._lookV2.copy(dir).applyQuaternion(camQ.invert());
      const inFront = v.z < -1e-3;
      const pm = cam.projectionMatrix.elements;
      const nx = inFront ? (pm[0] * v.x) / -v.z : 9, ny = inFront ? (pm[5] * v.y) / -v.z : 9;
      spaceGlare.visible = this.sunVisible;
      spaceGlare.ndcDist = Math.hypot(nx, ny);
      spaceGlare.onScreen = inFront && Math.abs(nx) < 1.15 && Math.abs(ny) < 1.15;
    }

    // Planetshine: the nearest big planet's lit albedo, from its direction, by its share of the sky.
    const ps = this.planetshine;
    if (ps) {
      let best = 0, bx = 0, by = -1, bz = 0, col = 0x8fb3dd;
      const consider = (x: number, y: number, z: number, r: number, rim: number): void => {
        const dx = x - this.pos.x, dy = y - this.pos.y, dz = z - this.pos.z;
        const d = Math.hypot(dx, dy, dz);
        if (d <= r) return;
        const ang = angularRadius(r, d);
        // The lit fraction of the face we see: (1 + cos phase)/2, the phase between planet→sun and planet→ship.
        const lit = 0.5 * (1 - (dx * dir.x + dy * dir.y + dz * dir.z) / d);
        const share = (1 - Math.cos(ang)) * lit;
        if (share > best) { best = share; bx = dx / d; by = dy / d; bz = dz / d; col = rim; }
      };
      if (this.system.id === 'sys0') for (const p of PLANETS) consider(p.pos[0], p.pos[1], p.pos[2], p.radius, p.rim);
      else for (const pl of this.planets) if (pl.abs && pl.radius) consider(pl.abs.x, pl.abs.y, pl.abs.z, pl.radius, 0x9fb0c8);
      seatHemisphereUp(ps, bx, by, bz); // THE HEMISPHERE'S UP: three reads the sky side from the light's WORLD position — a root-local unit vector plus the scene's shift lit every hull from the shift's own direction (the horizon), whatever planet shone
      ps.color.setHex(col);
      ps.intensity = Math.min(1.2, best * PLANETSHINE_K);
    }

    // The IBL lobe follows the sun (sys0: once; an alt system: past 3° of drift).
    if (this.envDir.lengthSq() === 0 || this.envDir.dot(dir) < 0.9986) {
      this.envDir.copy(dir);
      this.app.world.scene.environment = spaceEnvironment({ x: dir.x, y: dir.y, z: dir.z }, this.slEnvVis === 1 ? this.sunRgb : [this.sunRgb[0] * this.slEnvVis, this.sunRgb[1] * this.slEnvVis, this.sunRgb[2] * this.slEnvVis]); // shader gaps R4: the IBL's SUN lobe follows the ship's visibility (the floor is fill)
    }
  }
  private readonly eclipseOcc: Array<{ dir: { x: number; y: number; z: number }; angR: number }> = [];
  private altStarR = 0;

  /**
   * R7 — motion cues: the radial star streaks (overcruise → hyper), the FOV kick (+6° boost, +12° hyper,
   * eased) and the trauma² shake on top of the zoom's camera pose.
   */
  private updateLookFx(dt: number): void {
    const cam = this.cam();
    if (!cam || this.interior) return;
    const hyperJump = this.hyper === 'jump';
    const speed = hyperJump ? HYPER_SPEED : this.vel.length();
    // Streaks: toward the apex — the screen point the velocity points at (null behind the camera).
    const motion = this.spaceFlag('spaceMotion');
    // Space campaign L (2026-10-01, §22.2 / XAG 117): every SECONDARY effect below scales by the reduced-motion choice
    // (desktop: Options → Controls → Flight; headset: the Comfort page) — never the aim, the damage or the threats.
    const fxs = this.fxScale;
    const streak = this.sysJump || !motion ? 0 : starStreak(speed) * fxs.streaks;
    if (streak > 0.01 && this.vel.lengthSq() > 1) {
      const rig = atlas.localMesh;
      const camQ = this._lookQ.copy(rig ? rig.quaternion : this.quat).multiply(cam.quaternion).invert();
      const v = this._lookV2.copy(this.vel).normalize().applyQuaternion(camQ);
      const pm = cam.projectionMatrix.elements;
      setSpaceStreaks(streak, v.z < -1e-3 ? { x: (pm[0] * v.x) / -v.z, y: (pm[5] * v.y) / -v.z } : null);
    } else setSpaceStreaks(0, null);
    // Velocity dust: world-fixed motes (the ship's ABSOLUTE position mod the box — no jump at a rebase).
    driveDust(this.pos, this.vel, this.sysJump || !motion ? 0 : dustAlpha(speed) * fxs.dust);
    this.drivePlumeSet(); // R5: the player's and every near FSM hull's engine plumes
    this.driveWounds(dt); // R8: the shield's ripples, the venting under 40 % hull
    for (const f of this.rockFields) f.update(dt, this.pos, this.off); // R10: near fields tumble
    // The FOV kick, eased.
    if (this.baseFov > 0) {
      const want = motion ? fovKickDeg(this.boost && this.boosterE > 0 && this.throttle > 0.05 ? 1 : 0, hyperJump || this.sysJump ? 1 : 0) * fxs.fovKick : 0; // R13: the tunnel punches the FOV too
      this.fovKick += (want - this.fovKick) * Math.min(1, dt * FOV_EASE);
      const fov = this.baseFov + this.fovKick;
      if (Math.abs(cam.fov - fov) > 0.01) { cam.fov = fov; cam.updateProjectionMatrix(); }
    }
    // The shake: trauma² on the zoom's base pose (applyCamZoom records it).
    this.trauma = Math.max(0, this.trauma - TRAUMA_DECAY_PER_S * dt);
    if (!motion) this.trauma = 0;
    if (this.camBaseSet) {
      const a = shakeAmp(this.trauma) * fxs.shake;
      if (a > 1e-4) {
        const t = performance.now() / 1000;
        const n = (f: number, ph: number): number => Math.sin(t * f + ph) * 0.6 + Math.sin(t * f * 2.31 + ph * 1.7) * 0.4;
        cam.position.copy(this.camBasePos).add(this._lookV.set(n(23, 0.3), n(19, 1.9), n(17, 4.1)).multiplyScalar(SHAKE_POS * a));
        cam.rotation.set(this.camBaseRot.x + n(29, 2.2) * SHAKE_ROT * a, this.camBaseRot.y + n(31, 5.1) * SHAKE_ROT * a, this.camBaseRot.z + n(27, 0.7) * SHAKE_ROT * 0.6 * a);
        this.shaking = true;
      } else if (this.shaking) {
        cam.position.copy(this.camBasePos); cam.rotation.copy(this.camBaseRot); this.shaking = false;
      }
    }
  }
  /** A short history of the sky handovers (probe diagnostics — `look().log`). */
  private readonly lookEvents: string[] = [];
  private lookLog(s: string): void { this.lookEvents.push(`${Math.round(performance.now() / 100) / 10}s ${s}`); if (this.lookEvents.length > 24) this.lookEvents.shift(); }

  /** The picture's telemetry (`__space.look()`). */
  private lookState(): object {
    const post = this.app?.getModule('Post') as { spaceProfileOn?: boolean; spaceExposureStats?: object } | null;
    const scene = this.app?.world.scene;
    const lw = new THREE.Vector3(), tw = new THREE.Vector3();
    let lightDir: number[] | null = null;
    if (this.sunLight && this.sunTarget) {
      this.sunLight.updateMatrixWorld(true); this.sunTarget.updateMatrixWorld(true);
      this.sunLight.getWorldPosition(lw); this.sunTarget.getWorldPosition(tw);
      const d = lw.sub(tw).normalize();
      lightDir = [d.x, d.y, d.z];
    }
    const r4 = (v: number): number => Math.round(v * 1e4) / 1e4;
    return {
      system: this.system.id,
      sky: { ...spaceSkyState, legacy: this.legacySky.map((o) => o.visible), streaksMounted: !!this.streaks },
      background: scene ? { node: !!(scene as unknown as { backgroundNode?: unknown }).backgroundNode, space: (scene as unknown as { backgroundNode?: unknown }).backgroundNode === spaceBackgroundNode() && !!spaceBackgroundNode() } : null,
      environment: (scene?.environment as THREE.Texture | null)?.name ?? null,
      sun: { ...sunState, dirNow: [r4(this.sunDirNow.x), r4(this.sunDirNow.y), r4(this.sunDirNow.z)], angRNow: this.sunAngRNow, visible: this.sunVisible, lightDir, intensity: this.sunLight?.intensity ?? null },
      post: { ...spacePostState, on: post?.spaceProfileOn ?? null },
      exposure: post?.spaceExposureStats ?? null,
      glare: { ...spaceGlare },
      dust: { ...dustState },
      lights: { starlight: this.starlight?.intensity ?? null, planetshine: this.planetshine?.intensity ?? null, planetshineDir: this.planetshine ? this.planetshine.getWorldPosition(new THREE.Vector3()).toArray().map(r4) : null }, // the DIRECTION three lights from: the light's world position (its own position carries the scene's shift now)
      fov: { base: this.baseFov, kick: this.fovKick },
      trauma: this.trauma,
      fx: { ...spaceFxState }, plumes: { ...plumeState, player: !!this.playerPlumes, capitals: this.capPlumes.size, ...this.playerSeatLook() }, hulls: { ...hullLookState, unlit: this.spaceUnlit() },
      planets: { ...planetLookState }, shield: { ...shieldState }, rocks: { ...rockFieldState },
      patrols: this.patrolMesh ? { cap: this.patrolMesh.instanceMatrix.count, live: this.patrolMesh.count, pinned: this.patrolPinned } : null,
      log: [...this.lookEvents],
    };
  }
  /** The legacy lever: `localStorage atlas:space-unlit = 1` keeps the war's unlit bake and the old glow dots. */
  private spaceUnlit(): boolean {
    try { return localStorage.getItem('atlas:space-unlit') === '1'; } catch { return false; }
  }
  /** A realism-wave tier switch (lib/quality_tiers SPACE_TIER_FLAGS; a saved profile without the key takes its tier's). */
  private spaceFlag(k: 'spaceSky' | 'spaceMotion' | 'spacePlumes' | 'spaceLitHulls' | 'spaceShields'): boolean {
    if (this.spaceUnlit()) return false;
    const v = activePreset()[k];
    return v ?? SPACE_TIER_FLAGS[activeTier()][k] ?? true;
  }

  /**
   * R6 — a war hull under the key: PBR from the GLB's own maps + a civ fresnel rim + (capitals / escorts) the
   * procedural window layer (lib/space/hull_look.ts), materials shared across clones of one master. `wrap` must
   * be PARENTLESS (its own matrix is the nose −Z frame above it). The unlit bake behind `atlas:space-unlit`.
   */
  private dressWarHull(wrap: THREE.Object3D, side: 0 | 1, windows: boolean): void {
    if (!this.spaceFlag('spaceLitHulls')) { this.bakeShipUnlit(wrap); return; }
    const minted = lightWarHull(wrap, side === 0 ? 0xffd873 : 0x9fe8ff, side * 7 + 3, this.warLitCache, windows);
    for (const m of minted) this.warBakedMats.push(m);
  }

  /**
   * R5 — engine plumes, driven per frame: the player's (throttle / boost from the stick), every FSM hull's
   * inside plume range (throttle = speed over its class's top speed; attached lazily — a skin swap clears the
   * root and the next frame re-attaches), capitals are driven by updateBattle.
   */
  private drivePlumeSet(): void {
    if (!this.spaceFlag('spacePlumes')) {
      // Live off: every plume set hides (they stay seated — a flip back on shows them the next frame).
      if (this.playerPlumes) this.playerPlumes.mesh.visible = false;
      for (const p of this.pirates) if (p.plumes) p.plumes.mesh.visible = false;
      for (const hp of this.capPlumes.values()) hp.mesh.visible = false;
      return;
    }
    const ship = this.shipMesh;
    if (ship && (!this.playerPlumes || this.playerPlumes.mesh.parent !== ship)) {
      const spec = CHASSIS[this.chassisKey] ?? CHASSIS[DEFAULT_CHASSIS];
      this.playerPlumes = attachPlumes(ship, spec.hullSize, 0x6fb2ff, 1);
    }
    if (this.playerPlumes) drivePlumes(this.playerPlumes, Math.max(this.throttle, this.hyper === 'jump' ? 1 : 0), this.boost && this.boosterE > 0 && this.throttle > 0.05, this.cruise > 1.5 ? Math.min(1, (this.cruise - 1) / 9) : 0);
    for (const p of this.pirates) {
      const alive = p.deadUntil === 0 && p.root.visible;
      const len = p.cls && p.cls !== 'fighter' ? HULL_CLASS[p.cls].len : 11 + p.tier * 2;
      const range = len >= 90 ? 16_000 : len >= 25 ? 9_000 : 5_000;
      const near = alive && p.root.position.distanceToSquared(this._ship) < range * range;
      if (!near) { if (p.plumes) p.plumes.mesh.visible = false; continue; }
      if (!p.plumes || p.plumes.mesh.parent !== p.root) {
        const tint = p.ally ? 0x5cff9d : p.warSide === 0 ? 0xffb347 : p.warSide === 1 ? 0x7fdcff : 0xff5a3c;
        p.plumes = attachPlumes(p.root, len, tint, p.jinkSeed) ?? undefined;
        if (!p.plumes) continue;
      }
      const top = Math.max(1, this.statsOf(p).speed);
      const sp = p.vel.length() / top;
      drivePlumes(p.plumes, Math.min(1, 0.25 + sp * 0.9), sp > 1.1, p.ai === 'flee' || p.ai === 'evade' ? 0.6 : 0);
    }
  }

  /**
   * R8 — the player's shield and wounds, per frame: seat the shield ellipsoid on the current hull (a skin swap
   * clears shipMesh), tick its ripples with what each arc has left, and under 40 % hull VENT — gas jets and
   * embers off random plating (the batched FX, ~6 a second whatever the frame rate).
   */
  private driveWounds(dt: number): void {
    const ship = this.shipMesh;
    if (!ship || !this.spaceFlag('spaceShields')) { if (this.shield) this.shield.mesh.visible = false; return; }
    if (!this.shield || this.shield.mesh.parent !== ship) {
      const box = hullShellBox(ship);
      const half = box.getSize(new THREE.Vector3()).multiplyScalar(0.5);
      this.shield = new HullShield(half);
      this.shield.mesh.position.copy(box.getCenter(new THREE.Vector3()));
      ship.add(this.shield.mesh);
    }
    const arc = Math.max(1, this.arcMax());
    this.shield.tick(dt, this.shieldF / arc, this.shieldR / arc);
    if (!this.fx || this.hull >= this.stats.hp * 0.4 || this.hull <= 0) return;
    this.ventAcc += dt;
    const every = 1 / (4 + 6 * (1 - this.hull / (this.stats.hp * 0.4))); // more vents the worse it gets
    if (this.ventAcc < every) return;
    this.ventAcc = 0;
    const box = hullShellBox(ship);
    const p = new THREE.Vector3(
      THREE.MathUtils.lerp(box.min.x, box.max.x, Math.random()),
      THREE.MathUtils.lerp(box.min.y, box.max.y, 0.3 + Math.random() * 0.7),
      THREE.MathUtils.lerp(box.min.z, box.max.z, Math.random()));
    const out = p.clone().sub(box.getCenter(new THREE.Vector3())).normalize();
    ship.updateMatrixWorld(true);
    const w = ship.localToWorld(p);                                   // render space
    const sp = this.app?.world.scene.position ?? new THREE.Vector3();
    const d = out.transformDirection(ship.matrixWorld).multiplyScalar(22).add(this.vel);
    const size = Math.max(2, (box.max.z - box.min.z) * 0.25);
    this.fx.vent(w.x - sp.x + this.off.x, w.y - sp.y + this.off.y, w.z - sp.z + this.off.z, d.x, d.y, d.z, size);
  }

  /**
   * R8 — a strike on the player: the shield ripples where it came from (hull-local), an arc that just hit zero
   * collapses, and a HULL hit leaves a scorch decal ON the plating (a ray onto the hull from the attacker's side;
   * the decal rides the hull — impact_decals' `parent`).
   */
  private woundFx(boltVel: THREE.Vector3, rear: boolean, shieldTook: number, hullTook: number, arcBefore: number): void {
    const ship = this.shipMesh;
    if (!ship || !this.spaceFlag('spaceShields') || boltVel.lengthSq() < 1e-6) return;
    // The attacker's side, hull-local: against the bolt, into the ship's frame.
    const from = boltVel.clone().normalize().negate().applyQuaternion(this.quat.clone().invert());
    if (this.shield) {
      if (shieldTook > 0) this.shield.hit(from);
      const now = rear ? this.shieldR : this.shieldF;
      if (arcBefore > 0 && now <= 0) this.shield.collapse();
    }
    if (hullTook <= 0 || !this.app) return;
    ship.updateMatrixWorld(true);
    const box = hullShellBox(ship);
    const c = box.getCenter(new THREE.Vector3());
    const R = box.getSize(new THREE.Vector3()).length();
    const originL = c.clone().addScaledVector(from, R);
    const o = ship.localToWorld(originL.clone());
    const target = ship.localToWorld(c.clone());
    const ray = new THREE.Raycaster(o, target.sub(o).normalize(), 0, R * 2);
    const hits = ray.intersectObject(ship, true).filter((h) => (h.object as THREE.Mesh).isMesh && h.object.name !== 'space-shield' && h.object.name !== 'space-plumes');
    const hit = hits[0];
    if (!hit || !hit.face) return;
    const n = hit.face.normal.clone().transformDirection(hit.object.matrixWorld);
    try { impactDecals(this.app.world.scene).place('scorch', hit.point, n, { parent: ship, sizeMul: 0.6 + Math.min(1.4, hullTook / 12) }); } catch { /* the decal pool is optional */ }
  }

  private shaking = false;
  /** Add camera trauma (0..1) — hits, near blasts. */
  private addTrauma(t: number): void { this.trauma = Math.min(1, this.trauma + Math.max(0, t)); }

  private local(ax: number, ay: number, az: number, out: THREE.Vector3): THREE.Vector3 {
    return out.set(ax - this.off.x, ay - this.off.y, az - this.off.z);
  }

  /**
   * A sector sound AT an offset-local point (2026-09-20): the cockpit hears WHICH WAY the volley, the warp-in or the
   * death came from. Direction only — every call site's own gain keeps owning distance (the one-distance-owner law) —
   * and never occluded: there is no wall between two hulls, and physics holds no sector colliders worth a ray.
   * Offset-local → render space through the root's own matrix → the audio runtime's frame (render − scene.position),
   * the SAME conversion the listener pose gets, so the floating origin cancels exactly.
   */
  private sfxAtLocal(key: string, local: THREE.Vector3, opts: SfxAtOpts): void {
    const root = this.root, app = this.app;
    if (!root || !app) { sfx(key, opts); return; }
    const w = root.localToWorld(this._sfxV.copy(local));
    const sp = app.world.scene.position;
    sfxAt(key, { x: w.x - sp.x, y: w.y - sp.y, z: w.z - sp.z }, { ...opts, occlude: false });
  }

  /** The ship's own offset-local position (scratch _v3 — copy if you keep it). */
  private shipLocal(): THREE.Vector3 {
    return this.local(this.pos.x, this.pos.y, this.pos.z, this._v3);
  }

  // ── PUBLIC user worlds as planets (the galactic-map wave) ────────────────
  /** World rows that hang as planets: user-created, public, unquarantined. */
  private eligibleWorldRows(): Array<{ worldId: bigint; name: string; owner: { isEqual(o: unknown): boolean } }> {
    const conn = atlas.conn;
    if (!conn) return [];
    const out: Array<{ worldId: bigint; name: string; owner: { isEqual(o: unknown): boolean } }> = [];
    try {
      for (const w of conn.db.world.iter()) {
        const gov = conn.db.worldGovernance.worldId.find(w.worldId);
        if (worldPlanetEligible(w.kind, w.visibility, gov?.quarantined ?? false)) out.push(w);
      }
    } catch { /* not subscribed yet */ }
    return out;
  }

  /** Diff the replicated world table against the scene — planets appear the
   *  moment a public world is charted, and fade when it goes private. */
  private syncWorldPlanets(): void {
    if (!this.root) return;
    const rows = this.eligibleWorldRows();
    const seen = new Set<string>();
    for (const w of rows) {
      const key = w.worldId.toString();
      seen.add(key);
      if (this.worldPlanets.has(key)) continue;
      this.buildWorldPlanet(w.worldId, w.name);
    }
    for (const [key, wp] of this.worldPlanets) {
      if (seen.has(key)) continue;
      this.planetAnchors = this.planetAnchors.filter((a) => a.group !== wp.group);
      const col = this.worldClusterCols.get(key);
      if (col) {
        this.colliderGroups = this.colliderGroups.filter((g) => g !== col);
        this.worldClusterCols.delete(key);
      }
      this.root.remove(wp.group);
      wp.group.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh) { freeLater(m.geometry); freeLater(m.material as THREE.Material); }
      });
      this.worldPlanets.delete(key);
    }
  }

  /** A charted world's planet: pooled 4K face + an IDENTITY TINT from the
   *  world's own hue — five textures, endless worlds, no two alike. */
  private buildWorldPlanet(worldId: bigint, name: string): void {
    if (!this.root) return;
    const spec = worldPlanetOf(worldId);
    const face = WORLD_PLANET_POOL[spec.texIdx];
    const tint = new THREE.Color().setHSL(spec.hue, 0.38, 0.78);
    // space look O-6: in a derived system the one key light points star → SHIP, so a classic lit world was lit from the
    // ship's side (wrong for a world across its star). There it wears the v2 planet material, self-lit from the star's
    // own direction; in the Commons Reach (the key light IS the sun's direction) and under the unlit lever it is as it was.
    const sunW = this.system.id === 'sys0' ? SpaceModule.SUN_DIR : new THREE.Vector3(-spec.x, -spec.y, -spec.z).normalize();
    const rimHex = new THREE.Color().setHSL(spec.hue, 0.6, 0.6).getHex();
    const look: PlanetLook | null = this.system.id === 'sys0' || this.spaceUnlit() ? null
      : planetMaterial({ rim: rimHex, atmosphere: 0.6, ocean: false, tint: tint.getHex(), sunDir: sunW, light: null });
    const mat: THREE.Material = look ? look.material : new THREE.MeshStandardMaterial({ color: tint, roughness: 0.92, metalness: 0 });
    const texLoader = new THREE.TextureLoader();
    const genW = this.actGen;
    texLoader.load(`${ASSET_STORE_BASE}/${face.tex}`, (t) => {
      if (genW !== this.actGen) { t.dispose(); return; }
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = 8;
      if (look) { look.setMap(t); this.lookTex.push(t); } // a node-held texture: freed with this activation's list
      else { const sm = mat as THREE.MeshStandardMaterial; sm.map = t; sm.needsUpdate = true; } // the tint STAYS — it is the world's identity
    }, undefined, () => { /* tinted sphere is a legal fallback */ });
    if (face.normal) {
      texLoader.load(`${ASSET_STORE_BASE}/${face.normal}`, (t) => {
        if (genW !== this.actGen) { t.dispose(); return; }
        if (look) { look.setNormal(t, 0); this.lookTex.push(t); } // updatePlanetLod raises it (baseBump)
        else { const sm = mat as THREE.MeshStandardMaterial; sm.normalMap = t; sm.normalScale = new THREE.Vector2(face.bump, face.bump); sm.needsUpdate = true; }
      }, undefined, () => { /* flat is fine */ });
    }
    const group = new THREE.Group();
    const mesh = new THREE.Mesh(new THREE.SphereGeometry(spec.radius, 64, 48), mat);
    if (look) mesh.userData.planetLook = look;
    group.add(mesh);
    this.attachAtmosphere(group, spec.radius, spec.hue, sunW); // (its sun was the Commons' in every system)
    const rim = new THREE.Mesh(
      new THREE.SphereGeometry(spec.radius * 1.035, 48, 32),
      look ? planetRimMaterial(rimHex, look.sunDir, 0.16, null) : legacyRimMaterial(new THREE.Color().setHSL(spec.hue, 0.6, 0.6), 0.16), // on its body layer (THE BODY LAYER LAW)
    );
    group.add(rim);
    mesh.userData.baseBump = face.bump;   // distance normal-map fade (anti-speckle)
    mesh.userData.rimMat = rim.material;  // distance rim fade (anti-jagged-halo)
    mesh.userData.rimBase = 0.16;
    // ── The world's OWN space (the map-expansion law): a worldId-seeded rock
    // ring + a beacon spire hang around every charted planet, with real
    // collision — a user-built world extends the sector, not just the chart.
    const wseed = Number(worldId % 65521n);
    const h01 = (i: number, salt: number): number => {
      let h = 0x811c9dc5 ^ (wseed + i * 374761);
      h = Math.imul(h, 0x01000193) ^ salt;
      h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13;
      return (h >>> 0) / 4294967296;
    };
    const rockMat = new THREE.MeshStandardMaterial({ color: 0x77808f, roughness: 0.95 });
    const colGrp = { x: spec.x, y: spec.y, z: spec.z, reach: spec.radius * 3.6 + 900, list: [] as Array<{ x: number; y: number; z: number; r: number; halfH: number }> };
    const rocks = 10 + (wseed % 8);
    for (let i = 0; i < rocks; i++) {
      const a = (i / rocks) * Math.PI * 2 + h01(i, 11) * 0.8;
      const rr = spec.radius * (2.3 + h01(i, 23) * 0.9); // outside the landing lane
      const ry = (h01(i, 37) - 0.5) * spec.radius * 0.8;
      const size = 26 + h01(i, 51) * 58;
      const rock = new THREE.Mesh(new THREE.IcosahedronGeometry(size, 1), rockMat);
      rock.position.set(Math.cos(a) * rr, ry, Math.sin(a) * rr);
      rock.rotation.set(h01(i, 63) * 3, h01(i, 77) * 3, h01(i, 91) * 3);
      group.add(rock);
      colGrp.list.push({ x: spec.x + rock.position.x, y: spec.y + ry, z: spec.z + rock.position.z, r: size * 1.05, halfH: 0 });
    }
    const spire = new THREE.Mesh(
      new THREE.CylinderGeometry(6, 10, 90, 8),
      new THREE.MeshStandardMaterial({ color: 0x9fb6cf, roughness: 0.5, metalness: 0.6, emissive: 0x223a55 }),
    );
    spire.position.set(0, spec.radius * 1.9, 0); // over the pole — clear of planetfall
    group.add(spire);
    colGrp.list.push({ x: spec.x, y: spec.y + spec.radius * 1.9, z: spec.z, r: 14, halfH: 45 });
    this.colliderGroups.push(colGrp);
    this.worldClusterCols.set(worldId.toString(), colGrp);
    group.position.copy(this.local(spec.x, spec.y, spec.z, this._v1));
    this.root.add(group);
    this.worldPlanets.set(worldId.toString(), { group, mesh, spin: spec.spin, name, worldId, spec });
    this.planetAnchors.push({ group, abs: new THREE.Vector3(spec.x, spec.y, spec.z) });
  }

  /** Nearest charted-world planet within its landing window, or null. */
  private nearWorldPlanet(): { worldId: bigint; name: string; spec: WorldPlanet } | null {
    for (const wp of this.worldPlanets.values()) {
      const d = Math.hypot(wp.spec.x - this.pos.x, wp.spec.y - this.pos.y, wp.spec.z - this.pos.z);
      if (d < wp.spec.radius * WORLD_LAND_MULT) return wp;
    }
    return null;
  }

  /** Map-click HYPERWARP to a charted world: the server writes the arrival
   *  (the spaceMove cap would reject the jump), the client mirrors it. */
  private warpToWorld(worldId: bigint, name: string): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.dropHyper(null);
    this.autoCruise = false;
    conn.reducers.warpToWorld({ worldId })
      .then(() => {
        this.warpFlash(this.shipLocal().clone());
        const a = worldWarpArrival(worldId); // mirror of the server's write
        this.pos.set(a.x, a.y, a.z);
        this.vel.set(0, 0, 0);
        this.throttle = 0.25;
        this.cruise = 1;
        this.maybeRebase();
        this.warpFlash(this.shipLocal().clone());
        this.toast(`HYPERWARP — ${name}. ${keyHint('spaceLand')} makes planetfall`);
      })
      .catch((e: Error) => {
        this.lastError = `warpToWorld: ${String(e.message ?? e)}`;
        this.toast(`Warp refused: ${e.message ?? e}`);
      });
  }

  // ── Hyperdrive nav ───────────────────────────────────────────────────────
  /** Nav targets of THE CURRENT SYSTEM: the Commons keeps its hand-built
   *  POI list; every other system derives station/belts/dens/planets from
   *  systemSector — before this, N/M/autopilot cycled COMMONS targets while
   *  you flew a different star (the local-map mismatch report, 2026-08-24). */
  private navFor(): NavPoint[] {
    const base = this.system.id === 'sys0' ? navPoints() : systemNavPoints(this.system).map((n) => ({ name: n.name, pos: n.pos, arrive: n.arrive }));
    // Space Wave 5 (THE BOOKMARK LAW): this system's own and civ bookmarks are nav targets.
    // natural objects: this system's moons, comets, landmarks … are nav targets (a moving body's `pos` is refreshed in place)
    const natural = (this.natural?.navBodies() ?? []).map((b) => ({ name: b.name, pos: b.pos, arrive: b.arrive }));
    return [...base, ...natural, ...this.bookmarksHere().map((b) => ({ name: `⚑ ${b.label}`, pos: [b.x, b.y, b.z] as [number, number, number], arrive: 600 }))];
  }

  private cycleNav(): void {
    if (this.nav.length === 0) this.nav = this.navFor();
    this.navIdx = (this.navIdx + 1) % (this.nav.length + 1);
    if (this.navIdx === this.nav.length) { this.navIdx = -1; this.toast('Nav cleared'); return; }
    const n = this.nav[this.navIdx];
    this.toast(`Nav: ${n.name} — ${Math.round(this._v1.set(...n.pos).distanceTo(this.pos) / 1000)} km (${keyHint('spaceHyper')} to jump)`);
  }

  /** Tier-2 tackle-lite: my own standing scramble (server row). */
  private tackledNow(): boolean {
    try {
      const row = (atlas.conn?.db as unknown as { spaceTackle: { identity: { find(id: unknown): { until: { microsSinceUnixEpoch: bigint } } | null | undefined } } } | undefined)?.spaceTackle.identity.find(atlas.conn?.identity);
      return !!row && Number(row.until.microsSinceUnixEpoch / 1000n) > Date.now();
    } catch { return false; }
  }

  private toggleHyper(): void {
    if (this.tackledNow()) { this.toast('⚠ WARP SCRAMBLED — the tackle holds you'); return; }
    if (this.hyper !== '') { this.dropHyper('Hyperdrive disengaged'); return; }
    { // Slice D, THE MASS LOCK: inside a pinned war's theatre the drive will not spool (the record bounds every move there).
      const tc = this.pinnedTheatre();
      if (tc && massLocked(this.pos.distanceTo(tc), true)) { this.toast(`⚠ MASS LOCK — the fleet war's lines hold the drive within ${Math.round(MASS_LOCK_M / 1000)} km; fly clear to jump`); return; }
    }
    if (this.navIdx < 0) { this.toast(`No nav target — ${keyHint('spaceNav')} cycles the sector chart`); return; }
    const n = this.nav[this.navIdx];
    if (this._v1.set(...n.pos).distanceTo(this.pos) <= n.arrive * 1.1) { this.toast('Already on approach'); return; }
    this.hyper = 'align';
    this.hyperT = 0;
    this.toast(`Aligning for hyperspace — ${n.name}`);
  }

  private dropHyper(msg: string | null): void {
    if (this.hyper === '') return;
    if (this.hyper === 'jump') sfx('sfx_warp_exit', { gain: 0.55, cooldownMs: 800 }); // reversion boom (enter has its own cue)
    this.hyper = '';
    this.hyperT = 0;
    this.throttle = Math.min(this.throttle, 0.6);
    this.vel.clampLength(0, this.stats.maxSpeed); // shed the cruise velocity instantly — JTL drops you at combat speed
    if (msg) this.toast(msg);
  }

  // ── Freighter lanes: carriers warp in, cruise, warp out ─────────────────
  private buildFreighter(): THREE.Object3D {
    const g = new THREE.Group();
    const hullMat = new THREE.MeshStandardMaterial({ color: 0x8a93a0, roughness: 0.7, metalness: 0.35 });
    const cargoMat = new THREE.MeshStandardMaterial({ color: 0x5f6d5a, roughness: 0.85, metalness: 0.15 });
    const spine = new THREE.Mesh(new THREE.BoxGeometry(3, 3, 46), hullMat);
    const bridge = new THREE.Mesh(new THREE.BoxGeometry(6, 5, 7), hullMat);
    bridge.position.set(0, 2, 20);
    g.add(spine, bridge);
    for (let i = 0; i < 5; i++) {
      const box = new THREE.Mesh(new THREE.BoxGeometry(7, 6, 7), cargoMat);
      box.position.set(0, 0.6, -16 + i * 8);
      g.add(box);
    }
    const glow = this.spriteGlow(0x77bbff, 6);
    glow.position.set(0, 0, 24.5);
    g.add(glow);
    return g;
  }

  private warpFlash(atLocal: THREE.Vector3): void {
    if (!this.root) return;
    { const dW = this.shipLocal().distanceTo(atLocal);
      this.sfxAtLocal('sfx_freighter_warp', atLocal, { gain: Math.max(0.06, 0.5 - dW * 0.00008), cooldownMs: 350, caption: 'Warp signature' }); }
    const flash = this.spriteGlow(0xcfe8ff, 10);
    // Space campaign L (2026-10-01): a warp flash AT the eye (your own jump) is a full-screen white flash — it follows
    // the reduced-flash choice (lib/space_comfort.sectorEffects); a distant hull's warp flash is a cue and keeps its size.
    const flashK = this.shipLocal().distanceTo(atLocal) < 300 ? this.fxScale.warpFlash : 1;
    flash.position.copy(atLocal);
    this.root.add(flash);
    const t0 = performance.now();
    const anim = (): void => {
      const t = (performance.now() - t0) / 450;
      if (t >= 1 || !this.root) { this.root?.remove(flash); freeLater(flash.material); return; }
      flash.scale.setScalar(10 + t * 240);
      (flash.material as THREE.SpriteMaterial).opacity = (1 - t) * flashK;
      nextFrame(anim); // XR P0.5: the window rAF is silent in a headset — the flash froze at full size
    };
    anim();
  }

  // ── Space collision (the fly-through fix) ─────────────────────────────────
  /** Resolve this.pos against the sector's colliders: push out along the
   *  surface normal, kill the inward velocity component (slide), and drop the
   *  hyperdrive/overcruise on impact. Sphere ship (r 6) vs spheres (rocks,
   *  hulks) and vertical capsules (station spindles). */
  private resolveSpaceCollision(): void {
    // Hyperspace ignores realspace obstacles (the JTL convention) — and a
    // warp lane that merely CROSSES a den must never strand the jump
    // (the world-warp probe caught exactly that).
    if (this.hyper === 'jump') return;
    const SHIP_R = 6;
    const px = this.pos.x, py = this.pos.y, pz = this.pos.z;
    for (const grp of this.colliderGroups) {
      const gx = px - grp.x, gy = py - grp.y, gz = pz - grp.z;
      const reach = grp.reach + SHIP_R;
      if (gx * gx + gy * gy + gz * gz > reach * reach) continue;
      for (const c of grp.list) {
        // Capsule: clamp the test point to the spindle's vertical segment.
        const cy = c.halfH > 0 ? c.y + Math.max(-c.halfH, Math.min(c.halfH, py - c.y)) : c.y;
        let nx = px - c.x, ny = py - cy, nz = pz - c.z;
        const d = Math.hypot(nx, ny, nz);
        const minD = c.r + SHIP_R;
        if (d >= minD) continue;
        if (d < 1e-6) { nx = 0; ny = 1; nz = 0; } else { nx /= d; ny /= d; nz /= d; }
        this.pos.set(c.x + nx * minD, cy + ny * minD, c.z + nz * minD);
        // Slide: remove the into-surface velocity, keep the tangent (damped).
        const vn = this.vel.x * nx + this.vel.y * ny + this.vel.z * nz;
        if (vn < 0) {
          // A real strike clangs (glancing scrapes stay quiet); harder = louder.
          if (-vn > 6) sfx('sfx_ship_impact', { gain: Math.min(0.8, 0.25 + -vn / 90), cooldownMs: 350 });
          this.vel.x -= vn * nx; this.vel.y -= vn * ny; this.vel.z -= vn * nz;
          this.vel.multiplyScalar(0.82);
        }
        this.cruise = 1; // an impact spools overcruise down instantly
        if (this.hyper !== '') this.dropHyper('Collision — hyperdrive disengaged'); // align/spool only (jump skips collision)
      }
    }
  }

  // ── The ×50 patrol population ─────────────────────────────────────────────
  /** Bake the pirate hull into ONE instanced mesh (first mesh part, expanded
   *  to float32 before baking — the quantized-avatar law) and lay out every
   *  POI's patrol wing. */
  private buildPatrols(pirateMaster: THREE.Object3D | null): void {
    if (!this.root || this.patrolMesh) return;
    let geo: THREE.BufferGeometry | null = null;
    let mat: THREE.Material | null = null;
    if (pirateMaster) {
      pirateMaster.updateMatrixWorld(true);
      pirateMaster.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!geo && m.isMesh && m.geometry) {
          const g = m.geometry.clone();
          for (const name of ['position', 'normal'] as const) {
            const a = g.getAttribute(name) as THREE.BufferAttribute | undefined;
            if (a && a.normalized) {
              const f = new Float32Array(a.count * a.itemSize);
              for (let i = 0; i < a.count; i++) for (let k = 0; k < a.itemSize; k++) f[i * a.itemSize + k] = a.getComponent(i, k);
              g.setAttribute(name, new THREE.BufferAttribute(f, a.itemSize));
            }
          }
          g.applyMatrix4(m.matrixWorld);
          g.computeBoundingBox();
          const bb = g.boundingBox!;
          const longest = Math.max(1e-3, bb.max.x - bb.min.x, bb.max.y - bb.min.y, bb.max.z - bb.min.z);
          const center = bb.getCenter(new THREE.Vector3());
          g.translate(-center.x, -center.y, -center.z);
          g.scale(1 / longest, 1 / longest, 1 / longest); // unit hull; per-ship size in the matrix
          geo = g;
          mat = Array.isArray(m.material) ? m.material[0] : m.material;
        }
      });
    }
    if (!geo) { geo = new THREE.ConeGeometry(0.28, 1, 5); geo.rotateX(Math.PI / 2); }
    if (!mat) mat = new THREE.MeshStandardMaterial({ color: 0x8a2f2f, roughness: 0.6, metalness: 0.4 });
    // THE COUNT-PACKED POOL'S MOTION VECTORS (session 3, 2026-09-27): the pool re-packs its slots every frame (only the
    // patrols in range hold one), so a slot's PREVIOUS instance matrix is another ship's; and at 1,642 slots — over
    // WebGPU's 1,024-matrix uniform limit (WebGL2's 256) — three r185 keeps that previous copy in a vertex attribute it
    // never re-uploads (nodes/accessors/Instance.js; CLAUDE.md "Instanced/batched motion vectors are FROZEN"). Under
    // TAAU and the motion blur (the high / ultra defaults; the space profile keeps both, so velocity is in the sector's
    // MRT) every patrol hull read a velocity of kilometres. Pinned static — the CrowdInstancer rule for a count-packed
    // pool, and the rock fields' own (asteroids.ts pins its master material the same way): the camera still blurs them.
    this.patrolPinned = pinStaticMotionVectors(mat);
    this.patrols = [];
    for (let pi = 0; pi < POIS.length; pi++) {
      const poi = POIS[pi];
      for (let i = 0; i < poi.patrol; i++) {
        this.patrols.push({
          poiIdx: pi, orbit: patrolOrbit(poi, i),
          // Promotion reuses full-AI slot keys ABOVE the base count (server
          // bounty regex allows pirate0-99; concurrent promotions per POI are
          // capped far below that).
          key: `${poi.key}:pirate${poi.pirates + (i % 24)}`,
          deadUntil: 0, promoted: false,
        });
      }
    }
    const mesh = new THREE.InstancedMesh(geo, mat, this.patrols.length);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.frustumCulled = false;
    mesh.name = 'inst:space-patrols';
    this.root.add(mesh);
    this.patrolMesh = mesh;
  }

  private readonly _pm = new THREE.Matrix4();
  private readonly _pq = new THREE.Quaternion();
  private readonly _pv = new THREE.Vector3();
  private readonly _ps = new THREE.Vector3();

  /** Per-frame: orbit every patrol hull (shared wall clock), hide the far
   *  ones, and PROMOTE the nearest into full-AI combat pirates. */
  private updatePatrols(): void {
    const mesh = this.patrolMesh;
    if (!mesh || !this.root) return;
    // Rebase immunity (the battle group / wreck field carry the same guard):
    // every matrix below is recomputed absolute→local this tick against the
    // CURRENT offset, so the mesh node itself must never carry a rebase shift
    // (maybeRebase subtracts it from every root child → a 25 km double offset).
    mesh.position.set(0, 0, 0);
    const t = Date.now() / 1000;
    const nowMs = performance.now();
    const PROMOTE_M = 1_300;
    const DEMOTE_M = 6_000;
    const VIS_M = 60_000;
    const promotedPerPoi = new Map<number, number>();
    for (const p of this.patrols) if (p.promoted) promotedPerPoi.set(p.poiIdx, (promotedPerPoi.get(p.poiIdx) ?? 0) + 1);
    // COUNT-PACKING (the CrowdInstancer trick): only ships in visual range
    // occupy live instance slots — the GPU never sees the other ~1,600
    // hulls (frustumCulled=false + scale-0 still ran their vertex stage,
    // which crawled software rasterizers).
    let slot = 0;
    for (let i = 0; i < this.patrols.length; i++) {
      const p = this.patrols[i];
      if (p.promoted) continue;
      if (p.deadUntil > 0 && t < p.deadUntil) continue;
      p.deadUntil = 0;
      const o = p.orbit;
      // Cheap coarse reject BEFORE the trig: orbit center distance.
      const cdx = o.cx - this.pos.x, cdy = o.cy - this.pos.y, cdz = o.cz - this.pos.z;
      if (cdx * cdx + cdy * cdy + cdz * cdz > (VIS_M + o.r) * (VIS_M + o.r)) continue;
      const th = o.phase + o.omega * t;
      const cb = Math.cos(o.basis), sb = Math.sin(o.basis);
      const lx = Math.cos(th) * o.r, lz = Math.sin(th) * o.r;
      const ax = o.cx + lx * cb - lz * sb;
      const ay = o.cy + Math.sin(th) * o.r * o.tilt;
      const az = o.cz + lx * sb + lz * cb;
      const dx = ax - this.pos.x, dy = ay - this.pos.y, dz = az - this.pos.z;
      const dist = Math.hypot(dx, dy, dz);
      if (dist > VIS_M) continue;
      // Promote: close + slots free at this den → a REAL pirate takes over.
      if (dist < PROMOTE_M && this.pirateMaster && (promotedPerPoi.get(p.poiIdx) ?? 0) < 6 && !this.promotedKeys.has(p.key)) {
        this.promotePatrol(p, ax, ay, az);
        promotedPerPoi.set(p.poiIdx, (promotedPerPoi.get(p.poiIdx) ?? 0) + 1);
        continue;
      }
      // Heading = orbit tangent (rotated like the position).
      const tx = -Math.sin(th), tz = Math.cos(th);
      const hx = (tx * cb - tz * sb) * Math.sign(o.omega);
      const hz = (tx * sb + tz * cb) * Math.sign(o.omega);
      this._pq.setFromAxisAngle(this._pv.set(0, 1, 0), Math.atan2(hx, hz) + SHIP_MODEL_YAW + Math.PI);
      this._pm.compose(
        this.local(ax, ay, az, this._pv),
        this._pq,
        this._ps.setScalar(o.size),
      );
      mesh.setMatrixAt(slot++, this._pm);
    }
    mesh.count = slot;
    // Upload only the packed prefix (the CrowdInstancer discipline): flagging the
    // whole attribute re-sent the full 1,600-slot buffer every frame, even at 0.
    const attr = mesh.instanceMatrix;
    attr.clearUpdateRanges();
    if (slot > 0) { attr.addUpdateRange(0, slot * 16); attr.needsUpdate = true; }
    // Demote: promoted twins whose full-AI pirate died or drifted far.
    for (const p of this.patrols) {
      if (!p.promoted) continue;
      const twin = this.pirates.find((x) => x.key === p.key);
      if (!twin) { p.promoted = false; this.promotedKeys.delete(p.key); continue; }
      if (twin.deadUntil > 0) {
        p.promoted = false;
        this.promotedKeys.delete(p.key);
        p.deadUntil = t + PIRATE_RESPAWN_S;
        this.pirates = this.pirates.filter((x) => x !== twin);
        this.root.remove(twin.root);
        if (this.target === twin) this.target = null; // never leave the bracket on a ghost (2026-09-12)
      } else {
        const d = twin.root.position.length(); // OFFSET space ≈ distance to the player's neighborhood
        if (d > DEMOTE_M && this.pos.distanceTo(twin.home) > DEMOTE_M) {
          p.promoted = false;
          this.promotedKeys.delete(p.key);
          this.pirates = this.pirates.filter((x) => x !== twin);
          this.root.remove(twin.root);
          if (this.target === twin) this.target = null;
        }
      }
    }
  }

  /** A patrol hull wakes up: spawn a full-AI pirate at its exact position. */
  private promotePatrol(p: (typeof this.patrols)[number], ax: number, ay: number, az: number): void {
    if (!this.root) return;
    const poi = POIS[p.poiIdx];
    const tier = poi.tier;
    const root = new THREE.Group();
    const hull = new THREE.Group();
    if (this.pirateMaster) {
      const skin = this.pirateMaster.clone();
      const box = new THREE.Box3().setFromObject(skin);
      const longest = Math.max(1, box.max.x - box.min.x, box.max.y - box.min.y, box.max.z - box.min.z);
      skin.scale.setScalar((11 + tier * 2) / longest);
      hull.rotation.y = SHIP_MODEL_YAW;
      hull.add(skin);
    } else {
      hull.add(this.fallbackShip(0xb03030));
    }
    root.add(hull);
    const halo = this.spriteGlow(0xff7a5c, 2.4);
    (halo.material as THREE.SpriteMaterial).opacity = 0.5;
    root.add(halo);
    const home = new THREE.Vector3(ax, ay, az);
    root.position.copy(this.local(ax, ay, az, this._v1));
    this.root.add(root);
    this.pirates.push({
      key: p.key, poiKey: poi.key, tier, root,
      hp: TIER_STATS[tier].hp, deadUntil: 0, home, fireAt: 0,
      vel: new THREE.Vector3(), prevPos: root.position.clone(),
      ai: 'patrol', aiUntil: 0, jinkSeed: p.poiIdx * 13 + 5, evadeBrake: false, escaped: false, slowUntil: 0, jamUntil: 0,
    });
    p.promoted = true;
    this.promotedKeys.add(p.key);
  }

  // ── Faction wings + void raiders (the "space is too empty" wave) ─────────
  private nearestPoiIdx(x: number, y: number, z: number): number {
    let best = 0, bd = Infinity;
    for (let i = 0; i < POIS.length; i++) {
      const p = POIS[i];
      const d = (p.pos[0] - x) ** 2 + (p.pos[1] - y) ** 2 + (p.pos[2] - z) ** 2;
      if (d < bd) { bd = d; best = i; }
    }
    return best;
  }

  /** A full-AI hostile at an absolute position (marked wings, void raiders).
   *  Keys ride the server bounty regex — slots 80-95 wings, 96-99 void. */
  /** THE ESCORT LAW: a hull's class multipliers (a den pirate and a war fighter are the base). */
  private classOf(p: { cls?: HullClass }): (typeof HULL_CLASS)[HullClass] { return HULL_CLASS[p.cls ?? 'fighter']; }
  /** The FSM's stats for a hull: the tier's, scaled by its class. */
  private statsOf(p: { tier: 1 | 2 | 3; cls?: HullClass }): { hp: number; speed: number; fireCd: number; lead: number; label: string } {
    const t = TIER_STATS[p.tier];
    if (!p.cls || p.cls === 'fighter') return t;
    const c = HULL_CLASS[p.cls];
    return { hp: t.hp * c.hp, speed: t.speed * c.speed, fireCd: t.fireCd * c.fireCd, lead: t.lead, label: p.cls };
  }
  /** A bolt lands within this of the hull's origin — the hull's size, not a fighter's 9 m for a 110 m frigate. */
  private hitRadiusOf(p: { cls?: HullClass }): number { return this.classOf(p).hitR; }

  private spawnHostile(key: string, tier: 1 | 2 | 3, x: number, y: number, z: number, warSide?: 0 | 1, cls?: HullClass): Pirate | null {
    if (!this.root) return null;
    if (this.pirates.some((p) => p.key === key && p.deadUntil === 0)) return null;
    const root = new THREE.Group();
    const hull = new THREE.Group();
    // War gunships wear their civ's cooked hull (fallback: the pirate skin); an ESCORT its class's cooked hull, the
    // fighter's until it lands (the self-heal base — a missing `prop:war_frigate_*` is a bigger fighter, not a gap).
    const escortM = cls && cls !== 'fighter' && warSide !== undefined ? this.warEscortM[cls][warSide] : null;
    const master = escortM ?? (warSide !== undefined ? this.warShipM[warSide] : null) ?? this.pirateMaster;
    const size = cls && cls !== 'fighter' ? HULL_CLASS[cls].len : 11 + tier * 2;
    if (master) {
      const skin = master.clone();
      const box = new THREE.Box3().setFromObject(skin);
      const longest = Math.max(1, box.max.x - box.min.x, box.max.y - box.min.y, box.max.z - box.min.z);
      skin.scale.setScalar(size / longest);
      hull.rotation.y = SHIP_MODEL_YAW;
      hull.add(skin);
      // Realism wave R6: war hulls wear PBR under the one sun (+ the civ rim; escorts carry the window layer).
      if (warSide !== undefined) this.dressWarHull(hull, warSide, !!cls && cls !== 'fighter');
    } else {
      const fb = this.fallbackShip(warSide === 0 ? 0xd8b23c : warSide === 1 ? 0x4fd0e8 : 0xb03030);
      if (size > 20) fb.scale.setScalar(size / 15);
      hull.add(fb);
    }
    root.add(hull);
    const haloHex = warSide === 0 ? 0xffd873 : warSide === 1 ? 0x9fe8ff : 0xff7a5c;
    const halo = this.spriteGlow(haloHex, HULL_CLASS[cls ?? 'fighter'].halo);
    halo.name = 'engine-halo';
    (halo.material as THREE.SpriteMaterial).opacity = 0.55;
    root.add(halo); // engine halo — hostiles glow at combat range; a FRIEND's turns green (space_iff, per frame)
    root.position.copy(this.local(x, y, z, this._v1));
    this.root.add(root);
    const pr: Pirate = {
      key, poiKey: POIS[this.nearestPoiIdx(x, y, z)].key, tier, root,
      hp: TIER_STATS[tier].hp, deadUntil: 0, home: new THREE.Vector3(x, y, z), fireAt: 0,
      vel: new THREE.Vector3(), prevPos: root.position.clone(),
      ai: 'intercept', aiUntil: 0, jinkSeed: (key.length * 31 + key.charCodeAt(4)) % 97,
      evadeBrake: false, escaped: false, slowUntil: 0, jamUntil: 0, warSide, haloHex, haloShown: haloHex, cls,
    };
    pr.hp = this.statsOf(pr).hp;
    this.pirates.push(pr);
    return pr;
  }

  /** One instanced billboard mesh of stars: corners offset in clip space by a per-star pixel size
   *  (VERTEX BUFFER LAW: position + uv + two instanced attributes = four buffers). */
  private buildStarLayer(centers: Float32Array, sizes: Float32Array, color: number): THREE.Mesh {
    const quad = new THREE.PlaneGeometry(1, 1);
    const geo = new THREE.InstancedBufferGeometry();
    geo.setIndex(quad.getIndex());
    geo.setAttribute('position', quad.getAttribute('position'));
    geo.setAttribute('uv', quad.getAttribute('uv'));
    geo.setAttribute('aCenter', new THREE.InstancedBufferAttribute(centers, 3));
    geo.setAttribute('aSize', new THREE.InstancedBufferAttribute(sizes, 1));
    geo.instanceCount = sizes.length;
    const mat = new THREE.MeshBasicNodeMaterial({ color, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
    markMrtOverlay(mat);
    mat.vertexNode = Fn(() => {
      const center = attribute('aCenter', 'vec3');
      const size = attribute('aSize', 'float');
      const clip = cameraProjectionMatrix.mul(modelViewMatrix.mul(vec4(center, 1.0)));
      const off = positionGeometry.xy.mul(size.mul(2.0)).div(screenSize).mul(clip.w);
      return vec4(clip.xy.add(off), clip.zw);
    })();
    mat.opacityNode = float(1.0).sub(length(uv().sub(0.5)).mul(2.0)).clamp(0.0, 1.0); // a soft dot
    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false;
    mesh.name = 'star-layer';
    return mesh;
  }

  private makeWingShip(civ: 'aurelian' | 'borean'): THREE.Object3D {
    const g = new THREE.Group();
    const hull = new THREE.Group();
    // Real hulls, not markers: the civ's cooked war gunship (pirate skin as
    // the bridge, primitives only when nothing has cooked yet) — the wing
    // that escorts you should look like the ships it fights (user request
    // 2026-08-23).
    const master = this.warShipM[civ === 'aurelian' ? 0 : 1] ?? this.pirateMaster;
    if (master) {
      const wrap = this.wrapHull(master.clone(), 11);
      this.dressWarHull(wrap, civ === 'aurelian' ? 0 : 1, false); // realism wave R6: lit under the one sun
      hull.add(wrap);
    } else {
      hull.add(this.fallbackShip(civ === 'aurelian' ? 0xd8b23c : 0x4fd0e8));
    }
    g.add(hull);
    const halo = this.spriteGlow(civ === 'aurelian' ? 0xffd873 : 0x9fe8ff, 2.2);
    (halo.material as THREE.SpriteMaterial).opacity = 0.45;
    g.add(halo);
    g.scale.setScalar(1.35);
    return g;
  }

  private buildWings(): void {
    if (!this.root) return;
    for (const w of this.wings) for (const s of w.ships) if (s) this.root.remove(s);
    this.wings = FACTION_WINGS.map((spec) => ({
      spec,
      ships: Array.from({ length: spec.ships }, () => {
        const s = this.makeWingShip(spec.civ);
        this.root!.add(s);
        return s as THREE.Object3D | null;
      }),
      hostileCyc: -1,
      allyFireAt: 0,
    }));
  }

  /** Wings cruise their lanes on the shared clock. Your civ escorts and
   *  returns fire; the opposing civ MARKS you inside 1.5 km and its ships
   *  promote into full hostiles (server-valid bounty keys). */
  private updateWings(): void {
    if (this.system.id !== 'sys0') return; // the civ machinery lives in the Commons Reach
    if (!this.root || this.wings.length === 0) return;
    const t = Date.now() / 1000;
    const now = performance.now();
    const mine = this.faction;
    for (const [wi, w] of this.wings.entries()) {
      const lead = wingPos(w.spec, t);
      const cyc = Math.floor(t / w.spec.period + w.spec.phase);
      if (cyc !== w.hostileCyc) {
        w.hostileCyc = cyc;
        for (let i = 0; i < w.ships.length; i++) {
          if (w.ships[i]) continue;
          const s = this.makeWingShip(w.spec.civ); // lane wrap re-forms lost slots
          this.root.add(s);
          w.ships[i] = s;
        }
      }
      const distToPlayer = Math.hypot(lead.x - this.pos.x, lead.y - this.pos.y, lead.z - this.pos.z);
      const visible = distToPlayer < 70_000;
      const hostile = mine !== '' && w.spec.civ !== mine;
      const ally = mine !== '' && w.spec.civ === mine;
      for (let i = 0; i < w.ships.length; i++) {
        const s = w.ships[i];
        if (!s) continue;
        s.visible = visible;
        if (!visible) continue;
        const ox = (i % 2 === 0 ? 1 : -1) * (30 + i * 22), oy = ((i % 3) - 1) * 18, oz = -i * 46;
        let px = lead.x + lead.dz * ox + lead.dx * oz;
        let py = lead.y + oy;
        let pz = lead.z - lead.dx * ox + lead.dz * oz;
        if (ally && distToPlayer < 2400) {
          // Escort: fall out of the lane into orbit around the player.
          const a = (now / 1000) * 0.35 + i * (Math.PI * 2 / w.ships.length);
          px = this.pos.x + Math.cos(a) * 160;
          py = this.pos.y + 30 + i * 14;
          pz = this.pos.z + Math.sin(a) * 160;
        }
        s.position.copy(this.local(px, py, pz, this._v1));
        lookAtLocal(s, this._v2.copy(s.position).add(this._v3.set(lead.dx, lead.dy, lead.dz))); // THE SCENE SHIFT: lookAt aims from the WORLD position — a root-local target pointed every wing at −shift, not along its lane
        if (hostile && distToPlayer < 1500) {
          const slot = 80 + ((wi * 4 + i) % 16);
          const n = this.nearestPoiIdx(px, py, pz);
          const pr = this.spawnHostile(`poi${n}:pirate${slot}`, 2, px, py, pz);
          this.root.remove(s);
          w.ships[i] = null;
          if (pr && now - this.wingToastAt > 8000) {
            this.wingToastAt = now;
            this.toast(`${w.spec.civ === 'borean' ? 'Borean Compact' : 'Aurelian Concord'} wing marks you — hostile intercept!`);
          }
        }
      }
      // Ally fire support: the escort shoots whatever is pressing you.
      if (ally && distToPlayer < 2400 && now > w.allyFireAt) {
        const sl = this.shipLocal();
        const target = this.pirates.find((p) => p.deadUntil === 0
          && (p.ai === 'attack' || p.ai === 'intercept') && p.root.position.distanceTo(sl) < 1600);
        const src = target ? w.ships.find((x) => x) : null;
        if (target && src) {
          w.allyFireAt = now + 1200;
          const b = this.spriteGlow(w.spec.civ === 'aurelian' ? 0xffd873 : 0x9fe8ff, 3);
          b.position.copy(src.position);
          this.root.add(b);
          this.bolts.push({
            sprite: b,
            vel: this._v1.copy(target.root.position).sub(src.position).normalize().multiplyScalar(BOLT_SPEED).clone(),
            ttl: BOLT_LIFE_S * 1.4, hostile: false,
          });
        }
      }
    }
  }

  /** Civ patrols: field the posts around the player, shed the far ones. The
   *  hulls run the same FSM as every other gunship — the target rule
   *  (quarryOf) sends them at the other civ and at the pirates. */
  private updateCivPatrols(dt: number): void {
    if (this.system.id !== 'sys0') return; // the civ machinery lives in the Commons Reach
    this.patrolScanAcc += dt;
    if (this.patrolScanAcc < 2 || !this.root) return;
    this.patrolScanAcc = 0;
    const live = new Set(this.patrolLive.keys());
    const { spawn, cull } = postsInWindow(this.patrolPosts, this.pos.x, this.pos.y, this.pos.z, live);
    for (const post of cull) {
      const ships = this.patrolLive.get(post.key) ?? [];
      for (const pr of ships) { this.pirates = this.pirates.filter((x) => x !== pr); this.root.remove(pr.root); if (this.target === pr) this.target = null; }
      this.patrolLive.delete(post.key);
    }
    let liveN = 0;
    for (const ships of this.patrolLive.values()) liveN += ships.length;
    for (const post of spawn) {
      const postShips = this.patrolShipsFor(post); // Space Wave 3: THE READINESS LAW
      if (liveN + postShips > PATROL_LIVE_MAX) break;
      const ships: Pirate[] = [];
      for (let i = 0; i < postShips; i++) {
        const a = (i / postShips) * Math.PI * 2;
        const pr = this.spawnHostile(`${post.key}:${i}`, post.tier, post.pos[0] + Math.cos(a) * 140, post.pos[1] + i * 22, post.pos[2] + Math.sin(a) * 140, post.side);
        if (pr) ships.push(pr);
      }
      if (ships.length) { this.patrolLive.set(post.key, ships); liveN += ships.length; }
    }
  }

  /** Sparse ambushers hashed from 22 km cells — culled past 34 km, capped at
   *  6 live, never inside a den's own ground (dens keep their population). */
  private updateVoidRaiders(dt: number): void {
    if (this.system.id !== 'sys0') return; // the civ machinery lives in the Commons Reach
    this.voidScanAcc += dt;
    if (this.voidScanAcc < 2 || !this.root) return;
    this.voidScanAcc = 0;
    for (const [k, pr] of this.voidRaiders) {
      if (pr.escaped || this.pos.distanceTo(pr.home) > 34_000) {
        this.pirates = this.pirates.filter((x) => x !== pr);
        this.root.remove(pr.root);
        if (this.target === pr) this.target = null;
        this.voidRaiders.delete(k);
      }
    }
    if (this.voidRaiders.size >= 6) return;
    const c = VOID_CELL_M;
    const cx = Math.floor(this.pos.x / c), cy = Math.floor(this.pos.y / c), cz = Math.floor(this.pos.z / c);
    for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) for (let dz = -1; dz <= 1; dz++) {
      const key = `${cx + dx},${cy + dy},${cz + dz}`;
      if (this.voidRaiders.has(key)) continue;
      let h = 0x811c9dc5;
      for (const ch of key) { h ^= ch.charCodeAt(0); h = Math.imul(h, 0x01000193); }
      h >>>= 0;
      if ((h % 1000) / 1000 >= VOID_RAIDER_P) continue;
      const x = (cx + dx + 0.5) * c + (h % 977) * 8 - 3900;
      const y = (cy + dy + 0.5) * c;
      const z = (cz + dz + 0.5) * c + ((h >> 10) % 977) * 8 - 3900;
      const n = this.nearestPoiIdx(x, y, z);
      const poi = POIS[n];
      if (Math.hypot(poi.pos[0] - x, poi.pos[1] - y, poi.pos[2] - z) < poi.radius * 2 + 4000) continue;
      const pr = this.spawnHostile(`poi${n}:pirate${96 + (h % 4)}`, ((h >> 4) % 5) === 0 ? 2 : 1, x, y, z);
      if (pr) this.voidRaiders.set(key, pr);
      if (this.voidRaiders.size >= 6) return;
    }
  }

  // ── Cargo convoys: freighters + escorts + ambushing raiders ───────────────
  /** Shared-clock convoy schedule: a big freighter hauls the lane with
   *  escorts in formation; mid-route, raiders warp in and harry it while the
   *  escorts weave cover (living skirmish — display only; the BOUNTY pirates
   *  remain the combat system). */
  private updateConvoys(): void {
    if (this.system.id !== 'sys0') return; // the civ machinery lives in the Commons Reach
    if (!this.root) return;
    const t = Date.now() / 1000;
    for (let ci = 0; ci < CONVOY_ROUTES.length; ci++) {
      const route = CONVOY_ROUTES[ci];
      const len = Math.hypot(route.to[0] - route.from[0], route.to[1] - route.from[1], route.to[2] - route.from[2]);
      const flightS = Math.min(route.periodS * 0.8, len / CONVOY_SPEED);
      const phase = (t + ci * 211) % route.periodS;
      const flying = phase < flightS;
      const live = this.convoys.get(route.key);
      if (flying && !live) {
        const freighter = this.buildFreighter();
        freighter.scale.setScalar(4); // a CAPITAL hauler — ~185 m of hull
        const escorts: THREE.Object3D[] = [];
        for (let e = 0; e < route.escorts; e++) {
          const s = new THREE.Group();
          s.add(this.fallbackShip(0x7ec8e3));
          escorts.push(s);
          this.root.add(s);
        }
        const raiders: THREE.Object3D[] = [];
        for (let r = 0; r < route.raiders; r++) {
          const s = new THREE.Group();
          if (this.pirateMaster) {
            const skin = this.pirateMaster.clone();
            const box = new THREE.Box3().setFromObject(skin);
            const longest = Math.max(1, box.max.x - box.min.x, box.max.y - box.min.y, box.max.z - box.min.z);
            skin.scale.setScalar(13 / longest);
            const wrap = new THREE.Group();
            wrap.rotation.y = SHIP_MODEL_YAW;
            wrap.add(skin);
            s.add(wrap);
          } else s.add(this.fallbackShip(0xb03030));
          s.visible = false;
          raiders.push(s);
          this.root.add(s);
        }
        this.root.add(freighter);
        this.convoys.set(route.key, { freighter, escorts, raiders, bolts: [] });
        this.warpFlash(this.local(route.from[0], route.from[1], route.from[2], this._v1).clone());
      } else if (!flying && live) {
        this.warpFlash(live.freighter.position.clone());
        this.root.remove(live.freighter);
        for (const e of live.escorts) this.root.remove(e);
        for (const r of live.raiders) this.root.remove(r);
        for (const b of live.bolts) this.root.remove(b.s);
        this.convoys.delete(route.key);
      } else if (flying && live) {
        const f = phase / flightS;
        const cx = route.from[0] + (route.to[0] - route.from[0]) * f;
        const cy = route.from[1] + (route.to[1] - route.from[1]) * f;
        const cz = route.from[2] + (route.to[2] - route.from[2]) * f;
        const dirX = (route.to[0] - route.from[0]) / len, dirY = (route.to[1] - route.from[1]) / len, dirZ = (route.to[2] - route.from[2]) / len;
        this.local(cx, cy, cz, live.freighter.position);
        this._m1.lookAt(this._v2.set(0, 0, 0), this._v3.set(dirX, dirY, dirZ), UP);
        live.freighter.quaternion.setFromRotationMatrix(this._m1);
        live.freighter.rotateY(Math.PI); // bow is +Z-modeled
        const ambush = f > CONVOY_AMBUSH.start && f < CONVOY_AMBUSH.end;
        // Escorts: tight cover weave around the hauler (wider when raiders press in).
        for (let e = 0; e < live.escorts.length; e++) {
          const wob = t * (0.9 + e * 0.17) + e * 2.1;
          const rr = ambush ? 260 : 140;
          const ex = cx + Math.cos(wob) * rr - dirX * (60 + e * 40);
          const ey = cy + Math.sin(wob * 0.7) * rr * 0.4 + 30;
          const ez = cz + Math.sin(wob) * rr - dirZ * (60 + e * 40);
          const esc = live.escorts[e];
          const prev = this._v1.copy(esc.position);
          this.local(ex, ey, ez, esc.position);
          this._v2.copy(esc.position).sub(prev);
          if (this._v2.lengthSq() > 1e-4) lookAtLocal(esc, this._v3.copy(esc.position).add(this._v2)); // THE SCENE SHIFT (as the wings): a root-local target aimed the hull at −shift
        }
        // Raiders: attack runs during the ambush window only.
        for (let r = 0; r < live.raiders.length; r++) {
          const raider = live.raiders[r];
          raider.visible = ambush;
          if (!ambush) continue;
          const run = t * (0.5 + r * 0.11) + r * 2.6;
          const dive = Math.sin(run) * 0.5 + 0.5; // 0 far → 1 skimming the hull
          const rr = 700 - dive * 560;
          const rx = cx + Math.cos(run * 1.7 + r) * rr;
          const ry = cy + Math.sin(run * 1.3 + r * 1.9) * rr * 0.5;
          const rz = cz + Math.sin(run * 1.7 + r) * rr;
          const prev = this._v1.copy(raider.position);
          this.local(rx, ry, rz, raider.position);
          this._v2.copy(raider.position).sub(prev);
          if (this._v2.lengthSq() > 1e-4) lookAtLocal(raider, this._v3.copy(raider.position).add(this._v2)); // THE SCENE SHIFT (as the wings)
          // Tracer exchange: raider → freighter and a covering escort → raider.
          if (Math.floor(t * 2 + r * 7) !== Math.floor((t - 1 / 30) * 2 + r * 7)) {
            this.convoyBolt(live, raider, live.freighter, 0xff5a4a);
            const esc = live.escorts[r % live.escorts.length];
            if (esc) this.convoyBolt(live, esc, raider, 0x66ccff);
          }
        }
        // Bolts: glowing tracers lerping shooter → target.
        const nowB = performance.now() / 1000;
        for (let b = live.bolts.length - 1; b >= 0; b--) {
          const bolt = live.bolts[b];
          const bt = (nowB - bolt.t0) / 0.5;
          if (bt >= 1) { this.root.remove(bolt.s); live.bolts.splice(b, 1); continue; }
          bolt.s.position.lerpVectors(bolt.from.position, bolt.to.position, bt);
        }
      }
    }
  }

  // ── Space Wave 3: the firms' runs down the lane (space_shipment rows) ──────
  /** Transit runs in this system with their lerped position (THE LANE LAW). */
  private transitRuns(): Array<{ id: string; row: TransitRow; pos: [number, number, number]; toPos: [number, number, number]; toName: string; firmName: string }> {
    const conn = atlas.conn;
    if (!conn || this.system.id !== 'sys0') return [];
    const nowUs = BigInt(Date.now()) * 1000n;
    const sts = stationsOf(this.system);
    const out: Array<{ id: string; row: TransitRow; pos: [number, number, number]; toPos: [number, number, number]; toName: string; firmName: string }> = [];
    for (const s of conn.db.spaceShipment.iter()) {
      if (s.state !== 'transit') continue;
      const from = sts.find((x) => x.key === s.fromKey), to = sts.find((x) => x.key === s.toKey);
      if (!from || !to) continue;
      const firm = conn.db.spaceFirm.firmId.find(s.firmId);
      out.push({
        id: String(s.shipmentId), row: s,
        pos: shipmentPosAt(from.pos, to.pos, s.departAt.microsSinceUnixEpoch, s.arriveAt.microsSinceUnixEpoch, nowUs),
        toPos: to.pos, toName: to.name, firmName: firm?.name ?? 'the firm',
      });
    }
    return out;
  }

  private nearestRun(): (ReturnType<SpaceModule['transitRuns']>[number] & { dist: number }) | null {
    let best: (ReturnType<SpaceModule['transitRuns']>[number] & { dist: number }) | null = null;
    let bestD = Infinity;
    for (const r of this.transitRuns()) {
      const d = this.pos.distanceTo(this._v1.set(r.pos[0], r.pos[1], r.pos[2]));
      if (d < bestD) { bestD = d; best = { ...r, dist: d }; }
    }
    return best;
  }

  /** E in flight: ride with the nearest run within ESCORT_RANGE_M (THE ESCORT LAW). */
  private tryEscort(): void {
    const r = this.nearestRun();
    if (!r) { this.toast('No run within reach to escort'); return; }
    if (r.dist > ESCORT_RANGE_M) { this.toast(`Fly to the ${r.firmName} run to escort it (${Math.round(r.dist / 1000)} km)`); return; }
    const me = atlas.conn?.identity;
    if (me && r.row.escortedBy.isEqual(me)) { this.toast(`You are escorting the ${r.firmName} run — stay with it`); return; }
    (atlas.conn?.reducers as unknown as { escortShipment(a: { shipmentId: bigint }): Promise<void> } | undefined)
      ?.escortShipment({ shipmentId: r.row.shipmentId })
      .then(() => this.toast(`Escorting the ${r.firmName} run to ${r.toName} — ${ESCORT_REWARD_SOV} ⓒ at arrival`))
      .catch((e: Error) => this.toast(`Escort refused — ${String(e.message ?? e)}`));
  }

  /** THE DEPOSIT LAW readout: the belt in the beam's reach and what is left of it. */
  private seamLeftBit(): string {
    const conn = atlas.conn;
    if (!conn) return '';
    // THE DEPOSIT LAW everywhere: the Commons belts, or a derived system's fields (f<i>) — the same
    // list the server meters (beltsOf with the sector), the same reach rule.
    const belts = this.system.id === 'sys0' ? SYS0_BELTS : beltsOf(this.system.id, systemSector(this.system));
    let best: { key: string; name: string } | null = null;
    let bestD = Infinity;
    for (const b of belts) {
      const d = this.pos.distanceTo(this._v1.set(b.pos[0], b.pos[1], b.pos[2]));
      if (d <= depositReach(b.radius) && d < bestD) { bestD = d; best = b; }
    }
    if (!best) return '';
    for (const d of conn.db.spaceDeposit.iter()) if (d.systemId === this.system.id && d.beltKey === best.key) return ` · seam ${d.remaining}/${d.capacity} u`;
    return ' · seam untouched';
  }

  /** THE READINESS LAW (Space Wave 3, two lanes): each civ's yard staffs its own post (Aurel Prime / Nova Aurel). */
  private patrolShipsFor(post: PatrolPost): number {
    const rp = readinessPostFor(post.poiKey, post.side);
    if (!rp) return post.ships;
    const yard = atlas.conn?.db.spaceFirm.firmId.find(rp.yardId);
    if (!yard) return post.ships;
    return patrolShipsFor(post.ships, patrolReadiness(yard.hulls, yard.hullsTarget));
  }

  /** The runs as convoys: a hauler + two escorts on the weave at the row's
   *  lerped position; raiders press in from 40 s before the ambush moment until
   *  the row reads rolled (+45 s of fight after a miss). A row gone from the
   *  transit subscription (arrived or lost) takes its bodies with it. */
  private updateShipments(): void {
    if (!this.root) return;
    const runs = this.transitRuns();
    const t = Date.now() / 1000;
    const nowUs = BigInt(Date.now()) * 1000n;
    const seen = new Set<string>();
    const sts = stationsOf(this.system);
    for (const r of runs) {
      seen.add(r.id);
      let live = this.shipments.get(r.id);
      if (!live) {
        const freighter = this.buildFreighter();
        freighter.scale.setScalar(4); // the same capital hauler as the cosmetic lanes
        const escorts: THREE.Object3D[] = [];
        for (let e = 0; e < 2; e++) { const s = new THREE.Group(); s.add(this.fallbackShip(0x7ec8e3)); escorts.push(s); this.root.add(s); }
        const raiders: THREE.Object3D[] = [];
        for (let i = 0; i < 3; i++) {
          const s = new THREE.Group();
          if (this.pirateMaster) {
            const skin = this.pirateMaster.clone();
            const box = new THREE.Box3().setFromObject(skin);
            const longest = Math.max(1, box.max.x - box.min.x, box.max.y - box.min.y, box.max.z - box.min.z);
            skin.scale.setScalar(13 / longest);
            const wrap = new THREE.Group();
            wrap.rotation.y = SHIP_MODEL_YAW;
            wrap.add(skin);
            s.add(wrap);
          } else s.add(this.fallbackShip(0xb03030));
          s.visible = false;
          raiders.push(s);
          this.root.add(s);
        }
        this.root.add(freighter);
        live = { freighter, escorts, raiders, bolts: [] };
        this.shipments.set(r.id, live);
        this.warpFlash(this.local(r.pos[0], r.pos[1], r.pos[2], this._v1).clone());
      }
      const from = sts.find((x) => x.key === r.row.fromKey)!;
      const len = Math.max(1, Math.hypot(r.toPos[0] - from.pos[0], r.toPos[1] - from.pos[1], r.toPos[2] - from.pos[2]));
      const dirX = (r.toPos[0] - from.pos[0]) / len, dirY = (r.toPos[1] - from.pos[1]) / len, dirZ = (r.toPos[2] - from.pos[2]) / len;
      const [cx, cy, cz] = r.pos;
      this.local(cx, cy, cz, live.freighter.position);
      this._m1.lookAt(this._v2.set(0, 0, 0), this._v3.set(dirX, dirY, dirZ), UP);
      live.freighter.quaternion.setFromRotationMatrix(this._m1);
      live.freighter.rotateY(Math.PI); // bow is +Z-modeled
      const ambUs = r.row.ambushAt.microsSinceUnixEpoch;
      // The cosmetic raiders press in around the moment — unless REAL raiders (a space_ambush row) are on the run.
      const ambush = !this.ambushFor(r.row.shipmentId) && (!r.row.ambushRolled ? nowUs >= ambUs - 40_000_000n : nowUs < ambUs + 45_000_000n);
      for (let e = 0; e < live.escorts.length; e++) {
        const wob = t * (0.9 + e * 0.17) + e * 2.1;
        const rr = ambush ? 260 : 140;
        const ex = cx + Math.cos(wob) * rr - dirX * (60 + e * 40);
        const ey = cy + Math.sin(wob * 0.7) * rr * 0.4 + 30;
        const ez = cz + Math.sin(wob) * rr - dirZ * (60 + e * 40);
        const esc = live.escorts[e];
        const prev = this._v1.copy(esc.position);
        this.local(ex, ey, ez, esc.position);
        this._v2.copy(esc.position).sub(prev);
        if (this._v2.lengthSq() > 1e-4) lookAtLocal(esc, this._v3.copy(esc.position).add(this._v2)); // THE SCENE SHIFT (as the wings): a root-local target aimed the hull at −shift
      }
      for (let q = 0; q < live.raiders.length; q++) {
        const raider = live.raiders[q];
        raider.visible = ambush;
        if (!ambush) continue;
        const run = t * (0.5 + q * 0.11) + q * 2.6;
        const dive = Math.sin(run) * 0.5 + 0.5;
        const rr = 700 - dive * 560;
        const rx = cx + Math.cos(run * 1.7 + q) * rr;
        const ry = cy + Math.sin(run * 1.3 + q * 1.9) * rr * 0.5;
        const rz = cz + Math.sin(run * 1.7 + q) * rr;
        const prev = this._v1.copy(raider.position);
        this.local(rx, ry, rz, raider.position);
        this._v2.copy(raider.position).sub(prev);
        if (this._v2.lengthSq() > 1e-4) lookAtLocal(raider, this._v3.copy(raider.position).add(this._v2)); // THE SCENE SHIFT (as the wings)
        if (Math.floor(t * 2 + q * 7) !== Math.floor((t - 1 / 30) * 2 + q * 7)) {
          this.convoyBolt(live, raider, live.freighter, 0xff5a4a);
          const esc = live.escorts[q % live.escorts.length];
          if (esc) this.convoyBolt(live, esc, raider, 0x66ccff);
        }
      }
      const nowB = performance.now() / 1000;
      for (let b = live.bolts.length - 1; b >= 0; b--) {
        const bolt = live.bolts[b];
        const bt = (nowB - bolt.t0) / 0.5;
        if (bt >= 1) { this.root.remove(bolt.s); live.bolts.splice(b, 1); continue; }
        bolt.s.position.lerpVectors(bolt.from.position, bolt.to.position, bt);
      }
    }
    for (const [id, live] of this.shipments) {
      if (seen.has(id)) continue;
      this.warpFlash(live.freighter.position.clone());
      this.root.remove(live.freighter);
      for (const e of live.escorts) this.root.remove(e);
      for (const q of live.raiders) this.root.remove(q);
      for (const b of live.bolts) this.root.remove(b.s);
      this.shipments.delete(id);
    }
  }

  private convoyBolt(convoy: { bolts: Array<{ s: THREE.Sprite; from: THREE.Object3D; to: THREE.Object3D; t0: number }> }, from: THREE.Object3D, to: THREE.Object3D, tint: number): void {
    if (!this.root || convoy.bolts.length > 24) return;
    const s = this.spriteGlow(tint, 14);
    s.position.copy(from.position);
    this.root.add(s);
    convoy.bolts.push({ s, from, to, t0: performance.now() / 1000 });
  }

  /** Deterministic lane schedule: a carrier EXISTS while its lane phase sits
   *  inside the flight window — every client sees the same traffic. */
  private updateFreighters(): void {
    if (!this.root) return;
    const nowS = performance.now() / 1000;
    for (let li = 0; li < FREIGHT_LANES.length; li++) {
      const lane = FREIGHT_LANES[li];
      const laneLen = Math.hypot(lane.to[0] - lane.from[0], lane.to[1] - lane.from[1], lane.to[2] - lane.from[2]);
      const flightS = Math.min(lane.periodS * 0.72, laneLen / FREIGHTER_SPEED);
      const phase = (nowS + li * 17) % lane.periodS;
      const flying = phase < flightS;
      const live = this.freighters.get(lane.key);
      if (flying && !live) {
        const obj = this.buildFreighter();
        obj.position.copy(this.local(lane.from[0], lane.from[1], lane.from[2], this._v1));
        this._m1.lookAt(this._v1.set(0, 0, 0), this._v2.set(lane.to[0] - lane.from[0], lane.to[1] - lane.from[1], lane.to[2] - lane.from[2]).normalize(), UP);
        obj.quaternion.setFromRotationMatrix(this._m1);
        obj.rotateY(Math.PI); // the freighter's bow is +Z-modeled (bridge astern)
        this.root.add(obj);
        this.freighters.set(lane.key, { obj, flightS });
        this.warpFlash(obj.position);
      } else if (!flying && live) {
        this.warpFlash(live.obj.position);
        this.root.remove(live.obj);
        this.freighters.delete(lane.key);
      } else if (flying && live) {
        const f = phase / flightS;
        this.local(
          lane.from[0] + (lane.to[0] - lane.from[0]) * f,
          lane.from[1] + (lane.to[1] - lane.from[1]) * f,
          lane.from[2] + (lane.to[2] - lane.from[2]) * f,
          live.obj.position,
        );
      }
    }
  }

  // ── Station repair (SWG: "ask the station to repair your ship") ─────────
  /** THE REGISTRY LAW: the same station registry the server docks against
   *  (shared/space_stations) — `key` is the server's station key. */
  private nearStation(): PoiSpec | null {
    for (const st of stationsOf(this.system)) {
      if (this.pos.distanceTo(new THREE.Vector3(st.pos[0], st.pos[1], st.pos[2])) < st.dockRange) {
        return { key: st.key, name: st.name, kind: 'station', pos: st.pos, radius: st.radius, pirates: 0, tier: this.system.tier, patrol: 0 } as PoiSpec;
      }
    }
    return null;
  }

  private tryRepair(): void {
    // Your own depot resupplies for free — hull/shields/ammo are flight
    // state (client-side pools), so no fee and no reducer.
    const depot = this.nearOwnStructure('depot', 600);
    if (depot && stationServicesOnClient()) {
      // space campaign K (station_services_v1): the depot is a SERVER-acked, capped resupply — once per 10 min a depot.
      (atlas.conn?.reducers as unknown as { resupplyAtDepot(a: { structId: bigint }): Promise<void> } | undefined)?.resupplyAtDepot({ structId: depot.structId })
        .then(() => {
          this.hull = this.stats.hp; this.shieldF = this.arcMax(); this.shieldR = this.arcMax();
          this.missileAmmo = this.stats.missiles; this.counterAmmo = this.stats.counters;
          this.toast('Depot arms swing out — repaired & rearmed (your platform: once per 10 min)');
        })
        .catch((e: Error) => this.toast(`Depot refused: ${e.message ?? e}`));
      return;
    }
    if (depot) {
      this.hull = this.stats.hp;
      this.shieldF = this.arcMax();
      this.shieldR = this.arcMax();
      this.missileAmmo = this.stats.missiles;
      this.counterAmmo = this.stats.counters;
      this.toast('Depot arms swing out — repaired & rearmed (no charge at your own platform)');
      return;
    }
    const st = this.nearStation();
    if (!st) { this.toast('No repair dock in range — pull up to a station'); return; }
    if (stationServicesOnClient()) {
      // space campaign K: station repair is a QUOTED, material-backed job — the dock's Station tab holds the quote.
      if (!this.dock) this.toggleDock();
      this.dock?.showTab('station');
      return;
    }
    if (this.hull >= this.stats.hp && this.shieldF >= this.arcMax() && this.shieldR >= this.arcMax()) {
      this.toast('The hull is sound — nothing to repair');
      return;
    }
    atlas.conn?.reducers.spaceRepair({})
      .then(() => {
        this.hull = this.stats.hp;
        this.shieldF = this.arcMax();
        this.shieldR = this.arcMax();
        this.missileAmmo = this.stats.missiles;   // rearm rides the repair crews
        this.counterAmmo = this.stats.counters;
        this.toast(`${st.name} crews swarm the hull — repaired & rearmed (${REPAIR_COST_SOV} ⓒ)`);
      })
      .catch((e: Error) => this.toast(`Repair refused: ${e.message ?? e}`));
  }

  // ── 3D sector map + autopilot (SWG datapad-waypoint feel) ────────────────
  private toggleMap(): void {
    if (this.map) { this.map.unmount(); this.map = null; return; }
    this.map = new SpaceMap(
      (name) => { this.autopilotTo(name); this.toggleMap(); },
      (worldIdStr) => {
        const wp = this.eligibleWorldRows().find((w) => w.worldId.toString() === worldIdStr);
        if (wp) { this.warpToWorld(wp.worldId, wp.name); this.toggleMap(); }
      },
      () => this.toggleMap(),
    );
    this.map.mount();
    this.pushMapData();
  }

  /** Owner username for the galactic chart (account is fully replicated). */
  /** C5: one identity-hex → username map per map push (was a whole `account` scan PER WORLD at 5 Hz: worlds x accounts). */
  private ownerCache: Map<string, string> | null = null;
  private ownerName(owner: unknown): string {
    if (!this.ownerCache) {
      const m = new Map<string, string>();
      try { for (const a of atlas.conn!.db.account.iter()) m.set(a.identity.toHexString(), a.username); } catch { /* not subscribed yet */ }
      this.ownerCache = m;
    }
    const hex = (owner as { toHexString?: () => string } | null)?.toHexString?.();
    return (hex ? this.ownerCache.get(hex) : undefined) ?? 'unknown';
  }

  private pushMapData(): void {
    if (!this.map) return;
    this.ownerCache = null; // C5: rebuilt lazily, once, by the first ownerName of this push
    if (this.nav.length === 0) this.nav = this.navFor();
    const home = this.system.id === 'sys0';
    const sec = home ? null : systemSector(this.system);
    const fwd = this._v2.set(0, 0, -1).applyQuaternion(this.quat);
    // natural objects: the system's own bodies as chart markers (a comet where it is now, a moon beside its planet)
    const naturalPois = (this.natural?.navBodies() ?? []).map((b) => ({ name: b.name, kind: `n_${b.kind}`, pos: [b.pos[0], b.pos[1], b.pos[2]] as const, tier: 0, radius: b.radius, dist: this._v1.set(b.pos[0], b.pos[1], b.pos[2]).distanceTo(this.pos) }));
    this.map.update({
      systemName: this.system.name,
      star: sec ? { color: sec.star.color, radius: sec.star.radius } : null,
      heading: [fwd.x, fwd.z],
      you: [this.pos.x, this.pos.y, this.pos.z],
      nav: this.navIdx >= 0 ? this.nav[this.navIdx].name : null,
      // THIS system's POIs: the Commons hand-built list, or the derived
      // station/belts/dens of the star you are actually orbiting.
      pois: home
        ? [...POIS.map((p) => ({
          name: p.name, kind: p.kind, pos: p.pos, tier: p.tier, radius: p.radius,
          dist: this._v1.set(...p.pos).distanceTo(this.pos),
        })), ...naturalPois]
        : [...systemNavPoints(this.system).filter((n) => n.kind !== 'planet').map((n) => ({
          name: n.name, kind: n.kind, pos: n.pos, tier: n.tier, radius: n.radius,
          dist: this._v1.set(...n.pos).distanceTo(this.pos),
        })), ...naturalPois],
      planets: home
        ? PLANETS.map((p) => ({ name: p.name, pos: p.pos, radius: p.radius, rim: p.rim }))
        : sec!.planets.map((p) => ({ name: p.name, pos: p.pos, radius: p.radius, hue: p.hue })),
      // The GALACTIC layer: public user worlds hang in the COMMONS sector; a
      // user system charts only ITS world (the chart matches the system you are in).
      worlds: this.eligibleWorldRows().filter((w) => home || w.worldId.toString() === this.system.worldId).map((w) => {
        const spec = worldPlanetOf(w.worldId);
        return {
          name: w.name, owner: this.ownerName(w.owner), worldId: w.worldId.toString(),
          pos: [spec.x, spec.y, spec.z] as const, radius: spec.radius, hue: spec.hue,
          dist: this._v1.set(spec.x, spec.y, spec.z).distanceTo(this.pos),
        };
      }),
      battle: (() => {
        const nowS = Date.now() / 1000;
        const win = this.battleWindow();
        if (win) return { name: win.poi.name, pos: win.poi.pos, active: true, secs: BATTLE_LEN_S - win.t };
        const cyc = Math.floor(nowS / BATTLE_PERIOD_S) + 1;
        const forts = POIS.filter((p) => p.kind === 'fortress' || p.kind === 'megastation');
        if (!forts.length) return null;
        const poi = forts[cyc % forts.length];
        return { name: poi.name, pos: poi.pos, active: false, secs: cyc * BATTLE_PERIOD_S - nowS };
      })(),
    });
  }

  /** Map-click autopilot: far targets ride the hyperdrive; close ones steer
   *  themselves in at full throttle (overcruise winds up on its own). */
  private autopilotTo(name: string): void {
    if (this.nav.length === 0) this.nav = this.navFor();
    const idx = this.nav.findIndex((n) => n.name === name);
    if (idx < 0) return;
    this.navIdx = idx;
    const n = this.nav[idx];
    const dist = this._v1.set(...n.pos).distanceTo(this.pos);
    if (dist > n.arrive * 1.2) {
      this.autoCruise = dist < 60_000; // close: cruise there; far: hyperspace
      if (!this.autoCruise) { this.hyper = 'align'; this.hyperT = 0; this.toast(`Autopilot: hyperspace to ${n.name}`); }
      else this.toast(`Autopilot: cruising to ${n.name}`);
    } else this.toast('Already on approach');
  }

  /** The hyperdrive state machine (runs inside fixed()). Returns true while
   *  it OWNS the velocity (normal steering/throttle skipped). */
  private hyperTick(dt: number): boolean {
    if (this.hyper === '' || this.navIdx < 0) return false;
    const n = this.nav[this.navIdx];
    const toTarget = this._v1.set(...n.pos).sub(this.pos);
    const dist = toTarget.length();
    if (dist <= n.arrive) { this.dropHyper(`Dropping out of hyperspace — ${n.name}`); this.navIdx = -1; return false; }
    { // Slice D, THE MASS LOCK: a pinned war's theatre pulls a jump out at its edge (an honest pilot never outruns the
      // server's movement envelope there — the last kilometres are flown on engines).
      const tc = this.pinnedTheatre();
      if (tc && massLocked(this.pos.distanceTo(tc), true)) { this.dropHyper(`⚠ MASS LOCK — pulled out at the fleet war's edge (${Math.round(MASS_LOCK_M / 1000)} km); fly in on engines`); return false; }
    }
    const dir = toTarget.normalize();
    if (this.hyper === 'align') {
      // Swing the nose onto the lane (fast slerp), then spool. Matrix4.lookAt
      // puts −Z from eye toward target — the R-level convention above.
      this._m1.lookAt(this._v2.set(0, 0, 0), this._v3.copy(dir), UP);
      const want = this._q1.setFromRotationMatrix(this._m1);
      this.quat.slerp(want, Math.min(1, dt * 3.5));
      const fwd = this._v2.set(0, 0, -1).applyQuaternion(this.quat);
      if (fwd.dot(dir) > 0.998) { this.hyper = 'spool'; this.hyperT = 0; sfx('sfx_hyper_spool', { gain: 0.45, cooldownMs: 900 }); this.toast('Hyperdrive spooling…'); }
      return false; // normal flight continues while aligning
    }
    if (this.hyper === 'spool') {
      this.hyperT += dt;
      if (this.hyperT >= 2) { this.hyper = 'jump'; sfx('sfx_hyper_jump', { gain: 0.55, cooldownMs: 900 }); this.toast(`HYPERSPACE — ${n.name}`); }
      return false;
    }
    // jump: the drive owns the ship. Steering locked, velocity on the lane.
    this.vel.copy(dir).multiplyScalar(HYPER_SPEED);
    this.pos.addScaledVector(this.vel, dt);
    this.throttle = 1;
    return true;
  }

  // ── Targeting ────────────────────────────────────────────────────────────
  // ── THE SECTOR'S KEYS (space campaign B, 2026-09-30) — the dispatcher's helpers ─────────────────────────
  /** The frame the last key acted in (a change releases the held set). */
  private keyFrame: KeyFrameName = 'sector';
  /** Matching asks for more than this hull gives short of overcruise (the HUD says so). */
  private matchCapped = false;
  /** The utility beam's latch in TOGGLE mode (lib/space_input beamMode). */
  private beamLatched = false;
  /** The probe lever's hold (`__space.holdBeam`) — not a key, so a key release never drops it. */
  private beamProbeHold = false;
  /** Presses the dispatcher took, by action (a probe's census: which action a key REALLY triggered). */
  private readonly keyCensus = new Map<string, number>();

  /** Where focus is, for the key-owner rule (lib/space_input sectorKeyOwner). */
  private keyFacts(e: KeyboardEvent): KeyOwnerFacts {
    const t = e.target as HTMLElement | null;
    const act = document.activeElement as HTMLElement | null;
    const el = t && t !== document.body && t !== document.documentElement ? t : act && act !== document.body ? act : null;
    const ui = !el || typeof el.closest !== 'function' ? ''
      : el.closest('#space-ops') ? 'ops'
        : el.closest('#fleet-dock') ? 'dock'
          : el.closest('#space-map, #galaxy-map') ? 'chart'
            : el.closest('[data-atlas-nav], #atlas-options, #atlas-chat') ? 'window' : '';
    const opts = document.getElementById('atlas-options');
    return {
      tag: (t?.tagName ?? '').toUpperCase(), editable: !!t?.isContentEditable, composing: isComposingKey(e),
      focusUi: ui, optionsOpen: !!opts && opts.style.display === 'flex',
      interior: this.interior ? this.interior.kind : null, docked: !!this.dock,
    };
  }

  /** Enter a frame: a change (took off the dock, walked out of the airlock) drops what the old frame held. */
  private setKeyFrame(f: KeyFrameName): void {
    if (f === this.keyFrame) return;
    this.keyFrame = f;
    this.releaseHeld('frame');
  }

  /** Release every held input (the stuck-key law): keys, the booster, the beam's latch. The HELD throttle is a
   *  setting, not a held key — it keeps its value, as it always has. */
  private releaseHeld(_cause: ReleaseCause): void {
    this.keys.clear();
    this.boost = false;
    this.beamLatched = false;
  }

  /** The beam's HUD cue in the player's mode: "hold U" / "press U (toggle)". */
  private beamCue(): string { return beamMode() === 'toggle' ? `press ${keyHint('spaceBeam')} (toggle)` : `hold ${keyHint('spaceBeam')}`; }
  /** The utility beam is on (held, or latched in toggle mode). */
  private beamHeld(): boolean {
    return this.beamProbeHold || beamOn(beamMode(), keyHeldIn(this.keys, 'spaceBeam'), this.beamLatched);
  }

  /** One sector action. Every case is the handler the old hard-coded key called. */
  private sectorAction(a: KeyActionName): void {
    this.keyCensus.set(a, (this.keyCensus.get(a) ?? 0) + 1);
    switch (a) {
      case 'spaceLand': this.tryLand(); break;
      case 'spaceTarget': this.cycleTarget(); break;
      case 'spaceMatchSpeed':
        this.matchSpeed = !this.matchSpeed;
        if (!this.target) { this.matchSpeed = false; this.toast(`Match speed needs a target — ${keyHint('spaceTarget')} locks one`); }
        break;
      case 'spaceNav': this.cycleNav(); break;
      case 'spaceHyper': this.autoCruise = false; this.toggleHyper(); break;
      case 'spaceGalaxy': this.toggleGalaxyMap(); break;
      case 'spaceSystemMap': this.toggleMap(); break;
      case 'spaceRepair': this.tryRepair(); break;
      case 'spaceDock': this.toggleDock(); break;
      case 'spaceBoard': this.toggleInterior('station'); break;
      case 'spaceDeck': this.toggleInterior('deck'); break;
      case 'spaceTurret': this.toggleTurretSeat(); break;            // THE CREW LAW: a friend's Sovereign turret
      case 'spaceMissile': this.flightCombat.secondary(); break;      // space campaign I: the SECONDARY trigger — the active fire group's mount (the missile by default)
      case 'spaceFireGroup': this.flightCombat.cycleGroup(); break;  // space campaign I: swap fire group 1 ↔ 2
      case 'spaceCountermeasures': this.popCountermeasures(); break;
      case 'spaceSubTarget': this.cycleSubTarget(); break;
      case 'spaceDroid': this.droidCommand(); break;
      case 'spaceCockpit': this.toggleCockpit(); break;
      case 'spaceScan': this.scanPing(); break;                       // exploration scanner
      case 'spaceSurvey': surveyDesk.toggle(); break;                 // space campaign F: the survey desk
      case 'spaceOps': this.toggleOpsPanel(); break;                  // squadron + platforms
      case 'spaceEscort': this.tryEscort(); break;                    // Space Wave 3: ride with a firm's run
      case 'spaceBeam': if (beamMode() === 'toggle') this.beamLatched = !this.beamLatched; break;
      case 'spacePowerWeapons': this.setPower('weapons'); break;
      case 'spacePowerShields': this.setPower('shields'); break;
      case 'spacePowerEngines': this.setPower('engines'); break;
      case 'spacePowerBalanced': this.setPower('balanced'); break;
      case 'spaceFurnish': if (this.interior?.kind === 'deck') this.toggleFurnish(); break; // deck furnishing
      case 'spaceEngineering': engineeringPanel.toggle(); break;      // space campaign G: subsystems, crew, power, patches
      case 'spacePatch': shipEngineering.patchShortcut(); break;      // space campaign G: the field patch (again cancels)
      default: break; // the stick's actions (roll, level, fire, throttle, boost) are HELD — read in fixed()
    }
  }

  private cycleTarget(): void {
    const sl = this.shipLocal().clone();
    const mySide: 0 | 1 | -1 = this.faction === 'aurelian' ? 0 : this.faction === 'borean' ? 1 : -1;
    const alive = this.pirates
      // Tab cycles THREATS — never wingmen, never your own civ's gunships.
      .filter((p) => p.deadUntil === 0 && !p.ally && !(mySide !== -1 && this.sideOf(p) === mySide))
      .sort((a, b) => a.root.position.distanceTo(sl) - b.root.position.distanceTo(sl));
    if (alive.length === 0) { this.target = null; return; }
    const idx = this.target ? alive.indexOf(this.target) : -1;
    this.target = alive[(idx + 1) % alive.length];
    sfx('sfx_target_lock', { gain: 0.35, cooldownMs: 120 });
  }

  /** The fortress warlord's name for slot-0 den pirates ('' otherwise). */
  private aceName(p: Pirate): string {
    return p.key.endsWith(':pirate0') ? ACE_NAMES[p.poiKey] ?? '' : '';
  }

  private targetName(p: Pirate): string {
    const ace = this.aceName(p);
    if (ace) return `☠ ${ace}`;
    // THE GLYPH LAW (space campaign B, lib/space_iff): a war hull / patrol names its side and class with a word and a
    // glyph — the line read "Deep space raider 1" for every war gunship, escort and patrol.
    const mySide: 0 | 1 | -1 = this.faction === 'aurelian' ? 0 : this.faction === 'borean' ? 1 : -1;
    const iff = { ally: p.ally === true, side: this.sideOf(p), mySide };
    const sided = sidedHullLabel(p.key, iff);
    if (sided) return sided;
    if (p.ally) return '◆ wingman (friend)';
    if (p.key.startsWith('bounty:')) return '▲ bounty hunter';
    const poiKey = p.key.split(':')[0];
    const poi = POIS.find((x) => x.key === poiKey);
    const n = Number(p.key.split('pirate')[1] ?? 0) + 1;
    return `▲ ${poi?.name ?? 'Deep space'} raider ${Number.isFinite(n) ? n : 1}`;
  }

  // ── Combat ───────────────────────────────────────────────────────────────
  private fire(): void {
    if (this.fireCool > 0 || !this.root) return;
    if (!shipEngineering.gunsOnline()) { shipEngineering.gunsDownNote(); return; } // space campaign G §18.3: a disabled weapons group cannot fire the cannons
    if (this.cap < CAP_VOLLEY) { sfx('sfx_weapon_dry', { gain: 0.3, cooldownMs: 300 }); return; } // capacitor dry — JTL's sustained-fire limit
    this.cruise = Math.max(1, this.cruise * 0.5); // guns online = engines throttle back
    this.fireCool = FIRE_COOLDOWN_S * this.stats.fireCdMult * shipEngineering.gunCdMult(); // G: degraded weapons cycle ×1.3
    this.cap -= CAP_VOLLEY;
    sfx('sfx_ship_cannon', { gain: 0.42, cooldownMs: 90 });
    const fwd = this._v1.set(0, 0, -1).applyQuaternion(this.quat);
    for (const side of [-1.6, 1.6]) {
      const muzzle = this._v2.set(side, -0.4, -3).applyQuaternion(this.quat);
      const b = this.spriteGlow(0x66ddff, 3.2);
      b.position.copy(this._ship).add(muzzle);
      this.root.add(b);
      noteFired(b, b.position.x + this.off.x, b.position.y + this.off.y, b.position.z + this.off.z); // slice D: the intent's origin
      this.bolts.push({ sprite: b, vel: fwd.clone().multiplyScalar(BOLT_SPEED).add(this.vel), ttl: BOLT_LIFE_S, hostile: false });
    }
  }

  private pirateFire(p: Pirate, tgtPos: THREE.Vector3, tgtVel: THREE.Vector3): void {
    if (!this.root) return;
    if (p.jamUntil > performance.now()) return; // weapons subsystem dark (component targeting)
    // Lead-aimed by tier (the FreeSpace accuracy knob): reavers shoot at
    // where you WILL be; raiders shoot at where you were, plus scatter.
    const stats = this.statsOf(p);
    const boltSpeed = BOLT_SPEED * 0.78; // was 0.62 — a fleeing hull outran the bolt
    const t = Math.min(2.2, p.root.position.distanceTo(tgtPos) / boltSpeed);
    const jitter = (1 - stats.lead) * 26;
    const j = p.jinkSeed * 97 + Math.floor(performance.now() / 300);
    const aim = this._v1.copy(tgtPos)
      .addScaledVector(tgtVel, t * stats.lead)
      .add(this._v2.set(
        (Math.sin(j * 12.9898) * 43758.5453 % 1) * jitter,
        (Math.sin(j * 78.233) * 12543.1234 % 1) * jitter,
        (Math.sin(j * 39.425) * 26711.9876 % 1) * jitter,
      ));
    const dir = aim.sub(p.root.position).normalize();
    // Wingman bolts run GREEN (friends); war bolts wear the shooter's civ
    // color; den pirates keep the red-orange threat color. `hostile` means
    // MAY HIT THE PLAYER: any bolt from a side you have not sworn to (an
    // unaligned hull is hostile to both lines).
    const pSide = this.sideOf(p);
    const mySide: 0 | 1 | -1 = this.faction === 'aurelian' ? 0 : this.faction === 'borean' ? 1 : -1;
    const hostileToPlayer = p.ally ? false : pSide === -1 ? true : (mySide === -1 || pSide !== mySide);
    const b = this.spriteGlow(p.ally ? 0x5cff9d : pSide === 0 ? 0xffd873 : pSide === 1 ? 0x9fe8ff : 0xff8844, 3.6);
    b.position.copy(p.root.position).addScaledVector(dir, 8);
    { // hostile volleys are AUDIBLE — a dull distant pulse, louder the closer the shooter
      const dHear = this.shipLocal().distanceTo(p.root.position);
      if (dHear < 1600) this.sfxAtLocal('sfx_enemy_cannon', p.root.position, { gain: Math.max(0.05, 0.35 - dHear * 0.0002), cooldownMs: 140, caption: hostileToPlayer ? 'Hostile fire' : undefined });
    }
    this.root.add(b);
    this.bolts.push({
      // A bolt OWNS its velocity (2026-10-01, the three-way fight probe): `dir` IS the scratch `_v1`, so every hull bolt in
      // flight shared ONE vector — the next shot swung them all to its own aim and any later `_v1.copy(position)` (an
      // impact flash, a respawn) flung them kilometres a step. Hull-on-hull fire never landed; only lone shooters did.
      sprite: b, vel: dir.clone().multiplyScalar(boltSpeed),
      ttl: BOLT_LIFE_S * (pSide !== -1 && !hostileToPlayer ? 1.2 : 2.8) * this.classOf(p).range, // war-internal fire stays short — 60 ships would flood the pool; an escort's guns reach farther
      hostile: hostileToPlayer, fromAlly: p.ally === true,
      side: pSide === -1 ? undefined : pSide,
      dmg: 9 * this.classOf(p).dmg,
    });
  }

  /**
   * One pirate's decision + steering tick. Decision picks a STATE (the
   * gamedev-canon split: decide first, steer second); steering chases a
   * desired direction at tier speed with a smooth turn — pirates FLY their
   * maneuvers instead of gliding on rails.
   */
  private readonly _qPos = new THREE.Vector3();
  private readonly _v6 = new THREE.Vector3();
  private readonly _qVel = new THREE.Vector3();
  /**
   * The ship this pilot is hunting (wingman wave 2026-08-23). Hostiles hunt
   * the player — WAR pilots will take a NEARER wingman instead (allies are
   * fair game, "they can be destroyed by enemy ships"). Wingmen hunt the
   * nearest live hostile; with no enemy in reach they hold a formation slot
   * beside the player (live=false ⇒ never fire at the anchor).
   */
  /** A combatant's side: allies fly the player's civ; skirmishers/war
   *  pilots carry warSide; den pirates have none. */
  private sideOf(q: Pirate): 0 | 1 | -1 {
    if (q.ally) return this.faction === 'aurelian' ? 0 : this.faction === 'borean' ? 1 : -1;
    return q.warSide ?? -1;
  }

  /** The three-way fight's tally (probe diag, 2026-10-01): bolt hits and kills by shooter side × target side (index
   *  side + 1: 0 pirate, 1 Aurelian, 2 Borean), hits on the player by shooter side, the player's own bolts on hulls by
   *  target side (+ on the war's capitals / swarm), and own-side hits — 0 by construction (boltHits refuses them). */
  private readonly meleeTally = { hits: [[0, 0, 0], [0, 0, 0], [0, 0, 0]], kills: [[0, 0, 0], [0, 0, 0], [0, 0, 0]], onPlayer: [0, 0, 0], byPlayer: [0, 0, 0], byPlayerWar: 0, ownSide: 0 };
  private probeRaiders = 0;
  private simScale = 1;      // probe lever: fixed steps per FixedSim step (1 in play)
  private simTicks = 0;      // fixed steps run since boot (the probes' sim clock)
  private readonly _quarryCands: Array<{ side: 0 | 1 | -1; ally: boolean; bounty: boolean; dist: number }> = [];
  private readonly _quarryRefs: Pirate[] = [];
  private quarryOf(p: Pirate): { pos: THREE.Vector3; vel: THREE.Vector3; live: boolean; player: boolean } {
    const mySide: 0 | 1 | -1 = this.faction === 'aurelian' ? 0 : this.faction === 'borean' ? 1 : -1;
    const pSide = this.sideOf(p);
    // ONE target rule for every hull (lib/space_patrols.pickQuarry, 2026-09-08):
    // a civ hull hunts the OTHER civ AND the pirates (nearest first) and the
    // player when the player opposes it; a pirate keeps the pack law on the
    // player in aggro range and otherwise turns on the nearest civ hull.
    // Pooled candidate rows: this runs per pirate per 60 Hz tick (O(P²), P up
    // to ~150 in a fleet war) and used to allocate one object per pair (2026-09-12).
    // THE DEN SLOT LAW: an engaged den hull hunts the pilot the SERVER says it hunts — another pilot here means their ship.
    if (!p.ally && p.warSide === undefined) {
      const row = this.slotRowFor(p);
      const me = atlas.conn?.identity;
      if (row && me && !row.target.isEqual(me) && !row.target.isEqual(Identity.zero())) {
        const rs = this.remoteShips.get(row.target.toHexString());
        if (rs) return { pos: this._qPos.copy(rs.obj.position), vel: this._qVel.copy(rs.vel), live: true, player: false };
      }
    }
    const cands = this._quarryCands, refs = this._quarryRefs;
    let n = 0;
    for (const q of this.pirates) {
      if (q === p || q.deadUntil > 0) continue;
      let row = cands[n];
      if (!row) { row = { side: -1, ally: false, bounty: false, dist: 0 }; cands[n] = row; }
      row.side = this.sideOf(q); row.ally = q.ally === true; row.bounty = q.bountyId !== undefined; row.dist = q.root.position.distanceTo(p.root.position);
      refs[n] = q;
      n++;
    }
    cands.length = n; refs.length = n;
    const enemy = pSide === -1 ? -1 : (1 - pSide) as 0 | 1;
    // The player counts as a target when their allegiance opposes (or, unaligned,
    // for the enemy of nobody: BOTH lines read an unaligned hull as hostile).
    const playerHostile = pSide === -1 ? true : (!p.ally && (mySide === -1 || mySide === enemy));
    const pick = pickQuarry({ side: pSide, ally: p.ally === true }, cands, { dist: this._ship.distanceTo(p.root.position), hostile: playerHostile }, PIRATE_AGGRO_M);
    if (pick && pick.kind === 'hull') {
      const best = refs[pick.idx];
      return { pos: this._qPos.copy(best.root.position), vel: this._qVel.copy(best.vel), live: true, player: false };
    }
    if (pick && pick.kind === 'player') {
      return { pos: this._qPos.copy(this._ship), vel: this._qVel.copy(this.vel), live: true, player: true };
    }
    // Nothing in reach: wingmen hold a formation slot beside the player;
    // skirmishers and civ patrols loiter their anchor (patrol home).
    if (p.ally) {
      const slot = p.jinkSeed % 6;
      const a = (slot / 6) * Math.PI * 2;
      this._qPos.copy(this._ship);
      this._qPos.x += Math.cos(a) * 240;
      this._qPos.y += 30 + slot * 14;
      this._qPos.z += Math.sin(a) * 240;
      return { pos: this._qPos, vel: this._qVel.copy(this.vel), live: false, player: false };
    }
    return { pos: this.local(p.home.x, p.home.y, p.home.z, this._qPos), vel: this._qVel.set(0, 0, 0), live: false, player: false };
  }

  private pirateAi(p: Pirate, dt: number, now: number, denAggro: Set<string>, denAttackers: Map<string, number>): void {
    const stats = this.statsOf(p);
    // THE DEN POSITION LAW: a hull another pilot is fighting flies where THEY put it (the streamed seat).
    if (p.seat && now - p.seat.at < DEN_HULL_STALE_S * 1000) {
      p.quarryIsPlayer = false; // another pilot flies this hull at their own quarry
      // Physics follow-up item 5: the seats are played back on their server stamps (lib/remote_motion.ts) — the old
      // lerp(seat, dt·4) stepped every ~3 Hz seat and lagged at combat speeds.
      const pose = p.seatTrack?.pose(now, this._rPose);
      if (pose) {
        this.local(pose.x, pose.y, pose.z, p.root.position);
        p.root.rotation.set(0, pose.yaw, 0, 'YXZ');
      } else {
        this.local(p.seat.x, p.seat.y, p.seat.z, p.root.position);
        p.root.rotation.set(0, p.seat.yaw, 0, 'YXZ');
      }
      return;
    }
    const q = this.quarryOf(p); // the player, a wingman, or (for allies) a hostile
    p.quarryIsPlayer = q.player;
    const toShip = this._aim.copy(q.pos).sub(p.root.position);
    const d = toShip.length();
    toShip.normalize();
    const homeLocal = this.local(p.home.x, p.home.y, p.home.z, this._v2);
    const age = p.aiUntil - now; // >0 while the state's deadline is ahead

    // ── Decide ──
    switch (p.ai) {
      case 'patrol':
        if (d < PIRATE_AGGRO_M || denAggro.has(p.poiKey)) { p.ai = 'intercept'; p.aiUntil = 0; }
        break;
      case 'intercept':
        if (d > PIRATE_AGGRO_M * 2.5) { p.ai = 'patrol'; break; }
        if (d < 700) {
          const fwd = this._v3.set(0, 0, -1).applyQuaternion(p.root.quaternion);
          if (fwd.dot(toShip) > 0.9) {
            // Wave rhythm: only two den-mates press the run at once.
            if ((denAttackers.get(p.poiKey) ?? 0) < 2) {
              p.ai = 'attack';
              p.aiUntil = now + ATTACK_MAX_S * 1000;
              denAttackers.set(p.poiKey, (denAttackers.get(p.poiKey) ?? 0) + 1);
            } else { p.ai = 'circle'; p.aiUntil = now + 4000; }
          }
        }
        break;
      case 'attack':
        if (d < ATTACK_BREAK_M || age <= 0) { p.ai = 'break'; p.aiUntil = now + BREAK_S * 1000; }
        break;
      case 'break':
        if (age <= 0) { p.ai = 'circle'; p.aiUntil = now + 4000; }
        break;
      case 'circle': {
        const fwd = this._v3.set(0, 0, -1).applyQuaternion(p.root.quaternion);
        if (fwd.dot(toShip) > 0.92 && (denAttackers.get(p.poiKey) ?? 0) < 2) {
          p.ai = 'attack';
          p.aiUntil = now + ATTACK_MAX_S * 1000;
          denAttackers.set(p.poiKey, (denAttackers.get(p.poiKey) ?? 0) + 1);
        } else if (age <= 0) { p.ai = 'intercept'; }
        break;
      }
      case 'evade':
        if (age <= 0) { p.ai = 'break'; p.aiUntil = now + BREAK_S * 700; }
        break;
      case 'flee':
        if (age <= 0 || p.root.position.distanceTo(homeLocal) < 120) {
          // Escaped — SWG's driven-off ace: gone without a bounty.
          p.escaped = true;
          p.deadUntil = now + PIRATE_RESPAWN_S * 1000;
          p.root.visible = false;
          if (this.target === p) this.target = null;
          this.toast(`${this.targetName(p)} broke off and escaped`);
          return;
        }
        break;
    }

    // ── Steer ──
    const desired = this._v3;
    let speed = stats.speed;
    switch (p.ai) {
      case 'patrol': {
        // Loiter a slow circuit around the den. THE DEN POSITION LAW: a den slot's circuit is the SHARED
        // wall-clock formula (the same point on every screen); every other hull keeps its private one.
        const slot = slotOf(p.key);
        if (slot >= 0 && p.warSide === undefined && !p.ally) {
          const pt = denPatrolPoint([p.home.x, p.home.y, p.home.z], slot, p.poiKey, Date.now());
          desired.copy(this.local(pt[0], pt[1], pt[2], this._v6)).sub(p.root.position);
        } else {
          const t = now / 1000 * 0.06 + p.jinkSeed;
          desired.set(Math.cos(t), 0.12 * Math.sin(t * 0.7), Math.sin(t))
            .multiplyScalar(160).add(homeLocal).sub(p.root.position);
        }
        const dd = desired.length();
        desired.normalize();
        speed = Math.min(stats.speed * 0.35, dd);
        break;
      }
      case 'intercept': {
        // Chase the LEAD point, not the ship (they fly like pilots, not missiles).
        const t = Math.min(2, d / Math.max(1, stats.speed));
        desired.copy(q.pos).addScaledVector(q.vel, t * 0.7).sub(p.root.position).normalize();
        break;
      }
      case 'attack':
        desired.copy(toShip);
        break;
      case 'break': {
        // Deterministic break vector: hard off-axis + climb/dive — the pass ends.
        const side = (p.jinkSeed % 2 === 0 ? 1 : -1);
        const fwd = this._v1.set(0, 0, -1).applyQuaternion(p.root.quaternion);
        desired.crossVectors(fwd, UP).multiplyScalar(side).add(this._v1.set(0, side * 0.6, 0)).normalize();
        break;
      }
      case 'circle': {
        // Turn fight: orbit the player at ~300 m while working the nose on.
        const tangent = this._v1.crossVectors(toShip, UP).normalize();
        desired.copy(tangent).multiplyScalar(p.jinkSeed % 2 === 0 ? 1 : -1)
          .addScaledVector(toShip, d > 320 ? 0.7 : -0.2).normalize();
        speed = stats.speed * 0.8;
        break;
      }
      case 'evade': {
        if (p.evadeBrake) {
          // TIE Fighter's "hit the brakes" — force the overshoot.
          desired.copy(toShip).negate();
          speed = stats.speed * 0.3;
        } else {
          // Corkscrew jink around the current heading.
          const t = now / 1000 * 7 + p.jinkSeed;
          const fwd = this._v1.set(0, 0, -1).applyQuaternion(p.root.quaternion);
          const side = this._v2.crossVectors(fwd, UP).normalize();
          desired.copy(fwd).addScaledVector(side, Math.sin(t) * 0.9).addScaledVector(UP, Math.cos(t * 1.3) * 0.7).normalize();
          speed = stats.speed * 1.05;
        }
        break;
      }
      case 'flee': {
        // Run for the den with a defensive weave.
        const t = now / 1000 * 5 + p.jinkSeed;
        desired.copy(homeLocal).sub(p.root.position).normalize()
          .addScaledVector(this._v1.crossVectors(desired, UP).normalize(), Math.sin(t) * 0.35).normalize();
        speed = stats.speed * 1.15;
        break;
      }
    }
    // Smooth heading chase (turn-rate limit reads as banked flight), then face
    // the velocity — hulls point where they fly.
    p.vel.lerp(this._v1.copy(desired).multiplyScalar(speed), Math.min(1, dt * 1.8));
    p.root.position.addScaledVector(p.vel, dt);
    if (p.vel.lengthSq() > 1) {
      this._m1.lookAt(this._v1.set(0, 0, 0), this._v2.copy(p.vel).normalize(), UP);
      this._q1.setFromRotationMatrix(this._m1);
      p.root.quaternion.slerp(this._q1, Math.min(1, dt * 4));
    }

    // ── Fire ── (attack runs only: in cone, in range, off cooldown)
    if (p.ai === 'attack' && q.live && d < PIRATE_FIRE_M * this.classOf(p).range && now >= p.fireAt) {
      const fwd = this._v1.set(0, 0, -1).applyQuaternion(p.root.quaternion);
      if (fwd.dot(this._v2.copy(q.pos).sub(p.root.position).normalize()) > 0.88) {
        p.fireAt = now + this.statsOf(p).fireCd * 1000 + (p.key.length * 53) % 300;
        this.pirateFire(p, q.pos, q.vel);
        if (!this.target && !p.ally) this.target = p; // first aggressor auto-targets (never a wingman)
      }
    }
  }

  /** A player bolt connected (or screamed past): the JTL evade reflex. */
  private pirateShotAt(p: Pirate, hit: boolean): void {
    if (p.deadUntil > 0 || p.ai === 'flee') return;
    if (p.hp <= 1 && !p.ally) { // wingmen hold formation to the end — no flee/escape path
      p.ai = 'flee';
      p.aiUntil = performance.now() + FLEE_S * 1000;
      return;
    }
    if (hit || p.ai === 'attack' || p.ai === 'circle' || p.ai === 'intercept') {
      p.ai = 'evade';
      p.aiUntil = performance.now() + EVADE_S * 1000;
      // A third of evades are the TIE brake — but only when we're ahead of the
      // player's guns (that is what forces the overshoot).
      p.evadeBrake = (p.jinkSeed + Math.floor(performance.now() / 1000)) % 3 === 0;
    }
  }

  private explode(at: THREE.Vector3, color: number): void {
    if (!this.root) return;
    if (this.fx && !this.spaceUnlit()) { this.fx.flash(at.x + this.off.x, at.y + this.off.y, at.z + this.off.z, 6, color); return; } // R5: batched
    const boom = this.spriteGlow(color, 4);
    boom.position.copy(at);
    this.root.add(boom);
    const t0 = performance.now();
    const grow = (): void => {
      const t = (performance.now() - t0) / 650;
      // freeLater, NEVER a direct dispose: on deactivate every live VFX
      // closure sees !this.root on its NEXT tick and disposed mid-swap —
      // the post-eject "used in submit while destroyed" flood (2026-08-23).
      if (t >= 1 || !this.root) { this.root?.remove(boom); freeLater(boom.material); return; }
      boom.scale.setScalar(4 + t * 46);
      (boom.material as THREE.SpriteMaterial).opacity = 1 - t;
      nextFrame(grow);
    };
    grow();
  }

  // ── Simulation ───────────────────────────────────────────────────────────
  /** While the rails or the tower own the ship (2026-09-23): the mouse kept piling into steerX with nothing consuming
   *  it — applied in ONE tick at the tunnel's far mouth (a snap turn) — and the felt rates froze, so the picture
   *  stayed banked through the tunnel. Drop the input; let the bank level out. */
  private idleFlightInput(dt: number): void {
    this.steerX = 0; this.steerY = 0;
    smoothRates(this.aeroRates, 0, 0, dt);
  }

  private fixed(dt: number): void {
    if (!this.active) return;
    // Landing completes on the TICK, never on wall-clock timers.
    if (this.landingTicks >= 0) {
      if (this.mySession() === null || this.landingTicks === 0) {
        const arrival = this.landingArrival;
        this.landingTicks = -1;
        this.landingArrival = null;
        this.landingNow = false;
        this.stage = 'landed';
        if (arrival) {
          // Tear the sector down NOW, behind the cloud deck — world-entered
          // lags the adoption by SECONDS of resubscription, and until it
          // fired the sector (its coords overlap Commons coords) kept
          // rendering: a megastation stood over the arrival city (field
          // report 2026-08-23). deactivate() is idempotent; the
          // world-entered handler remains the safety net.
          if (this.active) this.deactivate();
          bus.emit('atlas:world-adopted', { worldId: arrival.worldId, x: arrival.x, z: arrival.z });
          // FLYING arrivals (descendToAtmosphere) reveal on world-entered;
          // GROUND arrivals are revealed by the avatar's planetfall gate
          // once real collidable ground exists at the pad (field report
          // 2026-08-23: a fixed reveal uncovered a player buried under
          // terrain that streamed in after the seat). 25 s failsafe either way.
          const c2 = atlas.conn;
          const flying = !!(c2?.identity && c2.db.playerState.identity.find(c2.identity)?.animState.startsWith('fly:'));
          if (cloudTunnel.scripted && flying) {
            const off = bus.on('atlas:world-entered', () => { off(); cloudTunnel.reveal(1.6, 3.6); });
            window.setTimeout(() => { off(); if (cloudTunnel.scripted) cloudTunnel.reveal(0.4, 3.0); }, 25_000);
          } else if (cloudTunnel.scripted) {
            window.setTimeout(() => { if (cloudTunnel.scripted) cloudTunnel.reveal(0.4, 3.0); }, 25_000);
          }
        } else {
          cloudTunnel.cancel();
        }
      } else {
        this.landingTicks--;
      }
      this.idleFlightInput(dt);
      return; // ship holds while the tower talks
    }

    // Walking an interior: the ship is parked — the walk step replaces flight.
    if (this.interior) { this.interiorStep(dt); return; }

    // Warp tunnel: the rails own the ship until the far mouth.
    if (this.sysJump) {
      this.idleFlightInput(dt);
      this.tickSysJump();
      this.maybeRebase();
      this.local(this.pos.x, this.pos.y, this.pos.z, this._ship);
      return;
    }
    this.tickCourse(); // manual interstellar courses arrive on the wall clock
    this.tickLocation(); // THE LOCATION LAW: follow the row when the server moves us
    this.tickPlayerWrecks(); // THE WRECK LAW: debris where a hull went down

    // Hyperdrive first: while jumping, the drive owns velocity + position.
    const hyperOwned = this.hyperTick(dt);

    // ── GAMEPAD (2026-09-16): the sector flies on a spacecraft's conventions —
    //    the left stick is throttle and roll, the right stick is the nose, the
    //    triggers are guns and burner. Blended with the keys so either drives. ──
    // space campaign B: DOCKED (the dock window up — the server's docked mode) the stick is not live: no pad throttle,
    // roll, look, level or fire drift the hull out of the clamps (the keys are already released at the dock).
    const stickLive = !this.dock;
    const pad = stickLive ? padSnap() : { ...padSnap(), present: false };
    // Space campaign L (2026-10-01, gap 27): a HEADSET flies the sector on its own map (lib/space_xr_flight — the
    // HEADSET RIDE's precedent): the move-hand stick is throttle + roll, the other stick the nose; B / Y / the stick
    // presses edge-fire through the ONE dispatch (once per polled frame — fixed() may run twice in one). Desktop pads
    // keep their table.
    const xrFly = stickLive && padXrActive() && spaceComfortOn() ? spaceXrFlight(pad, xrComfort().moveHand) : null;
    if (xrFly && pad !== this.xrEdgeSnap) { this.xrEdgeSnap = pad; for (const a of xrFly.actions) this.sectorAction(a); }
    // The pad's BOOST (LT in the flight table; the headset's left trigger) was read nowhere in the sector — a pad pilot
    // could not boost. `this.boost` follows its edges, so every reader (speed, booster, HUD, plumes) agrees.
    const padBoost = stickLive && (xrFly ? xrFly.boost : pad.present && padHeld('boost'));
    if (padBoost !== this.padBoostWas) { this.padBoostWas = padBoost; this.boost = padBoost || keyHeldIn(this.keys, 'boost'); }
    const padThr = xrFly ? xrFly.throttle : pad.present ? -pad.ly : 0;
    // Throttle (JTL: W/S set a HELD throttle; the ship keeps its speed).
    if (keyHeldIn(this.keys, 'throttleUp')) this.throttle = Math.min(1, this.throttle + THROTTLE_RATE * dt);
    if (keyHeldIn(this.keys, 'throttleDown')) this.throttle = Math.max(0, this.throttle - THROTTLE_RATE * dt);
    if (padThr > 0.15) this.throttle = Math.min(1, this.throttle + THROTTLE_RATE * dt * padThr);
    if (padThr < -0.15) this.throttle = Math.max(0, this.throttle + THROTTLE_RATE * dt * padThr);
    // The right stick flies the nose, where the pointer-locked mouse otherwise
    // does; the pilot taking the stick drops auto-cruise, exactly as the mouse.
    if (pad.present && this.hyper !== 'jump') {
      const look = xrFly ? spaceXrLook(xrFly, dt) : padLook(dt);
      if (look.dx !== 0 || look.dy !== 0) {
        if (Math.abs(look.dx) + Math.abs(look.dy) > 0.004) this.autoCruise = false;
        const turnP = this.flightModelNow === 'space' ? this.stats.turnRate : 1; // THE AERO LAW: the sky's stick, the rate capped below
        this.steerX += -look.dx * turnP;
        this.steerY += -look.dy * turnP;
        this.yawRate = THREE.MathUtils.clamp(this.yawRate - look.dx * 26, -1.6, 1.6);
        this.pitchRate = THREE.MathUtils.clamp(this.pitchRate - look.dy * 26, -1.6, 1.6);
      }
    }
    // THE AERO LAW: consume the look. Under `aero` the tick may turn the nose no faster than the hull's rate at this
    // speed (RATE_CAP × the chassis × the authority — full at the sweet spot, thinner at rest and at overcruise); the
    // rest of a whipped mouse waits for the next tick. Applied about the BODY axes exactly as the sky does.
    this.flightModelNow = flightModel();
    if (this.hyper !== 'jump') {
      let yawT = this.steerX, pitchT = this.steerY;
      this.steerX = 0; this.steerY = 0;
      if (this.flightModelNow === 'aero') {
        this.aeroAuth = turnAuthority(this.vel.length(), this.stats.maxSpeed * this.powerMult().speed);
        const capRate = AERO.RATE_CAP * this.stats.turnRate * this.aeroAuth;
        const yawC = capTurn(yawT, capRate * dt), pitchC = capTurn(pitchT, capRate * dt);
        // THE REST WAITS (the AERO LAW as documented, 2026-09-23): the input past the rate cap carries to the next
        // tick — it was zeroed before the cap, so a Sovereign at overcruise kept a few pixels of mouse a tick. The
        // carry is clamped to one second of the hull's rate, so a whip never keeps turning for long after the hand stops.
        this.steerX = THREE.MathUtils.clamp(yawT - yawC, -capRate, capRate);
        this.steerY = THREE.MathUtils.clamp(pitchT - pitchC, -capRate, capRate);
        yawT = yawC; pitchT = pitchC;
        smoothRates(this.aeroRates, yawT / dt, pitchT / dt, dt);
        this.aeroLoad = turnLoad(this.aeroRates, capRate);
      } else { this.aeroLoad = 0; this.aeroAuth = 1; smoothRates(this.aeroRates, 0, 0, dt); }
      if (yawT !== 0) this.quat.multiply(this._q1.setFromAxisAngle(this._v1.set(0, 1, 0), yawT));
      if (pitchT !== 0) this.quat.multiply(this._q1.setFromAxisAngle(this._v1.set(1, 0, 0), pitchT));
    } else { this.steerX = 0; this.steerY = 0; }
    // THE MATCH LAW (space campaign B, lib/space_squad_nav): the target's validated speed over THIS hull's top at its
    // power routing — the old `/ MAX_SPEED` (105, the Lancer's top) flew every other hull at the wrong speed, and a target
    // at ≥ 103 m/s set the throttle past the 0.98 overcruise gate, so "matching" wound the engines to ×10 and ran away.
    if (this.matchSpeed) {
      const m = this.target && this.target.deadUntil === 0
        ? matchSpeedThrottle(this.target.vel.length(), this.stats.maxSpeed * this.powerMult().speed) : null;
      if (m) { this.throttle = m.throttle; this.matchCapped = m.capped; } else { this.matchSpeed = false; this.matchCapped = false; }
    }

    // Roll: A/D (and Q/E) — roll into the turn to tighten it, exactly as JTL.
    let rollIn = (keyHeldIn(this.keys, 'rollLeft') ? 1 : 0) - (keyHeldIn(this.keys, 'rollRight') ? 1 : 0);
    if (rollIn === 0 && pad.present) {
      if (xrFly) rollIn = xrFly.roll;
      else {
        rollIn = (padHeld('rollLeft') ? 1 : 0) - (padHeld('rollRight') ? 1 : 0);
        if (rollIn === 0 && Math.abs(pad.lx) > 0.15) rollIn = -pad.lx;
      }
    }
    // Space campaign L: the headset's ROLL COMFORT — a rate cap, or the horizon lock (no roll at all); the desktop's
    // scale is exactly 1. `rollIdleS` lets the auto-level wait for a still hand.
    const rollK = rollRateScale(this.vrWas, xrComfort());
    this.rollIdleS = rollIn !== 0 && rollK > 0 ? 0 : this.rollIdleS + dt;
    if (rollIn !== 0 && rollK > 0) {
      const roll = this._q1.setFromAxisAngle(this._v1.set(0, 0, 1), rollIn * ROLL_RATE * rollK * dt);
      this.quat.multiply(roll);
    }
    // R: level the wings (keep nose direction, remove roll). In a headset the comfort page may also level for you: an
    // eased, rate-capped auto-level after a still moment (assist), or the horizon held every tick (lock).
    const levelHeld = keyHeldIn(this.keys, 'levelOut') || (stickLive && (xrFly ? xrFly.level : padHeld('levelOut')));
    const autoLevel = levelMode(this.vrWas, xrComfort().spaceRoll, this.rollIdleS, levelHeld);
    if (levelHeld || autoLevel !== 'none') {
      const fwd = this._v1.set(0, 0, -1).applyQuaternion(this.quat);
      if (Math.abs(fwd.y) < 0.985) { // gimbal-safe: no leveling while pointed at the poles
        this._m1.lookAt(this._v2.set(0, 0, 0), fwd, this._v3.set(0, 1, 0));
        this._q1.setFromRotationMatrix(this._m1);
        const t = autoLevel === 'assist' ? assistSlerpT(2 * Math.acos(Math.min(1, Math.abs(this.quat.dot(this._q1)))), dt) : Math.min(1, LEVEL_RATE * dt);
        this.quat.slerp(this._q1, t);
      }
    }

    // Booster energy: SHIFT burns the reservoir; it refills off-boost.
    const engFx = shipEngineering.flight(); // space campaign G §18.3: degraded ×0.7 accel; disabled limps at 15 %, no boost, no overcruise
    const boosting = this.boost && this.boosterE > 0 && engFx.boost;
    if (boosting && !this.spaceBoostPrev && this.boosterE > 8) sfx('sfx_ship_boost', { gain: 0.5, cooldownMs: 1200 });
    this.spaceBoostPrev = boosting;
    this.boosterE = boosting
      ? Math.max(0, this.boosterE - BOOSTER_DRAIN * dt)
      : Math.min(BOOSTER_MAX, this.boosterE + BOOSTER_REGEN * dt);

    // Sub-hyper AUTOPILOT (map click): steer onto the nav point, throttle up;
    // overcruise does the rest. Arrival hands the stick back.
    if (this.autoCruise && this.hyper === '' && this.navIdx >= 0) {
      const n = this.nav[this.navIdx];
      const to = this._v1.set(...n.pos).sub(this.pos);
      const d = to.length();
      if (d <= n.arrive * 0.7) {
        this.autoCruise = false;
        this.throttle = 0.25;
        this.toast(`Autopilot: arrived — ${n.name}`);
      } else {
        this._m1.lookAt(this._v2.set(0, 0, 0), this._v3.copy(to).normalize(), UP);
        this.quat.slerp(this._q1.setFromRotationMatrix(this._m1), Math.min(1, dt * 2.2));
        this.throttle = 1;
      }
    }

    // OVERCRUISE: steady full throttle winds the engines toward ×10; hard
    // steering, firing, boosting, or damage spools it back down.
    const steering = Math.abs(this.yawRate) > 0.3 || Math.abs(this.pitchRate) > 0.3 || this.aeroLoad > 0.25;
    if (this.throttle > 0.98 && !steering && this.hyper === '' && !this.boost && engFx.cruise) {
      this.cruise = Math.min(CRUISE_MAX, this.cruise + CRUISE_RAMP * dt);
    } else {
      this.cruise = Math.max(1, this.cruise - CRUISE_DECAY * dt);
    }

    // Velocity chases the throttle's commanded vector (exponential — reads as
    // engine inertia, never as strafing; JTL ships fly where the nose points).
    if (!hyperOwned) {
      // THE AERO LAW's G-bleed: a hard turn commands less speed (energy, as a plane pays for a high-G turn); straight
      // flight wins it back through the engine chase.
      const speedCap = this.stats.maxSpeed * this.powerMult().speed * Math.max(boosting ? BOOST_MULT * this.stats.boost : 1, engFx.cruise ? this.cruise : 1) * commandedSpeedScale(this.aeroLoad) * (engFx.limp ?? 1);
      const desired = this._v1.set(0, 0, -1).applyQuaternion(this.quat).multiplyScalar(this.throttle * speedCap);
      this.vel.lerp(desired, Math.min(1, ACCEL_K * engFx.accel * dt));
      this.pos.addScaledVector(this.vel, dt);
    }
    if (this.crew) this.rideCaptain(dt); // THE CREW LAW: the seat moves with the captain's hull
    const r = this.pos.length();
    if (r > SECTOR_EDGE_M) this.pos.multiplyScalar(SECTOR_EDGE_M / r); // sector edge
    this.resolveSpaceCollision(); // asteroids/stations/hulks are SOLID now
    this.maybeRebase();
    this.local(this.pos.x, this.pos.y, this.pos.z, this._ship);

    this.flightCombat.primaryHeld(keyHeldIn(this.keys, 'fire') || (stickLive && padHeld('fire'))); // space campaign I: the active fire group's primary (bolts while held; a missile / the jammer once per press)
    this.fireCool = Math.max(0, this.fireCool - dt);
    this.missileCool = Math.max(0, this.missileCool - dt);
    const pm = this.powerMult();
    this.cap = Math.min(this.stats.capacitor, this.cap + CAP_REGEN * this.stats.regenMult * pm.cap * dt);

    // Shield regen after 5 quiet seconds — both arcs (reactor + power routing).
    if (performance.now() - this.lastHit > 5000) {
      this.shieldF = Math.min(this.arcMax(), this.shieldF + 2.5 * this.stats.regenMult * pm.shield * dt);
      this.shieldR = Math.min(this.arcMax(), this.shieldR + 2.5 * this.stats.regenMult * pm.shield * dt);
    }
    // Droid port: a docked droid nurses the hull back between fights.
    if (this.stats.droid > 0 && this.hull < this.stats.hp) {
      // Space campaign G: never unlimited field regeneration — the droid nurses from a FINITE field stock a safe dock restocks.
      this.hull = Math.min(this.stats.hp, this.hull + shipEngineering.droidNurse(Math.min(this.stats.hp - this.hull, 0.8 * this.stats.droid * dt)));
    }

    // Stream position (the server's cap allows ship speeds). Paused while
    // landing — post-flip sends reject with 'not in space' noise.
    this.sendAcc += dt;
    if (!this.landingNow && this.sendAcc >= 1 / SEND_HZ) {
      this.sendAcc = 0;
      const e = new THREE.Euler().setFromQuaternion(this.quat, 'YXZ');
      atlas.conn?.reducers.spaceMove({ posX: this.pos.x, posY: this.pos.y, posZ: this.pos.z, yaw: e.y }).catch(() => { /* rejected write — next tick corrects */ });
    }
    atlas.localX = this.pos.x; atlas.localY = this.pos.y; atlas.localZ = this.pos.z;

    // Remote pilots' TRUE positions (posY included) at 5 Hz from playerState.
    this.remoteAcc += dt;
    if (this.remoteAcc >= 1 / SEND_HZ) {
      this.remoteAcc = 0;
      this.refreshRemoteRows();
    }

    // ── Pirates: JTL-pattern combat AI ──────────────────────────────────────
    // The research-grounded flight patterns (SWG waves/tiers, TIE Fighter's
    // brake-evade, FreeSpace attack-run fatigue, Elite's flee-and-return):
    //   patrol → intercept (pack aggro) → attack run → break-off → circle,
    //   with evade (corkscrew / brake) on being shot at, and flee at 1 hp —
    //   a fleeing pirate that survives ESCAPES (no bounty; quiet respawn).
    const now = performance.now();
    // Pack senses: any aggroed den-mate wakes the den; at most TWO den-mates
    // press an attack run at once — the rest hold the circle (wave rhythm).
    const denAggro = new Set<string>();
    const denAttackers = new Map<string, number>();
    for (const p of this.pirates) {
      if (p.deadUntil > 0) continue;
      if (p.ai !== 'patrol') denAggro.add(p.poiKey);
      if (p.ai === 'attack') denAttackers.set(p.poiKey, (denAttackers.get(p.poiKey) ?? 0) + 1);
    }
    for (const p of this.pirates) {
      if (p.deadUntil > 0) {
        if (now >= p.deadUntil) {
          p.deadUntil = 0;
          p.hp = this.statsOf(p).hp * (this.aceName(p) ? ACE_HP_MULT : 1);
          p.root.position.copy(this.local(p.home.x, p.home.y, p.home.z, this._v1));
          p.root.visible = true;
          p.ai = 'patrol';
          p.escaped = false;
        }
        continue;
      }
      this.pirateAi(p, dt, now, denAggro, denAttackers);
      // Engines subsystem out (component targeting): drag toward last position ≈55% slow.
      if (p.slowUntil > now) p.root.position.lerp(p.prevPos, 0.55);
      // Tier-3 reavers carry seekers — one every ~9 s while pressing an attack ON YOU
      // (2026-09-23: every frigate and corvette is tier 3, and your own side's escorts
      // chasing enemy gunships fired player-targeted seekers at you every ~9 s).
      if (p.tier === 3 && p.ai === 'attack' && p.quarryIsPlayer === true && p.jamUntil <= now
        && Math.floor((now + p.jinkSeed * 997) / 9000) !== Math.floor((now + p.jinkSeed * 997 - dt * 1000) / 9000)) {
        this.pirateMissile(p);
      }
      // Velocity track (feeds the lead solution + match speed).
      p.vel.copy(p.root.position).sub(p.prevPos).divideScalar(dt);
      p.prevPos.copy(p.root.position);
    }
    if (this.target && this.target.deadUntil > 0) this.target = null;

    // Bolts: integrate, collide.
    for (let i = this.bolts.length - 1; i >= 0; i--) {
      const b = this.bolts[i];
      b.ttl -= dt;
      b.sprite.position.addScaledVector(b.vel, dt);
      let dead = b.ttl <= 0;
      if (!dead && b.hostile && b.sprite.position.distanceTo(this._ship) < 9) {
        dead = true;
        this.meleeTally.onPlayer[(b.side ?? -1) + 1]++;
        // A battle-side bolt is the WAR's; anything else is the pirate we are
        // engaged with (the current target when it is alive).
        this.damagePlayer(b.dmg ?? 9, b.vel, b.side !== undefined
          ? { kind: 'war', key: 'war', side: b.side === 0 ? 'aurelian' : 'borean' }
          : this.target && this.target.hp > 0 ? { kind: 'pirate', key: this.target.key } : undefined);
        this.fxImpact(this._v1.copy(this._ship), 4, 0xffaa66); // hull spark on the player
      }
      if (!dead && (b.side !== undefined || (b.hostile && !b.fromAlly))) {
        // A WAR bolt (skirmisher / wingman / civ patrol) lands on the OTHER
        // civ's hulls AND on pirate hulls; a PIRATE's bolt lands on any civ
        // hull (lib/space_patrols.boltHits): three sides of one fight, and
        // never a hull of the shooter's own side.
        const shooter = { side: (b.side ?? -1) as 0 | 1 | -1, ally: b.fromAlly === true };
        for (const q2 of this.pirates) {
          if (q2.deadUntil > 0) continue;
          if (!boltHits(shooter, { side: this.sideOf(q2), ally: q2.ally === true, bounty: q2.bountyId !== undefined })) continue;
          if (b.sprite.position.distanceTo(q2.root.position) < this.hitRadiusOf(q2)) {
            dead = true;
            const si = shooter.side + 1, ti = this.sideOf(q2) + 1;
            this.meleeTally.hits[si][ti]++;
            if (si === ti && si !== 0) this.meleeTally.ownSide++;
            q2.hp -= b.dmg ?? 9;
            this.fxImpact(this._v1.copy(q2.root.position), 4, q2.ally ? 0x5cff9d : 0x88ccff);
            this.pirateShotAt(q2, true);
            if (q2.hp <= 0) { this.meleeTally.kills[si][ti]++; this.killPirate(q2, false); }
            break;
          }
        }
      } else if (!dead && !b.hostile) {
        // THE PVP LAW: my bolt on a pilot my guild is at war with — the server judges the blow (war, sky, range).
        if (!b.fromAlly && this.warTargets.size) {
          for (const [hex, rs] of this.remoteShips) {
            if (!this.warTargets.has(hex)) continue;
            if (b.sprite.position.distanceTo(rs.obj.position) < 14) {
              dead = true;
              this.pvpHit(hex, this.subTarget === 'hull' ? this.stats.weaponMult : this.stats.weaponMult * 0.5);
              this.fxImpact(b.sprite.position, 6, 0xff8866);
              sfx('sfx_bolt_hit', { gain: 0.3, cooldownMs: 70 });
              break;
            }
          }
        }
        if (!dead) for (const p of this.pirates) {
          if (p.deadUntil > 0) continue;
          if (b.fromAlly && p.ally) continue; // wingmen never hit each other
          const bd = b.sprite.position.distanceTo(p.root.position);
          if (bd < this.hitRadiusOf(p)) {
            dead = true;
            if (!b.fromAlly) this.meleeTally.byPlayer[this.sideOf(p) + 1]++;
            // FRIENDLY FIRE: the player's own bolts damage wingmen exactly
            // like hostiles (user request 2026-08-23); wingman bolts land a
            // flat tier hit (no player weapon scaling, no subsystem calls).
            const dmg = b.fromAlly ? 9 : this.applySubsystemHit(p, this.stats.weaponMult);
            if (!b.fromAlly) sfx('sfx_bolt_hit', { gain: 0.3, cooldownMs: 70 }); // the hit-marker confirm
            if (!b.fromAlly && this.denRowFor(p)) {
              // THE DEN SLOT LAW: my blow on a den hull lands on the SERVER's row; the killing one is judged there.
              this.denHit(p, dmg);
              if (p.hp > 0) { this.fxImpact(b.sprite.position, 5, 0x88ccff); this.pirateShotAt(p, true); }
            } else {
              if (!b.fromAlly) this.warEscortIntent(p, b.sprite, 'gun', BOLT_LIFE_S - b.ttl); // slice D: a pinned war's escort hit is an INTENT
              p.hp -= dmg;
              if (p.hp <= 0) this.killPirate(p, !b.fromAlly);
              else { this.fxImpact(b.sprite.position, 5, p.ally ? 0x5cff9d : 0x88ccff); this.pirateShotAt(p, true); }
            }
            break;
          }
          // A near miss still teaches: bolts screaming past trigger the jink.
          if (bd < 40 && !(b.fromAlly && p.ally)) this.pirateShotAt(p, false);
        }
        // The WAR is shootable: capitals take hull damage, swarm fighters
        // realize (damaged + hostile) — no player bolt dies for nothing.
        // (Wingman bolts stay out of the capital ledger — player-only credit.)
        if (!dead && !b.fromAlly && this.warBoltHit(b.sprite.position, 12 * this.stats.weaponMult, true, b.sprite, 'gun', BOLT_LIFE_S - b.ttl)) { dead = true; this.meleeTally.byPlayerWar++; }
      }
      if (dead) {
        this.root?.remove(b.sprite);
        freeLater((b.sprite as THREE.Sprite).material);
        this.bolts.splice(i, 1);
      }
    }
    this.updateMissiles(dt);
    this.updateBattle(dt);
    this.fxTick(dt);
    this.updateWrecks(dt);
    this.updateIndustryBeam(dt);
    this.updateSigs();
    this.updateWormholes(dt);
    this.updateSpaceStructures();
    this.updateIncursion();
    this.updateWings();
    this.updateVoidRaiders(dt);
    this.updateCivPatrols(dt);
  }

  /**
   * Physics follow-up item 5: every remote pilot's `player_state` row feeds its RemoteTrack the moment it lands — the
   * arrival on the local clock and the server's `updatedAt` — so the playback sees every 5 Hz sample (the 5 Hz
   * remoteRows scan would quantise arrivals by up to a whole interval). Bound once per connection (the SDK's row
   * callbacks cannot be removed); a closed sector ignores the rows.
   */
  private bindSysOf(conn: NonNullable<typeof atlas.conn>): void {
    if (this.sysOfConn === conn) return;
    this.sysOfConn = conn;
    this.sysOfMap.clear();
    try {
      const t = conn.db.spaceLocation;
      for (const l of t.iter()) this.sysOfMap.set(l.identity.toHexString(), l.systemId);
      t.onInsert((_c, l) => { this.sysOfMap.set(l.identity.toHexString(), l.systemId); });
      t.onUpdate((_c, _o, l) => { this.sysOfMap.set(l.identity.toHexString(), l.systemId); });
      t.onDelete((_c, l) => { this.sysOfMap.delete(l.identity.toHexString()); });
    } catch { this.sysOfConn = null; /* not subscribed yet: the next activate binds */ }
  }

  private bindRemoteFeed(): void {
    const conn = atlas.conn;
    if (conn) this.bindSysOf(conn);
    if (!conn || this.remoteFeedConn === conn) return;
    this.remoteFeedConn = conn;
    const feed = (row: { identity: { toHexString(): string; isEqual(o: unknown): boolean }; posX: number; posY: number; posZ: number; yaw: number; updatedAt: { microsSinceUnixEpoch: bigint } }): void => {
      if (!this.active || !conn.identity || row.identity.isEqual(conn.identity)) return;
      const hex = row.identity.toHexString();
      // C4: a walker, or a pilot in another system, is never drawn here — a track for them was kept for the whole session.
      if ((this.sysOfMap.get(hex) ?? 'sys0') !== this.system.id) return;
      let track = this.remoteTracks.get(hex);
      if (!track) { track = new RemoteTrack(); this.remoteTracks.set(hex, track); }
      const arrival = performance.now(), stamp = Number(row.updatedAt.microsSinceUnixEpoch / 1000n);
      track.push(arrival, stamp, row.posX, row.posY, row.posZ, row.yaw);
    };
    try {
      conn.db.playerState.onInsert((_c, row) => feed(row as never));
      conn.db.playerState.onUpdate((_c, _o, row) => feed(row as never));
    } catch { /* pre-subscription: the next activate binds */ this.remoteFeedConn = null; }
  }

  private refreshRemoteRows(): void {
    const conn = atlas.conn;
    if (!conn?.identity) return;
    this.remoteRows.clear();
    try {
      for (const row of conn.db.playerState.iter()) {
        if (row.identity.isEqual(conn.identity)) continue;
        this.remoteRows.set(row.identity.toHexString(), { x: row.posX, y: row.posY, z: row.posZ, yaw: row.yaw });
      }
    } catch { /* cache mid-churn */ }
  }

  /** Front/rear shield split: a bolt flying WITH our nose hits the rear arc.
   *  `source` names the shooter for the obituary (Space Wave 1). */
  private damagePlayer(amount: number, boltVel: THREE.Vector3, source?: { kind: 'pirate' | 'war' | 'patrol' | 'hunter' | 'player'; key: string; side?: string; hitId?: bigint }): void {
    this.lastHit = performance.now();
    if (source) this.lastHitBy = { ...source, at: performance.now() };
    const fwd = this._v1.set(0, 0, -1).applyQuaternion(this.quat);
    const rear = boltVel.dot(fwd) > 0; // bolt travels the way we face → struck from behind
    const arcBefore = rear ? this.shieldR : this.shieldF; // R8: an arc that goes to zero on THIS hit collapses
    let remaining = amount;
    if (rear) {
      const fromShield = Math.min(this.shieldR, remaining);
      this.shieldR -= fromShield; remaining -= fromShield;
    } else {
      const fromShield = Math.min(this.shieldF, remaining);
      this.shieldF -= fromShield; remaining -= fromShield;
    }
    // Space campaign G (crew_engineering_v1): §18.7 — 30 % of what the shields let through goes to the server-chosen
    // group (engine / weapons), the rest to the hull; the server judges the report with its OWN health and aim.
    remaining = shipEngineering.onDamage(remaining, source);
    this.hull -= remaining;
    // Feedback the arc that took it: a shielded hit shimmers, a hull hit rings.
    sfx(remaining > 0 ? 'sfx_hull_hit' : 'sfx_shield_hit', { gain: 0.6, cooldownMs: 160 });
    this.cruise = 1;
    this.autoCruise = false;
    this.dropHyper('Hyperdrive knocked offline — taking fire!');
    this.addTrauma(remaining > 0 ? 0.42 : 0.22); // R7: a hull hit rattles harder than a shield hit
    this.woundFx(boltVel, rear, amount - remaining, remaining, arcBefore); // R8: the ripple where it struck, a scorch on the hull
    this.explode(this._ship.clone(), 0xff6644);
    if (this.hull <= 0) this.eject();
  }

  /** Hull zero → the ESCAPE POD: the ship is lost where it flew; the pod
   *  drops the pilot at the origin pad and the recovery fee posts server-side
   *  (spaceEject). Mirrors land()'s fixed-tick session-delete watch. */
  private eject(): void {
    const conn = atlas.conn;
    if (!conn?.identity || this.landingNow) return;
    this.toast('HULL BREACH — escape pod away (recovery fee 50 ⓒ)');
    sfx('sfx_escape_pod', { gain: 0.6, cooldownMs: 1500 });
    cloudTunnel.hold(0.9); // the pod drop is the loading screen (arrival gate reveals)
    this.explode(this._ship.clone(), 0xffffff);
    this.landingNow = true;
    this.stage = 'ejecting';
    let commonsId: bigint | null = null;
    let seed = '';
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') { commonsId = w.worldId; seed = w.terrainSeed; }
    const sess = this.mySession();
    let ax = 24, az = 24;
    if (sess && seed !== '' && this.sitesSeed === seed) {
      const site = this.sites.find((st) => st.id === Number(sess.originSid) - 1);
      if (site) { const ap = arrivalPoint(seed, site); ax = ap.x; az = ap.z; }
    }
    // The hull is GONE (tier-1 law): components go down with her, a public
    // kill record is minted, insurance/SRP settle, accepted hauls fail.
    try {
      const active = [...conn.db.playerShip.iter()].find((sh) => sh.owner.isEqual(conn.identity!) && sh.active);
      if (active) {
        // Who killed us (Space Wave 1): the last hull that landed a hit inside
        // 20 s — a war hull is 'war' (§14 gap 12 closed for the war), a den or
        // void pirate is 'pirate'; the server names the SYSTEM from our row.
        const hit = this.lastHitBy && performance.now() - this.lastHitBy.at < 20_000 ? this.lastHitBy : null;
        // THE PVP LAW: a blow judged by the server names the ATTACKER ('player', their identity) on the killmail.
        const kind = hit?.kind === 'war' ? 'war' : hit?.kind === 'pirate' ? 'pirate' : hit?.kind === 'player' ? 'player' : 'unknown';
        const ref = hit?.kind === 'war' ? `war:${hit.side ?? '?'}` : hit?.kind === 'pirate' || hit?.kind === 'player' ? hit.key : '';
        (conn.reducers as unknown as { spaceShipDestroyed(a: { shipId: bigint; attackerKind: string; attackerRef: string }): Promise<void> })
          .spaceShipDestroyed({ shipId: active.shipId, attackerKind: kind, attackerRef: ref.slice(0, 64) })
          .catch(() => { /* already gone */ });
      }
    } catch { /* pre-publish server */ }
    // A live hunter watched the hull come apart — the contract closes FULFILLED.
    try {
      for (const b of (conn.db as unknown as { bountyContract: { iter(): Iterable<{ bountyId: bigint; state: string; target: { isEqual(o: unknown): boolean } }> } }).bountyContract.iter()) {
        if (b.state === 'attacking' && b.target.isEqual(conn.identity)) {
          (conn.reducers as unknown as { bountyHunterWon(a: { bountyId: bigint }): Promise<void> }).bountyHunterWon({ bountyId: b.bountyId }).catch(() => { /* raced */ });
          break;
        }
      }
    } catch { /* pre-publish */ }
    conn.reducers.spaceEject({}).catch((e: Error) => { this.lastError = `spaceEject: ${String(e.message ?? e)}`; });
    this.landingArrival = commonsId !== null ? { worldId: commonsId, x: ax, z: az } : null;
    this.landingTicks = 60;
  }

  /** The hired hunter in the void (bounty_contract targeting ME): a sensor
   *  GHOST holding ~5 km while it stalks — repositioning the instant you
   *  commit to an intercept — then, once the weakness window holds and the
   *  strike is marked, a full-AI hostile that NEVER flees. Its hp is the
   *  server's (shoot_bounty_hunter dips — the war-air law); the slain row
   *  closes the fight and BountyHunterModule surfaces the aftermath. */
  private bountyTick(dt: number): void {
    const conn = atlas.conn;
    if (!conn?.identity || !this.root) return;
    interface BRow { bountyId: bigint; state: string; tier: number; hunterName: string; hunterHp: number; target: { isEqual(o: unknown): boolean } }
    let row: BRow | null = null;
    try {
      for (const b of (conn.db as unknown as { bountyContract: { iter(): Iterable<BRow> } }).bountyContract.iter()) {
        if ((b.state === 'stalking' || b.state === 'attacking') && b.target.isEqual(conn.identity)) { row = b; break; }
      }
    } catch { /* pre-sub */ }
    const hostile = this.pirates.find((p) => p.bountyId !== undefined && p.deadUntil === 0);
    if (!row) {
      if (this.bountyGhost) { this.root.remove(this.bountyGhost); this.bountyGhost = null; }
      if (hostile) { hostile.deadUntil = Number.MAX_SAFE_INTEGER; hostile.root.visible = false; if (this.target === hostile) this.target = null; }
      this.bountyHoldS = 0;
      return;
    }
    if (row.state === 'stalking') {
      if (hostile) { hostile.deadUntil = Number.MAX_SAFE_INTEGER; hostile.root.visible = false; }
      // THE CONTACT IS A TRACK, NOT A RING (the stalking rework): it holds a bearing
      // and matches velocity; closing on it RESOLVES it (the contact is a hull with no
      // transponder — evidence the mark earned) and it breaks away on a real vector,
      // gone until it returns on a randomized clock. Investigation can succeed.
      if (this.bountyGhostHiddenUntil > performance.now()) {
        if (this.bountyGhost) { this.root.remove(this.bountyGhost); this.bountyGhost = null; }
      } else {
        if (!this.bountyGhost) {
          this.bountyGhost = this.spriteGlow(0xff2a20, 30);
          this.root.add(this.bountyGhost);
          const a0 = ((performance.now() / 7919) % 1) * Math.PI * 2;
          this.bountyGhostAbs.set(this.pos.x + Math.cos(a0) * 5200, this.pos.y + 320, this.pos.z + Math.sin(a0) * 5200);
          this.bountyGhostVel.copy(this.vel);
          this.bountyGhostFlee = false;
          this.bountyGhostResolved = false;
          this.toast('Unresolved contact at the edge of your scope. It is matching your velocity.');
          bus.emit('atlas:bounty-evidence', { level: 1, why: 'an unresolved contact' });
        }
        const gd = this.bountyGhostAbs.distanceTo(this.pos);
        if (!this.bountyGhostFlee) {
          // Matching velocity with a lag: it follows the course changes a few seconds late.
          this.bountyGhostVel.lerp(this.vel, Math.min(1, dt * 0.35));
          if (gd < 2200) {
            this.bountyGhostFlee = true;
            this.bountyGhostResolved = true;
            this.toast('The contact resolves for a moment: a dark hull, no transponder. It turns away.');
            sfx('sfx_stalker_sting', { gain: 0.4, cooldownMs: 15_000 });
            bus.emit('atlas:bounty-evidence', { level: 2, why: 'a hull with no transponder' });
          }
        }
        if (this.bountyGhostFlee) {
          const away = this.bountyGhostAbs.clone().sub(this.pos); away.y *= 0.2;
          if (away.lengthSq() < 1) away.set(1, 0, 0);
          away.normalize().multiplyScalar(900);
          this.bountyGhostVel.lerp(away, Math.min(1, dt * 1.5));
          if (gd > 12_000) {
            this.root.remove(this.bountyGhost); this.bountyGhost = null;
            this.bountyGhostHiddenUntil = performance.now() + (60 + ((performance.now() / 977) % 1) * 90) * 1000;
          }
        }
        if (this.bountyGhost) {
          this.bountyGhostAbs.addScaledVector(this.bountyGhostVel, dt);
          if (gd > 9500 && !this.bountyGhostFlee) this.bountyGhostAbs.lerp(this.pos, Math.min(1, dt * 0.05)); // a lost contact closes back to the edge of the scope
          this.local(this.bountyGhostAbs.x, this.bountyGhostAbs.y, this.bountyGhostAbs.z, this.bountyGhost.position);
        }
      }
      // The weakness window, fed at 1 Hz (deep space: nowhere to run).
      this.bountyEvalAcc += dt;
      if (this.bountyEvalAcc >= 1) {
        this.bountyEvalAcc = 0;
        let near = 0;
        try {
          const nowMicros = BigInt(Math.round(Date.now() * 1000));
          for (const pr of (conn.db as unknown as { spacePresence: { iter(): Iterable<{ identity: { isEqual(o: unknown): boolean }; systemKey: string; updatedAt: { microsSinceUnixEpoch: bigint } }> } }).spacePresence.iter()) {
            if (pr.systemKey === this.system.id && !pr.identity.isEqual(conn.identity)
              && nowMicros - pr.updatedAt.microsSinceUnixEpoch < 90n * 1_000_000n) near++;
          }
        } catch { /* fine */ }
        const m: WeaknessMetrics = {
          healthFrac: this.hull / Math.max(1, this.stats.hp),
          distToSafetyM: 900, playersNearby: near, night: false,
          recentlyHurt: this.hull < this.stats.hp * 0.9,
          inOtherCombat: atlas.combatSpace, onFoot: false,
          speedMps: this.vel.length() < 8 ? 0 : 3, unarmed: false, deepSpace: true,
        };
        const t = BOUNTY_TIERS[Math.max(0, Math.min(BOUNTY_TIERS.length - 1, row.tier - 1))];
        this.bountyHoldS = strikeHold(this.bountyHoldS, weaknessScore(m), t);
        if (this.bountyHoldS >= t.stalkHoldS) {
          this.bountyHoldS = 0;
          (conn.reducers as unknown as { markBountyStrike(a: { bountyId: bigint }): Promise<void> })
            .markBountyStrike({ bountyId: row.bountyId }).catch(() => { /* it is still watching */ });
        }
      }
      return;
    }
    // ATTACKING — weapons free, forever.
    if (this.bountyGhost) { this.root.remove(this.bountyGhost); this.bountyGhost = null; }
    if (!hostile) {
      const tier = Math.min(3, Math.max(1, row.tier)) as 1 | 2 | 3;
      const pr = this.spawnHostile(`bounty:${row.bountyId}`, tier, this.pos.x + 1400, this.pos.y + 150, this.pos.z + 400);
      if (pr) {
        pr.bountyId = row.bountyId;
        pr.hp = Math.max(1, Math.round(row.hunterHp * 1.6));
        this.bountyLastHp = pr.hp;
        this.toast(`☠ ${row.hunterName} drops out of cover — it is not leaving without you`);
        sfx('sfx_hunter_strike', { gain: 0.6, cooldownMs: 2000 });
      }
      return;
    }
    if (hostile.ai === 'flee' || hostile.escaped) {
      hostile.ai = 'attack'; hostile.escaped = false; hostile.aiUntil = performance.now() + 4000;
    }
    this.bountyShootCd -= dt;
    const scaled = Math.max(1, Math.round(row.hunterHp * 1.6));
    if (hostile.hp < this.bountyLastHp && this.bountyShootCd <= 0) {
      this.bountyShootCd = 0.35;
      (conn.reducers as unknown as { shootBountyHunter(a: { bountyId: bigint }): Promise<void> })
        .shootBountyHunter({ bountyId: row.bountyId }).catch(() => { /* cycling */ });
    }
    hostile.hp = scaled; // the server is the health authority
    this.bountyLastHp = scaled;
  }

  /** THE DEN SLOT LAW: a blow of mine on a den hull — predicted locally, judged by the server (coalesced while one is in flight). */
  private denHit(p: Pirate, dmg: number): void {
    if (p.deadUntil > 0 || dmg <= 0) return;
    p.hp -= dmg;
    const q = this.denHits.get(p.key) ?? { dmg: 0, inFlight: false };
    q.dmg += dmg;
    this.denHits.set(p.key, q);
    this.flushDenHit(p, q);
  }
  private flushDenHit(p: Pirate, q: { dmg: number; inFlight: boolean }): void {
    if (q.inFlight || q.dmg <= 0 || p.deadUntil > 0) return;
    const dmg = Math.min(DEN_HIT_MAX, q.dmg);
    q.dmg = Math.max(0, q.dmg - dmg);
    q.inFlight = true;
    const reducers = atlas.conn?.reducers as unknown as { spaceHit(a: { pirateKey: string; dmg: number }): Promise<void> } | undefined;
    if (!reducers) { q.inFlight = false; return; }
    reducers.spaceHit({ pirateKey: p.key, dmg })
      .then(() => {
        q.inFlight = false;
        if (p.deadUntil > 0) return;
        if (p.hp <= 0) { p.serverKill = true; this.killPirate(p, true); return; }
        if (q.dmg > 0) this.flushDenHit(p, q);
      })
      .catch((e: Error) => {
        // Not here / short of it / too far: the shared row disagrees with the prediction — the den sync reconciles the hull.
        q.inFlight = false; q.dmg = 0;
        this.lastError = `spaceHit: ${String(e.message ?? e)}`;
        this.toast(`⚠ ${String(e.message ?? e)}`);
        this.denSyncAt = 0;
      });
  }

  private killPirate(p: Pirate, byPlayer = true, rosterDeath = false): void {
    if (p.bountyId !== undefined) {
      // The hunter's death is the SERVER's call (shoot_bounty_hunter dips
      // report every bolt; the slain row closes the contract + pays salvage).
      // Locally just shed the hull — if the server disagrees, it returns.
      p.deadUntil = Number.MAX_SAFE_INTEGER;
      p.root.visible = false;
      if (this.target === p) this.target = null;
      this.fxShipDeath(p.root.position.clone(), 14, 0xff5a4a);
      void byPlayer;
      return;
    }
    if (!p.ally && p.key.startsWith('war') && p.warSide !== undefined) {
      const escort = p.cls === 'frigate' || p.cls === 'corvette';
      // THE PENDING-DEATH LAW (space campaign slice C, 2026-09-30): under a roster an ESCORT is never killed by the local
      // picture — a lethal blow leaves it PENDING (1 hp, engines and guns out; my blow sends ONE claim) until the roster
      // row flips (updateWarEscorts → rosterDeath); a refusal or no flip restores it. Gunships stay the pool's.
      if (escort && !rosterDeath && this.escortPendingBlow(p, byPlayer)) return;
      // A skirmisher dies: shed real wreckage, queue a +5 s replacement
      // launch from a living carrier, and retire the hull OUTSIDE the
      // pirate iteration (purgeDeadSkirmishers). No bounty — 60 farmable
      // war hulls would be a credit printer; dens and marked wings pay.
      p.deadUntil = Number.MAX_SAFE_INTEGER;
      p.root.visible = false;
      if (this.target === p) this.target = null;
      setPending(p, p.key, undefined);
      this.fxShipDeath(p.root.position.clone(), escort ? (p.cls === 'frigate' ? 120 : 40) : 16, p.warSide === 0 ? 0xffd873 : 0x9fe8ff);
      this.spawnWarDebris(p.root.position, escort ? (p.cls === 'frigate' ? 14 : 7) : 4, 14);
      // THE ESCORT LAW: an escort is a roster row — it never re-forms; a fighter is the pool's, and re-launches.
      if (escort) this.battle?.escortDead.add(p.key);
      else this.battle?.skRespawn[p.warSide].push(performance.now() + SKIRMISH_RESPAWN_MS);
      // THE RECEIPT LAW, from the client at last (2026-09-22): this branch returned BEFORE the receipt block further
      // down, so a pilot's gunship kill never reached the server since Wave 4 — the harness drove the reducer directly.
      if (byPlayer) {
        const camp = this.campaignRow();
        const kind = warHullKind(p.key);
        if (camp) {
          (atlas.conn?.reducers as unknown as { campaignKill(a: { campaignId: bigint; hullKey: string }): Promise<void> } | undefined)
            ?.campaignKill({ campaignId: camp.campaignId, hullKey: p.key })
            .then(() => this.toast(claimToast(kind === 'gunship' && warPinned(camp.campaignId) ? 'gunshipPinned' : 'gunship', `⚑ Receipt: a ${kind} struck off the line (+${WAR_POINTS[kind]} pts this war)`))) // space campaign L (gap 29): a pool kill is the client's word — said so, with its cap
            .catch(() => { /* out of zone / own banner / already claimed — the war keeps its own count */ });
        }
      }
      return;
    }
    if (p.ally) {
      // A wingman down: no bounty, no server kill — a battle respawn and a
      // moment of guilt if the trigger was yours.
      p.deadUntil = performance.now() + 15_000;
      p.root.visible = false;
      if (this.target === p) this.target = null;
      this.fxShipDeath(p.root.position.clone(), 12, 0x5cff9d);
      this.toast(byPlayer ? '⚠ You shot down your own wingman' : 'Wingman lost to enemy fire');
      return;
    }
    if (p.key.startsWith('patrol') && p.warSide !== undefined) {
      // A civ patrol hull down: real wreckage in its civ's colour, no bounty
      // (a war hull is never a credit printer), and it re-forms at its post.
      p.deadUntil = performance.now() + PATROL_RESPAWN_MS;
      p.root.visible = false;
      if (this.target === p) this.target = null;
      this.fxShipDeath(p.root.position.clone(), 14, p.warSide === 0 ? 0xffd873 : 0x9fe8ff);
      this.spawnWarDebris(p.root.position, 3, 12);
      if (byPlayer && this.faction !== '' && (this.faction === 'aurelian' ? 0 : 1) === p.warSide) this.toast('⚠ You shot down one of your own civ\'s patrol');
      return;
    }
    // Space Wave 6 (THE SHARED-DEN LAW): a den slot waits for the server's row to refill — no private timer.
    p.deadUntil = this.denRowFor(p) || p.key.startsWith('raid') ? Number.MAX_SAFE_INTEGER : performance.now() + PIRATE_RESPAWN_S * 1000;
    p.root.visible = false;
    if (this.target === p) this.target = null;
    this.fxShipDeath(p.root.position.clone(), 10 + p.tier * 4, 0xffaa44);
    // THE RAIDER LAW: a raider on the lane is one of the fight's counted hulls — the server pays and tallies.
    if (p.key.startsWith('raid')) {
      if (!byPlayer) return;
      const m = /^raid(\d+):(\d+)$/.exec(p.key);
      if (m) {
        (atlas.conn?.reducers as unknown as { ambushKill(a: { ambushId: bigint; idx: number }): Promise<void> } | undefined)
          ?.ambushKill({ ambushId: BigInt(m[1]), idx: Number(m[2]) })
          .then(() => { sfx('sfx_kill_confirm', { gain: 0.4, cooldownMs: 200 }); this.bounty += 15; this.toast(claimToast('ambush', 'Raider destroyed on the lane — 15 ⓒ', `(${this.bounty} this run)`)); })
          .catch((e: Error) => this.toast(`Bounty refused: ${e.message ?? e}`));
      }
      return;
    }
    if (!byPlayer) return; // a civ patrol's kill: the den loses a hull, nobody is paid — no bounty for a fight you watched
    const ace = this.aceName(p);
    if (p.serverKill) {
      // THE DEN SLOT LAW: the killing blow was judged and paid by spaceHit — never a second claim.
      p.serverKill = false;
      this.denHits.delete(p.key);
      sfx('sfx_kill_confirm', { gain: 0.4, cooldownMs: 200 });
      this.bounty += 15;
      this.toast(ace ? claimToast('den', `${ace} is DOWN — the den reels (15 ⓒ, ${this.bounty} this run)`) : claimToast('den', 'Raider destroyed — 15 ⓒ bounty', `(${this.bounty} this run)`)); // space campaign L: a den slot's killing blow was the record's
      return;
    }
    atlas.conn?.reducers.spaceKill({ pirateKey: p.key })
      .then(() => {
        sfx('sfx_kill_confirm', { gain: 0.4, cooldownMs: 200 });
        this.bounty += 15;
        this.toast(ace
          ? claimToast('void', `${ace} is DOWN — the den reels (15 ⓒ, ${this.bounty} this run)`)
          : claimToast('void', 'Raider destroyed — 15 ⓒ bounty', `(${this.bounty} this run)`)); // space campaign L (gap 29): no server body — the client's word, capped
      })
      .catch((e: Error) => { this.lastError = `spaceKill: ${String(e.message ?? e)}`; this.toast(`Bounty refused: ${e.message ?? e}`); });
    // (space campaign slice C, 2026-09-30: two dead blocks removed here — a war-hull receipt that could never run (the war
    // branch above returns first and sends the claim itself) and the `promoSide` → `warContribution` front push, which
    // lost its only writer in f5d4708 (2026-08-23). warContribution is RETIRED: only the campaign's outcome moves a front.)
    // INCURSION: kills in an occupied system grind the raiders' grip down.
    if (this.incursionHere()) {
      this.incursionKills++;
      // Space Wave 4 (THE INCURSION LAW): the grip is shared — the server counts each raider once.
      // The warlord is spawned on a den slot key; the server knows it by the epoch's boss key.
      const inc = incursionsOf(incursionEpoch(Date.now())).find((i) => i.systemId === this.system.id);
      (atlas.conn?.reducers as unknown as { incursionKill(a: { systemId: string; pirateKey: string }): Promise<void> } | undefined)
        ?.incursionKill({ systemId: this.system.id, pirateKey: p.key === this.incBossKey && inc ? inc.bossKey : p.key }).catch(() => { /* counted already, or not this system */ });
      if (p.key === this.incBossKey) {
        this.incBossKey = null;
        this.incursionKills += 25;
        this.toast('☠ THE INCURSION WARLORD FALLS — the occupation breaks');
      } else if (this.incursionInfluence() === 0) {
        this.toast('⚑ The raider grip is BROKEN — the system breathes again');
      }
    }
  }

  // ── Landing ──────────────────────────────────────────────────────────────
  private nearLanding(): string | null {
    const wp = this.nearWorldPlanet();
    if (wp) return wp.name;
    if (this.system.id === 'sys0') {
      for (const st of POIS) {
        if (st.kind !== 'station' && st.kind !== 'megastation') continue;
        if (this.pos.distanceTo(new THREE.Vector3(...st.pos)) < st.radius + 600) return st.name;
      }
    }
    // (Space Wave 1: a Waypoint outside the Commons Reach DOCKS (P); the
    // Commons is a warp away — exitSpace refuses from another sky.)
    const sess = this.mySession();
    if (sess) {
      const slot = entrySlot(Number(sess.originSid));
      if (this.pos.distanceTo(new THREE.Vector3(slot.x, 0, slot.z)) < 300) return 'descent beacon';
    }
    return null;
  }

  private mySession(): { originSid: bigint } | null {
    const conn = atlas.conn;
    if (!conn?.identity) return null;
    try {
      for (const s of (conn.db as unknown as { spaceSession: { iter(): Iterable<{ identity: { isEqual(o: unknown): boolean }; originSid: bigint }> } }).spaceSession.iter()) {
        if (s.identity.isEqual(conn.identity)) return s;
      }
    } catch { /* not subscribed yet */ }
    return null;
  }

  private tryLand(): void {
    if (this.nearLanding()) this.land();
    else if (this.system.id !== 'sys0' && this.nearStation()) this.toast(`Dock here (${keyHint('spaceDock')}) — the Commons is a warp away`);
    else this.toast('Nothing to dock with — a station, your descent beacon, or a charted world');
  }

  /** SYNC: fire exitSpace and hand completion to the FIXED TICK — setTimeout
   *  chains die under background timer throttling (probe finding: the first
   *  250 ms wait never fired at minute 5 headless), but the engine loop keeps
   *  ticking. fixed() watches for the session-delete (or times out) and adopts. */
  private land(): void {
    const conn = atlas.conn;
    if (!conn?.identity || this.landingNow) return;
    // PLANETFALL on a charted user world: the server validates proximity +
    // access and hops playerState; a REFUSAL must un-wedge the ship (never
    // adopt a world the server did not move us into).
    const wp = this.nearWorldPlanet();
    if (wp) {
      this.landingNow = true;
      this.stage = 'landing';
      sfx('sfx_ship_dock', { gain: 0.45, cooldownMs: 1000 }); // clamps release for atmospheric entry
      this.toast(`Making planetfall — ${wp.name}`);
      cloudTunnel.hold(1.1); // atmospheric entry: the swap + streaming hide in cloud
      conn.reducers.landOnWorld({ worldId: wp.worldId })
        .catch((e: Error) => {
          cloudTunnel.cancel();
          this.lastError = `landOnWorld: ${String(e.message ?? e)}`;
          this.toast(`Planetfall refused: ${e.message ?? e}`);
          this.landingNow = false;
          this.landingTicks = -1;
          this.landingArrival = null;
          this.stage = 'ready';
        });
      this.landingArrival = { worldId: wp.worldId, x: 0, z: 0 };
      // Adopt on the SESSION-DELETE (the server's success signal), not the
      // timeout — a slow refusal must never adopt a world we never entered.
      this.landingTicks = 600;
      return;
    }
    this.landingNow = true;
    this.stage = 'landing';
    sfx('sfx_ship_dock', { gain: 0.45, cooldownMs: 1000 }); // clamps release for planetfall home
    let commonsId: bigint | null = null;
    let seed = '';
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') { commonsId = w.worldId; seed = w.terrainSeed; }
    const sess = this.mySession();
    let ax = 24, az = 24;
    if (sess && seed !== '' && this.sitesSeed === seed) {
      const site = this.sites.find((st) => st.id === Number(sess.originSid) - 1);
      // Mirror of the server's exitSpace landing (spawn-stuck law: the pad, not the core).
      if (site) { const ap = arrivalPoint(seed, site); ax = ap.x; az = ap.z; }
    }
    // Atmospheric entry (2026-08-23): drop through the cloud deck STILL
    // FLYING (descendToAtmosphere puts the ship high in the Commons sky —
    // AtlasVehicleModule's resume path picks the stick back up); a refusal
    // (no active ship) falls back to the classic pad landing.
    cloudTunnel.hold(1.1);
    this.toast('Beginning atmospheric descent');
    conn.reducers.descendToAtmosphere({}).catch(() => {
      conn.reducers.exitSpace({}).catch((e: Error) => {
        this.lastError = `exitSpace: ${String(e.message ?? e)}`;
        cloudTunnel.cancel();
      });
    });
    this.landingArrival = commonsId !== null ? { worldId: commonsId, x: ax, z: az } : null;
    this.landingTicks = 60; // ≤1 s at speed (headless ~12 s at 1 fps × 5-step catchup); the session-delete exits earlier
  }

  /**
   * Planet distance-LOD (~2 Hz from frame()): CONTINUOUSLY fade the normal-map
   * influence AND the additive atmosphere rim toward 0 as a planet shrinks on
   * screen. A strong bump shades as black SPECKLE under minification and the rim
   * shell's hard edge aliases into a jagged halo — the crunchy far-planet report.
   * A smooth fade (not the old hard normal-map strip) removes both without any
   * on/off boundary flicker: normal flat below ang 0.10 → full above 0.26; rim
   * gone below 0.05 → full above 0.16.
   */
  private updatePlanetLod(): void {
    this.lodRuns++;
    const smoothstep = (lo: number, hi: number, x: number): number => {
      const t = Math.max(0, Math.min(1, (x - lo) / (hi - lo)));
      return t * t * (3 - 2 * t);
    };
    const lod = (mesh: THREE.Mesh, abs: THREE.Vector3, radius: number): void => {
      const look = mesh.userData.planetLook as PlanetLook | undefined;
      if (look) {
        // R4: the v2 material's normal strength is a uniform; the rim fade is the same material opacity.
        const ang = radius / Math.max(1, this.pos.distanceTo(abs));
        const ud2 = mesh.userData as { baseBump?: number; rimMat?: THREE.Material; rimBase?: number };
        if (look.hasNormal && ud2.baseBump !== undefined) look.bump.value = smoothstep(0.10, 0.26, ang) * ud2.baseBump;
        if (ud2.rimMat) ud2.rimMat.opacity = (ud2.rimBase ?? 0.16) * smoothstep(0.05, 0.16, ang);
        return;
      }
      const mat = mesh.material as THREE.MeshStandardMaterial;
      if (!mat?.isMeshStandardMaterial) return;
      const ang = radius / Math.max(1, this.pos.distanceTo(abs)); // apparent angular radius
      const ud = mesh.userData as { baseBump?: number; rimMat?: THREE.MeshBasicMaterial; rimBase?: number };
      if (mat.normalMap && ud.baseBump !== undefined) {
        const g = smoothstep(0.10, 0.26, ang) * ud.baseBump;
        mat.normalScale.set(g, g); // a uniform — no needsUpdate
      }
      if (ud.rimMat) ud.rimMat.opacity = (ud.rimBase ?? 0.16) * smoothstep(0.05, 0.16, ang);
    };
    for (const pl of this.planets) if (pl.abs && pl.radius) lod(pl.mesh, pl.abs, pl.radius);
    for (const wp of this.worldPlanets.values()) lod(wp.mesh, this._v1.set(wp.spec.x, wp.spec.y, wp.spec.z), wp.spec.radius);
  }

  // ── Frame: rig + remotes + HUD ───────────────────────────────────────────
  private frame(dt: number): void {
    if (!this.active) return;
    // Engine hum: gain from throttle, pitch from real speed (JTL feel).
    sfxLoop('sfx_space_engine', 'space').set(
      0.08 + this.throttle * 0.26,
      0.85 + Math.min(1, this.vel.length() / 300) * 0.45,
    );
    // Fleet-battle rumble: a live battle rolls thunder that grows on approach.
    const bat = this.battle;
    sfxLoop('sfx_fleet_battle', 'space').set(
      bat ? Math.min(0.55, 2.2 / (1 + bat.frame.c.distanceTo(this._ship) / 900)) : 0,
    );
    this.local(this.pos.x, this.pos.y, this.pos.z, this._ship);
    const rig = atlas.localMesh;
    // Space campaign L (2026-10-01, gap 27): a HEADSET in the sector (the lever `atlas:space-comfort-v1` on). Its edge
    // re-seats the view (the cockpit, never the chase — three replaces the camera with the head pose), and every frame
    // the comfort choices decide the bank and the secondary effects (lib/space_comfort).
    const xrOn = atlas.xrPresenting && spaceComfortOn();
    if (xrOn !== this.vrWas) { this.vrWas = xrOn; sectorMeter().reset(); this.applyCamZoom(); }
    (window as unknown as { __spaceComfort?: () => object }).__spaceComfort ??= () => this.comfortState(); // probes (read-only)
    this.fxScale = sectorEffects({
      on: spaceComfortOn(), presenting: atlas.xrPresenting, vrEffects: xrComfort().spaceEffects,
      desktopMotion: desktopSpaceComfort().motion, prefersReduced: prefersReducedMotion(),
    });
    this.hud?.setScale(spaceComfortOn() ? desktopSpaceComfort().hudScale : 1); // the desktop sector HUD's own size
    if (rig) {
      if (this.interior) {
        // Walking: the rig IS the head — first person through the room.
        const it = this.interior;
        rig.position.set(it.origin.x + it.wx, it.origin.y + 1.7, it.origin.z + it.wz);
        rig.quaternion.setFromEuler(this._e1.set(it.pitch, it.yaw, 0, 'YXZ'));
      } else {
        rig.position.copy(this._ship); // OFFSET space — the floating-origin law
        // THE AERO LAW: the rig — hull, glows, camera and so the horizon — carries the picture's coordinated bank on top
        // of the flight quaternion; `this.quat` (the guns, the velocity, the row) never does. The old model banks 0.
        // In a headset the rig carries the comfort page's choice instead (default OFF: an uncommanded world roll on every
        // yaw — roll is the most sickening axis; your own roll still rolls the world, it is commanded).
        const bank = xrOn ? headsetBank(this.aeroBank, xrComfort().spaceBank) : this.aeroBank;
        this._q2.copy(this.quat).multiply(this._q1.setFromAxisAngle(this._v1.set(0, 0, 1), bank));
        rig.quaternion.slerp(this._q2, Math.min(1, dt * 10));
        // The vignette's cue: the ship's acceleration and the rotation the eye rides — never the head (AtlasXRModule).
        sectorMeter().sample(dt, this.vel, rig.quaternion, performance.now());
      }
    }
    // Skybox followers ride the ship (void shell, starfields, sun glow).
    for (const f of this.followers) {
      f.obj.position.copy(this._ship);
      if (f.offset) f.obj.position.add(f.offset);
    }
    this.natural?.frame(dt);   // natural objects: orbits, tails, variability — before the sun (its occluders) and the far-body anchors
    this.updateSun(dt);        // realism wave: one sun — the light, the disc, the eclipse, planetshine, the IBL lobe
    this.updateLookFx(dt);     // realism wave R7: streaks, the FOV kick, the shake
    // Space campaign L: the headset's cockpit — the rest frame, the dash HUD, the boresight, the target ring.
    this.vrCockpit.update(dt, xrOn && !this.interior, rig, this.cam(), xrComfort(), xrOn && !this.interior ? this.vrView() : NO_VR_VIEW);
    this.blackHole?.frame(dt); // shader gaps: black hole — after the camera's pose (the lens is camera-relative)
    this.nebulae?.frame(dt); // space realism: nebulae — a slice of the bake in hand, the fades, the stars' pixel floor
    if (this.blackHoleViewUntil && performance.now() >= this.blackHoleViewUntil) this.debugRemoveBlackHole(); // the debug view's minute is up
    this.bountyTick(dt);
    for (const pl of this.planets) pl.mesh.rotation.y += pl.spin * dt;
    for (const wp of this.worldPlanets.values()) wp.mesh.rotation.y += wp.spin * dt;
    // PLANET NORMAL-MAP LOD (~2 Hz): under heavy minification the bump pass
    // shades as black SPECKLE (the blotchy-planets report) — strip the
    // normal map when the apparent size shrinks, restore it on approach
    // (hysteresis so the boundary never flickers).
    this.planetLodAcc += dt;
    if (this.planetLodAcc > 0.5) { this.planetLodAcc = 0; this.updatePlanetLod(); }
    // Far-planet impostors: same view ray, same angular size, SAFE offsets.
    // THE BACK SEAT (space look, 2026-10-08): a far body BEYOND a drawn black hole, in the cap's part of the sky, stands
    // behind the cap instead of at the 100 km anchor — where it was painted over the hole's shadow and disc. While one
    // does, the cap stands forward and a far body NEARER than the hole in that part of the sky stands before it (THE
    // THREE SEATS, black_hole.ts).
    const cap = this.blackHole?.capFrame() ?? null;
    if (cap) { this._capH.x = cap.x - this.pos.x; this._capH.y = cap.y - this.pos.y; this._capH.z = cap.z - this.pos.z; }
    let seated = 0;
    for (const a of this.planetAnchors) {
      const dx = a.abs.x - this.pos.x, dy = a.abs.y - this.pos.y, dz = a.abs.z - this.pos.z;
      const dist = Math.hypot(dx, dy, dz);
      const shipL = this.shipLocal();
      let back = false, front = false;
      if (cap && dist > FAR_ANCHOR_M) {
        this._capB.x = dx; this._capB.y = dy; this._capB.z = dz;
        back = bodyBehindCap(this._capH, cap.coreRad, this._capB, (a.reachM ??= this.anchorReachM(a.group)));
        front = !back && cap.forward && bodyBeforeCap(this._capH, cap.coreRad, this._capB, a.reachM);
      }
      if (back !== (a.back === true)) { a.back = back; this.seatOverLayers(a.group, back); }
      if (back) seated++;
      if (dist > FAR_ANCHOR_M) {
        const k = farBodyAnchorM(dist, FAR_ANCHOR_M, cap ? cap.backSeatM : FAR_ANCHOR_M, back, cap ? cap.frontSeatM : FAR_ANCHOR_M, front) / dist;
        a.group.position.set(shipL.x + dx * k, shipL.y + dy * k, shipL.z + dz * k);
        a.group.scale.setScalar(k);
      } else {
        a.group.position.set(shipL.x + dx, shipL.y + dy, shipL.z + dz);
        a.group.scale.setScalar(1);
      }
    }
    this.backSeated = seated;
    this.blackHole?.setCapForward(seated > 0); // the cap takes its forward seat at the next frame (the seat's margin covers the one between)
    // The chart breathes: new public worlds surface as planets mid-flight.
    this.worldSyncAcc += dt;
    if (this.worldSyncAcc > 2 && this.system.id === 'sys0') { this.worldSyncAcc = 0; this.syncWorldPlanets(); }
    for (const s of this.spinners) { s.obj.rotation.y += s.spin * 0.3 * dt; s.obj.rotation.x += s.spin * 0.12 * dt; }

    // Cosmetic bank/pitch: the hull leans into the mouse turn (decaying input
    // rate) — pure presentation on the ship GROUP, real roll stays on quat.
    this.yawRate *= Math.max(0, 1 - dt * 5);
    this.pitchRate *= Math.max(0, 1 - dt * 5);
    // THE AERO LAW: under `aero` the PICTURE banks into the turn — the rig (hull, glows, camera, horizon) rolls with the
    // coordinated bank in frame() below (`poseRig`), so the mesh keeps only a small lean; the old model keeps its lean.
    const aero = this.flightModelNow === 'aero';
    this.aeroBank = aero ? stepBank(this.aeroBank, bankTarget(this.aeroRates.yaw), dt) : stepBank(this.aeroBank, 0, dt);
    if (this.shipMesh) {
      this.shipMesh.rotation.z = THREE.MathUtils.lerp(this.shipMesh.rotation.z, aero ? this.aeroRates.yaw * 0.08 : this.yawRate * 0.45, Math.min(1, dt * 8));
      this.shipMesh.rotation.x = THREE.MathUtils.lerp(this.shipMesh.rotation.x, aero ? this.aeroRates.pitch * 0.06 : this.pitchRate * 0.18, Math.min(1, dt * 8));
    }
    if (this.cockpitView && !this.vrWas) this.cockpitOverlay.draw(aero ? this.aeroBank : this.yawRate * 0.5); // the canopy banks with the picture (a headset never sees this DOM canvas)
    // Engine glow tracks throttle + boost.
    const burn = this.throttle * (this.boost && this.boosterE > 0 ? 1.6 : 1);
    for (const eg of this.engineGlows) {
      (eg.material as THREE.SpriteMaterial).opacity = Math.min(1, burn * 0.85);
      eg.scale.setScalar(0.9 + burn * 2.2);
    }

    // Freighter lanes breathe (deterministic warp-in / cruise / warp-out).
    this.updateFreighters();
    this.updatePatrols();
    this.updateConvoys();
    this.updateShipments(); // Space Wave 3: the real runs
    this.syncDens(); // Space Wave 6: the shared dens decide which hulls stand
    this.syncAmbushes(); // THE RAIDER LAW: the lane's raiders are real hulls
    { const nowP = performance.now(); this.syncDenHulls(nowP); this.refreshWarTargets(nowP); this.applyPvpHits(); this.syncCrew(nowP); this.attachEngineering(); shipEngineering.tick(nowP); engineeringPanel.frame(nowP); }
    tickSpaceOps({ conn: atlas.conn, systemId: this.system.id, pos: this.pos }, performance.now()); // slice H: the scout's / the dwell's metered beats
    // The 3D map refreshes at ~5 Hz while open.
    if (this.map) {
      this.mapAcc += dt;
      if (this.mapAcc > 0.2) { this.mapAcc = 0; this.pushMapData(); }
    }

    // Remote pilots: TRUE 3D position + motion-derived pitch (posY streams).
    const conn = atlas.conn;
    if (conn && this.root) {
      const seen = new Set<string>();
      // THE LOCATION LAW: only pilots the server places in THIS system render
      // (the cross-system ghost of §14 is gone); no row = the Commons Reach.
      this.bindSysOf(conn); // C3: kept by row callbacks — no per-frame table scan
      const sysOf = this.sysOfMap;
      for (const r of atlas.remotes.values()) {
        if ((sysOf.get(r.identityHex) ?? 'sys0') !== this.system.id) continue;
        seen.add(r.identityHex);
        let rs = this.remoteShips.get(r.identityHex);
        if (!rs) {
          // Wrapper group carries the POSE; the inner hull keeps the π model
          // offset (setting rotation directly on the clone clobbered it — the
          // old remote ships flew BACKWARDS).
          const obj = new THREE.Group();
          if (this.shipMesh) {
            const inner = this.shipMesh.clone();
            inner.rotation.set(0, 0, 0); // strip the cosmetic bank; the hull group inside keeps its model yaw
            // The clone must not carry MY plumes (their vec4 buffer is mine — the remote would burn with my throttle).
            const mine: THREE.Object3D[] = [];
            inner.traverse((o) => { if (o.name === 'space-plumes') mine.push(o); });
            for (const o of mine) o.parent?.remove(o);
            obj.add(inner);
          } else obj.add(this.fallbackShip(0x7bc86c));
          this.root.add(obj);
          this.local(r.targetX, 0, r.targetZ, obj.position);
          let track = this.remoteTracks.get(r.identityHex);
          if (!track) { track = new RemoteTrack(); this.remoteTracks.set(r.identityHex, track); }
          if (track.size === 0) {
            // no row event since the sector opened (an idle pilot): seed from the cached row
            const row0 = this.remoteRows.get(r.identityHex);
            track.push(performance.now(), NaN, r.targetX, row0?.y ?? 0, r.targetZ, row0?.yaw ?? r.targetYaw);
          }
          rs = { obj, target: obj.position.clone(), track, vel: new THREE.Vector3(), pitch: 0 };
          this.remoteShips.set(r.identityHex, rs);
        }
        rs.obj.visible = !this.crewHexes.has(r.identityHex); // THE CREW LAW: a pilot in a turret seat sits inside another hull
        { const row = this.remoteRows.get(r.identityHex); this.local(r.targetX, row?.y ?? 0, r.targetZ, rs.target); } // the rules' server row
        // Physics follow-up item 5: the pose is the track's — buffered monotone interpolation on the rows' server stamps
        // (lib/remote_motion.ts); the yaw rides the same timeline and the pitch is the drawn velocity's.
        const pose = rs.track.pose(performance.now(), this._rPose);
        if (pose) {
          this.local(pose.x, pose.y, pose.z, rs.obj.position); // server abs → offset space
          rs.vel.set(pose.vx, pose.vy, pose.vz);
          const horiz = Math.hypot(pose.vx, pose.vz);
          if (horiz + Math.abs(pose.vy) > 2) {
            // +rotation.x noses the −Z forward vector UP, so climbing = +pitch.
            rs.pitch = THREE.MathUtils.lerp(rs.pitch, Math.atan2(pose.vy, Math.max(0.01, horiz)), Math.min(1, dt * 4));
          }
          rs.obj.rotation.set(rs.pitch, pose.yaw, 0, 'YXZ');
        }
      }
      for (const [hex, rs] of this.remoteShips) {
        if (!seen.has(hex)) { this.root.remove(rs.obj); this.remoteShips.delete(hex); this.remoteTracks.delete(hex); }
      }
    }

    this.updateHud(dt);
  }

  // ── HUD wiring ───────────────────────────────────────────────────────────
  private cam(): THREE.PerspectiveCamera | null {
    const rig = atlas.localMesh;
    return (rig?.children.find((c) => (c as THREE.Camera).isCamera) as THREE.PerspectiveCamera | undefined) ?? null;
  }

  /** Y: snap between the chase view and the first-person cockpit (a shortcut for
   *  the scroll zoom — scrolling all the way in reaches the same cockpit). */
  private toggleCockpit(): void {
    if (this.interior) return; // walking a deck/station — not flying
    this.camBack = this.cockpitView ? chaseFor(CHASSIS[this.chassisKey]) : ZOOM_MIN + 0.5; // out → chase, in → cockpit
    this.applyCamZoom();
    this.toast(this.cockpitView ? `Cockpit view — ${(CHASSIS[this.chassisKey] ?? CHASSIS[DEFAULT_CHASSIS]).name} (scroll to zoom)` : 'Chase view (scroll to zoom)');
  }

  /** Position the camera from the scroll `camBack` distance: first-person cockpit
   *  when zoomed in past ZOOM_COCKPIT (the 2-D canopy, or the Sovereign's 3-D
   *  captain's chair), else a chase that widens with distance. The rig is
   *  untouched — flight is identical; only the camera's local pose moves. */
  private applyCamZoom(): void {
    const cam = this.cam();
    if (!cam || this.interior) return;
    const spec = CHASSIS[this.chassisKey] ?? CHASSIS[DEFAULT_CHASSIS];
    const isSovereign = this.chassisKey === 'ship_sovereign';
    const eye = isSovereign ? SOVEREIGN_EYE : cockpitEye(spec);
    // Space campaign L (2026-10-01): in a headset the view is ALWAYS the cockpit — three replaces this camera's local
    // pose with the head pose every render, so a chase offset never reaches the eye (the head sat inside the hull mesh).
    // The headset's cockpit is modules/vr/SpaceVrCockpit (the rest frame, or the Sovereign's cabin seated on the head);
    // the 2-D canopy is DOM, which an immersive session never draws.
    const vrSeat = this.vrWas;
    const zc = vrSeat ? { x: 0, y: eye.y, z: eye.z, pitch: 0, cockpit: true } : zoomCamera(this.camBack, eye);
    cam.position.set(zc.x, zc.y, zc.z);
    cam.rotation.set(zc.pitch, 0, 0);
    cam.updateProjectionMatrix();
    // R7: the shake adds to THIS pose (it never accumulates into the zoom's own numbers).
    this.camBasePos.copy(cam.position); this.camBaseRot.copy(cam.rotation); this.camBaseSet = true; this.shaking = false;
    if (zc.cockpit && !this.cockpitView) sfx('sfx_cockpit_seat', { gain: 0.35, cooldownMs: 600 });
    this.cockpitView = zc.cockpit;
    if (zc.cockpit) {
      if (isSovereign) {
        // The Sovereign seats you in its REAL 3-D cabin, not the 2-D canopy.
        this.cockpitOverlay.hide();
        this.showSovereignCockpit(true);
      } else {
        this.showSovereignCockpit(false);
        this.cockpitOverlay.setChassis(spec);
        if (vrSeat) this.cockpitOverlay.hide(); else this.cockpitOverlay.show();
      }
      if (this.shipMesh) this.shipMesh.visible = false; // we're inside the hull
    } else {
      this.cockpitOverlay.hide();
      this.showSovereignCockpit(false);
      if (this.shipMesh) this.shipMesh.visible = true;
    }
  }

  /** Existing callers re-seat the pilot after a chassis swap / landing — route
   *  them through the zoom so the current scroll distance is preserved. */
  private applyCockpitView(): void { this.applyCamZoom(); }

  /** Space campaign L: the probe lever `__spaceComfort()` — what the headset sees and why. */
  private comfortState(): object {
    const rig = atlas.localMesh;
    const q = rig?.quaternion;
    const e = q ? new THREE.Euler().setFromQuaternion(q, 'YXZ') : null;
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(this.quat), right = new THREE.Vector3(1, 0, 0).applyQuaternion(this.quat), up = new THREE.Vector3(0, 1, 0).applyQuaternion(this.quat);
    const m = sectorMeter().s;
    return {
      active: this.active, lever: spaceComfortOn(), presenting: !!atlas.xrPresenting, vr: this.vrWas, xrPad: padXrActive(),
      fx: { ...this.fxScale }, desktop: { ...desktopSpaceComfort() }, comfort: { ...xrComfort() },
      aeroBankDeg: +(this.aeroBank * 57.2958).toFixed(2), rigRollDeg: e ? +(e.z * 57.2958).toFixed(2) : null,
      shipRollDeg: +((Math.atan2(right.y, up.y) * 180) / Math.PI).toFixed(2), noseUp: +fwd.y.toFixed(3),
      rollIdleS: +this.rollIdleS.toFixed(2), boost: this.boost, throttle: +this.throttle.toFixed(3),
      cockpitView: this.cockpitView, hullVisible: this.shipMesh?.visible ?? null, canopy: this.cockpitView && !this.vrWas,
      meter: { accel: +m.accel.toFixed(2), yawDps: +m.yawDps.toFixed(1), pitchDps: +m.pitchDps.toFixed(1), rollDps: +m.rollDps.toFixed(1), samples: m.samples, jumps: m.jumps },
      cockpit: this.vrCockpit.state(),
      hudScale: this.hud?.scale ?? null,
      census: Object.fromEntries(this.keyCensus),
    };
  }

  /** Space campaign L: what the headset's cockpit HUD mirrors — the numbers the desktop HUD last SHOWED (no second
   *  computation), the target's line of sight with its IFF glyph AND word (never colour alone), the ship's roll. */
  private vrView(): SectorVrView {
    const hud = this.hud;
    let target: SectorVrView['target'] = null;
    const t = this.target;
    if (t && t.deadUntil === 0) {
      this.root?.updateWorldMatrix(true, false);
      const world = this.root ? this.root.localToWorld(this.vrScratch.copy(t.root.position)) : this.vrScratch.copy(t.root.position);
      const mySide: 0 | 1 | -1 = this.faction === 'aurelian' ? 0 : this.faction === 'borean' ? 1 : -1;
      const side: 0 | 1 | -1 = t.warSide === 0 || t.warSide === 1 ? t.warSide : -1;
      const tag = iffTag({ ally: !!t.ally, side, mySide });
      target = { world, glyph: tag.glyph, word: tag.word, label: this.targetName(t).replace(/^[◆▲]\s*/, ''), friend: tag.word === 'friend' };
    }
    const right = this._v3.set(1, 0, 0).applyQuaternion(this.quat);
    const up = this._v4.set(0, 1, 0).applyQuaternion(this.quat);
    const c = xrComfort();
    const sov = this.chassisKey === 'ship_sovereign' && this.sovereignCockpit?.visible ? this.sovereignCockpit : null;
    return {
      status: hud?.lastStatus ?? null,
      prompt: hud?.lastPrompt ?? null,
      toast: hud?.lastToast ?? null,
      target,
      rollDeg: (Math.atan2(right.y, up.y) * 180) / Math.PI,
      chassis: (CHASSIS[this.chassisKey] ?? CHASSIS[DEFAULT_CHASSIS]).name,
      sovereignCabin: sov,
      sovereignEye: SOVEREIGN_EYE,
      rollMode: c.spaceRoll === 'lock' ? 'horizon lock' : c.spaceRoll === 'assist' ? `auto-level · roll ${Math.round(c.spaceRollRate * 100)}%` : `free roll ${Math.round(c.spaceRollRate * 100)}%`,
    };
  }

  /** Show/hide the Sovereign's rig-attached 3-D captain's-chair cockpit. */
  private showSovereignCockpit(on: boolean): void {
    const rig = atlas.localMesh;
    if (!rig) { if (this.sovereignCockpit) this.sovereignCockpit.visible = false; return; }
    if (on) {
      if (!this.sovereignCockpit) this.sovereignCockpit = buildSovereignCockpit();
      if (this.sovereignCockpit.parent !== rig) rig.add(this.sovereignCockpit);
      this.sovereignCockpit.visible = true;
    } else if (this.sovereignCockpit) {
      this.sovereignCockpit.visible = false;
    }
  }

  // ── Space campaign I (2026-09-30): the true lead, two fire groups, sensor interference (lib/space_flight_combat.ts) ──
  // A narrow host: the controller fires through fire() / fireMissile() UNCHANGED (their cooldowns, capacitor and slice
  // D's noted origins), reads the pose, the target, the remotes and the war targets this module already keeps.
  private readonly flightFwd = new THREE.Vector3();
  private readonly flightTp = new THREE.Vector3();
  private readonly flightCombat: FlightCombat = new FlightCombat({
    fireBolts: () => this.fire(),
    fireMissile: () => this.fireMissile(),
    cap: () => this.cap,
    spendCap: (n) => { this.cap = Math.max(0, this.cap - n); },
    fit: () => ({ missiles: this.stats.missiles, counters: this.stats.counters }),
    flying: () => this.active && !this.interior && !this.dock && !this.crew,
    crewSeated: () => !!this.crew,
    toast: (t) => this.toast(t),
    ship: () => ({ abs: this.pos, local: this._ship, vel: this.vel, fwd: this.flightFwd.set(0, 0, -1).applyQuaternion(this.quat) }),
    off: () => this.off,
    warTargets: () => this.warTargets,
    remotes: () => this.remoteShips.entries(),
    target: () => (this.target ? { key: this.target.key, pos: this.target.root.position, vel: this.target.vel, hitR: this.hitRadiusOf(this.target), seatAt: this.target.seat?.at ?? null } : null),
    surveying: () => this.beamHeld(),
    systemId: () => this.system.id,
    toWorld: (v) => (this.root ? this.root.localToWorld(v) : v), // THE SCENE SHIFT: offset space → the camera's world
    solidsNear: (abs, radius) => {
      const out: Array<{ x: number; y: number; z: number; r: number; halfH: number }> = [];
      for (const g of this.colliderGroups) {
        if (Math.hypot(g.x - abs.x, g.y - abs.y, g.z - abs.z) > g.reach + radius) continue;
        for (const c of g.list) out.push({ x: c.x, y: c.y, z: c.z, r: c.r, halfH: c.halfH });
      }
      return out;
    },
  });

  private updateHud(dt: number): void {
    const hud = this.hud;
    if (!hud) return;

    // Per-frame: target bracket + lead pip + off-screen arrow.
    const cam = this.cam();
    if (this.target && cam) {
      // Refresh the view matrices NOW — frame() runs before the renderer, and
      // project() reads matrixWorldInverse (stale by one frame otherwise).
      cam.updateWorldMatrix(true, false);
      cam.matrixWorldInverse.copy(cam.matrixWorld).invert();
      const w = window.innerWidth, h = window.innerHeight;
      // THE SCENE SHIFT (space campaign I): the ground's origin rebase (AtlasOriginRebaseModule) holds scene.position at
      // −(localX, 0, localZ), and in the sector localX/Z mirror the ship's ABSOLUTE position — the camera's world matrix
      // carries that shift, the root's offset-space children do not. A DOM projection of an offset-space point must go
      // through the root first, or every bracket / pip / arrow past 10 km from the sector origin lands in the wrong place.
      this.root?.updateWorldMatrix(true, false);
      const tp = this.root ? this.root.localToWorld(this.flightTp.copy(this.target.root.position)) : this.target.root.position;
      const local = this._v2.copy(tp).applyMatrix4(cam.matrixWorldInverse);
      const offAng = Math.atan2(local.x, local.y); // captured BEFORE _v2 is reused for the NDC
      if (local.z < 0) { // in front of the camera → bracket + lead pip
        const ndc = this._v2.copy(tp).project(cam);
        const bx = (ndc.x * 0.5 + 0.5) * w;
        const by = (-ndc.y * 0.5 + 0.5) * h;
        // Lead solution: aim where the bolt meets the target (JTL lead reticle). Space campaign I: THE TRUE INTERCEPT —
        // the smallest positive root of the bolt/target quadratic with the velocity the bolt really inherits; a solution
        // past the bolt's 1.6 s life draws no pip (a ghost "out of reach" mark), an outrunning target an ✕, and a note
        // says when the target manoeuvres, its track is old or my sensors are jammed (lib/space_lead.ts).
        const onScreen = Math.abs(ndc.x) <= 1.05 && Math.abs(ndc.y) <= 1.05;
        const pip = this.flightCombat.lead(cam, w, h, onScreen ? { x: bx, y: by } : null);
        hud.setTargetMarkers(
          onScreen ? { x: bx, y: by } : null,
          onScreen ? pip : null,
          onScreen ? null : offAng,
        );
      } else {
        // Behind: arrow around the reticle pointing the shortest turn.
        hud.setTargetMarkers(null, null, offAng);
      }
    } else {
      hud.setTargetMarkers(null, null, null);
    }
    // Space campaign I: the jammer contact (a ◇ bracket with its own lead, or a bearing), the EW strip and the fire-group
    // chip — their own layer, so the shared prompt line's war-vs-industry priority never hides a jam.
    if (cam) this.flightCombat.frame(cam, window.innerWidth, window.innerHeight, dt);

    // ~5 Hz numbers.
    this.hudAcc += dt;
    if (this.hudAcc > 0.2) {
      this.hudAcc = 0;
      // Critical-damage alarm (loop, gated) + the shield-collapse edge.
      const hullFrac = this.stats.hp > 0 ? this.hull / this.stats.hp : 1;
      sfxLoop('sfx_hull_alarm', 'space').set(hullFrac < 0.3 && this.hull > 0 ? 0.22 : 0, 1, 0.3);
      const shieldUp = this.shieldF + this.shieldR > 0.5;
      if (!shieldUp && this.shieldWasUp) sfx('sfx_shield_down', { gain: 0.5, cooldownMs: 1500 });
      this.shieldWasUp = shieldUp;
      // ~1.6 s fleet mirror: equip/activate changes land mid-flight.
      if ((++this.fleetTick & 7) === 0) this.refreshFleet();
      hud.setStatus({
        hull: this.hull, hullMax: this.stats.hp,
        shieldF: this.shieldF, shieldR: this.shieldR, shieldMax: this.arcMax(),
        cap: this.cap, capMax: this.stats.capacitor,
        booster: this.boosterE, boosterMax: BOOSTER_MAX,
        throttle: this.throttle,
        speed: this.vel.length(),
        boost: this.boost && this.boosterE > 0,
        cruise: this.cruise,
        matchSpeed: this.matchSpeed,
        bounty: this.bounty,
        target: this.target ? {
          name: this.targetName(this.target),
          dist: this.target.root.position.distanceTo(this._ship),
          hp: this.target.hp, hpMax: this.statsOf(this.target).hp,
        } : null,
        nav: this.navIdx >= 0 ? {
          name: this.nav[this.navIdx].name,
          dist: this._v1.set(...this.nav[this.navIdx].pos).distanceTo(this.pos),
          state: this.hyper === 'jump' ? 'HYPER' : this.hyper === 'spool' ? 'SPOOL' : this.hyper === 'align' ? 'ALIGN' : '',
        } : null,
      });
      if (this.interior) {
        const it = this.interior;
        const nearTerm = it.terminal && Math.hypot(it.wx - it.terminal.x, it.wz - it.terminal.z) < 4;
        const zone = it.wx > 35.4 ? 'the docking bay' : it.wx > 21.4 ? 'the access corridor' : 'the promenade';
        hud.prompt(it.kind === 'station'
          ? `${nearTerm ? `${keyHint('spaceDock')} — dock terminal · ` : ''}${keyHint('spaceBoard')} — board your ship & undock (${zone})`
          : `${keyHint('spaceDeck')} — return to the cockpit`);
      } else {
        const landing = this.nearLanding();
        const damaged = this.hull < this.stats.hp || this.shieldF < this.arcMax() || this.shieldR < this.arcMax();
        const near = this.nearStation();
        const repairBit = near && damaged ? ` · ${keyHint('spaceRepair')} — repair (${stationServicesOnClient() ? 'a quote' : `${REPAIR_COST_SOV} ⓒ`})` : ''; // space campaign K
        const yardBit = near ? ` · ${keyHint('spaceDock')} — shipyard · ${keyHint('spaceBoard')} — board` : '';
        // Space Wave 3 (THE ESCORT LAW): ride with a firm's run for its reward at arrival.
        const run = this.nearestRun();
        const meId = atlas.conn?.identity;
        const fight = run ? this.ambushFor(run.row.shipmentId) : null;
        const raidBit = run && fight && run.dist <= ESCORT_RANGE_M * 2 ? ` · ⚔ RAIDERS on the ${run.firmName} run — ${fight.raidersAlive} left (kill them)` : '';
        const escortBit = run && run.dist <= ESCORT_RANGE_M * 1.5
          ? (meId && run.row.escortedBy.isEqual(meId)
            ? ` · escorting the ${run.firmName} run · ${Math.round(this.pos.distanceTo(this._v1.set(run.toPos[0], run.toPos[1], run.toPos[2])) / 1000)} km to ${run.toName}`
            : ` · ${keyHint('spaceEscort')} — escort the ${run.firmName} run (${ESCORT_REWARD_SOV} ⓒ at ${run.toName})`)
          : '';
        // The war is always findable: with no docking prompt, the line carries
        // the LIVE battle (or the countdown to the next) — M marks it on the map.
        let battleLine: string | null = null;
        {
          const nowS = Date.now() / 1000;
          const win = this.battleWindow();
          const mmss = (sec: number): string => `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;
          if (win) {
            battleLine = this.campaignLine(win, mmss) ?? `⚔ FLEET WAR at ${win.poi.name} — ${BATTLE_CAPS * 2} capitals · ${WAR_SKIRMISHERS * 2} gunships — ${mmss(BATTLE_LEN_S - win.t)} left (M — chart it)`;
          } else {
            const cyc = Math.floor(nowS / BATTLE_PERIOD_S) + 1;
            const forts = POIS.filter((p) => p.kind === 'fortress' || p.kind === 'megastation');
            if (forts.length) {
              const cfUntil = this.ceasefireUntilCyc(cyc - 1);
              if (cfUntil !== null) battleLine = `☮ CEASEFIRE — the councils' truce holds ${cfUntil - (cyc - 1)} more cycle${cfUntil - (cyc - 1) === 1 ? '' : 's'}; no fleet war until it lapses`;
              else battleLine = `next fleet war at ${forts[cyc % forts.length].name} in ${mmss(cyc * BATTLE_PERIOD_S - nowS)}`;
            }
          }
        }
        let courseLine: string | null = null;
        if (this.course) {
          const rem = Math.max(0, (this.course.arriveAt - Date.now()) / 1000);
          const tgt = this.galaxySystems().find((sy) => sy.id === this.course!.targetId);
          courseLine = `⛵ sublight course — ${tgt?.name ?? '…'} in ${Math.floor(rem / 86400)}d ${Math.floor((rem % 86400) / 3600)}h ${Math.floor((rem % 3600) / 60)}m (${keyHint('spaceGalaxy')} — galaxy)`;
        }
        const jumpLine = this.sysJump
          ? (this.sysJump.retrying
            ? `⚡ HYPER-WARP → ${this.sysJump.target.name} — arrival delayed, holding the tunnel` // a held tunnel's countdown looped 3 → 0
            : `⚡ HYPER-WARP → ${this.sysJump.target.name} — ${Math.ceil(Math.max(0, (this.sysJump.until - performance.now()) / 1000))} s`)
          : null;
        // Space Wave 1: a live wormhole's effect on THIS system (shipStats applies it).
        const fx = systemEffectOf(this.system.id, Date.now());
        const fxLine = fx ? `⨀ ${fx.toUpperCase()} space — ${fx === 'pulsar' ? 'shields +25 %' : fx === 'magnetar' ? 'damage +20 %' : 'capacitor −25 %'} while the wormhole stands` : null;
        // Industry lines: the beam's work, the occupation, the exploration
        // trail — shown when nothing more urgent (landing/course) holds it.
        let industryLine: string | null = null;
        {
          const tgt = this.beamHeld() ? this.beamTarget() : null;
          if (tgt) {
            industryLine = tgt.mode === 'mine'
              ? `⛏ MINING ${tgt.label} — hold ${this.cargoUnits()}/${this.holdCap()} u (unload at a station · ${keyHint('spaceDock')})${this.seamLeftBit()}`
              : tgt.mode === 'salvage'
                ? `🛠 SALVAGING the war grave — hold ${this.cargoUnits()}/${this.holdCap()} u`
                : tgt.mode === 'strip'
                  ? `🛠 STRIPPING ${tgt.label} — hold ${this.cargoUnits()}/${this.holdCap()} u`
                  : discovery.handles(tgt.key) ? discovery.hudLine(tgt.key, true) : '📡 RECOVERING the signature…';
          } else if (this.incursionHere()) {
            industryLine = `☠ INCURSION — raider grip ${this.incursionInfluence()}% (kills push it back)`;
          } else {
            const idle = this.beamTarget();
            if (idle) {
              industryLine = idle.mode === 'mine'
                ? `⛏ ${idle.label} seam in reach — ${this.beamCue()} to mine${this.seamLeftBit()}`
                : idle.mode === 'salvage'
                  ? `🛠 wrecks in cutter range — ${this.beamCue()} to salvage`
                  : idle.mode === 'strip'
                    ? `🛠 ${idle.label} in strip range — ${this.beamCue()} to take its cargo`
                    : discovery.handles(idle.key) ? `${discovery.hudLine(idle.key, false)} (${this.beamCue()}) · survey desk ${keyHint('spaceSurvey')}` : `📡 signature beacon in reach — ${this.beamCue()} to recover`;
            } else {
              const sigN = this.unresolvedCount();
              if (sigN > 0 && Math.floor(performance.now() / 12_000) % 3 === 0) {
                industryLine = `📡 ${sigN} hidden site${sigN > 1 ? 's' : ''} in-system (signatures, throats) — ${keyHint('spaceScan')} to scan`;
              }
            }
          }
        }
        // A LIVE war outranks industry chatter; the idle countdown yields.
        const warActive = this.battleWindow() !== null;
        hud.prompt(jumpLine ?? (landing
          ? `${keyHint('spaceLand')} — dock & land (${landing})${repairBit}${yardBit}${escortBit}${raidBit}`
          : courseLine ?? (warActive ? battleLine : industryLine ?? fxLine ?? battleLine)));
        this.pushGalaxyData(); // live countdowns while the chart is open
      }
    }

    // ~10 Hz radar sweep (ship-local frame: rotate world deltas by quat⁻¹).
    this.radarAcc += dt;
    if (this.radarAcc > 0.1) {
      this.radarAcc = 0;
      // Combat flag for the music layer: recent hits, or a pirate pressing
      // an attack (intercepts only count once they are CLOSE — flying past a
      // den's aggro ring is not a battle).
      let combat = performance.now() - this.lastHit < 6000;
      if (!combat) {
        const sl = this.shipLocal();
        for (const pr of this.pirates) {
          if (pr.deadUntil !== 0) continue;
          if (pr.ai === 'attack' || (pr.ai === 'intercept' && pr.root.position.distanceTo(sl) < 700)) { combat = true; break; }
        }
      }
      atlas.combatSpace = combat;
      const inv = this._q1.copy(this.quat).invert();
      const contacts: RadarContact[] = [];
      const push = (wx: number, wy: number, wz: number, kind: RadarContact['kind']): void => {
        const v = this._v1.set(wx - this.pos.x, wy - this.pos.y, wz - this.pos.z);
        if (v.lengthSq() > RADAR_RANGE_M * RADAR_RANGE_M * 4) return;
        v.applyQuaternion(inv);
        contacts.push({ x: v.x, y: v.y, z: v.z, kind });
      };
      const POI_RADAR: Record<PoiSpec['kind'], RadarContact['kind']> = {
        station: 'station', megastation: 'station', asteroids: 'field', junkyard: 'wreck', nebula: 'hazard',
        ice: 'hazard', derelict: 'wreck', comet: 'hazard', mine: 'wreck', fortress: 'pirate',
      };
      // Pirates/remotes live in OFFSET space — push() wants absolute.
      const radarMySide: 0 | 1 | -1 = this.faction === 'aurelian' ? 0 : this.faction === 'borean' ? 1 : -1;
      for (const p of this.pirates) if (p.deadUntil === 0) push(p.root.position.x + this.off.x, p.root.position.y + this.off.y, p.root.position.z + this.off.z, p.ally || (radarMySide !== -1 && this.sideOf(p) === radarMySide) ? 'remote' : 'pirate');
      if (this.system.id === 'sys0') {
        for (const poi of POIS) push(poi.pos[0], poi.pos[1], poi.pos[2], POI_RADAR[poi.kind]);
        for (const pl of PLANETS) push(pl.pos[0], pl.pos[1], pl.pos[2], 'planet');
      } else {
        // The system you are IN — the radar used to sweep Commons POIs here.
        const NAV_RADAR: Record<'planet' | 'station' | 'asteroids' | 'fortress', RadarContact['kind']> =
          { planet: 'planet', station: 'station', asteroids: 'field', fortress: 'pirate' };
        for (const n of systemNavPoints(this.system)) push(n.pos[0], n.pos[1], n.pos[2], NAV_RADAR[n.kind]);
      }
      for (const rs of this.remoteShips.values()) push(rs.obj.position.x + this.off.x, rs.obj.position.y + this.off.y, rs.obj.position.z + this.off.z, 'remote');
      const sess = this.mySession();
      if (sess) {
        const slot = entrySlot(Number(sess.originSid));
        push(slot.x, 0, slot.z, 'beacon');
      }
      hud.drawRadar(contacts, RADAR_RANGE_M);
      // Distance-scaled contact markers: a constant-angular-size diamond over
      // every combatant inside 8 km — GREEN for your side (wingmen and your
      // sworn civ's war hulls — they used to read red until the marker faded
      // inside 220 m and the green halo showed: "ships on your side glow red
      // at a certain distance", 2026-09-08), red for everything else,
      // gold/ice for the other civ's lane wings. Scale ∝ distance keeps them
      // readable at any range without swallowing the close-up silhouette
      // (they fade out inside 220 m); the hull's engine halo follows the
      // same rule so a friend is green at EVERY range.
      if (this.root) {
        if (this.contactMarkers.length === 0) {
          for (let i = 0; i < 28; i++) {
            const m = this.spriteGlow(0xff5555, 1);
            (m.material as THREE.SpriteMaterial).depthTest = false;
            m.visible = false;
            this.root.add(m);
            this.contactMarkers.push(m);
          }
        }
        let mi = 0;
        const sl2 = this.shipLocal();
        const place = (pos: THREE.Vector3, color: number): void => {
          if (mi >= this.contactMarkers.length) return;
          const d = pos.distanceTo(sl2);
          if (d > 8000 || d < 220) return;
          const m = this.contactMarkers[mi++];
          const mat = m.material as THREE.SpriteMaterial;
          mat.color.setHex(color);
          mat.opacity = 0.85;
          m.scale.setScalar(Math.min(90, Math.max(3, d * 0.016)));
          m.position.copy(pos); // dead-on the hull — a lift offset read as a wrong position
          m.visible = true;
        };
        const mySideIff: 0 | 1 | -1 = this.faction === 'aurelian' ? 0 : this.faction === 'borean' ? 1 : -1;
        for (const p of this.pirates) {
          if (p.deadUntil !== 0) continue;
          const iff = { ally: p.ally === true, side: this.sideOf(p), mySide: mySideIff };
          place(p.root.position, contactColor(iff));
          // the hull's own halo: green for a friend, else its civ/threat colour
          const want = haloColor(iff, p.haloHex ?? 0xff7a5c);
          if (want !== p.haloShown) {
            const halo = p.root.getObjectByName('engine-halo') as THREE.Sprite | undefined;
            if (halo) { (halo.material as THREE.SpriteMaterial).color.setHex(want); p.haloShown = want; }
          }
        }
        for (const w of this.wings) {
          const c = wingMarkerColor(w.spec.civ, this.faction);
          for (const sh of w.ships) if (sh && sh.visible) place(sh.position, c);
        }
        for (; mi < this.contactMarkers.length; mi++) this.contactMarkers[mi].visible = false;
      }
    }
  }

  /** The last toasts (the key census reads them — space campaign B). */
  private readonly recentToasts: string[] = [];
  private toast(text: string): void {
    this.recentToasts.push(text);
    if (this.recentToasts.length > 12) this.recentToasts.shift();
    this.hud?.toast(text);
  }

  // ── P2 combat: power routing · missiles · countermeasures · subsystems ───
  private powerMult(): { cap: number; shield: number; speed: number } {
    switch (this.power) {
      case 'weapons': return { cap: 1.6, shield: 0.5, speed: 0.85 };
      case 'shields': return { cap: 0.5, shield: 2.2, speed: 0.85 };
      case 'engines': return { cap: 0.5, shield: 0.5, speed: 1.15 };
      default: return { cap: 1, shield: 1, speed: 1 };
    }
  }

  private setPower(p: PowerPreset): void {
    if (shipEngineering.routesFor(p)) return; // space campaign G: at the engineer station 1–4 route the CAPTAIN's power (epoch-fenced)
    if (this.power === p) return;
    this.power = p;
    shipEngineering.pilotSetPower(p); // G: a crewed captain's press reclaims the routing (one writer); a solo pilot's stays local
    this.toast(`Power → ${p.toUpperCase()} (${keyHint('spacePowerWeapons')} weapons · ${keyHint('spacePowerShields')} shields · ${keyHint('spacePowerEngines')} engines · ${keyHint('spacePowerBalanced')} balanced)`);
  }

  private cycleSubTarget(): void {
    this.subTarget = this.subTarget === 'hull' ? 'engines' : this.subTarget === 'engines' ? 'weapons' : 'hull';
    this.toast(`Targeting ${this.subTarget.toUpperCase()}${this.subTarget === 'hull' ? '' : ' — hits disable it, half hull damage'}`);
    shipEngineering.aim(this.subTarget); // G: the server stores my selection — it picks a judged PvP hit's group
  }

  /** Subsystem doctrine on a landed player hit: engines/weapons go dark for
   *  SUBSYS_OUT_S at half hull damage; hull shots deal full damage. */
  private applySubsystemHit(p: Pirate, dmg: number): number {
    if (this.subTarget === 'hull') return dmg;
    const now = performance.now();
    if (this.subTarget === 'engines') p.slowUntil = now + SUBSYS_OUT_S * 1000;
    else p.jamUntil = now + SUBSYS_OUT_S * 1000;
    return dmg * 0.5;
  }

  private fireMissile(): void {
    if (!this.root || this.missileCool > 0) return;
    if (this.stats.missiles <= 0) { this.toast('No missile rack fitted — equip one at a station dock'); return; }
    if (this.missileAmmo <= 0) { this.toast(`Missile rack dry — rearm at a station (${keyHint('spaceRepair')} repair rearms)`); return; }
    if (!this.target || this.target.deadUntil > 0) { this.toast(`No target lock (${keyHint('spaceTarget')})`); return; }
    this.missileAmmo--;
    this.missileCool = MISSILE_COOL_S;
    sfx('sfx_missile', { gain: 0.55, cooldownMs: 300 });
    const fwd = this._v1.set(0, 0, -1).applyQuaternion(this.quat);
    const m = this.spriteGlow(0xaaeeff, 4.4);
    m.position.copy(this._ship).addScaledVector(fwd, 6);
    this.root.add(m);
    noteFired(m, m.position.x + this.off.x, m.position.y + this.off.y, m.position.z + this.off.z); // slice D: the intent's origin
    this.missiles.push({ sprite: m, vel: fwd.clone().multiplyScalar(MISSILE_SPEED).add(this.vel), ttl: 9, target: this.target, dumb: false, mine: true });
    this.toast(`Missile away (${this.missileAmmo} left)`);
  }

  /** A tier-3 reaver answers with its own seeker (fired from pirateAi). */
  private pirateMissile(p: Pirate): void {
    if (!this.root) return;
    const m = this.spriteGlow(0xffcc66, 4.8);
    m.position.copy(p.root.position);
    this.root.add(m);
    const dir = this._v1.copy(this._ship).sub(p.root.position).normalize();
    this.missiles.push({ sprite: m, vel: dir.clone().multiplyScalar(MISSILE_SPEED * 0.85), ttl: 11, target: 'player', dumb: false }); // its own vector — `dir` is the scratch `_v1` (the bolt's bug)
    sfx('sfx_missile_warning', { gain: 0.5, cooldownMs: 1200 });
    this.toast(`MISSILE INBOUND — ${keyHint('spaceCountermeasures')} to launch countermeasures`);
  }

  /** V — command the docked droid: an emergency hull patch on a 60 s cooldown
   *  (on top of its passive nursing). Strength scales with droid quality. */
  private droidCommand(): void {
    if (this.stats.droid <= 0) { this.toast('No droid docked — fit one at a station dock'); return; }
    const now = performance.now();
    if (now < this.droidCoolUntil) {
      this.toast(`The droid is still recharging (${Math.ceil((this.droidCoolUntil - now) / 1000)} s)`);
      return;
    }
    if (this.hull >= this.stats.hp) { this.toast('Droid: hull integrity nominal'); return; }
    // Space campaign G: V draws on the droid's FINITE field stock (never unlimited field regeneration).
    const patch = Math.round(shipEngineering.droidPatch(Math.min(Math.round(12 + 18 * this.stats.droid), this.stats.hp - this.hull)));
    if (patch <= 0) { this.toast('Droid: the field stock is spent — a safe dock restocks it'); return; }
    this.droidCoolUntil = now + 60_000;
    this.hull = Math.min(this.stats.hp, this.hull + patch);
    this.toast(`Droid: emergency patch +${patch} hull`);
  }

  private popCountermeasures(): void {
    // Space campaign I: while my sensors are jammed the countermeasure is the SENSOR BURST (every hull's sensors — no pod
    // needed); flares still answer inbound seekers below, so a press with neither threat says so as before.
    if (this.flightCombat.countermeasure() && !this.missiles.some((m) => m.target === 'player' && !m.dumb)) return;
    if (this.stats.counters <= 0) { this.toast('No countermeasure pod fitted'); return; }
    const inbound = this.missiles.filter((m) => m.target === 'player' && !m.dumb);
    if (!inbound.length) { this.toast('No inbound seekers'); return; }
    if (this.counterAmmo <= 0) { this.toast('Countermeasures spent — rearm at a station'); return; }
    this.counterAmmo--;
    for (const m of inbound) m.dumb = true;
    // Flare fan: drifting glow sprites, cleaned up by the missile loop (dumb + short ttl).
    for (let i = 0; i < 6; i++) {
      const flare = this.spriteGlow(0xffe9a0, 2.2);
      flare.position.copy(this._ship).add(this._v1.set(Math.sin(i * 1.05) * 8, Math.cos(i * 1.4) * 5, 4 + i));
      this.root?.add(flare);
      this.missiles.push({ sprite: flare, vel: new THREE.Vector3(Math.sin(i * 2.1) * 24, -14, Math.cos(i * 1.7) * 24), ttl: 1.2, target: null, dumb: true });
    }
    sfx('sfx_countermeasures', { gain: 0.45, cooldownMs: 400 });
    this.toast(`Flares out — ${inbound.length} seeker(s) spoofed (${this.counterAmmo} charges left)`);
  }

  /** Integrate + home every live missile; hits apply the subsystem doctrine. */
  private updateMissiles(dt: number): void {
    for (let i = this.missiles.length - 1; i >= 0; i--) {
      const m = this.missiles[i];
      m.ttl -= dt;
      let dead = m.ttl <= 0;
      if (!dead && !m.dumb && m.target) {
        const aim = m.target === 'player'
          ? this._v1.copy(this._ship)
          : this._v1.copy(m.target.root.position);
        if (m.target !== 'player' && m.target.deadUntil > 0) { m.dumb = true; }
        else {
          const want = this._v2.copy(aim).sub(m.sprite.position).normalize().multiplyScalar(m.vel.length());
          const maxStep = MISSILE_TURN * dt * m.vel.length();
          const delta = want.sub(m.vel);
          if (delta.length() > maxStep) delta.setLength(maxStep);
          m.vel.add(delta);
        }
      }
      m.sprite.position.addScaledVector(m.vel, dt);
      if (!dead && m.target === 'player' && !m.dumb && m.sprite.position.distanceTo(this._ship) < 9) {
        dead = true;
        sfx('sfx_ship_explosion', { gain: 0.5, rate: 1.3, cooldownMs: 400 }); // warhead crack, tighter than a hull death
        this.damagePlayer(MISSILE_DMG_PLAYER, m.vel, this.target && this.target.hp > 0 ? { kind: 'pirate', key: this.target.key } : undefined);
      } else if (!dead && m.target && m.target !== 'player' && m.sprite.position.distanceTo(m.target.root.position) < 14) {
        dead = true;
        const p = m.target;
        const mdmg = this.applySubsystemHit(p, 6 * this.stats.weaponMult);
        this.fxImpact(m.sprite.position, 12, 0xffdd88);
        if (this.denRowFor(p)) { this.denHit(p, mdmg); if (p.hp > 0) this.pirateShotAt(p, true); }
        else {
          if (m.mine) this.warEscortIntent(p, m.sprite, 'missile', 9 - m.ttl); // slice D: a pinned war's escort hit is an INTENT
          p.hp -= mdmg;
          if (p.hp <= 0) this.killPirate(p);
          else this.pirateShotAt(p, true);
        }
      }
      // A stray warhead detonating inside the war chews the walls hard — credited to the player only when it is
      // the player's (2026-09-23: a hostile seeker or a flare crossing a 1-hp capital filed a kill in your name).
      if (!dead && this.warBoltHit(m.sprite.position, 90, m.mine === true, m.sprite, 'missile', 9 - m.ttl)) {
        dead = true;
      }
      if (dead) {
        this.root?.remove(m.sprite);
        freeLater((m.sprite as THREE.Sprite).material);
        this.missiles.splice(i, 1);
      }
    }
  }

  // ── P5: walkable interiors — docking bay → corridor → promenade + deck ───
  private toggleInterior(kind: 'station' | 'deck'): void {
    if (this.interior) { this.exitInterior(); return; }
    if (this.landingNow || this.hyper === 'jump') return;
    if (kind === 'station') {
      const st = this.nearStation();
      if (!st) { this.toast(`No station to board — pull up to one (${keyHint('spaceBoard')})`); return; }
      // Boarding docks (the server mode) — the interior is a client state on top.
      const conn = atlas.conn;
      if (conn && this.myLocation()?.mode !== 'docked') {
        (conn.reducers as unknown as { dockAtStation(a: { stationKey: string }): Promise<void> })
          .dockAtStation({ stationKey: st.key }).catch(() => { /* the promenade is walkable either way */ });
      }
    } else {
      // Read the hangar truth NOW — the periodic mirror lags a fresh
      // setActiveShip (frame-cadenced; starved headless it can trail 30 s+).
      this.refreshFleet();
      if (this.chassisKey !== 'ship_sovereign') {
        this.toast(`Only the Sovereign carries a walkable deck (${keyHint('spaceDeck')})`);
        return;
      }
    }
    this.enterInterior(kind);
  }

  private enterInterior(kind: 'station' | 'deck'): void {
    if (!this.root) return;
    this.throttle = 0;
    this.vel.set(0, 0, 0);
    this.cruise = 1;
    const origin = this.shipLocal().clone();
    origin.y -= 2600; // staged far under the flight lane, inside the void shell
    const doors: Array<{ mesh: THREE.Mesh; x: number; z: number; open: number }> = [];
    let group: THREE.Group;
    let deckWindows = 0;
    if (kind === 'station') {
      group = this.buildStation(doors);
    } else {
      const built = this.buildDeck();
      group = built.group;
      deckWindows = built.windows;
    }
    group.position.copy(origin);
    this.root.add(group);
    if (kind === 'station') {
      this.interior = {
        kind, group, origin, doors,
        // Arrival theater: you step down BESIDE your parked ship (never on
        // the pad itself) facing it, then walk bay → corridor → promenade.
        wx: 76, wz: 13, yaw: 1.1, pitch: 0,
        rects: [
          { x0: -21.4, x1: 22.6, z0: -12.4, z1: 12.4 },   // promenade
          { x0: 21.4, x1: 36.6, z0: -2, z1: 2 },          // corridor
          { x0: 35.4, x1: 94.5, z0: -17.4, z1: 17.4 },    // docking bay
        ],
        terminal: { x: -16, z: 0 },
      };
      atlas.spaceInterior = true; // the ground-style menu bar applies inside (space menu bar wave 2026-08-24)
    } else {
      const furn = new THREE.Group();
      group.add(furn);
      this.interior = {
        kind, group, origin, doors,
        // Spawn mid-hold facing the bridge (forward = −x at yaw π/2).
        wx: 8, wz: 0, yaw: Math.PI / 2, pitch: 0,
        rects: [
          { x0: -12.7, x1: -4, z0: -4.7, z1: 4.7 },   // bridge
          { x0: -4, x1: 4, z0: -1.7, z1: 1.7 },       // spine
          { x0: 4, x1: 13.7, z0: -5.7, z1: 5.7 },     // main hold
        ],
        terminal: null,
        windows: deckWindows,
        shipId: this.activeShipId(),
        furn,
        furnMap: new Map(),
        furnAcc: 0,
      };
      atlas.spaceInterior = true; // the ground-style menu bar applies inside (space menu bar wave 2026-08-24)
    }
    if (this.shipMesh) this.shipMesh.visible = false;
    for (const eg of this.engineGlows) eg.visible = false;
    // First-person: pull the chase camera into the head.
    const rig = atlas.localMesh;
    const cam = rig?.children.find((c) => (c as THREE.Camera).isCamera) as THREE.PerspectiveCamera | undefined;
    if (cam) { cam.position.set(0, 0, 0.01); cam.rotation.set(0, 0, 0); }
    this.toast(kind === 'station'
      ? (sfx('sfx_ship_dock', { gain: 0.45, cooldownMs: 1000 }), `Docking clamps engaged — walk the bay through to the promenade · ${keyHint('spaceDock')} at the terminal · ${keyHint('spaceBoard')} to undock`)
      : 'Walking the Sovereign deck — WASD · E furnishes the hull · I returns to the cockpit');
    // The furnishing rows ride their own sub — ONCE per session (each
    // subscribe() stacks a live server subscription; boarding nightly
    // would quietly accumulate them).
    if (kind === 'deck' && !this.deckSubbed) {
      try {
        atlas.conn?.subscriptionBuilder().subscribe(['SELECT * FROM ship_deck_item']);
        this.deckSubbed = true;
      } catch { /* fine */ }
    }
  }

  private exitInterior(): void {
    const it = this.interior;
    if (!it) return;
    if (this.furnPanel) { window.clearInterval(this.furnTimer); this.furnPanel.remove(); this.furnPanel = null; }
    this.root?.remove(it.group);
    // Dispose ONLY interior-OWNED resources: the HD props (and the parked
    // hull) are CLONES of shared masters — their geometry backs the FLIGHT
    // ship and the prop cache, and disposing it destroys live GPU buffers
    // ("used in submit while destroyed" — the undock-blackout bug).
    it.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      const geo = m.geometry as THREE.BufferGeometry;
      if (geo?.userData?.interiorOwned) freeLater(geo);
      for (const mat of Array.isArray(m.material) ? m.material : [m.material]) {
        const mm = mat as THREE.MeshBasicMaterial | undefined;
        if (mm?.userData?.interiorOwned) { if (mm.map?.userData?.interiorOwned) freeLater(mm.map); freeLater(mm); }
      }
    });
    this.interior = null; atlas.spaceInterior = false;
    if (this.shipMesh) this.shipMesh.visible = true;
    for (const eg of this.engineGlows) eg.visible = true;
    const rig = atlas.localMesh;
    const cam = rig?.children.find((c) => (c as THREE.Camera).isCamera) as THREE.PerspectiveCamera | undefined;
    if (cam) { cam.position.set(0, 4.2, 15); cam.rotation.set(-0.07, 0, 0); }
    this.applyCockpitView(); // if the pilot was in first person, re-seat them
    sfx('sfx_ship_undock', { gain: 0.45, cooldownMs: 1000 });
    this.toast('Clamps released — back at the controls');
  }

  /** Tag a geometry/material as interior-OWNED — exitInterior disposes only
   *  tagged resources, never the shared master-GLB buffers. */
  private own<T extends THREE.BufferGeometry | THREE.Material | THREE.Texture>(res: T): T {
    res.userData.interiorOwned = true;
    return res;
  }

  /** Interior meshes render BAKED: swap lit materials for basic ones keeping
   *  the texture (the HD look survives; the sector pipeline's runtime-lit
   *  material failure can't touch it). */
  /** Register a textured material's cloned map for disposal, return the mat. */
  private ownTx(mat: THREE.MeshBasicMaterial): THREE.MeshBasicMaterial {
    if (mat.map) this.own(mat.map);
    return mat;
  }

  private bakeInteriorMaterials(obj: THREE.Object3D): void {
    obj.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const src = mesh.material as THREE.MeshStandardMaterial & { isMeshBasicMaterial?: boolean };
      if (!src || src.isMeshBasicMaterial) return;
      mesh.material = this.own(new THREE.MeshBasicMaterial({
        map: src.map ?? null,
        color: src.map ? 0xffffff : (src.color?.clone() ?? new THREE.Color(0x8a94a8)),
      }));
    });
  }

  /** Normalize a generated prop: longest dimension → `size` m, seated on y=0. */
  private seatProp(obj: THREE.Object3D, size: number): THREE.Group {
    const box = new THREE.Box3().setFromObject(obj);
    const longest = Math.max(0.001, box.max.x - box.min.x, box.max.y - box.min.y, box.max.z - box.min.z);
    const s = size / longest;
    obj.scale.setScalar(s);
    obj.position.y = -box.min.y * s;
    const g = new THREE.Group();
    g.add(obj);
    return g;
  }

  /** Swap the HD station props in as their GLBs land (primitive shell stays
   *  the fallback — the prefab self-heal law, indoors). */
  private decorateStation(
    group: THREE.Group,
    spots: Array<{ key: string; x: number; y: number; z: number; size: number; rotY: number; ph: THREE.Object3D | null }>,
  ): void {
    void (async () => {
      const cache = new Map<string, THREE.Object3D | null>();
      for (const s of spots) {
        if (!cache.has(s.key)) cache.set(s.key, await this.master(`prop:${s.key}`));
        const m = cache.get(s.key);
        if (!m) continue;                                   // placeholder stays
        if (!this.active || this.interior?.group !== group) return; // walked out mid-load
        const inst = this.seatProp(m.clone(), s.size);
        this.bakeInteriorMaterials(inst);
        inst.position.set(s.x, s.y, s.z);
        inst.rotation.y = s.rotY;
        if (s.ph) {
          group.remove(s.ph);
          // Out of the group = out of the exit sweep's reach — free the
          // placeholder's buffers here (the same leak the deck swap had).
          const phm = s.ph as THREE.Mesh;
          if (phm.isMesh) {
            freeLater(phm.geometry);
            for (const mm of Array.isArray(phm.material) ? phm.material : [phm.material]) freeLater(mm);
          }
        }
        group.add(inst);
      }
    })();
  }

  /** The station interior — three zones on one floor plan (room space, +x east):
   *  promenade x[-22,22] · corridor x[22,36] · docking bay x[36,96]. */
  private buildStation(doors: Array<{ mesh: THREE.Mesh; x: number; z: number; open: number }>): THREE.Group {
    const g = new THREE.Group();
    // BAKED look (MeshBasicMaterial): runtime-added LIT materials fail to
    // draw in the sector pipeline (memory: interior-materials law) — and a
    // sealed room reads best hand-shaded anyway.
    // PBR-baked panels (baked-lit albedo — the interior scene is unlit; the
    // seams/rivets/bevels/AO/spec are baked into the map). Walls double as the
    // ceiling slabs; floors get a coarser deck-plate tiling.
    const wall = this.own(this.ownTx(interiorMaterial('wall', 5)));
    const floorM = this.own(this.ownTx(interiorMaterial('floor', 8)));
    const glow = this.own(new THREE.MeshBasicMaterial({ color: 0x6fa9e8 }));
    const warm = this.own(new THREE.MeshBasicMaterial({ color: 0xe8b26a }));
    const viewM = this.own(new THREE.MeshBasicMaterial({ color: 0x0a1526 }));
    const add = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number): THREE.Mesh => {
      const m = new THREE.Mesh(this.own(geo), mat);
      m.position.set(x, y, z);
      g.add(m);
      return m;
    };
    const spots: Array<{ key: string; x: number; y: number; z: number; size: number; rotY: number; ph: THREE.Object3D | null }> = [];

    // ── PROMENADE (two-story social hall) ──
    add(new THREE.BoxGeometry(44, 0.4, 26), floorM, 0, -0.2, 0);
    add(new THREE.BoxGeometry(44, 0.4, 26), wall, 0, 8.7, 0);
    add(new THREE.BoxGeometry(44, 8.5, 0.4), wall, 0, 4.25, -13);      // kiosk wall
    add(new THREE.BoxGeometry(44, 8.5, 0.4), wall, 0, 4.25, 13);
    add(new THREE.BoxGeometry(0.3, 8.5, 26), viewM, -21.9, 4.25, 0);   // the viewport wall
    add(new THREE.BoxGeometry(44, 0.9, 0.2), glow, 0, 7.6, -12.8);     // high light strips
    add(new THREE.BoxGeometry(44, 0.9, 0.2), warm, 0, 3.0, -12.8);
    // Upper balcony ribbon (visual second story) + rail glow.
    add(new THREE.BoxGeometry(44, 0.3, 3.2), wall, 0, 4.4, -11.4);
    add(new THREE.BoxGeometry(44, 0.12, 0.12), glow, 0, 5.1, -9.9);
    // East wall with the doorway to the corridor (gap z ∈ [-2, 2]).
    add(new THREE.BoxGeometry(0.4, 8.5, 11), wall, 22, 4.25, -7.5);
    add(new THREE.BoxGeometry(0.4, 8.5, 11), wall, 22, 4.25, 7.5);
    add(new THREE.BoxGeometry(0.4, 5.1, 4), wall, 22, 6.2, 0);         // header
    // Kiosk row + hanging signage + street furniture (HD spots).
    for (const [i, kx] of [-12, -2, 8].entries()) {
      spots.push({ key: 'station_kiosk', x: kx, y: 0, z: -10.4, size: 3.6, rotY: 0, ph: null });
      if (i < 2) spots.push({ key: 'station_signage', x: kx + 5, y: 3.4, z: -9.6, size: 2, rotY: 0, ph: null });
    }
    spots.push({ key: 'station_bench', x: -6, y: 0, z: 4, size: 2.4, rotY: 0.4, ph: null });
    spots.push({ key: 'station_bench', x: 5, y: 0, z: 5, size: 2.4, rotY: -0.7, ph: null });
    spots.push({ key: 'station_planter', x: -14, y: 0, z: 6, size: 2.2, rotY: 0, ph: null });
    spots.push({ key: 'station_planter', x: 12, y: 0, z: -6, size: 2.2, rotY: 1.1, ph: null });
    spots.push({ key: 'station_pillar', x: -18, y: 0, z: -10, size: 8, rotY: 0, ph: null });
    spots.push({ key: 'station_pillar', x: -18, y: 0, z: 10, size: 8, rotY: 0, ph: null });
    spots.push({ key: 'station_pillar', x: 16, y: 0, z: -10, size: 8, rotY: 0, ph: null });
    spots.push({ key: 'station_pillar', x: 16, y: 0, z: 10, size: 8, rotY: 0, ph: null });
    // The dock terminal (placeholder pedestal stays functional until HD lands).
    const ped = add(new THREE.CylinderGeometry(0.5, 0.7, 1.2, 10), glow, -16, 0.6, 0);
    spots.push({ key: 'station_console', x: -16, y: 0, z: 0, size: 2.6, rotY: Math.PI / 2, ph: ped });
    // The two civ duty officers — versona holograms (Concord gold · Compact ice).
    for (const [x, c] of [[-5, 0xd8b23c], [5, 0x4fd0e8]] as Array<[number, number]>) {
      const holo = this.own(new THREE.MeshBasicMaterial({ color: c, transparent: true, opacity: 0.75 }));
      add(new THREE.CylinderGeometry(0.34, 0.44, 1.5, 10), holo, x, 0.75, -6);
      add(new THREE.SphereGeometry(0.26, 12, 10), holo, x, 1.78, -6);
      add(new THREE.CylinderGeometry(0.7, 0.85, 0.18, 12), glow, x, 0.09, -6);
    }

    // ── CORRIDOR (the compression, chamfered + ribbed) ──
    add(new THREE.BoxGeometry(14, 0.4, 5.2), floorM, 29, -0.2, 0);
    add(new THREE.BoxGeometry(14, 0.4, 5.2), wall, 29, 3.6, 0);
    add(new THREE.BoxGeometry(14, 3.4, 0.3), wall, 29, 1.7, -2.6);
    add(new THREE.BoxGeometry(14, 3.4, 0.3), wall, 29, 1.7, 2.6);
    add(new THREE.BoxGeometry(14, 0.14, 0.14), glow, 29, 2.9, -2.45);   // cable glow runs
    add(new THREE.BoxGeometry(14, 0.14, 0.14), glow, 29, 2.9, 2.45);
    // Chamfer wedges (the octagonal cross-section read).
    const cham = new THREE.Mesh(this.own(new THREE.BoxGeometry(14, 1.1, 1.1)), wall);
    cham.position.set(29, 3.1, -2.3); cham.rotation.x = Math.PI / 4; g.add(cham);
    const cham2 = new THREE.Mesh(this.own(new THREE.BoxGeometry(14, 1.1, 1.1)), wall);
    cham2.position.set(29, 3.1, 2.3); cham2.rotation.x = -Math.PI / 4; g.add(cham2);
    // The two HEAVY DOORS — slabs that roll up on approach (interiorStep).
    for (const dx of [22, 36]) {
      const slab = add(new THREE.BoxGeometry(0.7, 3.4, 4.4), glow, dx, 1.7, 0);
      doors.push({ mesh: slab, x: dx, z: 0, open: 0 });
      spots.push({ key: 'station_door', x: dx, y: 0, z: -4.6, size: 3.2, rotY: Math.PI / 2, ph: null });
    }

    // ── DOCKING BAY (arrival theater — your ship parked on the pad) ──
    add(new THREE.BoxGeometry(60, 0.4, 36), floorM, 66, -0.2, 0);
    add(new THREE.BoxGeometry(60, 0.4, 36), wall, 66, 14.2, 0);
    add(new THREE.BoxGeometry(60, 14, 0.4), wall, 66, 7, -18);
    add(new THREE.BoxGeometry(60, 14, 0.4), wall, 66, 7, 18);
    add(new THREE.BoxGeometry(0.4, 14, 36), wall, 96, 7, 0);
    add(new THREE.BoxGeometry(0.3, 10, 22), viewM, 95.7, 5.4, 0);       // the space doors
    add(new THREE.BoxGeometry(0.2, 10.4, 0.6), warm, 95.6, 5.4, -11.2); // door edge warnings
    add(new THREE.BoxGeometry(0.2, 10.4, 0.6), warm, 95.6, 5.4, 11.2);
    // West wall with the corridor doorway (gap z ∈ [-2, 2]).
    add(new THREE.BoxGeometry(0.4, 14, 16), wall, 36, 7, -10);
    add(new THREE.BoxGeometry(0.4, 14, 16), wall, 36, 7, 10);
    add(new THREE.BoxGeometry(0.4, 10.6, 4), wall, 36, 8.7, 0);
    // Landing pad ring under the parked ship + approach guide lights.
    const ring = new THREE.Mesh(this.own(new THREE.TorusGeometry(7, 0.16, 8, 40)), glow);
    ring.rotation.x = Math.PI / 2;
    ring.position.set(66, 0.08, 8);
    g.add(ring);
    for (let i = 0; i < 7; i++) {
      add(new THREE.BoxGeometry(0.5, 0.08, 0.5), warm, 40 + i * 4, 0.06, 4 + i * 0.6); // taxi lane to the pad
      add(new THREE.BoxGeometry(0.4, 0.08, 2.2), glow, 92, 0.06, -9 + i * 3);          // bay-door threshold row
    }
    // Hazard striping + alternating wall panels (the lived-in wear pass).
    const hazard = this.own(new THREE.MeshBasicMaterial({ color: 0xd9a13a }));
    const panelA = this.own(new THREE.MeshBasicMaterial({ color: 0x2c3850 }));
    add(new THREE.BoxGeometry(60, 0.06, 1), hazard, 66, 0.05, -15.5);
    add(new THREE.BoxGeometry(60, 0.06, 1), hazard, 66, 0.05, 15.5);
    for (let i = 0; i < 6; i++) {
      add(new THREE.BoxGeometry(6, 9, 0.5), panelA, 40 + i * 10, 4.5, -17.6);
      add(new THREE.BoxGeometry(6, 9, 0.5), panelA, 45 + i * 10, 4.5, 17.6);
      add(new THREE.BoxGeometry(0.5, 0.5, 34), glow, 40 + i * 10, 13.6, 0); // ceiling light rails
    }
    // YOUR ship, parked where you left it (active chassis, fighter fallback).
    const spec = CHASSIS[this.chassisKey] ?? CHASSIS[DEFAULT_CHASSIS];
    const ph = this.fallbackShip(0x4f8cf0);
    ph.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { this.own(m.geometry); this.own(m.material as THREE.Material); } });
    this.bakeInteriorMaterials(ph);
    ph.scale.setScalar(0.9);
    ph.position.set(66, 1.2, 8);
    g.add(ph);
    spots.push({ key: spec.prefab.replace('prop:', ''), x: 66, y: 0.3, z: 8, size: spec.hullSize, rotY: 0, ph });
    // Bay dressing: crate clusters + gantry cranes + wall vault-door relief.
    spots.push({ key: 'station_crates', x: 48, y: 0, z: -12, size: 2.8, rotY: 0.3, ph: null });
    spots.push({ key: 'station_crates', x: 53, y: 0, z: 13, size: 2.6, rotY: 1.2, ph: null });
    spots.push({ key: 'station_crates', x: 82, y: 0, z: -14, size: 3, rotY: 2.1, ph: null });
    spots.push({ key: 'station_crates', x: 88, y: 0, z: 10, size: 2.4, rotY: 0.8, ph: null });
    spots.push({ key: 'station_gantry', x: 44, y: 0, z: 14, size: 7, rotY: -Math.PI / 2, ph: null });
    spots.push({ key: 'station_gantry', x: 86, y: 0, z: -15, size: 7, rotY: Math.PI / 2, ph: null });
    spots.push({ key: 'station_door', x: 60, y: 0, z: -17.4, size: 9, rotY: 0, ph: null });
    spots.push({ key: 'station_pillar', x: 40, y: 0, z: -16, size: 12, rotY: 0, ph: null });
    spots.push({ key: 'station_pillar', x: 92, y: 0, z: 16, size: 12, rotY: 0, ph: null });

    this.decorateStation(g, spots);
    return g;
  }

  /** The Sovereign's deck: a snug crew cabin behind the cockpit dais. */
  /** The Sovereign deck v2 — a BASIC furnishable hull the owner fills piece
   *  by piece (E opens the furnishing palette), with real VIEWPORTS: the
   *  room sits inside the sector scene, so the glass looks out on the live
   *  starfield, sun, and planets exactly where the ship is. Three zones:
   *  bridge x[−13,−4] · spine x[−4,4] · main hold x[4,14]. Baked basic
   *  materials only (the interior-materials law). */
  private buildDeck(): { group: THREE.Group; windows: number } {
    const g = new THREE.Group();
    const hullM = this.own(this.ownTx(interiorMaterial('wall', 3)));
    const floorM = this.own(this.ownTx(interiorMaterial('floor', 4)));
    const accentM = this.own(new THREE.MeshBasicMaterial({ color: 0x1e2634 }));
    const glow = this.own(new THREE.MeshBasicMaterial({ color: 0x7db4f0 }));
    const warm = this.own(new THREE.MeshBasicMaterial({ color: 0xe8b26a }));
    const frameM = this.own(new THREE.MeshBasicMaterial({ color: 0x151b28 }));
    const glassM = this.own(new THREE.MeshBasicMaterial({
      color: 0x9fd4ff, transparent: true, opacity: 0.13, depthWrite: false, side: THREE.DoubleSide,
    }));
    const add = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number): THREE.Mesh => {
      const m = new THREE.Mesh(this.own(geo), mat);
      m.position.set(x, y, z);
      g.add(m);
      return m;
    };
    let windows = 0;
    /** A viewport: frame sill + header + glass pane in a wall opening. */
    const viewport = (x: number, y: number, z: number, w: number, h: number, alongX: boolean): void => {
      const fw = alongX ? w + 0.3 : 0.34, fd = alongX ? 0.34 : w + 0.3;
      add(new THREE.BoxGeometry(fw, 0.16, fd), frameM, x, y - h / 2 - 0.08, z);
      add(new THREE.BoxGeometry(fw, 0.16, fd), frameM, x, y + h / 2 + 0.08, z);
      const gw = alongX ? w : 0.06, gd = alongX ? 0.06 : w;
      add(new THREE.BoxGeometry(gw, h, gd), glassM, x, y, z);
      windows++;
    };
    /** A wall run with viewport OPENINGS: solid below the sill, solid above
     *  the header, solid piers between panes. */
    const H = 3.4, SILL = 0.9, HEAD = 2.7;
    const wallWithPanes = (x0: number, x1: number, z: number, panes: Array<[number, number]>): void => {
      const len = x1 - x0, cx = (x0 + x1) / 2;
      add(new THREE.BoxGeometry(len, SILL, 0.3), hullM, cx, SILL / 2, z);              // below sills
      add(new THREE.BoxGeometry(len, H - HEAD, 0.3), hullM, cx, (H + HEAD) / 2, z);   // above headers
      let cursor = x0;
      for (const [px, pw] of panes) {
        const pier = px - pw / 2 - cursor;
        if (pier > 0.05) add(new THREE.BoxGeometry(pier, HEAD - SILL, 0.3), hullM, cursor + pier / 2, (SILL + HEAD) / 2, z);
        viewport(px, (SILL + HEAD) / 2, z, pw, HEAD - SILL, true);
        cursor = px + pw / 2;
      }
      if (x1 - cursor > 0.05) add(new THREE.BoxGeometry(x1 - cursor, HEAD - SILL, 0.3), hullM, cursor + (x1 - cursor) / 2, (SILL + HEAD) / 2, z);
    };
    const wallWithPanesZ = (z0: number, z1: number, x: number, panes: Array<[number, number]>): void => {
      const len = z1 - z0, cz = (z0 + z1) / 2;
      add(new THREE.BoxGeometry(0.3, SILL, len), hullM, x, SILL / 2, cz);
      add(new THREE.BoxGeometry(0.3, H - HEAD, len), hullM, x, (H + HEAD) / 2, cz);
      let cursor = z0;
      for (const [pz, pw] of panes) {
        const pier = pz - pw / 2 - cursor;
        if (pier > 0.05) add(new THREE.BoxGeometry(0.3, HEAD - SILL, pier), hullM, x, (SILL + HEAD) / 2, cursor + pier / 2);
        viewport(x, (SILL + HEAD) / 2, pz, pw, HEAD - SILL, false);
        cursor = pz + pw / 2;
      }
      if (z1 - cursor > 0.05) add(new THREE.BoxGeometry(0.3, HEAD - SILL, z1 - cursor), hullM, x, (SILL + HEAD) / 2, cursor + (z1 - cursor) / 2);
    };

    // ── Floors + ceilings (two-tone: bridge cool, hold warm-dark) ──
    add(new THREE.BoxGeometry(9, 0.3, 10), floorM, -8.5, -0.15, 0);
    add(new THREE.BoxGeometry(8, 0.3, 4), accentM, 0, -0.15, 0);
    add(new THREE.BoxGeometry(10, 0.3, 12), floorM, 9, -0.15, 0);
    add(new THREE.BoxGeometry(9, 0.3, 10), hullM, -8.5, H + 0.15, 0);
    add(new THREE.BoxGeometry(8, 0.3, 4), hullM, 0, H + 0.15, 0);
    add(new THREE.BoxGeometry(10, 0.3, 12), hullM, 9, H + 0.15, 0);

    // ── BRIDGE (fore): wraparound viewports ──
    wallWithPanesZ(-5, 5, -13, [[-2.9, 2.4], [0, 2.4], [2.9, 2.4]]); // the front glass
    wallWithPanes(-13, -4, -5, [[-8.5, 3]]);                          // port pane
    wallWithPanes(-13, -4, 5, [[-8.5, 3]]);                           // starboard pane
    add(new THREE.BoxGeometry(3.4, 0.4, 2.6), glow, -10.5, 0.2, 0);   // command dais
    add(new THREE.BoxGeometry(9, 0.1, 0.5), glow, -8.5, H - 0.1, -4.6);
    add(new THREE.BoxGeometry(9, 0.1, 0.5), glow, -8.5, H - 0.1, 4.6);

    // ── SPINE corridor: rib frames + slim viewports ──
    add(new THREE.BoxGeometry(0.4, H, 3.2), hullM, -4, H / 2, -3.6);  // fore bulkhead cheeks
    add(new THREE.BoxGeometry(0.4, H, 3.2), hullM, -4, H / 2, 3.6);
    add(new THREE.BoxGeometry(0.4, 0.7, 10), hullM, -4, H - 0.35, 0); // fore lintel
    wallWithPanes(-4, 4, -2, [[0, 2.2]]);
    wallWithPanes(-4, 4, 2, [[0, 2.2]]);
    add(new THREE.BoxGeometry(8, 0.12, 0.4), warm, 0, H - 0.12, -1.7);
    add(new THREE.BoxGeometry(8, 0.12, 0.4), warm, 0, H - 0.12, 1.7);
    add(new THREE.BoxGeometry(0.4, H, 4.4), hullM, 4, H / 2, -4.2);   // aft bulkhead cheeks
    add(new THREE.BoxGeometry(0.4, H, 4.4), hullM, 4, H / 2, 4.2);
    add(new THREE.BoxGeometry(0.4, 0.7, 12), hullM, 4, H - 0.35, 0);  // aft lintel

    // ── MAIN HOLD (aft): the furnishable space — bare by design ──
    wallWithPanes(4, 14, -6, [[9, 2.6]]);
    wallWithPanes(4, 14, 6, [[9, 2.6]]);
    wallWithPanesZ(-6, 6, 14, [[-3, 1.6], [3, 1.6]]);                 // aft portholes
    add(new THREE.BoxGeometry(10, 0.12, 0.4), warm, 9, H - 0.12, -5.7);
    add(new THREE.BoxGeometry(10, 0.12, 0.4), warm, 9, H - 0.12, 5.7);
    // Rib columns give the cladding modules an anchor rhythm.
    for (const rx of [6.5, 9, 11.5]) {
      add(new THREE.BoxGeometry(0.35, H, 0.5), accentM, rx, H / 2, -5.75);
      add(new THREE.BoxGeometry(0.35, H, 0.5), accentM, rx, H / 2, 5.75);
    }

    // The ONLY fixed furnishing: the flight console + captain's seat on the
    // dais (everything else is player-placed — E opens the palette). Cooked
    // props swap over these placeholders; wall cladding modules dress the
    // hold when the wallset lands.
    const spots: Array<{ key: string; x: number; y: number; z: number; size: number; rotY: number; ph: THREE.Object3D | null }> = [];
    const ph1 = add(new THREE.BoxGeometry(2.6, 1, 0.8), accentM, -11.6, 0.9, 0);
    spots.push({ key: 'sovdeck_console', x: -11.6, y: 0.4, z: 0, size: 2.6, rotY: Math.PI / 2, ph: ph1 });
    const ph2 = add(new THREE.BoxGeometry(0.9, 1.2, 0.9), hullM, -10, 1, 0);
    spots.push({ key: 'sovdeck_chair', x: -10, y: 0.4, z: 0, size: 1.3, rotY: -Math.PI / 2, ph: ph2 });
    for (const [i, rx] of [5.5, 9, 12.5].entries()) {
      spots.push({ key: 'sovdeck_wallset', x: rx, y: 1.1, z: -5.6, size: 2.6, rotY: 0, ph: null });
      if (i !== 1) spots.push({ key: 'sovdeck_wallset', x: rx, y: 1.1, z: 5.6, size: 2.6, rotY: Math.PI, ph: null });
    }
    this.decorateStation(g, spots);
    return { group: g, windows };
  }

  /** The active player_ship row id (0n when flying the stock hull). */
  private activeShipId(): bigint {
    const conn = atlas.conn;
    try {
      if (conn?.identity) {
        let first: bigint = 0n;
        for (const s of conn.db.playerShip.iter()) {
          if (!s.owner.isEqual(conn.identity)) continue;
          if (s.active) return s.shipId;
          if (first === 0n) first = s.shipId;
        }
        return first;
      }
    } catch { /* rows mid-churn */ }
    return 0n;
  }

  /** Mirror the replicated ship_deck_item rows into the walkable deck —
   *  clones of cooked masters (baked, the interiors law) with primitive
   *  stand-ins until each master lands. */
  private syncDeckFurnishings(it: NonNullable<typeof this.interior>): void {
    const conn = atlas.conn;
    if (!conn || !it.furn || !it.furnMap || it.shipId === undefined) return;
    const live = new Set<bigint>();
    try {
      for (const row of conn.db.shipDeckItem.iter()) {
        if (row.shipId !== it.shipId) continue;
        live.add(row.deckItemId);
        if (it.furnMap.has(row.deckItemId)) continue;
        const holder = new THREE.Group();
        holder.position.set(row.x, 0, row.z);
        holder.rotation.y = row.yaw;
        const ph = new THREE.Mesh(
          this.own(new THREE.BoxGeometry(0.9, 0.9, 0.9)),
          this.own(new THREE.MeshBasicMaterial({ color: 0x40506a })),
        );
        ph.position.y = 0.45;
        holder.add(ph);
        it.furn.add(holder);
        it.furnMap.set(row.deckItemId, holder);
        void (async () => {
          const m = await this.master(`prop:${row.propKey}`);
          if (!m || this.interior !== it) return;
          const inst = this.seatProp(m.clone(), 1.4);
          this.bakeInteriorMaterials(inst);
          holder.remove(ph);
          // Dispose the placeholder NOW — once out of the group, the exit
          // sweep's traverse can never find it (each placement was leaking
          // a GPU buffer pair; enough furnishings read as flight lag).
          freeLater(ph.geometry);
          freeLater(ph.material as THREE.Material);
          holder.add(inst);
        })();
      }
    } catch { /* rows mid-churn */ }
    for (const [id, obj] of [...it.furnMap]) {
      if (!live.has(id)) { it.furn.remove(obj); it.furnMap.delete(id); }
    }
  }

  /** E on the deck: the furnishing palette — place catalog props where you
   *  stand, remove what you placed. The deck ships BASIC on purpose. */
  private toggleFurnish(): void {
    if (this.furnPanel) {
      window.clearInterval(this.furnTimer);
      this.furnPanel.remove();
      this.furnPanel = null;
      return;
    }
    const it = this.interior;
    if (!it || it.kind !== 'deck') return;
    const root = document.createElement('div');
    root.id = 'deck-furnish';
    root.style.cssText =
      'position:fixed;right:16px;top:64px;z-index:37;width:300px;max-height:70vh;overflow-y:auto;' +
      'background:color-mix(in srgb, var(--ax-bg) 94%, transparent);border:1px solid var(--ax-line);border-radius:var(--ax-win-radius);padding:12px;' +
      'font:12px var(--ax-font);color:var(--ax-text);pointer-events:auto';
    document.body.appendChild(root);
    this.furnPanel = root;
    const CATALOG: Array<[string, string]> = [
      ['sovdeck_chair', 'Command chair'], ['sovdeck_console', 'Console bank'],
      ['sovdeck_holotable', 'Holotable'], ['sovdeck_bunk', 'Crew bunk'],
      ['sovdeck_mess', 'Mess table'], ['sovdeck_reactor', 'Reactor column'],
      ['sovdeck_bench', 'Workbench'], ['station_bench', 'Bench'],
      ['station_crates', 'Cargo crates'], ['station_planter', 'Planter'],
      ['station_console', 'Terminal'],
    ];
    const render = (): void => {
      const conn = atlas.conn;
      const cur = this.interior;
      if (!conn || !cur || cur.kind !== 'deck') return;
      root.replaceChildren();
      const h = document.createElement('div');
      h.textContent = `FURNISH THE DECK — ${keyHint('spaceFurnish')} / Esc closes`;
      h.style.cssText = 'font:700 11px var(--ax-mono);color:var(--ax-muted);letter-spacing:1.4px;margin-bottom:6px';
      root.appendChild(h);
      const note = document.createElement('div');
      note.textContent = 'Placed where you stand, facing your view. 24 pieces max — your ship, your layout.';
      note.style.cssText = 'color:var(--ax-soft);font:10px var(--ax-font);margin-bottom:6px';
      root.appendChild(note);
      const btnCss = 'background:var(--ax-bg3);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-text);padding:3px 8px;cursor:pointer;font:11px var(--ax-mono);margin:2px';
      const grid = document.createElement('div');
      grid.style.cssText = 'display:flex;flex-wrap:wrap;gap:2px';
      for (const [key, label] of CATALOG) {
        const b = document.createElement('button');
        b.textContent = label;
        b.style.cssText = btnCss;
        b.onclick = () => {
          const sin = Math.sin(cur.yaw), cos = Math.cos(cur.yaw);
          conn.reducers.placeDeckItem({
            shipId: cur.shipId ?? 0n, propKey: key,
            x: cur.wx + -sin * 1.7, z: cur.wz + -cos * 1.7,
            yaw: cur.yaw + Math.PI,
          }).catch((e: Error) => this.toast(`Placement refused: ${e.message ?? e}`));
        };
        grid.appendChild(b);
      }
      root.appendChild(grid);
      const mine = document.createElement('div');
      mine.textContent = 'PLACED';
      mine.style.cssText = 'font:700 10px var(--ax-mono);color:var(--ax-muted);letter-spacing:1.2px;margin:8px 0 4px';
      root.appendChild(mine);
      try {
        let n = 0;
        for (const row of conn.db.shipDeckItem.iter()) {
          if (row.shipId !== cur.shipId) continue;
          n++;
          const r = document.createElement('div');
          r.style.cssText = 'display:flex;align-items:center;gap:6px;padding:1px 0';
          const d = document.createElement('span');
          d.style.flex = '1';
          d.textContent = row.propKey.replace(/^(sovdeck|station)_/, '').replace(/_/g, ' ');
          const rm = document.createElement('button');
          rm.textContent = '✕';
          rm.style.cssText = btnCss;
          rm.onclick = () => { conn.reducers.removeDeckItem({ deckItemId: row.deckItemId }).catch(() => { /* gone */ }); };
          r.append(d, rm);
          root.appendChild(r);
        }
        if (!n) {
          const d = document.createElement('div');
          d.textContent = '(bare hull — place your first piece)';
          d.style.cssText = 'color:var(--ax-muted)';
          root.appendChild(d);
        }
      } catch { /* rows mid-churn */ }
    };
    render();
    this.furnTimer = window.setInterval(render, 1000);
  }

  /** WASD walk over the rect-union floor plan (fixed tick, replaces flight). */
  private interiorStep(dt: number): void {
    const it = this.interior;
    if (!it) return;
    const speed = 4.2;
    const f = (keyHeldIn(this.keys, 'moveForward') ? 1 : 0) - (keyHeldIn(this.keys, 'moveBack') ? 1 : 0);
    const r = (keyHeldIn(this.keys, 'moveRight') ? 1 : 0) - (keyHeldIn(this.keys, 'moveLeft') ? 1 : 0);
    if (f !== 0 || r !== 0) {
      const len = Math.hypot(f, r);
      const sin = Math.sin(it.yaw), cos = Math.cos(it.yaw);
      const nx = it.wx + ((-sin * f + cos * r) / len) * speed * dt;
      const nz = it.wz + ((-cos * f - sin * r) / len) * speed * dt;
      const ok = (x: number, z: number): boolean => it.rects.some((rc) => x >= rc.x0 && x <= rc.x1 && z >= rc.z0 && z <= rc.z1);
      if (ok(nx, nz)) { it.wx = nx; it.wz = nz; }
      else if (ok(nx, it.wz)) it.wx = nx;   // wall slide
      else if (ok(it.wx, nz)) it.wz = nz;
    }
    // The heavy doors roll open as you approach, and seal behind you.
    for (const d of it.doors) {
      const near = Math.hypot(it.wx - d.x, it.wz - d.z) < 5.5;
      d.open = THREE.MathUtils.clamp(d.open + (near ? dt * 2 : -dt * 1.3), 0, 1);
      d.mesh.position.y = 1.7 + d.open * d.open * 3.9; // ease-in roll-up
    }
    // Deck furnishings mirror the replicated rows (~1 Hz on the WALL clock —
    // sim-dt accumulation starves headless and lags placements by minutes;
    // the beam-pacing law applies to every client cadence).
    if (it.kind === 'deck') {
      const now = performance.now();
      if (now - (it.furnAcc ?? 0) > 1000) {
        it.furnAcc = now;
        this.syncDeckFurnishings(it);
      }
    }
  }

  /** Catalog faces of this sector's derived planets: asked for, worn, or replaced by their pooled face (`__natural().faces`). */
  private planetFaces: { asked: string[]; loaded: string[]; fellBack: string[] } = { asked: [], loaded: [], fellBack: [] };

  // ── The GALACTIC layer: systems, the galaxy map, warp + courses ──────────
  /** A non-Commons system's sector: the star, its planets, asteroid fields
   *  with collision, pirate dens keyed onto the server bounty bands, an
   *  optional station, and — for a user-world system — THE landable world
   *  at its canonical coordinates (the server validates planetfall there). */
  private buildAltSector(rock: THREE.Object3D, station: THREE.Object3D | null, iceMat: THREE.Material): void {
    if (!this.root) return;
    const spec = systemSector(this.system);
    this.planetFaces = { asked: [], loaded: [], fellBack: [] };
    // The star: since the realism wave (2026-09-25, report §9 R3) it is the sun rig itself — an HDR disc of its
    // true angular radius asin(R/d), a corona and an eclipse-gated glare along the direction to the origin
    // (updateSun). The three LDR glow sprites that stood here are gone (they would double the disc).
    this.altStarR = spec.star.radius;
    this.starRNow = 0; // natural objects: the next system's star states its own size on its first frame
    // Planets: pooled 4K faces + identity tints (the world-planet recipe).
    const texLoader = new THREE.TextureLoader();
    for (const [pi, pl] of spec.planets.entries()) {
      const nl = this.natural?.planetLook(pi) ?? null; // natural objects: the planet's KIND decides its face, tint, air and ring
      const face = WORLD_PLANET_POOL[nl ? nl.face : pl.texIdx] ?? WORLD_PLANET_POOL[pl.texIdx];
      const tint = new THREE.Color(nl?.opts.tint ?? new THREE.Color().setHSL(pl.hue, 0.35, 0.75).getHex());
      // R4: lit from ITS star at the origin (the one sector light points star → ship, wrong for a planet across the star).
      // shader gaps R4 — a derived system's planet as a receiver; a gas-faced one may wear a ring (rings.derivedRingFor)
      const slSetA = this.spaceLight && !this.spaceUnlit() ? new EclipseSet(pl.name) : null;
      const slRingA = slSetA && this.slFlag('planetRings') && this.natural?.makeup.planets[pi]?.ringed !== false ? derivedRingFor(this.system.id, pi, pl.texIdx, { x: pl.pos[0], y: pl.pos[1], z: pl.pos[2] }) : null;
      const lookA: PlanetLook | null = this.spaceUnlit() ? null : planetMaterial({
        ...(nl ? nl.opts : { rim: new THREE.Color().setHSL(pl.hue, 0.55, 0.6).getHex(), atmosphere: 0.6, ocean: false, tint: tint.getHex() }),
        sunDir: new THREE.Vector3(-pl.pos[0], -pl.pos[1], -pl.pos[2]).normalize(),
        light: slSetA ? { set: slSetA, R: pl.radius, ring: !!slRingA } : null,
      });
      const mat = lookA ? lookA.material : new THREE.MeshStandardMaterial({ color: tint, roughness: 0.92 });
      const genA = this.actGen;
      const wearA = (t: THREE.Texture): void => {
        if (genA !== this.actGen) { t.dispose(); return; }
        t.colorSpace = THREE.SRGBColorSpace;
        t.anisotropy = 8; // distant minification speckles without it
        if (lookA) { lookA.setMap(t); this.lookTex.push(t); } // a node-held face is this activation's to free (pooled or catalog — each load is its own texture)
        else { (mat as THREE.MeshStandardMaterial).map = t; mat.needsUpdate = true; }
      };
      const poolA = (): void => { texLoader.load(`${ASSET_STORE_BASE}/${face.tex}`, wearA, undefined, () => { /* tinted sphere is legal */ }); };
      // natural objects (2026-10-08): the kind may name a CATALOG face (face_catalog.ts) — worn instead of the pooled one;
      // THE FALLBACK: if it does not load, the pooled face does (the sector never waits on or breaks over a texture)
      const nf = lookA ? nl?.faceTex ?? null : null;
      let reliefA: (() => void) | null = null; // (set below, once the mesh stands)
      if (nf && lookA) {
        this.planetFaces.asked.push(nf.name);
        texLoader.load(`${ASSET_STORE_BASE}/${nf.tex}`, (t) => { if (genA === this.actGen) this.planetFaces.loaded.push(nf.name); wearA(t); reliefA?.(); }, undefined, () => { if (genA !== this.actGen) return; this.planetFaces.fellBack.push(nf.name); lookA.wearPooled(); poolA(); });
      } else poolA();
      const mesh = new THREE.Mesh(new THREE.SphereGeometry(pl.radius, 64, 48), mat);
      if (lookA) mesh.userData.planetLook = lookA;
      // its relief (solid surfaces only): the strength rides the planet's distance LOD (updatePlanetLod), flat until then —
      // asked for only once the face itself stands (a pooled face never wears a catalog face's relief)
      if (nf?.normal && lookA) {
        const reliefKey = nf.normal;
        reliefA = (): void => {
          if (genA !== this.actGen) return;
          texLoader.load(`${ASSET_STORE_BASE}/${reliefKey}`, (t) => {
            if (genA !== this.actGen) { t.dispose(); return; }
            lookA.setNormal(t, 0); this.lookTex.push(t); // (as the face: freed with the activation)
            mesh.userData.baseBump = nf.bump;
          }, undefined, () => { /* flat is a legal fallback */ });
        };
      }
      const pg = new THREE.Group();
      pg.add(mesh);
      if (!nl || nl.shell) this.attachAtmosphere(pg, pl.radius, nl?.shell ? nl.shell.hue : pl.hue, new THREE.Vector3(-pl.pos[0], -pl.pos[1], -pl.pos[2]).normalize(), false, slSetA, nl?.shell?.extra); // lit from the star at the origin (natural objects: an airless kind wears no shell)
      if (slSetA && this.spaceLight) this.attachSpaceLight(pl.name, pl.pos, pl.radius, pg, mesh, null, slSetA, slRingA, null); // shader gaps R4
      pg.position.copy(this.local(pl.pos[0], pl.pos[1], pl.pos[2], this._v1));
      this.root.add(pg);
      this.planets.push({ mesh, spin: nl?.spin ?? 0.004, abs: new THREE.Vector3(pl.pos[0], pl.pos[1], pl.pos[2]), radius: pl.radius, name: pl.name, reach: slRingA?.rOut });
      const absA = new THREE.Vector3(pl.pos[0], pl.pos[1], pl.pos[2]);
      this.planetAnchors.push({ group: pg, abs: absA });
      this.natural?.attachPlanet(pi, { group: pg, mesh, radius: pl.radius, abs: absA, look: lookA, set: slSetA, name: pl.name }); // natural objects: its moons, tail, aurora, disc
    }
    // Asteroid fields with real collision.
    for (const [fi, f] of spec.fields.entries()) {
      const grp = { x: f.pos[0], y: f.pos[1], z: f.pos[2], reach: f.radius * 1.2 + 900, list: [] as Array<{ x: number; y: number; z: number; r: number; halfH: number }> };
      for (let i = 0; i < f.count; i++) {
        const a = (i / f.count) * Math.PI * 2 * 3.7 + fi;
        const rr = f.radius * (0.2 + ((i * 47) % 100) / 125);
        const px = f.pos[0] + Math.cos(a) * rr;
        const py = f.pos[1] + Math.sin(a * 1.7) * f.radius * 0.3;
        const pz = f.pos[2] + Math.sin(a) * rr;
        const scale = 24 + ((i * 31) % 90);
        const r = rock.clone();
        if (this.system.tier >= 3) r.traverse((o) => { if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).material = iceMat; });
        r.position.copy(this.local(px, py, pz, this._v1));
        r.scale.setScalar(scale);
        this.natural?.dressRock(fi, i, r); // natural objects: the belt's taxonomy (carbonaceous / stony / metallic / icy) and structure
        this.root.add(r);
        this.spinners.push({ obj: r, spin: 0.02 + (i % 7) * 0.004 });
        grp.list.push({ x: px, y: py, z: pz, r: scale * 1.05, halfH: 0 });
      }
      this.colliderGroups.push(grp);
    }
    // Pirate dens on the server bounty bands (kills pay + progress contracts).
    // An INCURSION multiplies the occupation and seats a warlord at den 0.
    const occupied = this.incursionHere();
    const spawnMult = occupied ? INCURSION_SPAWN_MULT : 1;
    for (const den of spec.dens) {
      for (let i = 0; i < den.count * spawnMult; i++) {
        const px = den.pos[0] + Math.cos(i * 2.4) * (900 + Math.floor(i / den.count) * 500);
        const pz = den.pos[2] + Math.sin(i * 2.4) * (900 + Math.floor(i / den.count) * 500);
        this.spawnHostile(`poi${den.poiSlot}:pirate${40 + (i % 20)}`, den.tier, px, den.pos[1], pz);
      }
    }
    if (occupied && spec.dens.length) {
      const den = spec.dens[0];
      const boss = this.spawnHostile(`poi${den.poiSlot}:pirate0`, 3, den.pos[0], den.pos[1] + 300, den.pos[2]);
      if (boss) {
        boss.hp = Math.round(boss.hp * ACE_HP_MULT);
        boss.root.scale.setScalar(1.5);
        this.incBossKey = boss.key;
      }
      this.toast(`☠ RAIDER INCURSION — an armada occupies ${this.system.name}. Break its grip (kills push it back).`);
    }
    // The optional station (dock/repair/shipyard all work — nearStation is
    // positional): a spindle + capsule collider.
    if (spec.station && station) {
      const st = station.clone();
      st.position.copy(this.local(spec.station[0], spec.station[1], spec.station[2], this._v1));
      const box = new THREE.Box3().setFromObject(st);
      const height = 700;
      st.scale.setScalar(height / Math.max(1, box.max.y - box.min.y));
      if (this.spaceFlag('spaceLitHulls')) {
        // space look O-5: the station of a derived system was a raw clone — no lit dressing, no nav lights, unlike every
        // sys0 station (placeStation). Dressed while parentless, after its place and scale (the lit swap reads its scale).
        this.dressWarHull(st, 1, true);
        const mb = new THREE.Box3().setFromObject(station);
        const mh = Math.max(1, mb.max.y - mb.min.y);
        const g = new THREE.Group(); g.add(station.clone());
        const set = navPointSet(station, g, 150, mh * 0.004, { axis: new THREE.Vector3(1, 0, 0), from: mh * 0.35, to: mh * 1.5, n: 14 });
        if (set) st.add(navMesh(set));
      }
      this.root.add(st);
      this.spinners.push({ obj: st, spin: 0.015 });
      this.colliderGroups.push({
        x: spec.station[0], y: spec.station[1], z: spec.station[2], reach: 2000,
        list: [{ x: spec.station[0], y: spec.station[1], z: spec.station[2], r: height * 0.17, halfH: height * 0.45 }],
      });
      // Register it as a virtual station POI? nearStation walks POIS — alt
      // systems piggyback a synthetic entry via altStation below.
      this.altStation = { x: spec.station[0], y: spec.station[1], z: spec.station[2], name: `${this.system.name} Waypoint` };
    } else {
      this.altStation = null;
    }
    // A user world's system carries THE world at its canonical coordinates.
    if (this.system.worldId) {
      try {
        const wid = BigInt(this.system.worldId);
        const row = this.eligibleWorldRows().find((w) => w.worldId === wid);
        if (row) this.buildWorldPlanet(wid, row.name);
      } catch { /* world rows mid-churn */ }
    }
  }

  /** Alt-system synthetic station (nearStation/nearLanding read it). */
  private altStation: { x: number; y: number; z: number; name: string } | null = null;

  private galaxySystems(): StarSystem[] {
    const out = [...prebuiltSystems()];
    try {
      for (const w of this.eligibleWorldRows()) out.push(userSystem(w.worldId.toString(), w.name));
    } catch { /* rows mid-churn */ }
    return out;
  }

  private toggleGalaxyMap(): void {
    if (this.galaxyMap) { this.galaxyMap.unmount(); this.galaxyMap = null; return; }
    if (!this.active) return;
    this.map?.unmount();
    this.map = null;
    this.galaxyMap = new GalaxyMap(
      (id) => { this.warpToSystem(id); this.toggleGalaxyMap(); },
      (id) => { this.setCourse(id); },
      () => { this.clearCourse(); this.pushGalaxyData(); },
      () => this.toggleGalaxyMap(),
    );
    this.galaxyMap.mount();
    this.pushGalaxyData();
  }

  private pushGalaxyData(): void {
    if (!this.galaxyMap) return;
    const systems = this.galaxySystems();
    // Space Wave 5 (THE INTEL LAW): the chart shows what has been OBSERVED — the newest observation per system
    // among my own and my civ's — with its age; the system I am in is live; nothing else leaks.
    const control: Record<string, 'aurelian' | 'borean' | 'commons'> = {};
    const intelAge: Record<string, 'fresh' | 'stale'> = {};
    const intelSeenS: Record<string, number> = {};
    const incursions: string[] = [];
    const nowUs = BigInt(Date.now()) * 1000n;
    const view = this.intelView();
    for (const [sysId, row] of view) {
      const fresh = intelFreshness(row.observedAt.microsSinceUnixEpoch, nowUs);
      if (fresh === 'gone') continue;
      const pay = parseIntel(row.payload);
      control[sysId] = pay.control;
      intelAge[sysId] = fresh;
      intelSeenS[sysId] = Number(nowUs - row.observedAt.microsSinceUnixEpoch) / 1_000_000;
      if (pay.incursionGrip !== null && pay.incursionGrip > 0) incursions.push(sysId);
    }
    control[this.system.id] = this.controllerOf(this.system.id);
    intelAge[this.system.id] = 'fresh';
    intelSeenS[this.system.id] = 0;
    if (this.incursionHere() && this.incursionInfluence() > 0 && !incursions.includes(this.system.id)) incursions.push(this.system.id);
    const known = (id: string): boolean => id === this.system.id || intelAge[id] !== undefined;
    const eph = scanEpoch(Date.now());
    this.galaxyMap.update({
      systems,
      currentId: this.system.id,
      course: this.course ? { targetId: this.course.targetId, etaS: Math.max(0, (this.course.arriveAt - Date.now()) / 1000) } : null,
      control,
      incursions,
      wormholes: wormholesOf(eph).filter((w) => known(w.a) || known(w.b)).map((w) => ({ a: w.a, b: w.b })),
      intelAge,
      intelSeenS,
    });
  }

  /** The warp-tunnel enclosure: two counter-rotating tapered streak tubes
   *  around the camera axis + a pulsing far-mouth glow — the classic
   *  cylinder-with-scrolling-UVs technique, riding the ship every tick.
   *  Everything built here is procedural and registered in `own` (the
   *  release-closure law: we free only what we made). */
  private buildWarpTunnel(own: Array<{ dispose(): void }>): {
    tunnel: THREE.Group;
    tubes: Array<{ m: THREE.Mesh; tex: THREE.Texture; spin: number; rush: number }>;
    mouth: THREE.Sprite;
  } {
    const tunnel = new THREE.Group();
    const c = document.createElement('canvas');
    c.width = 256; c.height = 256;
    const g = c.getContext('2d')!;
    g.clearRect(0, 0, 256, 256);
    for (let i = 0; i < 56; i++) {
      const x = (i * 61 + ((i * i * 37) % 23)) % 256;
      const y0 = (i * 149) % 256;
      const len = 46 + ((i * 97) % 170);
      const wdt = 1 + (i % 3);
      g.fillStyle = `rgba(255,255,255,${(0.1 + ((i * 13) % 12) / 34).toFixed(2)})`;
      g.fillRect(x, y0, wdt, len);
      if (y0 + len > 256) g.fillRect(x, y0 - 256, wdt, len); // seamless vertical wrap
    }
    const mkTube = (radius: number, len: number, repU: number, repV: number, opacity: number, spin: number, rush: number): { m: THREE.Mesh; tex: THREE.Texture; spin: number; rush: number } => {
      const tex = new THREE.CanvasTexture(c);
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(repU, repV);
      const geo = new THREE.CylinderGeometry(radius, radius * 0.5, len, 24, 1, true);
      geo.rotateX(Math.PI / 2);        // axis → Z; the tapered mouth converges ahead (−Z)
      geo.translate(0, 0, -len * 0.3);
      const mat = markMrtOverlay(new THREE.MeshBasicMaterial({
        map: tex, transparent: true, opacity, blending: THREE.AdditiveBlending,
        side: THREE.BackSide, depthWrite: false, color: 0x9fd0ff,
      }));
      const m = new THREE.Mesh(geo, mat);
      m.frustumCulled = false;
      own.push(tex, geo, mat);
      tunnel.add(m);
      return { m, tex, spin, rush };
    };
    const tubes = [mkTube(85, 2600, 5, 2, 0.85, 0.14, 2.4), mkTube(135, 3600, 9, 3, 0.45, -0.08, 1.5)];
    // Realism wave R13 (the cheap half): the streaks burn HDR (they bloom) with a CHROMATIC split — the inner tube
    // blue-white, the outer violet — and the mouth is a white-hot HDR disc, not an LDR glow.
    if (!this.spaceUnlit()) {
      (tubes[0].m.material as THREE.MeshBasicMaterial).color.setRGB(1.3, 2.1, 3.8);
      (tubes[1].m.material as THREE.MeshBasicMaterial).color.setRGB(2.4, 1.1, 3.4);
    }
    const mouth = this.spriteGlow(0xffffff, 420);
    if (!this.spaceUnlit()) (mouth.material as THREE.SpriteMaterial).color.setRGB(9, 9, 11);
    mouth.position.set(0, 0, -1400);
    own.push(mouth.material);
    tunnel.add(mouth);
    this.root?.add(tunnel);
    return { tunnel, tubes, mouth };
  }

  /** HYPER-WARP: a 30 s crossing INSIDE a visible warp tunnel, then the
   *  sector rebuilds as the target system. */
  /** THE TRAVEL LAW: the server owns the crossing. We REQUEST a hyper order
   *  (refused while docked, in transit or tackled), build the tunnel only on
   *  its acceptance, and at the deadline ask the server to land us. */
  private warpToSystem(id: string): void {
    if (this.sysJump || id === this.system.id) return;
    const target = this.galaxySystems().find((sy) => sy.id === id);
    if (!target) { this.toast('Uncharted system'); return; }
    const conn = atlas.conn;
    if (!conn?.identity) return;
    const requestId = `h${Date.now().toString(36)}`;
    (conn.reducers as unknown as { requestTravel(a: { toSystem: string; mode: string; requestId: string }): Promise<void> })
      .requestTravel({ toSystem: id, mode: 'hyper', requestId })
      .then(() => {
        if (this.sysJump || !this.active) return;
        let travelId: bigint | null = null;
        try {
          for (const o of conn.db.travelOrder.iter()) {
            if (o.identity.isEqual(conn.identity!) && o.state === 'transit' && o.mode === 'hyper') { travelId = o.travelId; break; }
          }
        } catch { /* mid-churn */ }
        this.travelId = travelId;
        this.dropHyper('');
        this.vel.set(0, 0, 0);
        this.throttle = 0;
        const streaks: THREE.Sprite[] = [];
        if (this.root) {
          for (let i = 0; i < 40; i++) {
            const st = this.spriteGlow(0x9fd0ff, 1.2 + (i % 5));
            st.position.copy(this._ship).add(this._v1.set((Math.sin(i * 2.7) * 60), (Math.cos(i * 1.9) * 40), -120 - i * 36).applyQuaternion(this.quat));
            this.root.add(st);
            streaks.push(st);
          }
        }
        const own: Array<{ dispose(): void }> = [];
        const { tunnel, tubes, mouth } = this.buildWarpTunnel(own);
        this.sysJump = { target, until: performance.now() + SYSJUMP_S * 1000, streaks, tunnel, tubes, mouth, own, completing: false };
        sfx('sfx_warp_enter', { gain: 0.7, cooldownMs: 2000 });
        this.toast(`Warp tunnel opening — ${target.name} (${SYSJUMP_S} s crossing)`);
      })
      .catch((e: Error) => this.toast(`⚡ ${String(e.message ?? e)}`));
  }

  private tickSysJump(): boolean {
    const j = this.sysJump;
    if (!j) return false;
    // The tunnel owns the ship: streaks rush past, the hull rides the rails.
    const fwd = this._v1.set(0, 0, -1).applyQuaternion(this.quat);
    this.pos.addScaledVector(fwd, HYPER_SPEED * 2 * (1 / 60));
    for (const st of j.streaks) {
      st.position.addScaledVector(fwd, -(HYPER_SPEED * 2.4) * (1 / 60) * (0.4 + Math.random() * 0.2));
      const rel = this._v2.copy(st.position).sub(this._ship);
      if (rel.dot(fwd) < -1200 || rel.lengthSq() > 4_000_000) {
        st.position.copy(this._ship).add(this._v3.set(Math.sin(Math.random() * 6.28) * 70, Math.cos(Math.random() * 6.28) * 50, -900).applyQuaternion(this.quat));
      }
    }
    // The enclosure rides the ship; streak UVs rush aft while the tubes
    // counter-rotate, and the palette crossfades current star → target star.
    j.tunnel.position.copy(this._ship);
    j.tunnel.quaternion.copy(this.quat);
    const prog = 1 - Math.max(0, j.until - performance.now()) / (SYSJUMP_S * 1000);
    const col = this._c1.setHex(this.system.starColor)
      .lerp(this._c2.setHex(j.target.starColor), prog)
      .lerp(this._c2.setRGB(1, 1, 1), 0.35);
    for (const tb of j.tubes) {
      tb.tex.offset.y -= tb.rush / 60;
      tb.tex.offset.x += (tb.spin * this.fxScale.tunnelSpin) / 60; // space campaign L: the tubes' counter-ROTATION is a full-field roll — reduced motion stills it
      (tb.m.material as THREE.MeshBasicMaterial).color.copy(col);
    }
    j.mouth.scale.setScalar(390 + Math.sin(performance.now() / 130) * 50);
    (j.mouth.material as THREE.SpriteMaterial).color.copy(col);
    if (performance.now() >= j.until && !j.completing) {
      const finish = (): void => {
        for (const st of j.streaks) { this.root?.remove(st); this.disposeLater(st.material as THREE.SpriteMaterial); }
        this.root?.remove(j.tunnel);
        for (const d of j.own) this.disposeLater(d);
        this.flushDisposeSoon();
        this.sysJump = null;
        this.travelId = null;
      };
      const conn = atlas.conn;
      const tid = this.travelId;
      if (!conn || tid === null) { const target = j.target; finish(); this.switchSystem(target); return true; }
      // THE TRAVEL LAW: the SERVER lands us (position → the arrival point,
      // the location row → the new system); the sector rebuilds after.
      j.completing = true;
      (conn.reducers as unknown as { completeTravel(a: { travelId: bigint }): Promise<void> })
        .completeTravel({ travelId: tid })
        .then(() => { const target = j.target; finish(); this.switchSystem(target); })
        .catch((e: Error) => {
          const why = String(e.message ?? e);
          if (/Still in transit/.test(why)) {
            // Our clock ran ahead of the server's — ride the tunnel a little longer.
            j.completing = false;
            j.until = performance.now() + 700;
            return;
          }
          // A refusal that is not a verdict (a server fault: the order is still in transit and the sweep lands it within
          // HYPER_GRACE_S) used to drop the pilot out of the tunnel tens of km from the server's row — every position write
          // refused until the sweep's resync, 1,900 of them in one measured arrival. Keep the tunnel and ask again.
          j.giveUpAt ??= performance.now() + ARRIVAL_RETRY_FOR_MS;
          if (!/No such transit/.test(why) && performance.now() < j.giveUpAt) {
            if (!j.retrying) { j.retrying = true; this.toast('Arrival delayed — holding the tunnel'); }
            j.completing = false;
            j.retryMs = Math.min(ARRIVAL_RETRY_MAX_MS, (j.retryMs ?? ARRIVAL_RETRY_MS / 2) * 2); // 3, 6, 12, 15 … s
            j.until = performance.now() + j.retryMs;
            return;
          }
          finish();
          if (!j.retrying) this.toast(`⚡ ${why}`); // after a held tunnel the watcher's own "resynced" line says where we are
          else this.seatAtServerRow(); // the rails flew the ship on while the tunnel was held
          this.locSeq = -1; // the watcher resyncs us to the row
        });
    }
    return true;
  }

  /** Put the ship where the SERVER has it. A tunnel that ends without an arrival leaves the hull where the rails flew
   *  it — far from the row, every position write refused (the stream's jump cap) until something else moves the row. */
  private seatAtServerRow(): void {
    const conn = atlas.conn;
    const ps = conn?.identity ? conn.db.playerState.identity.find(conn.identity) : null;
    if (!ps || !Number.isFinite(ps.posX) || !Number.isFinite(ps.posY) || !Number.isFinite(ps.posZ)) return;
    this.pos.set(ps.posX, ps.posY, ps.posZ);
    this.vel.set(0, 0, 0);
    this.throttle = 0;
  }

  /** THE LOCATION LAW's watcher: the server moved us (a sweep-completed
   *  course, a debug teleport, a reconnect mid-transit) → follow the row.
   *  Also the safety undock: a docked mode with no dock window and no
   *  interior open is a stale mode (the pilot flew off) — clear it. */
  private tickLocation(): void {
    const conn = atlas.conn;
    if (!conn?.identity || this.sysJump || this.landingNow) return;
    const loc = this.myLocation();
    if (!loc) return;
    if (loc.seq !== this.locSeq) {
      this.locSeq = loc.seq;
      if (loc.systemId !== this.system.id) {
        const sy = this.galaxySystems().find((s) => s.id === loc.systemId);
        if (sy) { this.toast(`Navigation resynced — ${sy.name}`); this.switchSystem(sy); return; }
      }
    }
    if (loc.mode === 'docked' && !this.dock && !this.interior && performance.now() > this.undockRetryAt) {
      this.undockRetryAt = performance.now() + 3000;
      (conn.reducers as unknown as { undock(a: Record<string, never>): Promise<void> }).undock({}).catch(() => { /* already flying */ });
    }
  }

  /** Tear the sector down and rebuild it as `target` (the client is the
   *  system authority — the server sector is system-blind; the position
   *  stream walks the row across at the 40 km/write cap). */
  private switchSystem(target: StarSystem): void {
    this.presencePingAt = 0; // the local roster learns the move at once
    sfx('sfx_warp_exit', { gain: 0.5, cooldownMs: 1500 }); // arrival in the new sector
    this.systemSwapping = true; // in-space swap: the space ENV (fog null, lights hidden) persists
    this.deactivate();
    this.system = target;
    void this.activate(); // the env-sensitive prefix runs synchronously
    this.systemSwapping = false;
    const ctl = this.controllerOf(target.id);
    const ctlBit = ctl === 'commons' ? '' : ` · ${ctl === 'aurelian' ? 'Aurelian Concord' : 'Borean Compact'} space`;
    const starBit = this.natural ? ` · ${this.natural.starLabel}` : ''; // natural objects: say what kind of sun this is
    this.toast(`Arrived — ${target.name}${starBit}${target.tier > 1 ? ` · danger ${'☠'.repeat(target.tier)}` : ''}${ctlBit}`);
  }

  /** THE TRAVEL LAW: a course is a `course` travel_order the server owns —
   *  set it, abort it, and let the sweep (or our own completion once the
   *  ETA passes) land us; the location watcher rebuilds the sector. */
  private setCourse(id: string): void {
    const target = this.galaxySystems().find((sy) => sy.id === id);
    const conn = atlas.conn;
    if (!target || id === this.system.id || !conn) return;
    const etaS = galacticDistance(this.system, target) * COURSE_S_PER_LH;
    (conn.reducers as unknown as { requestTravel(a: { toSystem: string; mode: string; requestId: string }): Promise<void> })
      .requestTravel({ toSystem: id, mode: 'course', requestId: `c${Date.now().toString(36)}` })
      .then(() => {
        this.pushGalaxyData();
        this.toast(`Course set — ${target.name}, ${(etaS / 86400).toFixed(1)} days at sublight (it runs while you're away)`);
      })
      .catch((e: Error) => this.toast(`⛵ ${String(e.message ?? e)}`));
  }

  private clearCourse(): void {
    const c = this.course;
    const conn = atlas.conn;
    if (!c || !conn) return;
    (conn.reducers as unknown as { abortTravel(a: { travelId: bigint }): Promise<void> })
      .abortTravel({ travelId: c.travelId })
      .then(() => this.pushGalaxyData())
      .catch((e: Error) => this.toast(`⛵ ${String(e.message ?? e)}`));
  }

  private tickCourse(): void {
    const c = this.course;
    if (!c || this.courseCompleting || performance.now() < this.courseRetryAt) return;
    if (Date.now() < c.arriveAt) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.courseCompleting = true;
    (conn.reducers as unknown as { completeTravel(a: { travelId: bigint }): Promise<void> })
      .completeTravel({ travelId: c.travelId })
      .then(() => { this.courseCompleting = false; this.toast('Sublight crossing complete'); })
      .catch(() => { this.courseCompleting = false; this.courseRetryAt = performance.now() + 2000; });
  }

  // ── P4: faction fleet WARS (shared wall clock; the lines warp in) ────────
  /** The current battle window, or null. Deterministic for every client.
   *  A dev/probe force anchors a synthetic window so harnesses never wait
   *  out the 20-min wall-clock cycle. */
  private battleWindow(): { cyc: number; poi: PoiSpec; t: number } | null {
    const forts = warForts();
    if (!forts.length) return null;
    if (this.battleForce > 0) {
      // Space campaign slice D: a probe's PINNED sandbox (`__warContract.override(id)`) is drawn on ITS cycle and
      // opening — the seats are then the server's, so a validated hit lands where the record looks for it.
      const ov = campaignOverride();
      if (ov !== null && warPinned(ov)) {
        let row: CampaignRow | null = null;
        try { row = (atlas.conn?.db.spaceCampaign.campaignId.find(ov) as CampaignRow | undefined) ?? null; } catch { /* pre-publish */ }
        if (row) {
          const cyc = Number(row.cyc);
          const tw = warTimeS(cyc, Number(row.openedAt.microsSinceUnixEpoch) / 1e6, Date.now() / 1000);
          return { cyc, poi: forts[cyc % forts.length], t: Math.max(0, Math.min(tw, BATTLE_LEN_S - 1)) };
        }
      }
      const t = Math.min(Date.now() / 1000 - this.battleForce, BATTLE_LEN_S - 1);
      return { cyc: 424242, poi: forts[0], t: Math.max(0, t) };
    }
    const now = Date.now() / 1000;
    const cyc = Math.floor(now / BATTLE_PERIOD_S);
    const t = now - cyc * BATTLE_PERIOD_S;
    if (t > BATTLE_LEN_S) return null;
    if (this.ceasefireStands(cyc)) return null; // Space Wave 7 (THE TREATY LAW): no war under a standing ceasefire
    return { cyc, poi: forts[cyc % forts.length], t };
  }

  /** A ceasefire that covers this war cycle (the server's civ_treaty rows). */
  private ceasefireStands(cyc: number): boolean {
    try { for (const tr of atlas.conn!.db.civTreaty.iter()) if (tr.state === 'standing' && Number(tr.fromCyc) <= cyc && Number(tr.untilCyc) > cyc) return true; } catch { /* pre-publish */ }
    return false;
  }
  /** The ceasefire's end, for the HUD. */
  private ceasefireUntilCyc(cyc: number): number | null {
    try { for (const tr of atlas.conn!.db.civTreaty.iter()) if (tr.state === 'standing' && Number(tr.fromCyc) <= cyc && Number(tr.untilCyc) > cyc) return Number(tr.untilCyc); } catch { /* pre-publish */ }
    return null;
  }

  /** THE FINALE CONTRACT: planned once at te = 0 from the OUTCOME — the server row's retreat (else its living
   *  counts; the local picture on a forced window). The loser's capitals jump from END_WARP_FROM_S (+ up to 8 s,
   *  hashed per hull), the winner's from END_WARP_WINNER_S; a draw sends both from END_WARP_FROM_S (+ up to 10 s).
   *  Each skirmisher follows its side (+ up to 9 s). NOTHING is destroyed here: the finale never contradicts the
   *  server's survivors. */
  private planBattleEnd(b: NonNullable<typeof this.battle>, win: { cyc: number; t: number }): void {
    b.endPlanned = true;
    b.endOutcome = this.finaleOutcome(b);
    const loser = b.endOutcome;
    for (const side of [0, 1] as const) {
      const base = loser < 0 ? END_WARP_FROM_S : side === loser ? END_WARP_FROM_S : END_WARP_WINNER_S;
      const spread = loser < 0 ? 10 : 8;
      for (let k = 0; k < BATTLE_CAPS; k++) b.capLeave[side][k] = base + warH(`l${win.cyc}s${side}k${k}`) * spread;
    }
    for (const p of this.pirates) {
      if (p.ally || p.warSide === undefined || !p.key.startsWith('war')) continue;
      const base = loser < 0 ? END_WARP_FROM_S : p.warSide === loser ? END_WARP_FROM_S : END_WARP_WINNER_S;
      b.skLeave.set(p.key, base + warH(p.key + 'leave') * 9);
    }
    const name = (sd: number): string => (sd === 0 ? 'Concord' : 'Compact');
    this.toast(loser < 0
      ? '⚔ The fleets disengage — neither line yields; both warp out'
      : `⚔ The ${name(loser)} breaks off — its line warps out first; the ${name(1 - loser)} holds the field, then withdraws`);
  }

  /** The finale's loser: the row's retreating side; else the side with fewer capitals (the row's count, or the local
   *  picture on a forced window); −1 when equal. */
  private finaleOutcome(b: NonNullable<typeof this.battle>): number {
    const camp = this.campaignRow();
    if (camp) {
      if (camp.state === 'retreat' && camp.retreatSide >= 0) return camp.retreatSide;
      const [a, bb] = this.campaignSides(camp.campaignId);
      if (a && bb) return a.capsAlive === bb.capsAlive ? -1 : a.capsAlive < bb.capsAlive ? 0 : 1;
    }
    const living = (sd: 0 | 1): number => { let n = 0; for (let k = 0; k < b.capN[sd]; k++) if (!b.capDead[sd][k] && !b.capGone[sd][k]) n++; return n; };
    const l0 = living(0), l1 = living(1);
    return l0 === l1 ? -1 : l0 < l1 ? 0 : 1;
  }

  private teardownBattle(): void {
    const b = this.battle;
    if (!b) return;
    for (const tp of this.torpedoes) { this.root?.remove(tp.s); this.disposeLater(tp.s.material); }
    this.torpedoes = [];
    this.despawnWingmen(true); // wingmen AND skirmishers leave with the battle
    this.allyRespawnAt = 0;
    this.root?.remove(b.group);
    this.colliderGroups = this.colliderGroups.filter((g) => g !== b.warCols);
    for (const x of b.flashes) this.disposeLater(x.s.material);
    for (const d of b.own) this.disposeLater(d); // procedural geos/mats/glow mats — ours, never shared masters
    // item 7: the breakups' roots were in the battle group (removed above); their dissolve clones are ours
    for (const bk of this.breakups) for (const c of bk.chunks) for (const m of c.mats) this.disposeLater(m);
    this.breakups = [];
    this.battle = null;
    // Teardown also fires MID-PLAY (cycle end, cooked-model swap) — the
    // deferred flush guards the live scene's in-flight frames there too.
    this.flushDisposeSoon();
  }

  /** The battle line's stand-off from the center — the walls slowly CLOSE
   *  over the war (16 km → ~13.5 km), which reads as a real engagement. */
  private warLineD(t: number): number {
    return warLineDOf(t); // space campaign slice D: the shared formula (the server derives the same seats)
  }

  /** A capital's ABSOLUTE seat at war-time t (wall drift + sway) — `warCapAnchor`, the ONE formula the server judges
   *  validated hits against (verify:war-authority § SEAT pins it bit for bit against this method's old arithmetic). */
  private warCapPos(side: number, p: WarCap, t: number, out: THREE.Vector3): THREE.Vector3 {
    const f = this.battle!.frame;
    warCapAnchor(f, f.c, side, p, t, out);
    return out;
  }



  // ── COMBAT FX ────────────────────────────────────────────────────────────
  /** Shockwave ring texture (cached): transparent core, bright ring, soft
   *  falloff — the classic capital-death expanding ring. */
  private ringTexture(): THREE.CanvasTexture {
    if (this.ringTex) return this.ringTex;
    const c = document.createElement('canvas');
    c.width = c.height = 128;
    const g = c.getContext('2d')!;
    const grad = g.createRadialGradient(64, 64, 34, 64, 64, 63);
    grad.addColorStop(0, 'rgba(255,255,255,0)');
    grad.addColorStop(0.55, 'rgba(255,255,255,0.9)');
    grad.addColorStop(0.75, 'rgba(255,255,255,0.35)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, 128, 128);
    this.ringTex = new THREE.CanvasTexture(c);
    this.ringTex.userData.shared = true; // session cache — never swept
    return this.ringTex;
  }

  private fxSprite(color: number, size: number, ring = false): THREE.Sprite {
    const sp = new THREE.Sprite(markMrtOverlay(new THREE.SpriteMaterial({
      map: ring ? this.ringTexture() : this.glowTexture(),
      color, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true,
    })));
    sp.scale.setScalar(size);
    return sp;
  }

  /** UNIVERSAL projectile-impact burst at a LOCAL position: a hot core
   *  flash that peaks instantly, sparks thrown from the point, and a brief
   *  smoke puff at projectile scale (the researched anatomy). */
  private fxImpact(local: THREE.Vector3, scale: number, tint: number): void {
    const root = this.root;
    // Realism wave R5 (vacuum rules, batched): a white flash, sparks, a hot ember — ONE instanced batch, no
    // per-sprite material, no smoke puff (there is no air to hold it). The legacy sprites below stand only
    // under `atlas:space-unlit`.
    if (root && this.fx && !this.spaceUnlit()) {
      this.fxStats.impacts++;
      this.fx.impact(local.x + this.off.x, local.y + this.off.y, local.z + this.off.z, scale, tint);
      return;
    }
    if (!root || this.fxLive.length > 140) return;
    this.fxStats.impacts++;
    const flash = this.fxSprite(0xffffff, scale * 2.2);
    flash.position.copy(local);
    root.add(flash);
    this.fxLive.push({ s: flash, vel: null, ttl: 0.09, life: 0.09, grow: scale * 2.8, from: scale * 2.2 });
    const nSparks = Math.min(10, 4 + Math.round(scale / 6));
    for (let i = 0; i < nSparks; i++) {
      const sp = this.fxSprite(tint, scale * 0.34);
      sp.position.copy(local);
      root.add(sp);
      const v = new THREE.Vector3((Math.random() - 0.5), (Math.random() - 0.5), (Math.random() - 0.5))
        .normalize().multiplyScalar(scale * (8 + Math.random() * 14));
      this.fxLive.push({ s: sp, vel: v, ttl: 0.34 + Math.random() * 0.2, life: 0.5, grow: scale * 0.06, from: scale * 0.34 });
    }
    const smoke = this.fxSprite(0x33302c, scale * 1.1);
    (smoke.material as THREE.SpriteMaterial).blending = THREE.NormalBlending;
    (smoke.material as THREE.SpriteMaterial).opacity = 0.5;
    smoke.position.copy(local);
    root.add(smoke);
    this.fxLive.push({ s: smoke, vel: null, ttl: 0.8, life: 0.8, grow: scale * 3.2, from: scale * 1.1 });
  }

  /** A ship DIES: secondary detonations walk the hull, then the core goes —
   *  a major central blast with an expanding SHOCKWAVE RING and lingering
   *  fireballs, every element scaled to the hull length. */
  private fxShipDeath(local: THREE.Vector3, lenM: number, tint: number): void {
    const root = this.root;
    if (!root) return;
    this.fxStats.deaths++;
    // The boom rides the VFX: gain falls with distance, size deepens the pitch.
    const dKm = local.distanceTo(this._ship) / 1000;
    this.sfxAtLocal('sfx_ship_explosion', local, {
      gain: Math.min(0.75, 0.85 / (1 + dKm * 0.9)),
      rate: lenM > 200 ? 0.72 : 1,
      cooldownMs: 220,
      caption: 'Explosion',
    });
    this.addTrauma(blastTrauma(local.distanceTo(this._ship), lenM)); // R7: a near blast shakes the camera
    const now = performance.now();
    const secondaries = Math.max(2, Math.min(14, Math.round(lenM / 320)));
    for (let i = 0; i < secondaries; i++) {
      const at = now + i * (lenM > 1000 ? 160 : 60);
      const off = new THREE.Vector3(
        (Math.random() - 0.5) * lenM * 0.35,
        (Math.random() - 0.5) * lenM * 0.12,
        (Math.random() - 0.5) * lenM * 0.7,
      );
      if (this.fx && !this.spaceUnlit()) {
        // Realism wave R5: queued in ABSOLUTE coordinates (a rebase between the death and the blast never shifts it).
        const ax = local.x + this.off.x + off.x, ay = local.y + this.off.y + off.y, az = local.z + this.off.z + off.z;
        this.fxQueue.push({ at, fn: () => { this.fxStats.impacts++; this.fx?.impact(ax, ay, az, Math.max(8, lenM / 26), tint); } });
      } else this.fxQueue.push({ at, fn: () => this.fxImpact(this._v1.copy(local).add(off), Math.max(8, lenM / 26), tint) });
    }
    if (this.fx && !this.spaceUnlit()) {
      // The core, under vacuum rules: a 30+ flash + a pooled light, fireballs that cool (no smoke), the PLANAR
      // shock in the hull's plane, velocity-stretched shrapnel (lib/space/space_fx.ts).
      const cx = local.x + this.off.x, cy = local.y + this.off.y, cz = local.z + this.off.z;
      this.fxQueue.push({ at: now + secondaries * (lenM > 1000 ? 160 : 60) + 120, fn: () => this.fx?.detonate(cx, cy, cz, lenM, tint, UP) });
      return;
    }
    const coreAt = now + secondaries * (lenM > 1000 ? 160 : 60) + 120;
    this.fxQueue.push({
      at: coreAt,
      fn: () => {
        const r = this.root;
        if (!r) return;
        // The core flash.
        const core = this.fxSprite(0xffffff, lenM * 0.8);
        core.position.copy(local);
        r.add(core);
        this.fxLive.push({ s: core, vel: null, ttl: 0.22, life: 0.22, grow: lenM * 1.4, from: lenM * 0.8 });
        // The expanding shockwave ring (camera-facing sprite of the ring tex).
        const ring = this.fxSprite(tint, lenM * 0.3, true);
        ring.position.copy(local);
        r.add(ring);
        this.fxLive.push({ s: ring, vel: null, ttl: 1.5, life: 1.5, grow: lenM * 3.6, from: lenM * 0.3 });
        // Lingering fireballs + a smoke shroud.
        for (let i = 0; i < 5; i++) {
          const fb = this.fxSprite(i % 2 ? 0xff8833 : tint, lenM * (0.16 + Math.random() * 0.16));
          fb.position.copy(local).add(this._v1.set((Math.random() - 0.5) * lenM * 0.4, (Math.random() - 0.5) * lenM * 0.2, (Math.random() - 0.5) * lenM * 0.4));
          r.add(fb);
          this.fxLive.push({ s: fb, vel: null, ttl: 1.6 + Math.random() * 0.8, life: 2.4, grow: lenM * 0.5, from: lenM * 0.2 });
        }
        const shroud = this.fxSprite(0x2a2724, lenM * 0.5);
        (shroud.material as THREE.SpriteMaterial).blending = THREE.NormalBlending;
        (shroud.material as THREE.SpriteMaterial).opacity = 0.45;
        shroud.position.copy(local);
        r.add(shroud);
        this.fxLive.push({ s: shroud, vel: null, ttl: 2.6, life: 2.6, grow: lenM * 2.2, from: lenM * 0.5 });
      },
    });
  }

  /** Per-frame FX integration: scheduled stages fire, live sprites drift,
   *  swell, and fade out on their curves. */
  private fxTick(dt: number): void {
    const now = performance.now();
    for (let i = this.fxQueue.length - 1; i >= 0; i--) {
      if (this.fxQueue[i].at <= now) {
        const q = this.fxQueue[i];
        this.fxQueue.splice(i, 1);
        q.fn();
      }
    }
    this.fx?.tick(dt, this.off, this.bolts, this.pos); // realism wave R5: the batched vacuum FX (offset-local at commit) + HDR bolt streaks; significance from the ship
    for (let i = this.fxLive.length - 1; i >= 0; i--) {
      const f = this.fxLive[i];
      f.ttl -= dt;
      if (f.vel) f.s.position.addScaledVector(f.vel, dt);
      const k = Math.max(0, f.ttl / f.life);
      f.s.scale.setScalar(f.from + (f.grow - f.from) * (1 - k));
      (f.s.material as THREE.SpriteMaterial).opacity = Math.min(1, k * 1.6);
      if (f.ttl <= 0) {
        this.root?.remove(f.s);
        freeLater(f.s.material as THREE.SpriteMaterial);
        this.fxLive.splice(i, 1);
      }
    }
  }

  /** Bake a war-hull clone UNLIT (keep its texture): the deep-space battle
   *  sits 1000+ km from the sector sun, so lit materials render BLACK — the
   *  fleet-visibility law says combatants are self-lit. Minted materials go
   *  to warBakedMats (deactivate-disposed; clones share master GEOMETRY,
   *  which is never touched). */
  private bakeShipUnlit(obj: THREE.Object3D): void {
    obj.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const src = mesh.material as THREE.MeshStandardMaterial & { isMeshBasicMaterial?: boolean };
      if (!src || src.isMeshBasicMaterial) return;
      const baked = new THREE.MeshBasicMaterial({
        map: src.map ?? null,
        color: src.map ? 0xffffff : (src.color?.clone() ?? new THREE.Color(0x8a94a8)),
      });
      this.warBakedMats.push(baked);
      mesh.material = baked;
    });
  }

  /** Break a cooked GLB into PER-PART instancing sets: each source mesh
   *  keeps its own geometry (world-baked, uniformly centered/yawed/scaled
   *  to targetLen) and its own texture on an unlit basic material — the
   *  closest instancing gets to the proven clone renderer. */
  private shipPartSets(obj: THREE.Object3D, targetLen: number): Array<{ geo: THREE.BufferGeometry; mat: THREE.Material }> | null {
    obj.updateMatrixWorld(true);
    const bb = new THREE.Box3().setFromObject(obj);
    if (bb.isEmpty()) return null;
    const longest = Math.max(bb.max.x - bb.min.x, bb.max.y - bb.min.y, bb.max.z - bb.min.z, 1e-3);
    const s = targetLen / longest;
    // One shared normalization: translate to center → yaw → scale.
    const norm = new THREE.Matrix4().makeScale(s, s, s)
      .multiply(new THREE.Matrix4().makeRotationY(SHIP_MODEL_YAW))
      .multiply(new THREE.Matrix4().makeTranslation(
        -(bb.min.x + bb.max.x) / 2, -(bb.min.y + bb.max.y) / 2, -(bb.min.z + bb.max.z) / 2));
    const parts: Array<{ geo: THREE.BufferGeometry; mat: THREE.Material }> = [];
    obj.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || !m.geometry) return;
      const g = (m.geometry as THREE.BufferGeometry).clone();
      // THE WRAP LAW (2026-10-01, the LOD2 agent's find, verified on the served files): the war fighters store POSITION
      // as normalized int16 under a node scale; transformed in place to an 8.5 m hull, every coordinate past ±1 WRAPPED
      // the Int16Array and the swarm drew as shards. Float-copy first, as the pirate path above does.
      for (const name of ['position', 'normal'] as const) {
        const a = g.getAttribute(name) as THREE.BufferAttribute | undefined;
        if (a && a.normalized) {
          const f = new Float32Array(a.count * a.itemSize);
          for (let i = 0; i < a.count; i++) for (let k = 0; k < a.itemSize; k++) f[i * a.itemSize + k] = a.getComponent(i, k);
          g.setAttribute(name, new THREE.BufferAttribute(f, a.itemSize));
        }
      }
      g.applyMatrix4(new THREE.Matrix4().multiplyMatrices(norm, m.matrixWorld));
      const src = (Array.isArray(m.material) ? m.material[0] : m.material) as THREE.MeshStandardMaterial;
      const mat = new THREE.MeshBasicMaterial({
        map: src?.map ?? null,
        color: src?.color ? src.color.clone() : new THREE.Color(0xb8bec8),
      });
      parts.push({ geo: g, mat });
    });
    return parts.length ? parts : null;
  }

  /** Sample an object's meshes into a HULL-HUGGING sphere cloud ("the
   *  hitbox is the mesh"): vertices voxel-bin into per-axis cells sized by
   *  the true post-normalization extents, one sphere per occupied cell —
   *  flight collision and bolt tests both ride it. Local frame matches the
   *  RENDERED hull (cooked hulls carry SHIP_MODEL_YAW; procedural do not). */
  private hullSphereCloud(obj: THREE.Object3D, targetLen: number, yaw: number): Array<{ x: number; y: number; z: number; r: number }> {
    obj.updateMatrixWorld(true);
    const bb = new THREE.Box3().setFromObject(obj);
    if (bb.isEmpty()) return [];
    const size = new THREE.Vector3(); bb.getSize(size);
    const center = new THREE.Vector3(); bb.getCenter(center);
    const longest = Math.max(size.x, size.y, size.z, 1e-3);
    const s = targetLen / longest;
    const norm = new THREE.Matrix4().makeScale(s, s, s)
      .multiply(new THREE.Matrix4().makeRotationY(yaw))
      .multiply(new THREE.Matrix4().makeTranslation(-center.x, -center.y, -center.z));
    // Post-normalization bounds (transform the 8 box corners).
    const post = new THREE.Box3();
    const corner = new THREE.Vector3();
    for (let ci = 0; ci < 8; ci++) {
      corner.set(ci & 1 ? bb.max.x : bb.min.x, ci & 2 ? bb.max.y : bb.min.y, ci & 4 ? bb.max.z : bb.min.z).applyMatrix4(norm);
      post.expandByPoint(corner);
    }
    const span = new THREE.Vector3(); post.getSize(span);
    const NX = 5, NY = 5, NZ = 14;
    const cx = Math.max(1, span.x / NX), cy = Math.max(1, span.y / NY), cz = Math.max(1, span.z / NZ);
    const r = Math.hypot(cx, cy, cz) * 0.55;
    const cells = new Map<number, { n: number; x: number; y: number; z: number }>();
    const v = new THREE.Vector3();
    const M = new THREE.Matrix4();
    obj.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || !m.geometry) return;
      const posAttr = (m.geometry as THREE.BufferGeometry).attributes.position as THREE.BufferAttribute | undefined;
      if (!posAttr) return;
      M.multiplyMatrices(norm, m.matrixWorld);
      const step = Math.max(1, Math.floor(posAttr.count / 4000));
      for (let i = 0; i < posAttr.count; i += step) {
        v.fromBufferAttribute(posAttr, i).applyMatrix4(M);
        const ix = Math.min(NX - 1, Math.max(0, Math.floor((v.x - post.min.x) / cx)));
        const iy = Math.min(NY - 1, Math.max(0, Math.floor((v.y - post.min.y) / cy)));
        const iz = Math.min(NZ - 1, Math.max(0, Math.floor((v.z - post.min.z) / cz)));
        const key = (iz * NX + ix) * NY + iy;
        let cell = cells.get(key);
        if (!cell) { cell = { n: 0, x: 0, y: 0, z: 0 }; cells.set(key, cell); }
        cell.n++; cell.x += v.x; cell.y += v.y; cell.z += v.z;
      }
    });
    const out: Array<{ x: number; y: number; z: number; r: number }> = [];
    for (const c of cells.values()) {
      out.push({ x: c.x / c.n, y: c.y / c.n, z: c.z / c.n, r });
    }
    return out;
  }

  /** A capital's CURRENT position — the live kinematic state when it has
   *  materialized, else its deterministic anchor seat. */
  private capPosNow(side: number, k: number, t: number, out: THREE.Vector3): THREE.Vector3 {
    const b = this.battle;
    const kin = b?.capKin[side]?.[k];
    if (kin) return out.copy(kin.p);
    return this.warCapPos(side, b!.capP[side][k], t, out);
  }

  /** War arrival/impact flash at a LOCAL position (spectacle; capped). */
  /** Live skirmishers on a side (wingmen and dead hulls excluded). */
  private warSkirmisherCount(side: number): number {
    let n = 0;
    for (const q of this.pirates) {
      if (!q.ally && q.warSide === side && q.key.startsWith('war') && q.deadUntil === 0 && q.cls !== 'corvette' && q.cls !== 'frigate') n++;
    }
    return n;
  }

  /** Shed `n` tumbling hull chunks at a LOCAL position (pooled battle debris). */
  private spawnWarDebris(localPos: THREE.Vector3, n: number, speed: number, lifeS = 26): void {
    const b = this.battle;
    if (!b) return;
    for (let i = 0; i < n && b.debris.length < 90; i++) {
      const m = new THREE.Mesh(b.debrisGeos[(b.skSerial + i + b.debris.length) % b.debrisGeos.length], b.debrisMat);
      const sc = 0.5 + ((b.debris.length * 37 + i * 11) % 20) / 22;
      m.userData.baseScale = sc;
      m.scale.setScalar(sc);
      m.position.copy(localPos);
      m.rotation.set(i * 1.7, i * 2.3, i * 0.9);
      b.group.add(m);
      b.debris.push({
        m,
        vel: new THREE.Vector3((Math.sin(i * 12.9) * 43758 % 1) - 0.5, (Math.sin(i * 78.2) * 12543 % 1) - 0.5, (Math.sin(i * 39.4) * 26711 % 1) - 0.5)
          .normalize().multiplyScalar(speed * (0.5 + ((i * 7) % 10) / 10)),
        spin: new THREE.Vector3(((i * 3) % 7 - 3) * 0.25, ((i * 5) % 7 - 3) * 0.3, ((i * 11) % 7 - 3) * 0.2),
        ttl: lifeS, life: lifeS,
      });
    }
  }

  /** Retire dead skirmisher hulls OUTSIDE the pirate iteration (killPirate
   *  runs mid-loop — a splice there would skip a ship). */
  private purgeDeadSkirmishers(): void {
    for (let i = this.pirates.length - 1; i >= 0; i--) {
      const q = this.pirates[i];
      if (!q.ally && q.key.startsWith('war') && q.deadUntil === Number.MAX_SAFE_INTEGER) {
        if (this.target === q) this.target = null;
        this.root?.remove(q.root);
        this.disposeSectorRoot(q.root as THREE.Group);
        this.pirates.splice(i, 1);
      }
    }
    this.flushDisposeSoon();
  }

  private warFlash(localPos: THREE.Vector3, side: number, size: number): void {
    const b = this.battle;
    // Realism wave R5: a batched HDR bloom (no SpriteMaterial per flash).
    if (b && this.fx && !this.spaceUnlit()) { this.fx.flash(localPos.x + this.off.x, localPos.y + this.off.y, localPos.z + this.off.z, size, side === 0 ? 0xffe6a8 : 0xbdf0ff); return; }
    if (!b || b.flashes.length > 24) return;
    const s = this.spriteGlow(side === 0 ? 0xffe6a8 : 0xbdf0ff, size);
    s.position.copy(localPos);
    b.group.add(s);
    b.flashes.push({ s, ttl: 0.55, grow: size * 2.2 });
  }

  /** Build the war: per side one instanced capital wall + one instanced
   *  fighter screen, all seats/orbits precomputed and SORTED BY ARRIVAL so
   *  the warp-in staggering is a pure count-pack (instanceCount grows as
   *  the shared clock passes each ship's stamp — no scale-0 instances). */
  private buildBattle(win: { cyc: number; poi: PoiSpec }, center: THREE.Vector3): void {
    if (!this.root) return;
    this.teardownBattle();
    sfx('sfx_war_horn', { gain: 0.5, cooldownMs: 4000 }); // the walls warp in — the war opens
    const own: Array<{ dispose(): void }> = [];
    const group = new THREE.Group();
    const th = warH(`bw${win.cyc}`) * Math.PI;
    const frame = {
      c: center.clone(),
      ax: new THREE.Vector3(Math.cos(th), 0, Math.sin(th)),
      az: new THREE.Vector3(-Math.sin(th), 0, Math.cos(th)),
    };
    // The capital silhouette: a 4.5 km dreadnought merged from primitive
    // blocks (hull, spine tower, twin prow prongs, engine block) — nose −Z.
    const CS = WAR_CAP_LEN / 300; // the ×15 hull scale
    const capGeo = mergeGeometries([
      new THREE.BoxGeometry(46 * CS, 34 * CS, 250 * CS),
      new THREE.BoxGeometry(16 * CS, 30 * CS, 62 * CS).translate(0, 30 * CS, 58 * CS),
      new THREE.BoxGeometry(12 * CS, 10 * CS, 96 * CS).translate(23 * CS, -3 * CS, -104 * CS),
      new THREE.BoxGeometry(12 * CS, 10 * CS, 96 * CS).translate(-23 * CS, -3 * CS, -104 * CS),
      new THREE.BoxGeometry(34 * CS, 24 * CS, 34 * CS).translate(0, 0, 138 * CS),
    ]);
    const beamGeo = new THREE.BoxGeometry(1, 1, 1).translate(0, 0, -0.5);
    own.push(capGeo, beamGeo);
    // Realism wave R5: broadsides on the engine BeamMaterial — a hot core, a civ glow and a scrolling ripple over
    // two crossed planes (uv.x down the beam, uv.y across), one material per civ (the pool swaps by side).
    const beamV2 = this.spaceUnlit() ? null : (() => {
      const a = new THREE.PlaneGeometry(1, 1).rotateY(Math.PI / 2).translate(0, 0, -0.5);
      const bq = new THREE.PlaneGeometry(1, 1).rotateY(Math.PI / 2).rotateZ(Math.PI / 2).translate(0, 0, -0.5);
      const geo = mergeGeometries([a, bq]) ?? a;
      const mk = (glow: [number, number, number]): THREE.Material => {
        const m = createBeamMaterial({ coreColor: [6, 6, 5.5], glowColor: glow, corePower: 7, intensity: 1.6, scrollSpeed: 6, rippleFrequency: 18, endFade: 0.04 }).material;
        m.name = 'space-broadside';
        return markMrtOverlay(m);
      };
      const mats: [THREE.Material, THREE.Material] = [mk([1.6, 1.0, 0.35]), mk([0.45, 1.1, 1.6])];
      own.push(geo, mats[0], mats[1]);
      return { geo, mats };
    })();
    // Hull-chunk DEBRIS assets: three jagged shard silhouettes + ONE dark
    // self-lit material shared by every piece (MATERIAL LAW — the pool only
    // toggles visibility/transforms, never allocates).
    const debrisGeos = [0, 1, 2].map((v) => {
      // 2026-09-09: a TetrahedronGeometry is NON-indexed while the boxes are
      // indexed — mergeGeometries returned null ("index attribute exists among
      // all geometries, or in none"), the debris InstancedMesh read
      // morphAttributes of null, and the frame system threw every tick the
      // battle tried to build: the war never assembled past the capitals.
      // Merge everything non-indexed.
      const g2 = mergeGeometries([
        new THREE.BoxGeometry(9 + v * 4, 2.2, 4 + v).rotateZ(0.5 + v * 0.6).toNonIndexed(),
        new THREE.BoxGeometry(3.5, 6 + v * 2, 2.5).rotateX(0.8 + v).translate(2, 0, 1).toNonIndexed(),
        new THREE.TetrahedronGeometry(3.5 + v).translate(-2.5, 1, -1),
      ]);
      own.push(g2);
      return g2;
    });
    const debrisMat = new THREE.MeshBasicMaterial({ color: 0x343a44 });
    own.push(debrisMat);
    const mkInst = (geo: THREE.BufferGeometry, mat: THREE.Material, n: number): THREE.InstancedMesh => {
      const m = new THREE.InstancedMesh(geo, mat, n);
      m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      m.count = 0;
      m.frustumCulled = false; // the wall spans kilometres — whole-bounds culling would pop it
      group.add(m);
      return m;
    };
    // Capitals: individual objects, filled in as each one warps in — the
    // clone path is the SAME renderer the player's own hull uses (the
    // merged-instanced GLBs drew nothing; clones are proven on screen).
    const capObjs: [Array<THREE.Object3D | null>, Array<THREE.Object3D | null>] =
      [Array(BATTLE_CAPS).fill(null), Array(BATTLE_CAPS).fill(null)];
    // (2026-08-23) The instanced fighter swarm — the "tiny blue ships" — is
    // GONE. Skirmishers are REAL FSM gunships launched from the capital
    // carriers (see the skirmisher block in updateBattle).
    // Two sprites per capital: a stern engine glow + a soft hull-length halo
    // (interleaved [stern, halo] pairs) — the wall must POP at chart range.
    const capGlows: THREE.Sprite[] = [];
    for (let k = 0; k < BATTLE_CAPS * 2; k++) {
      const civColor = k < BATTLE_CAPS ? 0xffd873 : 0x9fe8ff;
      const stern = this.spriteGlow(civColor, 420);
      (stern.material as THREE.SpriteMaterial).opacity = 0.5;
      const halo = this.spriteGlow(civColor, 1500);
      (halo.material as THREE.SpriteMaterial).opacity = 0.12;
      for (const g of [stern, halo]) {
        g.visible = false;
        own.push(g.material);
        group.add(g);
        capGlows.push(g);
      }
    }
    const beamPool: THREE.Mesh[] = [];
    for (let k = 0; k < 8; k++) {
      // markMrtOverlay (session 3): an additive overlay must not write the velocity / normals of the hull behind it (the MRT overlay law — TAAU and the blur read velocity)
      const mat = markMrtOverlay(new THREE.MeshBasicMaterial({
        color: 0xffffff, transparent: true, opacity: 0.85,
        blending: THREE.AdditiveBlending, depthWrite: false,
      }));
      own.push(mat);
      const m = beamV2 ? new THREE.Mesh(beamV2.geo, beamV2.mats[0]) : new THREE.Mesh(beamGeo, mat);
      if (beamV2) m.userData.beamMats = beamV2.mats;
      m.visible = false;
      m.frustumCulled = false;
      group.add(m);
      beamPool.push(m);
    }
    // Capital seats: a 5×2 staggered wall per side, spaced for 4.5 km
    // hulls (5.2 km lateral · 4.2 km row depth). Fighters: hashed orbits
    // in the furball band between the walls. Space campaign slice D: the seats are the SHARED formula (sorted by the
    // hashed arrival — roster slot k is the k-th to warp in), so the server judges a hit against these same seats.
    const capP = warCapSeats(win.cyc) as [WarCap[], WarCap[]];
    this.root.add(group);
    // MESH-TRUE hitboxes: sample each civ's rendered hull into a sphere
    // cloud once per battle (cooked hulls carry the wrapHull yaw; the
    // procedural silhouette is already nose −Z).
    for (const side of [0, 1] as const) {
      if (this.capCloud[side]) continue;
      const master = this.warCapM[side];
      if (master) {
        this.capCloud[side] = this.hullSphereCloud(master, WAR_CAP_LEN, SHIP_MODEL_YAW);
      } else {
        const probe = new THREE.Mesh(capGeo);
        this.capCloud[side] = this.hullSphereCloud(probe, WAR_CAP_LEN, 0);
      }
    }
    // The war is SOLID: a dynamic collider group whose sphere list follows
    // the living capital walls every tick (absolute coords, like any rock).
    const warCols = { x: center.x, y: center.y, z: center.z, reach: 26_000, list: [] as Array<{ x: number; y: number; z: number; r: number; halfH: number }> };
    this.colliderGroups.push(warCols);
    // Staggered launch waves: each side releases a gunship every ~0.9 s as
    // its carriers arrive; kills re-queue at +5 s (live + queued ≤ 30).
    const now0 = performance.now();
    this.battle = {
      cyc: win.cyc, poiKey: win.poi.key, group, frame, capObjs, capP,
      capN: [0, 0],
      skRespawn: [
        Array.from({ length: WAR_SKIRMISHERS }, (_, i) => now0 + 2500 + i * 900),
        Array.from({ length: WAR_SKIRMISHERS }, (_, i) => now0 + 2500 + i * 900),
      ],
      skSerial: this.skSerialBase(win.cyc),
      capHp: [Array(BATTLE_CAPS).fill(WAR_CAP_HP), Array(BATTLE_CAPS).fill(WAR_CAP_HP)],
      capDead: [Array(BATTLE_CAPS).fill(false), Array(BATTLE_CAPS).fill(false)],
      capKin: [Array(BATTLE_CAPS).fill(null), Array(BATTLE_CAPS).fill(null)],
      flakAcc: 0, torpAcc: 0, pdAcc: 0,
      warCols, capFireAcc: 0, capGeo,
      capGlows, beamPool, beams: [], flashes: [],
      debris: [], debrisGeos, debrisMat,
      beamAcc: 0, rumbleAcc: 0, markAcc: 0,
      hostileKeys: new Set(), promoN: 0, warnedCyc: -1, own,
      capMax: [BATTLE_CAPS, BATTLE_CAPS], skMax: [WAR_SKIRMISHERS, WAR_SKIRMISHERS], syncAt: 0, escortSyncAt: 0,
      endPlanned: false, endBooms: [], capLeave: [Array(BATTLE_CAPS).fill(0), Array(BATTLE_CAPS).fill(0)],
      capGone: [Array(BATTLE_CAPS).fill(false), Array(BATTLE_CAPS).fill(false)], skLeave: new Map(),
      endStats: { te: -1, booms: 0, capsWarped: 0, skWarped: 0 },
      endOutcome: -2, rosterMode: false, capClaimSent: [Array(BATTLE_CAPS).fill(false), Array(BATTLE_CAPS).fill(false)], lastLoss: null,
      escortDead: new Set(),
    };
    // A drifting field of cold wreckage between the lines from the start —
    // the battle floats in the bones of earlier engagements.
    for (let i = 0; i < 14; i++) {
      const lc = this.local(center.x, center.y, center.z, this._v1);
      this.spawnWarDebris(
        this._v2.set(lc.x + (warH(`d${i}x`) - 0.5) * 9000, lc.y + (warH(`d${i}y`) - 0.5) * 1400, lc.z + (warH(`d${i}z`) - 0.5) * 9000),
        1, 4, 9999,
      );
    }
  }

  private updateBattle(dt: number): void {
    if (this.system.id !== 'sys0') return; // the civ machinery lives in the Commons Reach
    const win = this.battleWindow();
    const root = this.root;
    if (!win || !root) {
      this.teardownBattle();
      return;
    }
    const center = this._v1.set(win.poi.pos[0] + win.poi.radius * 1.6, win.poi.pos[1] + 400, win.poi.pos[2]);
    const dist = this.pos.distanceTo(center);
    if (this.battleToastCyc !== win.cyc) {
      this.battleToastCyc = win.cyc;
      this.toast(`FLEET WAR — the Aurelian Concord and the Borean Compact warp their battle lines in over ${win.poi.name} (${BATTLE_CAPS} capitals + ${WAR_SKIRMISHERS} gunships a side)`);
    }
    if (dist > BATTLE_VIS_M) {
      this.teardownBattle();
      return;
    }
    if (!this.battle || this.battle.cyc !== win.cyc) this.buildBattle(win, center);
    const b = this.battle;
    if (!b) return;
    // The war's own score while the lines are still fought — a field of warped-out hulks after the finale is quiet.
    // A capital still fought = neither warped out NOR dead (the old predicate read a dead hull as a fight).
    const fought = (sd: 0 | 1): boolean => { for (let k = 0; k < b.capN[sd]; k++) if (!b.capGone[sd][k] && !b.capDead[sd][k]) return true; return false; };
    if (dist < FLEET_MUSIC_M && (fought(0) || fought(1))) reportThreat('fleet', performance.now());
    // Rebase immunity: every matrix below is recomputed absolute→local this
    // tick, so the group itself must never carry a rebase shift.
    b.group.position.set(0, 0, 0);
    const f = b.frame;
    let flashBudget = 6;
    // Space Wave 4: the campaign row steers the renderer — ceilings, deaths to match the line, the finale on a retreat.
    const camp = this.campaignRow();
    if (camp) this.syncBattleToCampaign(b, win, camp);
    if (camp) this.syncWarLives(b, camp); // space campaign slice D: the picture follows the record's hp (a pinned war)
    // ── THE FINALE: te counts up through the window's last BATTLE_END_S ──
    let te = win.t - (BATTLE_LEN_S - BATTLE_END_S);
    if (camp && camp.state !== 'engaged') {
      const endsS = Number(camp.endsAt.microsSinceUnixEpoch / 1_000_000n);
      te = Math.max(te, Date.now() / 1000 - (endsS - BATTLE_END_S));
    }
    const ending = te >= 0;
    if (ending) {
      b.endStats.te = te;
      if (!b.endPlanned) this.planBattleEnd(b, win);
      // (No scripted deaths here — THE FINALE CONTRACT. `endBooms` stays empty; a capital that dies in the finale's
      //  seconds died on the server's roster and arrives through syncBattleToCampaign like any other.)
      // Skirmishers jump on their own second: a flash, the hull is gone.
      if (te >= END_WARP_FROM_S) {
        let removed = false;
        for (let i2 = this.pirates.length - 1; i2 >= 0; i2--) {
          const p2 = this.pirates[i2];
          if (p2.ally || p2.warSide === undefined || !p2.key.startsWith('war') || p2.deadUntil > 0) continue;
          const leave = b.skLeave.get(p2.key) ?? (END_WARP_FROM_S + warH(p2.key + 'leave') * 9);
          if (te < leave) continue;
          if (flashBudget-- > 0) this.warFlash(p2.root.position.clone(), p2.warSide, 70);
          if (this.target === p2) this.target = null;
          this.root?.remove(p2.root);
          this.disposeSectorRoot(p2.root as THREE.Group);
          this.pirates.splice(i2, 1);
          b.endStats.skWarped++;
          removed = true;
        }
        if (removed) { this.flushDisposeSoon(); sfx('sfx_warp_exit', { gain: 0.35, cooldownMs: 400 }); }
      }
    }
    for (let side = 0; side < 2; side++) {
      const sgn = side === 0 ? 1 : -1;
      // Capitals: count-packed warp-in, wall seats, stern glows.
      // NO ROW, NO LINE (2026-09-23): a real window warps nothing in until the server's campaign
      // row is here — capMax starts at the full line and only the row lowers it, so a side that
      // commits fewer would keep capitals no roster names (they fired at you and never died).
      const cp = b.capP[side];
      while ((camp !== null || this.battleForce > 0) && b.capN[side] < b.capMax[side] && cp[b.capN[side]].arr <= win.t) {
        const p = cp[b.capN[side]];
        this.warCapPos(side, p, win.t, this._v2);
        if (flashBudget-- > 0) this.warFlash(this.local(this._v2.x, this._v2.y, this._v2.z, this._v4), side, 420);
        b.capN[side]++;
      }
      if (side === 0) b.warCols.list.length = 0; // rebuilt fresh both sides each tick
      for (let k = 0; k < b.capN[side]; k++) {
        const p = cp[k];
        const gi = (side * BATTLE_CAPS + k) * 2;
        if (b.capDead[side][k] || b.capGone[side][k]) {
          // A killed (or warped-out) capital leaves the field: hidden hull, no collider, dark.
          const dead = b.capObjs[side][k];
          if (dead) dead.visible = false;
          b.capGlows[gi].visible = false;
          b.capGlows[gi + 1].visible = false;
          continue;
        }
        // Materialize the hull on arrival: the cooked dreadnought clone
        // (wrapHull — the player-ship renderer), else the emissive
        // procedural silhouette. Clones share master GPU buffers (never
        // disposed here — the release-closure law).
        let obj = b.capObjs[side][k];
        if (!obj) {
          obj = new THREE.Group();
          const master = this.warCapM[side];
          if (master) {
            const hull = this.wrapHull(master.clone(), WAR_CAP_LEN);
            // Realism wave R6: lit under the one sun (the old "self-lit — the battle is far from any sun" bake
            // was a workaround for the key light that aimed at the world origin), a civ rim + the window layer.
            this.dressWarHull(hull, side as 0 | 1, true);
            // HITBOX = MESH: hullSphereCloud recentres on the bbox centre but
            // wrapHull only scales — an off-centre GLB pivot displaced the
            // rendered dreadnought from its own sphere cloud (bolts hit thin
            // air beside the hull, user report 2026-08-23). Centre the render
            // exactly like the cloud.
            this.recenterHull(hull);
            // R6: a few hundred nav / running lights sampled ONCE per master (the recentred, still parentless wrap).
            const navSet = !this.spaceFlag('spaceLitHulls') ? null : navPointSet(master, hull, navPointCount(WAR_CAP_LEN));
            obj.add(hull);
            if (navSet) obj.add(navMesh(navSet));
          } else {
            const mat = new THREE.MeshStandardMaterial({
              color: 0x8a919e, roughness: 0.55, metalness: 0.35,
              emissive: side === 0 ? 0xd8b23c : 0x4fd0e8, emissiveIntensity: 0.5,
            });
            b.own.push(mat);
            const hull = new THREE.Mesh(b.capGeo, mat);
            const g = new THREE.Group();
            g.add(hull);
            this.recenterHull(g); // the silhouette's spine/prow offsets shift its centre too
            obj.add(g);
          }
          b.group.add(obj);
          b.capObjs[side][k] = obj;
          // R5: eight plumes on the dreadnought's stern face, in its civ's colour.
          if (!this.spaceUnlit()) { const hp = attachPlumes(obj, WAR_CAP_LEN, side === 0 ? 0xffb347 : 0x7fdcff, side * 10 + k); if (hp) this.capPlumes.set(obj, hp); }
        }
        obj.visible = true;
        // DREADNOUGHT KINEMATICS: seek the drifting anchor seat with heavy
        // inertia, and SEPARATE from every other living hull — the walls
        // cruise slowly and never grind through each other.
        let kin = b.capKin[side][k];
        if (!kin) {
          kin = { p: this.warCapPos(side, p, win.t, this._v2).clone(), v: new THREE.Vector3(), q: new THREE.Quaternion() };
          b.capKin[side][k] = kin;
        }
        // Space campaign slice D: the step is the SHARED `capKinStep` (op for op the three.js arithmetic it replaced) —
        // the function verify:war-authority replays to measure the envelope (KIN_TOL_M) the server allows around the anchor.
        const anchor = this.warCapPos(side, p, win.t, this._v2);
        const others = kinOthers();
        for (let s2 = 0; s2 < 2; s2++) {
          for (let k2 = 0; k2 < b.capN[s2]; k2++) {
            if ((s2 === side && k2 === k) || b.capDead[s2][k2] || b.capGone[s2][k2]) continue;
            const other = b.capKin[s2][k2];
            if (other) others.push(other.p);
          }
        }
        capKinStep(kin, anchor, others, dt);
        const pos = kin.p;
        const lp = this.local(pos.x, pos.y, pos.z, this._v4);
        // Face across the center: nose −Z toward the enemy wall.
        const lt = this._v5.set(lp.x - sgn * f.ax.x * 1000, lp.y, lp.z - sgn * f.ax.z * 1000);
        this._m1.lookAt(lp, lt, UP);
        this._q1.setFromRotationMatrix(this._m1);
        const age = win.t - p.arr;
        const sz = age < 0.7 ? 1 + 3 * (1 - age / 0.7) : 1; // deceleration stretch out of warp
        obj.position.copy(lp);
        obj.quaternion.copy(this._q1);
        kin.q.copy(this._q1);
        obj.scale.set(1, 1, sz);
        // R5: a cruising dreadnought burns low; arriving out of warp and leaving it, full.
        const cp5 = this.capPlumes.get(obj);
        if (cp5) {
          const leaving = ending && b.capLeave[side][k] > 0 && te >= b.capLeave[side][k] - 1.2;
          drivePlumes(cp5, leaving || age < 2 ? 1 : 0.3 + Math.min(1, kin.v.length() / WAR_CAP_SPEED) * 0.45, leaving, leaving ? 1 : 0);
        }
        if (ending) {
          // Warp-out: 1.2 s of stretch + acceleration back out of the line, then the flash and gone.
          const leave = b.capLeave[side][k];
          if (leave > 0 && te >= leave - 1.2) {
            const u = Math.min(1, (te - (leave - 1.2)) / 1.2);
            kin.v.set(0, 0, 0);
            kin.p.addScaledVector(this._v5.set(sgn * f.ax.x, 0, sgn * f.ax.z).normalize(), dt * 3200 * u * u); // astern = back toward its own side
            obj.scale.set(1, 1, 1 + 4 * u);
            if (te >= leave) {
              this.warFlash(lp.clone(), side, 900);
              b.capGone[side][k] = true;
              obj.visible = false;
              b.capGlows[gi].visible = false;
              b.capGlows[gi + 1].visible = false;
              this.sfxAtLocal('sfx_warp_exit', lp, { gain: 0.55, cooldownMs: 250 });
              b.endStats.capsWarped++;
              continue;
            }
          }
        }
        const stern = b.capGlows[gi];
        stern.visible = true;
        stern.position.set(lp.x + sgn * f.ax.x * 2250, lp.y, lp.z + sgn * f.ax.z * 2250);
        const halo = b.capGlows[gi + 1];
        halo.visible = true;
        halo.position.copy(lp);
        // MESH-TRUE collision: the hull's sphere cloud, carried by the live
        // pose (absolute coords — the flight loop treats it like any rock).
        const cloud = this.capCloud[side];
        if (age < WARP_STRETCH_S) {
          // A warp signature, not a hull: nothing to hit and nothing hits it (the stretched picture matched no collider).
        } else if (cloud) {
          for (const c of cloud) {
            const w = this._v5.set(c.x, c.y, c.z).applyQuaternion(this._q1);
            b.warCols.list.push({ x: pos.x + w.x, y: pos.y + w.y, z: pos.z + w.z, r: c.r, halfH: 0 });
          }
        } else {
          for (const off of [-1950, -1300, -650, 0, 650, 1300, 1950]) {
            b.warCols.list.push({
              x: pos.x - sgn * f.ax.x * off, y: pos.y, z: pos.z - sgn * f.ax.z * off,
              r: 330, halfH: 0,
            });
          }
        }
        // A burning hull vents — sub-40% capitals shed ember flashes at ~3.6 a second whatever the frame rate
        // (a per-frame 6 % was a different fire at 30 and 144 fps).
        if (age >= WARP_STRETCH_S && b.capHp[side][k] < WAR_CAP_HP * 0.4 && Math.random() < 1 - Math.exp(-3.6 * dt)) {
          this.warFlash(this._v3.set(lp.x + (Math.random() - 0.5) * 2400, lp.y + (Math.random() - 0.5) * 450, lp.z + (Math.random() - 0.5) * 900), side, 220);
        }
      }
      // SKIRMISHERS (2026-08-23): real FSM gunships launch from LIVING
      // arrived carriers — the staggered opening waves plus a +5 s entry
      // per kill. They fight the OTHER side in the center (quarryOf).
      const rq = b.skRespawn[side];
      const nowMs = performance.now();
      while (!ending && rq.length && rq[0] <= nowMs) {
        if (this.warSkirmisherCount(side) >= (this.battle?.skMax[side] ?? WAR_SKIRMISHERS)) break; // Space Wave 4: the campaign's living gunships
        // A living, arrived carrier releases the ship; none left = this
        // side's hangars are ash — no more launches (real attrition).
        let capIdx = -1;
        for (let k = 0; k < b.capN[side]; k++) {
          if (!b.capDead[side][k] && b.capKin[side][k]) { capIdx = (capIdx === -1 || (b.skSerial + k) % 3 === 0) ? k : capIdx; }
        }
        if (capIdx === -1) break;
        rq.shift();
        const kin = b.capKin[side][capIdx]!;
        const serial = b.skSerial++;
        this.skSerialNext = b.skSerial;
        const launch = this._v2.set(
          kin.p.x + (warH(`sk${serial}x`) - 0.5) * 900,
          kin.p.y - 120 - warH(`sk${serial}y`) * 200, // out of the belly bay
          kin.p.z + (warH(`sk${serial}z`) - 0.5) * 900,
        );
        const pr = this.spawnHostile(`war${side}:sk${serial}`, (serial % 5) === 4 ? 3 : 2, launch.x, launch.y, launch.z, side as 0 | 1);
        if (pr) {
          // Mid-field anchor: patrol/loiter happens between the lines, and
          // the attack-token pools (6 per side) keep runs staggered.
          pr.home.set(
            b.frame.c.x + (warH(`sk${serial}hx`) - 0.5) * 5000,
            b.frame.c.y + (warH(`sk${serial}hy`) - 0.5) * 1200,
            b.frame.c.z + (warH(`sk${serial}hz`) - 0.5) * 5000,
          );
          pr.poiKey = `war${side}:${serial % 6}`;
          if (flashBudget-- > 0) this.warFlash(this.local(launch.x, launch.y, launch.z, this._v4), side, 26);
        } else {
          break; // key collision beat us this tick — retry next
        }
      }
    }
    this.updateWarEscorts(win, b, ending);
    this.stepBreakups(dt); // item 7: the dead capitals' chunks drift, tumble, vent and burn away
    this.prebuildBreakups(b);
    // DEBRIS: tumbling hull chunks — dead ships shed them, the field keeps
    // a cold drift of old bones. Pooled meshes, one shared material.
    for (let i = b.debris.length - 1; i >= 0; i--) {
      const dd = b.debris[i];
      dd.ttl -= dt;
      dd.m.position.addScaledVector(dd.vel, dt);
      dd.vel.multiplyScalar(1 - dt * 0.12); // vacuum drift with a hint of drag for readability
      dd.m.rotation.x += dd.spin.x * dt;
      dd.m.rotation.y += dd.spin.y * dt;
      dd.m.rotation.z += dd.spin.z * dt;
      const frac = dd.ttl / dd.life;
      if (frac < 0.15) dd.m.scale.setScalar(Math.max(0.001, dd.m.userData.baseScale * (frac / 0.15)));
      if (dd.ttl <= 0) {
        b.group.remove(dd.m);
        b.debris.splice(i, 1); // geometry + material are the battle's shared assets — never disposed here
      }
    }
    // Capital broadsides: pooled beam volleys between the walls.
    b.beamAcc += dt;
    if (b.beamAcc > 0.8 && b.capN[0] > 0 && b.capN[1] > 0 && !(ending && te >= END_WARP_FROM_S)) {
      b.beamAcc = 0;
      const free = b.beamPool.find((m) => !m.visible);
      if (free) {
        const sside = Math.random() < 0.5 ? 0 : 1;
        const i = (Math.random() * b.capN[sside]) | 0;
        const j = (Math.random() * b.capN[1 - sside]) | 0;
        const from = this.warCapPos(sside, b.capP[sside][i], win.t, this._v2);
        const to = this.warCapPos(1 - sside, b.capP[1 - sside][j], win.t, this._v3);
        const lf = this.local(from.x, from.y, from.z, this._v4);
        const lt = this.local(to.x, to.y, to.z, this._v5);
        free.position.copy(lf);
        // Matrix4.lookAt → −Z toward the target (matches the beam geometry,
        // which extends along −Z; Object3D.lookAt would flip it for meshes).
        this._m1.lookAt(lf, lt, UP);
        free.quaternion.setFromRotationMatrix(this._m1);
        const bm2 = free.userData.beamMats as [THREE.Material, THREE.Material] | undefined;
        if (bm2) free.material = bm2[sside]; // R5: the civ's BeamMaterial
        else (free.material as THREE.MeshBasicMaterial).color.setHex(sside === 0 ? 0xffe2a0 : 0xb8ecff);
        free.scale.set(30, 30, lf.distanceTo(lt));
        free.visible = true;
        b.beams.push({ m: free, ttl: 0.32 });
        this.warFlash(lt, 1 - sside, 260); // impact bloom on the struck wall
        if (Math.random() < 0.3) this.fxImpact(lt, 40, sside === 0 ? 0xffd8a0 : 0xaee6ff);
      }
    }
    for (let i = b.beams.length - 1; i >= 0; i--) {
      const bm = b.beams[i];
      bm.ttl -= dt;
      if (bm.ttl <= 0) { bm.m.visible = false; b.beams.splice(i, 1); }
    }
    // Warp-in flashes: bloom out, fade, die.
    for (let i = b.flashes.length - 1; i >= 0; i--) {
      const fl = b.flashes[i];
      fl.ttl -= dt;
      const k = Math.max(0, fl.ttl / 0.55);
      fl.s.scale.setScalar(fl.grow * (1 - k * 0.6));
      (fl.s.material as THREE.SpriteMaterial).opacity = k;
      if (fl.ttl <= 0) {
        b.group.remove(fl.s);
        freeLater(fl.s.material);
        b.flashes.splice(i, 1);
      }
    }
    // Ambient attrition rumble across the furball.
    b.rumbleAcc += dt;
    if (b.rumbleAcc > 2.1 && this.warSkirmisherCount(0) + this.warSkirmisherCount(1) > 0) {
      b.rumbleAcc = 0;
      const live = this.pirates.filter((q) => !q.ally && q.warSide !== undefined && q.key.startsWith('war') && q.deadUntil === 0);
      if (live.length > 0) {
        const q = live[(Math.random() * live.length) | 0];
        this.fxImpact(this._v4.copy(q.root.position), 8, q.warSide === 0 ? 0xffd8a0 : 0xaee6ff);
      }
    }
    // The war reads YOU: an unaligned hull is hostile to BOTH lines; sworn
    // pilots are hunted only by the enemy civ (quarryOf carries it).
    b.markAcc += dt;
    if (b.markAcc > 0.7 && !this.interior) {
      b.markAcc = 0;
      if (dist < WAR_ZONE_M) {
        if (b.warnedCyc !== win.cyc) {
          b.warnedCyc = win.cyc;
          this.toast(this.faction === ''
            ? 'UNALIGNED HULL IN THE WAR ZONE — both battle lines read you as hostile (dock at a shipyard to swear a civ)'
            : `${this.faction === 'aurelian' ? 'Borean Compact' : 'Aurelian Concord'} gunships vector onto you — your line screens you!`);
        }
        // (The old realize-a-swarm-fighter machinery is gone — every
        // skirmisher is already a full FSM ship whose quarry logic vectors
        // onto hostile hulls, the player included, by allegiance.)
      }
    }
    this.purgeDeadSkirmishers();
    this.updateCapWeapons(dt, win, b, dist);
    this.updateTorpedoes(dt, win, b);
    this.updateWarAllies(dt, win, b, dist);
  }

  /** The capital ARSENAL — four weapon systems, each with its own envelope:
   *  · heavy VOLLEY bolts at detected enemies out to 10 km
   *  · FLAK airbursts peppering the space around fast targets (3–10 km)
   *  · TORPEDOES — slow, heavy, capital-vs-capital ship killers
   *  · POINT-DEFENSE tracer streams inside 2.6 km (fast, chip damage) */
  private updateCapWeapons(dt: number, win: { cyc: number; t: number }, b: NonNullable<typeof this.battle>, dist: number): void {
    if (!this.root || this.interior) return;
    const hostileSides: number[] = this.faction === '' ? [0, 1] : this.faction === 'aurelian' ? [1] : [0];
    /** Nearest living hostile capital to the player within `range`. */
    const nearestHostileCap = (range: number): { pos: THREE.Vector3; side: number } | null => {
      let best: { pos: THREE.Vector3; side: number } | null = null;
      let bestD = range;
      for (const side of hostileSides) {
        for (let k = 0; k < b.capN[side]; k++) {
          // GONE IS GONE (2026-09-23): a capital the finale warped out kept its frozen
          // pose — volleys, flak and point defence fired at you from empty space for the
          // rest of the window, and your bolts could still file a kill on it.
          if (b.capDead[side][k] || b.capGone[side][k]) continue;
          const p = this.capPosNow(side, k, win.t, this._v2);
          const d = Math.hypot(p.x - this.pos.x, p.y - this.pos.y, p.z - this.pos.z);
          if (d < bestD) { bestD = d; best = { pos: p.clone(), side }; }
        }
      }
      return best;
    };
    // ── VOLLEY (10 km detection — the wall opens up the moment it reads you) ──
    b.capFireAcc += dt;
    if (b.capFireAcc > 2.2 && dist < WAR_ZONE_M) {
      b.capFireAcc = 0;
      const src = nearestHostileCap(WAR_CAP_AGGRO);
      if (src) {
        const lp = this.local(src.pos.x, src.pos.y, src.pos.z, this._v4);
        const bolt = this.spriteGlow(src.side === 0 ? 0xffd873 : 0x9fe8ff, 6);
        bolt.position.copy(lp);
        this.root.add(bolt);
        { const dCap = this.shipLocal().distanceTo(lp);
          this.sfxAtLocal('sfx_capital_gun', lp, { gain: Math.max(0.1, 0.5 - dCap * 0.00012), cooldownMs: 700, caption: 'Capital broadside' }); }
        this.bolts.push({
          sprite: bolt,
          vel: this._v5.copy(this.shipLocal()).sub(lp).normalize().multiplyScalar(BOLT_SPEED * 0.78).clone(),
          // The capital's SIDE rides the bolt (2026-10-01): sideless, the bolt loop read it as PIRATE fire — boltHits
          // let it land on the capital's own gunships on its way to you, and a hit was blamed on the pirate you target.
          ttl: BOLT_LIFE_S * 3.4, hostile: true, side: src.side as 0 | 1,
        });
      }
    }
    // ── FLAK: predicted airbursts around the player (area denial) ──
    b.flakAcc += dt;
    if (b.flakAcc > 1.6 && dist < WAR_ZONE_M) {
      b.flakAcc = 0;
      const src = nearestHostileCap(WAR_CAP_AGGRO * 1.2);
      if (src) {
        for (let i = 0; i < 3; i++) {
          const lead = 0.9 + i * 0.5;
          const burstAbs = this._v2.set(
            this.pos.x + this.vel.x * lead + (Math.random() - 0.5) * 220,
            this.pos.y + this.vel.y * lead + (Math.random() - 0.5) * 160,
            this.pos.z + this.vel.z * lead + (Math.random() - 0.5) * 220,
          ).clone();
          this.fxQueue.push({
            at: performance.now() + 350 + i * 260,
            fn: () => {
              const l = this.local(burstAbs.x, burstAbs.y, burstAbs.z, this._v1);
              this.fxImpact(l, 9, 0xffc06a);
              if (Math.hypot(burstAbs.x - this.pos.x, burstAbs.y - this.pos.y, burstAbs.z - this.pos.z) < 70) {
                this.damagePlayer(6, this._v2.set(0, 0, 1), { kind: 'war', key: 'war', side: src.side === 0 ? 'aurelian' : 'borean' });
              }
            },
          });
        }
      }
    }
    // ── TORPEDOES: each side's walls pound the other's (ship killers) ──
    b.torpAcc += dt;
    if (b.torpAcc > 4.5 && this.torpedoes.length < 8 && b.capN[0] > 0 && b.capN[1] > 0) {
      b.torpAcc = 0;
      const sside = Math.random() < 0.5 ? 0 : 1;
      let fromK = -1, tgtK = -1;
      for (let k = 0; k < b.capN[sside]; k++) if (!b.capDead[sside][k] && !b.capGone[sside][k]) { fromK = k; break; }
      for (let k = 0; k < b.capN[1 - sside]; k++) if (!b.capDead[1 - sside][k] && !b.capGone[1 - sside][k] && win.t - b.capP[1 - sside][k].arr >= WARP_STRETCH_S) { tgtK = k; break; }
      if (fromK >= 0 && tgtK >= 0) {
        const from = this.capPosNow(sside, fromK, win.t, this._v2).clone();
        const s = this.spriteGlow(sside === 0 ? 0xffe2a0 : 0xb8ecff, 26);
        s.position.copy(this.local(from.x, from.y, from.z, this._v1));
        this.root.add(s);
        this.torpedoes.push({ s, from, tgtSide: 1 - sside, tgtK, speed: 170, trailAcc: 0 });
      }
    }
    // ── POINT-DEFENSE: rapid chip-damage streams inside the envelope ──
    b.pdAcc += dt;
    if (b.pdAcc > 0.14) {
      b.pdAcc = 0;
      const src = nearestHostileCap(WAR_PD_RANGE);
      if (src) {
        const lp = this.local(src.pos.x, src.pos.y, src.pos.z, this._v4);
        // Fire from a random point on the hull face (turret spread).
        lp.x += (Math.random() - 0.5) * 900; lp.y += (Math.random() - 0.5) * 200; lp.z += (Math.random() - 0.5) * 900;
        const tracer = this.spriteGlow(0xffb090, 2.2);
        tracer.position.copy(lp);
        this.root.add(tracer);
        this.sfxAtLocal('sfx_point_defense', lp, { gain: 0.2, cooldownMs: 500 });
        this.bolts.push({
          sprite: tracer,
          vel: this._v5.copy(this.shipLocal()).sub(lp).normalize().multiplyScalar(BOLT_SPEED * 1.5)
            .add(this._v3.set((Math.random() - 0.5) * 60, (Math.random() - 0.5) * 60, (Math.random() - 0.5) * 60)).clone(),
          ttl: 2.4, hostile: true, dmg: 3, side: src.side as 0 | 1, // the point defence is the capital's too (never its own line's)
        });
      }
    }
  }

  /** Torpedo flight: heavy, slow, trailing — detonates on the target's hull
   *  cloud for major capital damage (the walls genuinely kill each other). */
  private updateTorpedoes(dt: number, win: { cyc: number; t: number }, b: NonNullable<typeof this.battle>): void {
    const root = this.root;
    if (!root) return;
    for (let i = this.torpedoes.length - 1; i >= 0; i--) {
      const tp = this.torpedoes[i];
      const gone = b.capDead[tp.tgtSide][tp.tgtK] || b.capGone[tp.tgtSide][tp.tgtK];
      const tgt = gone ? null : this.capPosNow(tp.tgtSide, tp.tgtK, win.t, this._v2);
      if (!tgt) {
        root.remove(tp.s);
        freeLater(tp.s.material);
        this.torpedoes.splice(i, 1);
        continue;
      }
      const dir = this._v3.copy(tgt).sub(tp.from).normalize();
      tp.from.addScaledVector(dir, tp.speed * dt);
      tp.s.position.copy(this.local(tp.from.x, tp.from.y, tp.from.z, this._v1));
      tp.trailAcc += dt;
      if (tp.trailAcc > 0.12 && this.fx && !this.spaceUnlit()) {
        tp.trailAcc = 0; // realism wave R5: a hot exhaust knot in the batch (no smoke puff, no SpriteMaterial)
        this.fx.knot(tp.from.x, tp.from.y, tp.from.z, 12);
      } else if (tp.trailAcc > 0.12 && this.fxLive.length < 140) {
        tp.trailAcc = 0;
        const puff = this.fxSprite(0x8fb0c8, 12);
        (puff.material as THREE.SpriteMaterial).opacity = 0.4;
        puff.position.copy(tp.s.position);
        root.add(puff);
        this.fxLive.push({ s: puff, vel: null, ttl: 0.9, life: 0.9, grow: 34, from: 12 });
      }
      if (tp.from.distanceTo(tgt) < 700) {
        this.fxImpact(this.local(tp.from.x, tp.from.y, tp.from.z, this._v1), 90, tp.tgtSide === 0 ? 0xffd8a0 : 0xaee6ff);
        this.damageCapital(tp.tgtSide as 0 | 1, tp.tgtK, 46, false, win);
        root.remove(tp.s);
        freeLater(tp.s.material);
        this.torpedoes.splice(i, 1);
      }
    }
  }

  /** Sworn pilots fly with an ESCORT SCREEN: your civ's gunships realize at
   *  your wing, weave with you, and gun down the war hostiles — both sides
   *  of the furball genuinely fight, and your allegiance decides which
   *  side is yours. */
  private updateWarAllies(dt: number, win: { cyc: number; t: number }, b: NonNullable<typeof this.battle>, dist: number): void {
    void dt; void win; void b;
    if (!this.root) return;
    const mySide: 0 | 1 | -1 = this.faction === 'aurelian' ? 0 : this.faction === 'borean' ? 1 : -1;
    const inZone = dist < WAR_ZONE_M && !this.interior;
    const wingmen = this.pirates.filter((q) => q.ally);
    if (mySide === -1 || !inZone) {
      if (wingmen.length) this.despawnWingmen();
      return;
    }
    // WINGMEN (2026-08-23, replaces the orbit-rig escorts): REAL gunships on
    // the pirate FSM — the enemy's exact hull class in your civ's colors,
    // the same movement, weapons, hp and evasion. The green halo says
    // friend; enemies gun them down and so can you (killPirate's ally path
    // carries the no-bounty death + the guilt toast).
    if (wingmen.length < WAR_ALLIES_MAX && performance.now() > this.allyRespawnAt) {
      this.allyRespawnAt = performance.now() + 1600;
      const i2 = wingmen.length;
      const a = (i2 / WAR_ALLIES_MAX) * Math.PI * 2;
      const pr = this.spawnHostile(`wing${i2}:ally`, 2,
        this.pos.x + Math.cos(a) * 260, this.pos.y + 40 + i2 * 14, this.pos.z + Math.sin(a) * 260, mySide);
      if (pr) {
        pr.ally = true;
        const g = this.spriteGlow(0x5cff9d, 26); // GREEN at range — a friend, not a threat
        (g.material as THREE.SpriteMaterial).opacity = 0.5;
        pr.root.add(g);
        this.warFlash(this.shipLocal().clone(), mySide, 20);
      }
    }
    // The formation home rides the player — respawns and patrol loiters
    // happen beside you, never at a spawn point kilometres behind the line.
    for (const q2 of wingmen) q2.home.copy(this.pos);
  }

  /** Remove every wingman — and, with `includeSkirmishers` (battle
   *  teardown), the whole war's gunships. The zone-exit/unsworn gate passes
   *  false: the battle is WORLD-anchored, a spectator leaving must never
   *  delete the two lines' fighters. */
  private despawnWingmen(includeSkirmishers = false): void {
    for (let i2 = this.pirates.length - 1; i2 >= 0; i2--) {
      const p2 = this.pirates[i2];
      const skirmisher = p2.warSide !== undefined && p2.key.startsWith('war');
      if (!p2.ally && !(includeSkirmishers && skirmisher)) continue;
      if (this.target === p2) this.target = null;
      this.root?.remove(p2.root);
      this.disposeSectorRoot(p2.root as THREE.Group); // traverse-only; shared master buffers survive (userData.shared)
      this.pirates.splice(i2, 1);
    }
    this.flushDisposeSoon();
  }



  /** Player fire vs the WAR: capitals take hull damage; hitting a swarm
   *  fighter realizes it (damaged and angry). Returns true if consumed. */
  private warBoltHit(at: THREE.Vector3, dmg: number, byPlayer = true, shot?: object, mount: 'gun' | 'missile' = 'gun', ageS?: number): boolean {
    const b = this.battle;
    if (!b) return false;
    const win = this.battleWindow();
    if (!win) return false;
    // A sworn pilot's own fire passes through their own line (2026-09-23): the server refuses the kill ("your own
    // banner"), and the torpedo war keeps a capital at 1 hp, so every friendly hit re-sent a doomed claim.
    const ownSide = byPlayer ? (this.faction === 'aurelian' ? 0 : this.faction === 'borean' ? 1 : -1) : -1;
    const abs = this._v1.set(at.x + this.off.x, at.y + this.off.y, at.z + this.off.z);
    if (Math.hypot(abs.x - b.frame.c.x, abs.z - b.frame.c.z) > WAR_ZONE_M) return false;
    for (let side = 0 as 0 | 1; side <= 1; side = (side + 1) as 0 | 1) {
      if (side === ownSide) { if (side === 1) break; continue; }
      for (let k = 0; k < b.capN[side]; k++) {
        if (b.capDead[side][k] || b.capGone[side][k] || win.t - b.capP[side][k].arr < WARP_STRETCH_S) continue;
        const p = this.capPosNow(side, k, win.t, this._v2);
        const broad = Math.hypot(p.x - abs.x, p.y - abs.y, p.z - abs.z);
        if (broad > WAR_CAP_LEN * 0.75) continue;
        // MESH-TRUE hit: the hull's sphere cloud in its live pose.
        const cloud = this.capCloud[side];
        const kin = b.capKin[side][k];
        let hit = broad < WAR_CAP_HIT_R; // fallback when no cloud/pose yet
        if (cloud && kin) {
          hit = false;
          for (const c of cloud) {
            const w = this._v3.set(c.x, c.y, c.z).applyQuaternion(kin.q);
            if (Math.hypot(p.x + w.x - abs.x, p.y + w.y - abs.y, p.z + w.z - abs.z) < c.r + 14) { hit = true; break; }
          }
        }
        if (hit) {
          this.warCapHits++;
          // Space campaign slice D: under a pinned war MY hit is an intent the record judges (the cue below is a prediction).
          if (byPlayer) this.warCapitalIntent(side, k, abs, shot, mount, ageS);
          this.fxImpact(this.local(abs.x, abs.y, abs.z, this._v3), 14, side === 0 ? 0xffd8a0 : 0xaee6ff);
          this.damageCapital(side, k, dmg, byPlayer, win);
          return true;
        }
      }
      // (Skirmishers are REAL pirates — the ordinary bolt-vs-pirate pass
      // owns those hits; this test is capitals-only now.)
      if (side === 1) break;
    }
    return false;
  }

  /** Route ALL capital damage here — bolts, missiles, torpedoes. THE ROSTER LAW: with a server roster the local
   *  picture never kills a capital — hp floors at 1 (a hull burning at its last), a PLAYER's finishing blow sends
   *  the kill request once, and the death arrives when the roster row flips (syncBattleToCampaign). Without a
   *  roster (a forced window, a pre-publish row) the old local authority stands. */
  private damageCapital(side: 0 | 1, k: number, dmg: number, byPlayer: boolean, win: { cyc: number; t: number }): void {
    const b = this.battle;
    if (!b || b.capDead[side][k] || b.capGone[side][k]) return;
    b.capHp[side][k] -= dmg;
    if (b.capHp[side][k] > 0) return;
    if (!b.rosterMode) { this.warCapitalKilled(side, k, win, byPlayer); return; }
    b.capHp[side][k] = 1;
    if (!byPlayer || b.capClaimSent[side][k]) return;
    const camp = this.campaignRow();
    if (!camp) return;
    // Space campaign slice D: a PINNED war takes no claim — the record's hp kills the hull and the roster row flips
    // (this hull burns at 1 hp until then, and the once-a-second reconcile restores it if the record disagrees).
    if (warPinned(camp.campaignId)) return;
    b.capClaimSent[side][k] = true;
    (atlas.conn?.reducers as unknown as { campaignKill(a: { campaignId: bigint; hullKey: string }): Promise<void> } | undefined)
      ?.campaignKill({ campaignId: camp.campaignId, hullKey: `cap${side}:${k}` })
      .then(() => this.toast('⚑ Receipt: a capital struck off the line (+10 pts this war)'))
      .catch((e: Error) => {
        const why = String(e?.message ?? e);
        // Re-arm only a refusal that can change (out of the zone, out of flight, the minute's budget); a claim the
        // record will never take — own banner, no such slot, already down, on the record, the war over — stays sent.
        if (/outside the war zone|flight|system|minute/i.test(why)) b.capClaimSent[side][k] = false;
        this.toast(`The record refuses the kill: ${why}`);
      });
  }

  // ── THE COMBAT AUTHORITY PILOT (space campaign slice D, 2026-09-30) ──────────────────────────────────────────────
  /** My hit on a capital of a PINNED war: one intent (the bolt's absolute fire origin and age, the impact point). */
  private warCapitalIntent(side: 0 | 1, k: number, abs: THREE.Vector3, shot: object | undefined, mount: 'gun' | 'missile', ageS?: number): void {
    const camp = this.campaignRow();
    if (!camp || !warPinned(camp.campaignId)) return;
    const origin = firedFrom(shot) ?? { x: this.pos.x, y: this.pos.y, z: this.pos.z, atMs: Date.now() };
    sendWarShot({ campaignId: camp.campaignId, kind: 'capital', side, slot: k, mount, origin, impact: { x: abs.x, y: abs.y, z: abs.z }, ageS: firedFrom(shot) ? ageS : undefined }, (m) => this.toast(m));
  }

  /** My hit on a frigate / corvette of a PINNED war (client-flown hulls: the record bounds them by the theatre). */
  private warEscortIntent(p: Pirate, shot: THREE.Object3D, mount: 'gun' | 'missile', ageS: number): void {
    if (p.ally || p.warSide === undefined || (p.cls !== 'frigate' && p.cls !== 'corvette')) return;
    const m = /^war([01]):(fri|cor)(\d+)$/.exec(p.key);
    const origin = firedFrom(shot);
    if (!m || !origin) return;
    const camp = this.campaignRow();
    if (!camp || !warPinned(camp.campaignId)) return;
    const at = shot.position;
    sendWarShot({ campaignId: camp.campaignId, kind: p.cls, side: Number(m[1]), slot: Number(m[3]), mount, origin, impact: { x: at.x + this.off.x, y: at.y + this.off.y, z: at.z + this.off.z }, ageS }, (msg) => this.toast(msg));
  }

  /** THE PICTURE FOLLOWS THE RECORD: under a pinned war, once a second every living capital and escort takes its
   *  `war_life` hp (my predicted hits, other pilots' hits, a judged miss restored, the AI war's local damage undone).
   *  Deaths stay the roster's (syncBattleToCampaign / updateWarEscorts). A PENDING escort resolves from the row: once
   *  my last intent has had its answer, an escort the record keeps alive fights on at the record's hp. */
  private warLivesSyncAt = 0;
  private syncWarLives(b: NonNullable<typeof this.battle>, camp: CampaignRow): void {
    const nowMs = performance.now();
    if (nowMs - this.warLivesSyncAt < 1000) return;
    this.warLivesSyncAt = nowMs;
    if (!warPinned(camp.campaignId)) return;
    for (const side of [0, 1] as const) {
      for (let k = 0; k < b.capN[side]; k++) {
        if (b.capDead[side][k] || b.capGone[side][k]) continue;
        const r = warLifeRow(camp.campaignId, 'capital', side, k);
        if (r && !lifeEnded(r)) b.capHp[side][k] = Math.max(1, r.hp);
      }
    }
    for (const p of this.pirates) {
      if (p.deadUntil !== 0 || p.ally || p.warSide === undefined || (p.cls !== 'frigate' && p.cls !== 'corvette')) continue;
      const m = /^war([01]):(fri|cor)(\d+)$/.exec(p.key);
      const r = m ? warLifeRow(camp.campaignId, p.cls, Number(m[1]), Number(m[3])) : null;
      if (!r || lifeEnded(r)) continue;
      if (pendingOf(p)) {
        if (msSinceIntent() < 1500) continue; // my lethal intent is still being judged
        setPending(p, p.key, undefined);
        p.slowUntil = 0; p.jamUntil = 0;
      }
      p.hp = Math.max(1, r.hp);
    }
  }

  /** THE LETHAL SALVO (report §9.2, P0): a ROUND's named death is drawn — the killer line's nearest living capitals put
   *  a heavy beam volley into THAT hull at that moment, so the visible fire explains the death. One damage budget: the
   *  round already decided it; these beams carry no hp. Other broadsides stay cosmetic. */
  private salvoCount = 0;
  /** Probe telemetry: capital hits this client saw (any shooter). */
  private warCapHits = 0;
  private drawLethalSalvo(b: NonNullable<typeof this.battle>, win: { cyc: number; t: number }, side: 0 | 1, atAbs: THREE.Vector3): void {
    this.salvoCount++;
    const killer = (1 - side) as 0 | 1;
    const line: Array<{ k: number; p: WarV3; alive: boolean }> = [];
    for (let j = 0; j < b.capN[killer]; j++) {
      if (b.capDead[killer][j] || b.capGone[killer][j]) continue;
      const q = this.capPosNow(killer, j, win.t, this._v2);
      line.push({ k: j, p: { x: q.x, y: q.y, z: q.z }, alive: true });
    }
    const lt = this.local(atAbs.x, atAbs.y, atAbs.z, new THREE.Vector3());
    for (const j of salvoShooters(atAbs, line, 3)) {
      const free = b.beamPool.find((mm) => !mm.visible);
      if (!free) break;
      const from = this.capPosNow(killer, j, win.t, this._v2);
      const lf = this.local(from.x, from.y, from.z, this._v4);
      free.position.copy(lf);
      this._m1.lookAt(lf, lt, UP); // −Z toward the dying hull (the beam geometry's axis)
      free.quaternion.setFromRotationMatrix(this._m1);
      const bm2 = free.userData.beamMats as [THREE.Material, THREE.Material] | undefined;
      if (bm2) free.material = bm2[killer];
      else (free.material as THREE.MeshBasicMaterial).color.setHex(killer === 0 ? 0xffe2a0 : 0xb8ecff);
      free.scale.set(55, 55, lf.distanceTo(lt));
      free.visible = true;
      b.beams.push({ m: free, ttl: 1.1 });
    }
    this.warFlash(lt, side, 900);
  }

  /** The pinned live war's theatre centre (absolute) while it is fought, else null — THE MASS LOCK's anchor. */
  private readonly theatreScratch = new THREE.Vector3();
  private pinnedTheatre(): THREE.Vector3 | null {
    if (this.system.id !== 'sys0') return null;
    const camp = this.campaignRow();
    if (!camp || (camp.state !== 'engaged' && camp.state !== 'retreat') || !warPinned(camp.campaignId)) return null;
    if (Date.now() / 1000 >= Number(camp.endsAt.microsSinceUnixEpoch / 1_000_000n)) return null;
    if (this.battle) return this.battle.frame.c;
    const win = this.battleWindow();
    if (!win) return null;
    return this.theatreScratch.set(win.poi.pos[0] + win.poi.radius * 1.6, win.poi.pos[1] + 400, win.poi.pos[2]);
  }

  /** The HUD's word on the record: the war judges every named hit, and the capital I last hit at its RECORD hull. */
  private warAuthorityLine(camp: CampaignRow): string {
    if (!warPinned(camp.campaignId)) return '';
    const mk = lastMark(60_000);
    const r = mk ? warLifeRow(camp.campaignId, 'capital', mk.side, mk.k) : null;
    return authorityHud(true, mk && r ? { name: `${mk.side === 0 ? 'Concord' : 'Compact'} capital #${mk.k + 1}`, hp: lifeEnded(r) ? 0 : r.hp, hpMax: r.hpMax } : null);
  }

  /**
   * A CAPITAL THAT DIES SLOWLY (physics follow-up item 7, 2026-10-05; audit-feel G11): the dead capital's hull — cut once
   * per hull model into 4–6 chunks (lib/space/breakup_mesh.ts) — drifts apart and TUMBLES as torque-free rigid bodies
   * with each chunk's own asymmetric inertia (lib/space/breakup.ts: Euler's equations, RK4 — precession and the
   * intermediate-axis flip emerge), venting gas and embers off its cut faces (the vacuum FX), then burns away (the
   * dissolve) over its last seconds, 20–40 s after the blast. Returns the longest chunk life (s) — the hulk joins the
   * yard then — or 0 when no breakup runs (the hull never materialised here, too far, the unlit path's hull).
   */
  private startBreakup(side: 0 | 1, k: number, cyc: number): number {
    const b = this.battle;
    const obj = b?.capObjs[side][k] ?? null;
    const kin = b?.capKin[side][k] ?? null;
    if (!b || !obj || !kin) { this.breakupStats.skipped = 'no hull'; return 0; }
    if (kin.p.distanceTo(this.pos) > 90_000) { this.breakupStats.skipped = 'far'; return 0; }
    const variant = breakupH(`bk:${cyc}:${side}:${k}`) < 0.5 ? 0 : 1;
    const master = this.warCapM[side];
    const mkey = `${side}:${variant}:${master ? master.uuid : 'procedural'}`;
    let model = this.breakupModels.get(mkey);
    if (model === undefined) {
      try { model = buildBreakupModel(obj, `cap${side}v${variant}`); } catch (e) { model = null; this.breakupStats.skipped = `build: ${String(e).slice(0, 80)}`; }
      this.breakupModels.set(mkey, model);
      if (model) { this.breakupStats.built++; this.breakupStats.buildMs = Math.round(model.buildMs * 10) / 10; }
    }
    if (!model || model.chunks.length < 2) return 0;
    while (this.breakups.length >= BREAKUP.maxLive) {
      // the oldest ends early (rare: six capitals inside half a minute)
      const old = this.breakups.shift()!;
      for (const c of old.chunks) { b.group.remove(c.root); for (const m of c.mats) this.disposeLater(m); }
    }
    const dissolve = activePreset().vfxShaders !== false && this.spaceFlag('spaceLitHulls');
    const qc = kin.q;
    const capQ: Q4 = [qc.w, qc.x, qc.y, qc.z];
    let longest = 0;
    const chunks: (typeof this.breakups)[number]['chunks'] = [];
    for (let ci = 0; ci < model.chunks.length; ci++) {
      const cm = model.chunks[ci];
      const key = `bk:${cyc}:${side}:${k}:${ci}`;
      const root = new THREE.Group();
      root.name = 'capital-chunk';
      const mats: THREE.Material[] = [];
      const handles: Array<{ progress: { value: number } }> = [];
      for (const m of cm.meshes) {
        let mat = m.material;
        // the burn-away: a dissolve clone per chunk (its pipeline compiles now, masked by the blast, not at the fade)
        if (dissolve && (mat as unknown as { isNodeMaterial?: boolean }).isNodeMaterial) {
          const c = mat.clone();
          // the noise reads positionLocal (the mesh's own units): ~9 burn patches along the 4.5 km hull whatever its units
          handles.push(applyDissolve(c as never, { progress: 0, edgeWidth: 0.06, edgeColor: [5, 1.6, 0.4], noiseScale: (9 * m.unitM) / WAR_CAP_LEN }));
          mats.push(c);
          mat = c;
        }
        const mesh = new THREE.Mesh(m.geo, mat);
        mesh.matrixAutoUpdate = false;
        mesh.matrix.copy(m.matrix);
        mesh.frustumCulled = false; // a kilometre chunk near the camera must not pop at a frustum edge
        root.add(mesh);
      }
      // the rigid body: centre of mass and the body frame in the world (ABSOLUTE), the capital's drift + a push outward
      const com = qrot(capQ, cm.com);
      const pos = new THREE.Vector3(kin.p.x + com[0], kin.p.y + com[1], kin.p.z + com[2]);
      const out = new THREE.Vector3(com[0], com[1], com[2]);
      if (out.lengthSq() < 1) out.set(0, 1, 0);
      out.normalize();
      const drift = BREAKUP.driftMps[0] + (BREAKUP.driftMps[1] - BREAKUP.driftMps[0]) * breakupH(`${key}|drift`);
      const vel = new THREE.Vector3().copy(kin.v).addScaledVector(out, drift).add(new THREE.Vector3(0, (breakupH(`${key}|up`) - 0.5) * 3, 0));
      const qa = cm.axes;
      const q0: Q4 = [ // capital ⊗ principal axes = the body frame's orientation in the world
        capQ[0] * qa[0] - capQ[1] * qa[1] - capQ[2] * qa[2] - capQ[3] * qa[3],
        capQ[0] * qa[1] + capQ[1] * qa[0] + capQ[2] * qa[3] - capQ[3] * qa[2],
        capQ[0] * qa[2] - capQ[1] * qa[3] + capQ[2] * qa[0] + capQ[3] * qa[1],
        capQ[0] * qa[3] + capQ[1] * qa[2] - capQ[2] * qa[1] + capQ[3] * qa[0],
      ];
      const rate = (BREAKUP.spinK / cm.lengthM) * (0.8 + 0.7 * breakupH(`${key}|spin`));
      const I = cm.I;
      const tumble: Tumble = { q: q0, L: initialSpin(key, I, rate), I };
      const life = chunkLife(key);
      longest = Math.max(longest, life);
      this.local(pos.x, pos.y, pos.z, root.position);
      root.quaternion.set(q0[1], q0[2], q0[3], q0[0]);
      b.group.add(root);
      chunks.push({ root, model: cm, tumble, pos, vel, life, age: 0, mats, handles, ventAcc: 0, key });
    }
    this.breakups.push({ cyc, side, k, blastAcc: 0, chunks });
    this.breakupStats.started++;
    this.breakupStats.chunks += chunks.length;
    return longest;
  }

  /**
   * Cut the hull models before anyone dies (item 7): ~20 ms a model (measured, warm), four per session at most (two civs
   * × two variants), in idle time — a capital's death never waits on the cut. A tab that never idles cuts at the death.
   */
  private prebuildBreakups(b: NonNullable<typeof this.battle>): void {
    if (this.breakupPrebuild) return;
    for (const side of [0, 1] as const) for (const v of [0, 1]) {
      const master = this.warCapM[side];
      const mkey = `${side}:${v}:${master ? master.uuid : 'procedural'}`;
      if (this.breakupModels.has(mkey)) continue;
      let obj: THREE.Object3D | null = null;
      for (let k = 0; k < b.capN[side] && !obj; k++) if (b.capObjs[side][k] && !b.capDead[side][k] && !b.capGone[side][k]) obj = b.capObjs[side][k];
      if (!obj) continue;
      this.breakupPrebuild = true;
      const src = obj;
      const run = (): void => {
        this.breakupPrebuild = false;
        if (!this.battle || this.breakupModels.has(mkey) || !src.parent) return;
        let m: BreakupModel | null = null;
        try { m = buildBreakupModel(src, `cap${side}v${v}`); } catch { m = null; }
        this.breakupModels.set(mkey, m);
        if (m) { this.breakupStats.prebuilt++; this.breakupStats.buildMs = Math.round(m.buildMs * 10) / 10; }
      };
      const ric = (globalThis as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
      if (ric) ric(run, { timeout: 5000 }); else run();
      return;
    }
  }

  /** One fixed step of every live breakup: drift, tumble, vent, burn away. */
  private stepBreakups(dt: number): void {
    const b = this.battle;
    if (!b || !this.breakups.length) return;
    const fx = this.fx && !this.spaceUnlit() ? this.fx : null;
    for (let bi = this.breakups.length - 1; bi >= 0; bi--) {
      const bk = this.breakups[bi];
      for (let ci = bk.chunks.length - 1; ci >= 0; ci--) {
        const c = bk.chunks[ci];
        c.age += dt;
        stepTumble(c.tumble, dt);
        c.pos.addScaledVector(c.vel, dt);
        const q = c.tumble.q;
        this.local(c.pos.x, c.pos.y, c.pos.z, c.root.position);
        c.root.quaternion.set(q[1], q[2], q[3], q[0]);
        // the cut faces vent: gas jets and embers off a seat on the face, along its outward normal (vacuum rules)
        if (fx && c.model.seats.length && c.age < c.life - BREAKUP.fadeS * 0.5) {
          c.ventAcc += ventRate(c.age, c.life) * dt;
          while (c.ventAcc >= 1) {
            c.ventAcc -= 1;
            const st = c.model.seats[(Math.random() * c.model.seats.length) | 0];
            const w = qrot(q, st.b), n = qrot(q, st.n);
            const size = Math.min(140, Math.max(30, c.model.lengthM * 0.05));
            fx.vent(c.pos.x + w[0], c.pos.y + w[1], c.pos.z + w[2], n[0] * size * 4 + c.vel.x, n[1] * size * 4 + c.vel.y, n[2] * size * 4 + c.vel.z, size);
          }
        }
        for (const h of c.handles) h.progress.value = fadeProgress(c.age, c.life);
        if (c.age >= c.life) {
          b.group.remove(c.root);
          for (const m of c.mats) this.disposeLater(m);
          bk.chunks.splice(ci, 1);
          this.breakupStats.ended++;
        }
      }
      // secondaries walk the cuts in the first seconds after the blast
      const first = bk.chunks[0];
      if (fx && first && first.age < BREAKUP.blastS) {
        bk.blastAcc += dt * 1.4;
        while (bk.blastAcc >= 1) {
          bk.blastAcc -= 1;
          const c = bk.chunks[(Math.random() * bk.chunks.length) | 0];
          const st = c.model.seats[(Math.random() * Math.max(1, c.model.seats.length)) | 0];
          if (!st) continue;
          const w = qrot(c.tumble.q, st.b);
          fx.impact(c.pos.x + w[0], c.pos.y + w[1], c.pos.z + w[2], WAR_CAP_LEN / 40, bk.side === 0 ? 0xffd8a0 : 0xaee6ff);
        }
      }
      if (!bk.chunks.length) this.breakups.splice(bi, 1);
    }
  }

  /** A capital dies: secondaries walk the hull, the core detonates with a
   *  shockwave ring scaled to the 4.5 km hull, hulks join the yard — and a
   *  PLAYER kill pays the t3 bounty. */
  private warCapitalKilled(side: 0 | 1, k: number, win: { cyc: number; t: number }, byPlayer: boolean, why?: string, ageS = 0): void {
    const b = this.battle;
    if (!b || b.capDead[side][k]) return;
    b.capDead[side][k] = true;
    const p = this.capPosNow(side, k, win.t, this._v2);
    const lp = this.local(p.x, p.y, p.z, this._v3).clone();
    this.fxShipDeath(lp, WAR_CAP_LEN, side === 0 ? 0xffd8a0 : 0xaee6ff);
    // A CAPITAL THAT DIES SLOWLY (item 7): the hull breaks into tumbling chunks under the explosion (cosmetic: nothing
    // here touches the roster or the server; the hulk joins the yard when the chunks have burned away)
    // a death a late joiner reads from the roster (minutes old) does not break apart in front of them now
    const breakLife = ageS <= 20 ? this.startBreakup(side, k, win.cyc) : 0;
    if (byPlayer) {
      const camp = this.campaignRow();
      if (camp && !b.rosterMode) { // in roster mode the claim went out from damageCapital and this death IS its answer
        (atlas.conn?.reducers as unknown as { campaignKill(a: { campaignId: bigint; hullKey: string }): Promise<void> } | undefined)
          ?.campaignKill({ campaignId: camp.campaignId, hullKey: `cap${side}:${k}` })
          .then(() => this.toast('⚑ Receipt: a capital struck off the line (+10 pts this war)'))
          .catch(() => { /* the war keeps its own count */ });
      }
      // The kill pays like a fortress ace (t3 den band, slots 60–63).
      const fortIdx = this.nearestPoiIdx(b.frame.c.x, b.frame.c.y, b.frame.c.z);
      atlas.conn?.reducers.spaceKill({ pirateKey: `poi${fortIdx}:pirate${60 + (k % 4)}` })
        .then(() => this.toast(`☄ CAPITAL DOWN — the ${side === 0 ? 'Concord' : 'Compact'} wall breaks (t3 bounty)`))
        .catch(() => this.toast('☄ CAPITAL DOWN — the wall breaks'));
    } else {
      this.toast(why ?? `☄ A ${side === 0 ? 'Concord' : 'Compact'} capital breaks apart under torpedo fire`);
    }
    // The hulk joins the salvage yard (the yard pre-allocates spare instanced slots for exactly this) — when the
    // breakup's chunks have burned away (item 7), at once when there was none. INSERTED IN ORDER: the yard count-packs
    // its timeline (`appearAt` ascending), so an entry pushed to the end waited behind every later timeline wreck.
    const wf = this.wreckField;
    if (wf) {
      const serial = b.capDead[0].filter(Boolean).length + b.capDead[1].filter(Boolean).length;
      for (let i = 0; i < 2 && wf.wrecks.length < wf.cap; i++) {
        const e = { key: `wreck:${win.cyc}:${side}:c${6 + serial * 2 + i}`, pos: new THREE.Vector3(p.x + (i * 1300 - 650), p.y, p.z + i * 800), cls: 'c' as const, appearAt: win.t + breakLife };
        let at = wf.wrecks.length;
        while (at > 0 && wf.wrecks[at - 1].appearAt > e.appearAt) at--;
        wf.wrecks.splice(at, 0, e);
      }
    }
  }

  // ── SPACE INDUSTRY: mining · salvage · exploration · sov · incursions ────
  /** My claim keys (wrecks stripped, sigs looted, cooldowns) — replicated by
   *  the `space_claim WHERE identity = :sender` sub. */
  /** My claimed keys — MEMOISED on the space_claim table's mutation stamp (lib/fast_find): the fixed step
   *  asked every tick, in every system, and walked every claim row each time (436 on the live shard,
   *  2026-09-22). Callers only read the set. */
  private claimsMemo: { stamp: number; me: string; set: Set<string> } | null = null;
  private myClaims(): Set<string> {
    const conn = atlas.conn;
    const stamp = tableStamp(conn?.db.spaceClaim);
    const me = conn?.identity ? conn.identity.toHexString() : '';
    if (this.claimsMemo && this.claimsMemo.stamp === stamp && this.claimsMemo.me === me) return this.claimsMemo.set;
    const out = new Set<string>();
    try {
      if (conn?.identity) {
        for (const c of conn.db.spaceClaim.iter()) {
          if (c.identity.isEqual(conn.identity)) out.add(c.claimKey);
        }
      }
    } catch { /* rows mid-churn */ }
    this.claimsMemo = { stamp, me, set: out };
    return out;
  }

  /** Total units of stock in the hold (all resource stacks). */
  /** THE HOLD LAW (Space Wave 2): the units aboard the ACTIVE hull — cargo_lot
   *  rows at ship:<activeShipId> (the bag is the ground; it never counts here). */
  private myActiveShipId(): bigint | null {
    const conn = atlas.conn;
    if (!conn?.identity) return null;
    try { for (const s of conn.db.playerShip.iter()) if (s.owner.isEqual(conn.identity) && s.active) return s.shipId; } catch { /* fine */ }
    return null;
  }
  private cargoUnits(): number {
    const conn = atlas.conn;
    const sid = this.myActiveShipId();
    let n = 0;
    try {
      if (conn?.identity && sid !== null) {
        const loc = `ship:${sid}`;
        for (const l of conn.db.cargoLot.iter()) if (l.owner.isEqual(conn.identity) && l.locKind === 'ship' && l.locId === loc) n += l.qty;
      }
    } catch { /* fine */ }
    return n;
  }
  private holdCap(): number {
    return this.stats.cargo * HOLD_UNITS_PER_CARGO;
  }
  /** Player wrecks in THIS system (THE WRECK LAW): rendered as debris, strippable within range. */
  private playerWrecks = new Map<string, { obj: THREE.Group; pos: THREE.Vector3; wreckId: bigint; name: string }>();
  private wrecksTickAt = 0;
  private tickPlayerWrecks(): void {
    const conn = atlas.conn;
    const root = this.root;
    if (!conn || !root || performance.now() < this.wrecksTickAt) return;
    this.wrecksTickAt = performance.now() + 2000;
    const live = new Set<string>();
    try {
      for (const w of conn.db.shipWreck.iter()) {
        if (w.systemId !== this.system.id) continue;
        const key = String(w.wreckId);
        live.add(key);
        let e = this.playerWrecks.get(key);
        if (!e) {
          const obj = new THREE.Group();
          const core = this.spriteGlow(0xffa070, 40);
          (core.material as THREE.SpriteMaterial).opacity = 0.75;
          obj.add(core);
          for (let i = 0; i < 4; i++) {
            const sp = this.spriteGlow(0xff8a50, 9);
            sp.position.set(Math.sin(i * 1.7) * 26, Math.cos(i * 2.3) * 12, Math.cos(i * 1.7) * 26);
            obj.add(sp);
          }
          root.add(obj);
          e = { obj, pos: new THREE.Vector3(w.x, w.y, w.z), wreckId: w.wreckId, name: w.victimName };
          this.playerWrecks.set(key, e);
        }
        e.obj.position.copy(this.local(e.pos.x, e.pos.y, e.pos.z, this._v1));
        e.obj.visible = e.obj.position.distanceTo(this.shipLocal()) < 20_000;
      }
    } catch { /* not subscribed yet */ }
    for (const [key, e] of this.playerWrecks) {
      if (live.has(key)) continue;
      root.remove(e.obj);
      for (const c of e.obj.children) { const m = (c as THREE.Sprite).material as THREE.SpriteMaterial | undefined; if (m) this.disposeLater(m); } // the teardown-flood law
      this.playerWrecks.delete(key);
    }
  }

  /** The ore a given field carries — hashed per (system, field), tier-gated
   *  so rimward systems yield the rich seams. */
  private fieldOre(fieldIdx: number): { key: string; label: string } {
    const pool = oresForTier(sysTierOf(this.system.id));
    const o = pool[Math.floor(warH(`${this.system.id}|field${fieldIdx}`) * pool.length) % pool.length];
    return { key: o.key, label: o.label };
  }

  /** The beam's current work target, by priority: revealed signature →
   *  unclaimed wreck → asteroid field. */
  private beamTarget(): { mode: 'recover' | 'salvage' | 'mine' | 'strip'; pos: THREE.Vector3; label: string; key: string } | null {
    // 1) A resolved signature beacon in reach.
    for (const [key, obj] of this.sigBeacons) {
      const d = obj.position.distanceTo(this.shipLocal());
      if (d < 600) {
        return { mode: 'recover', pos: obj.position.clone(), label: 'signature', key };
      }
    }
    // 1½) A player wreck in strip range (Space Wave 2 — THE WRECK LAW). Among several in reach the
    // beam takes the one you FACE (the smallest angle off the nose, inside BEAM_PICK_CONE); nothing
    // ahead → the nearest. Point the ship to choose.
    {
      const sl = this.shipLocal();
      const fwd = this._v2.set(0, 0, -1).applyQuaternion(this.quat);
      let best: { key: string; pos: THREE.Vector3; d: number; name: string; ang: number } | null = null;
      for (const [key, e] of this.playerWrecks) {
        const d = e.obj.position.distanceTo(sl);
        if (d >= WRECK_STRIP_RANGE_M) continue;
        const ang = d > 1 ? Math.acos(THREE.MathUtils.clamp(this._v1.copy(e.obj.position).sub(sl).divideScalar(d).dot(fwd), -1, 1)) : 0;
        const facing = ang < BEAM_PICK_CONE, bestFacing = best !== null && best.ang < BEAM_PICK_CONE;
        const better = !best || (facing && !bestFacing) || (facing === bestFacing && (facing ? ang < best.ang : d < best.d));
        if (better) best = { key, pos: e.obj.position.clone(), d, name: e.name, ang };
      }
      if (best) return { mode: 'strip', pos: best.pos, label: `the wreck of ${best.name}${this.playerWrecks.size > 1 ? (best.ang < BEAM_PICK_CONE ? ' (ahead)' : ' (nearest — face a wreck to choose)') : ''}`, key: best.key };
    }
    // 2) A wreck in cutter range.
    const wf = this.wreckField;
    if (wf) {
      const claims = this.myClaims();
      const sl = this.shipLocal().clone();
      let best: { key: string; pos: THREE.Vector3; d: number } | null = null;
      const nowCount = wf.inst.count;
      for (let i = 0; i < nowCount; i++) {
        const w = wf.wrecks[i];
        if (claims.has(w.key)) continue;
        const lp = this.local(w.pos.x, w.pos.y, w.pos.z, this._v1);
        const d = lp.distanceTo(sl);
        if (d < 220 && (!best || d < best.d)) best = { key: w.key, pos: lp.clone(), d };
      }
      if (best) return { mode: 'salvage', pos: best.pos, label: 'wreck', key: best.key };
    }
    // 3) Inside an asteroid field → the field's seam.
    for (const [gi, g] of this.colliderGroups.entries()) {
      const d = Math.hypot(g.x - this.pos.x, g.y - this.pos.y, g.z - this.pos.z);
      if (d < g.reach + 400) {
        const ore = this.fieldOre(gi);
        // Aim the beam at the nearest rock of the group (visual anchor).
        const lp = this.local(g.x, g.y, g.z, this._v1).clone();
        return { mode: 'mine', pos: lp, label: ore.label, key: ore.key };
      }
    }
    return null;
  }

  /** U held → the utility beam works the target on server-mirrored cycles. */
  private updateIndustryBeam(dt: number): void {
    const root = this.root;
    const active = this.beamHeld() && !this.interior && !this.sysJump && this.active;
    const tgt = active ? this.beamTarget() : null;
    if (!tgt || !root) {
      if (this.beam) this.beam.visible = false;
      sfxLoop('sfx_mining_laser', 'space').set(0, 1, 0.2);
      this.beamNextAt = 0;
      return;
    }
    // The cutting hum rides the live beam (salvage runs slightly lower-pitched).
    sfxLoop('sfx_mining_laser', 'space').set(0.3, tgt.mode === 'mine' ? 1 : 0.85, 0.2);
    if (!this.beam) {
      const geo = new THREE.BoxGeometry(1, 1, 1).translate(0, 0, -0.5);
      // markMrtOverlay (session 3): the cutting beam is an additive overlay — it must leave the rock's velocity / normals alone (the MRT overlay law)
      const mat = markMrtOverlay(new THREE.MeshBasicMaterial({
        color: 0xffc46a, transparent: true, opacity: 0.7,
        blending: THREE.AdditiveBlending, depthWrite: false,
      }));
      this.beam = new THREE.Mesh(geo, mat);
      this.beam.frustumCulled = false;
      root.add(this.beam);
    }
    const sl = this.shipLocal();
    this.beam.visible = true;
    this.beam.position.copy(sl);
    this._m1.lookAt(sl, tgt.pos, UP);
    this.beam.quaternion.setFromRotationMatrix(this._m1);
    const len = Math.min(sl.distanceTo(tgt.pos), 2400);
    const pulse = 1 + Math.sin(performance.now() / 90) * 0.35;
    this.beam.scale.set(0.9 * pulse, 0.9 * pulse, len);
    (this.beam.material as THREE.MeshBasicMaterial).color.setHex(
      tgt.mode === 'mine' ? 0xffc46a : tgt.mode === 'salvage' ? 0x9fe8ff : 0xc9a6ff,
    );
    // Cycle → claim (server enforces the true cooldown; we pace to match).
    void dt;
    // space campaign F: a shared site / the mystery's place beats once a second (the server meters and credits it)
    const cycle = tgt.mode === 'recover' && discovery.handles(tgt.key) ? 1.0 : tgt.mode === 'mine' ? MINE_CYCLE_S : tgt.mode === 'salvage' || tgt.mode === 'strip' ? SALVAGE_CYCLE_S : 1.6;
    const now = performance.now();
    if (this.beamNextAt === 0) { this.beamNextAt = now + cycle * 1000; return; }
    if (now < this.beamNextAt || this.beamBusy) return;
    this.beamNextAt = now + cycle * 1000;
    this.beamBusy = true;
    const conn = atlas.conn;
    const done = (): void => { this.beamBusy = false; };
    if (tgt.mode === 'mine') {
      conn?.reducers.spaceMine({ oreKey: tgt.key })
        .then(() => { sfx('sfx_ore_yield', { gain: 0.4, cooldownMs: 500 }); this.toast(`⛏ extraction cycle — ${tgt.label} (hold ${this.cargoUnits()}/${this.holdCap()} u)`); done(); })
        .catch((e: Error) => { this.toast(`Beam sputters: ${e.message ?? e}`); done(); });
    } else if (tgt.mode === 'salvage') {
      conn?.reducers.spaceSalvage({ wreckKey: tgt.key })
        .then(() => { sfx('sfx_ore_yield', { gain: 0.4, rate: 0.9, cooldownMs: 500 }); this.toast(`🛠 wreck stripped — scrap + circuitry aboard (hold ${this.cargoUnits()}/${this.holdCap()} u)`); done(); })
        .catch((e: Error) => { this.toast(`Cutter skips: ${e.message ?? e}`); done(); });
    } else if (tgt.mode === 'strip') {
      // THE WRECK LAW: a player wreck's cargo comes aboard, up to the free space.
      (conn?.reducers as unknown as { spaceStripWreck(a: { wreckId: bigint }): Promise<void> } | undefined)
        ?.spaceStripWreck({ wreckId: BigInt(tgt.key) })
        .then(() => { sfx('sfx_loot_pickup', { gain: 0.45 }); this.toast(`🛠 ${tgt.label} stripped — cargo aboard (hold ${this.cargoUnits()}/${this.holdCap()} u)`); done(); })
        .catch((e: Error) => { this.toast(`Cutter skips: ${e.message ?? e}`); done(); });
    } else if (discovery.handles(tgt.key)) {
      // space campaign F: DISCOVERY THAT MAKES WORK — a heartbeat, never a client timer that grants anything.
      discovery.beat(tgt.key, (okBeat, msg) => {
        if (okBeat) return;
        const t = performance.now();
        if (msg !== this.surveyNote.text || t - this.surveyNote.at > 6_000) { this.surveyNote = { text: msg, at: t }; this.toast(`📡 ${msg}`); }
      });
      done();
    } else {
      conn?.reducers.spaceScanLoot({ sigKey: tgt.key })
        .then(() => {
          sfx('sfx_loot_pickup', { gain: 0.45 });
          this.toast('📡 signature recovered — the vault yields');
          const b = this.sigBeacons.get(tgt.key);
          if (b) { this.root?.remove(b); this.sigBeacons.delete(tgt.key); }
          done();
        })
        .catch((e: Error) => { this.toast(`Recovery failed: ${e.message ?? e}`); done(); });
    }
  }

  // ── Wreck fields: the war leaves a salvage yard on the shared clock ──────
  private wreckWindow(): { cyc: number; t: number; poi: PoiSpec } | null {
    const forts = warForts();
    if (!forts.length || this.system.id !== 'sys0') return null;
    if (this.battleForce > 0) {
      return { cyc: 424242, t: Math.max(0, Date.now() / 1000 - this.battleForce), poi: forts[0] };
    }
    const now = Date.now() / 1000;
    const cyc = Math.floor(now / BATTLE_PERIOD_S);
    return { cyc, t: now - cyc * BATTLE_PERIOD_S, poi: forts[cyc % forts.length] };
  }

  private teardownWrecks(): void {
    const wf = this.wreckField;
    if (!wf) return;
    this.root?.remove(wf.group);
    // freeLater, NEVER direct: this ran SYNCHRONOUSLY inside switchSystem —
    // the multi-second swap frame still referenced the yard's instanced
    // buffers in its in-flight submit (THE hyperwarp destroyed-buffer
    // flood, forensically pinned 2026-08-23; it hid from the static gate
    // because its loop variable matched the gate's flush-internals allowlist).
    for (const d of wf.own) freeLater(d);
    this.wreckField = null;
  }

  private updateWrecks(dt: number): void {
    void dt;
    const win = this.wreckWindow();
    const root = this.root;
    if (!win || !root || win.t < 8) { this.teardownWrecks(); return; }
    const center = this._v1.set(win.poi.pos[0] + win.poi.radius * 1.6, win.poi.pos[1] + 400, win.poi.pos[2]);
    if (this.pos.distanceTo(center) > BATTLE_VIS_M) { this.teardownWrecks(); return; }
    if (!this.wreckField || this.wreckField.cyc !== win.cyc) {
      this.teardownWrecks();
      const own: Array<{ dispose(): void }> = [];
      const group = new THREE.Group();
      // Shard silhouette: three skewed plates — reads as a broken hull.
      const shard = mergeGeometries([
        new THREE.BoxGeometry(4, 1, 7),
        new THREE.BoxGeometry(1.4, 3.4, 3).translate(1.2, 0.8, -1),
        new THREE.BoxGeometry(2.6, 0.8, 4).translate(-1.6, -0.7, 2.2),
      ]);
      const mat = new THREE.MeshStandardMaterial({ color: 0x3c4048, roughness: 0.9, metalness: 0.4, emissive: 0xff7733, emissiveIntensity: 0.08 });
      own.push(shard, mat);
      // Wrecks appear in the order the war chews through hulls: fighters
      // every ~7 s, capitals every ~110 s — one count-packed timeline.
      const entries: Array<{ key: string; pos: THREE.Vector3; cls: 'c' | 'f'; appearAt: number }> = [];
      for (let i = 0; i < 80; i++) {
        const s = `wr${win.cyc}f${i}`;
        const a = warH(s + 'a') * Math.PI * 2;
        const r = 300 + warH(s + 'r') * 2100;
        entries.push({
          key: `wreck:${win.cyc}:${i % 2}:f${i}`,
          pos: new THREE.Vector3(center.x + Math.cos(a) * r, center.y + (warH(s + 'y') - 0.5) * 900, center.z + Math.sin(a) * r),
          cls: 'f', appearAt: 8 + i * 7,
        });
      }
      for (let j = 0; j < 6; j++) {
        const s = `wr${win.cyc}c${j}`;
        const a = warH(s + 'a') * Math.PI * 2;
        const r = 1400 + warH(s + 'r') * 1200;
        entries.push({
          key: `wreck:${win.cyc}:${j % 2}:c${j}`,
          pos: new THREE.Vector3(center.x + Math.cos(a) * r, center.y + (warH(s + 'y') - 0.5) * 500, center.z + Math.sin(a) * r),
          cls: 'c', appearAt: 110 + j * 110,
        });
      }
      entries.sort((x, y) => x.appearAt - y.appearAt);
      // +12 spare slots: capitals killed by PLAYERS shed hulks mid-war.
      const cap = entries.length + 12;
      const inst = new THREE.InstancedMesh(shard, mat, cap);
      inst.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      inst.count = 0;
      inst.frustumCulled = false;
      group.add(inst);
      const embers: THREE.Sprite[] = [];
      for (let k = 0; k < 8; k++) {
        const e = this.spriteGlow(0xff9955, 9);
        (e.material as THREE.SpriteMaterial).opacity = 0.4;
        own.push(e.material);
        group.add(e);
        embers.push(e);
      }
      root.add(group);
      this.wreckField = { cyc: win.cyc, group, inst, cap, wrecks: entries, embers, own };
    }
    const wf = this.wreckField;
    wf.group.position.set(0, 0, 0);
    const entries = wf.wrecks;
    let count = 0;
    while (count < entries.length && entries[count].appearAt <= win.t) count++;
    for (let i = 0; i < count; i++) {
      const w = entries[i];
      const lp = this.local(w.pos.x, w.pos.y, w.pos.z, this._v2);
      const spin = performance.now() / 1000 * 0.07 + i;
      this._q1.setFromEuler(this._e1.set(spin * 0.4, spin, spin * 0.23));
      const s = w.cls === 'c' ? 40 : 2.6; // capital hulks read at dreadnought scale
      this._m2.compose(lp, this._q1, this._v3.set(s, s, s));
      wf.inst.setMatrixAt(i, this._m2);
    }
    wf.inst.count = count;
    wf.inst.instanceMatrix.needsUpdate = true;
    for (const [k, e] of wf.embers.entries()) {
      if (k >= count) { e.visible = false; continue; }
      const w = entries[(k * 9) % Math.max(1, count)];
      e.visible = true;
      e.position.copy(this.local(w.pos.x, w.pos.y, w.pos.z, this._v2));
      (e.material as THREE.SpriteMaterial).opacity = 0.25 + Math.sin(performance.now() / 300 + k) * 0.15;
    }
  }

  // ── Exploration: scanner pings narrow hidden sites (THE HIDDEN-POSITION LAW: the seat is the server's) ──
  /** My reveal of one hidden site (a signature key or a throat's `wh:…@sys` key). */
  /** My scanner reveals by site key — memoised on the sig_reveal stamp (asked per live site, per tick).
   *  The FIRST matching row in iteration order wins, exactly as the scan it replaces. */
  private revealsMemo: { stamp: number; me: string; byKey: Map<string, RevealRow> } | null = null;
  private revealOf(key: string): RevealRow | null {
    const conn = atlas.conn;
    if (!conn?.identity) return null;
    const stamp = tableStamp(conn.db.sigReveal);
    const me = conn.identity.toHexString();
    if (!this.revealsMemo || this.revealsMemo.stamp !== stamp || this.revealsMemo.me !== me) {
      const byKey = new Map<string, RevealRow>();
      try { for (const r of conn.db.sigReveal.iter()) if (r.identity.isEqual(conn.identity) && !byKey.has(r.sigKey)) byKey.set(r.sigKey, r as RevealRow); } catch { /* pre-publish */ }
      this.revealsMemo = { stamp, me, byKey };
    }
    return this.revealsMemo.byKey.get(key) ?? null;
  }
  /** The wormhole_flux rows by hole key — memoised on the table's stamp (read per hole, per tick). */
  private fluxMemo: { stamp: number; byKey: Map<string, { whKey: string; massUsed: number; collapsed: boolean }> } | null = null;
  private fluxByKey(): Map<string, { whKey: string; massUsed: number; collapsed: boolean }> {
    const tbl = (atlas.conn?.db as unknown as { wormholeFlux?: { iter(): Iterable<{ whKey: string; massUsed: number; collapsed: boolean }> } } | undefined)?.wormholeFlux;
    const stamp = tableStamp(tbl);
    if (this.fluxMemo && this.fluxMemo.stamp === stamp) return this.fluxMemo.byKey;
    const byKey = new Map<string, { whKey: string; massUsed: number; collapsed: boolean }>();
    try { for (const f of tbl?.iter() ?? []) if (!byKey.has(f.whKey)) byKey.set(f.whKey, f); } catch { /* pre-publish */ }
    this.fluxMemo = { stamp, byKey };
    return byKey;
  }
  private myReveals(): RevealRow[] {
    const conn = atlas.conn;
    if (!conn?.identity) return [];
    try { return [...conn.db.sigReveal.iter()].filter((r) => r.identity.isEqual(conn.identity!)) as RevealRow[]; } catch { return []; }
  }
  /** A resolved site's seat (my round-3 reveal), else null — nothing on this client can compute it. */
  private sigPos(key: string, out: THREE.Vector3): THREE.Vector3 | null {
    const r = this.revealOf(key);
    if (!r || r.round < SIG_SCAN_CYCLES) return null;
    return out.set(r.x, r.y, r.z);
  }

  private liveSigs(): SpaceSig[] {
    const claims = this.myClaims();
    return sigsOf(this.system.id, scanEpoch(Date.now())).filter((s) => !claims.has(s.key));
  }
  /** The wormholes open in this system right now (born, not evaporated, not collapsed). */
  private liveWormholes(): Wormhole[] {
    const eph = scanEpoch(Date.now());
    const ageS = (Date.now() - eph * 4 * 3600_000) / 1000;
    const flux = this.fluxByKey();
    return wormholesOf(eph).filter((w) => {
      if (w.a !== this.system.id && w.b !== this.system.id) return false;
      const at = whAttrsOf(w.key);
      return ageS >= at.bornOffsetS && ageS <= at.bornOffsetS + at.lifetimeS && !flux.get(w.key)?.collapsed;
    });
  }
  /** Hidden sites here I have not resolved: signatures and the throats of live wormholes. */
  private unresolvedCount(): number {
    let n = 0;
    for (const sg of this.liveSigs()) if ((this.revealOf(sg.key)?.round ?? 0) < SIG_SCAN_CYCLES) n++;
    for (const w of this.liveWormholes()) if ((this.revealOf(whHiddenKey(w.key, this.system.id))?.round ?? 0) < SIG_SCAN_CYCLES) n++;
    return n;
  }

  private scanPing(): void {
    if (!this.active || this.interior || this.sysJump) return;
    const now = performance.now();
    if (now < this.scanCooldownUntil) { this.toast('Scanner recharging…'); return; }
    this.scanCooldownUntil = now + discovery.scanCooldown(this.system.id) * 1000; // space campaign F: the Fenmark relay's aid (3 s in the Commons Reach)
    sfx('sfx_scan_ping', { gain: 0.4, cooldownMs: 800 });
    const conn = atlas.conn;
    if (!conn) return;
    // The SERVER narrows the nearest unresolved site from the streamed position; the answer is the reveal row that advanced.
    const before = new Map<string, number>();
    for (const r of this.myReveals()) before.set(r.sigKey, r.round);
    (conn.reducers as unknown as { scanPing(a: Record<string, never>): Promise<void> }).scanPing({})
      .then(async () => {
        let hit: RevealRow | null = null;
        for (let i = 0; i < 20 && !hit; i++) {
          for (const r of this.myReveals()) if ((before.get(r.sigKey) ?? 0) < r.round) { hit = r; break; }
          if (!hit) await new Promise((res) => setTimeout(res, 100));
        }
        if (!hit) { this.toast('📡 Ping — nothing new'); return; }
        const what = hit.kind === 'wormhole' ? 'WORMHOLE throat' : `${hit.kind} signature`;
        const dist = Math.round(hit.rangeM / 1000);
        if (hit.round < SIG_SCAN_CYCLES) {
          this.toast(`📡 Ping ${hit.round}/${SIG_SCAN_CYCLES} — a ${what}, ~${dist} km, bearing ${Math.round(hit.bearingDeg)}° (keep pinging to resolve)`);
        } else if (hit.kind === 'wormhole') {
          this.toast(`📡 THROAT RESOLVED — the wormhole's mouth is marked, ${dist} km out (fly into it to cross)`);
        } else {
          this.toast(`📡 SIGNATURE RESOLVED — ${hit.kind} beacon marked, ${dist} km out (fly to it, hold U)`);
        }
      })
      .catch((e: Error) => this.toast(`📡 ${String(e.message ?? e)}`));
  }

  private updateSigs(): void {
    if (!this.root) return;
    discovery.ensure(); // space campaign F: the survey rows this pilot may see (idempotent)
    const live = new Set<string>();
    const extra = discovery.extraBeacons(this.system.id);
    for (const sg of [...this.liveSigs(), ...extra.map((e) => ({ key: e.key }))]) {
      const ex = extra.find((e) => e.key === sg.key);
      const p = ex ? this._v1.set(ex.x, ex.y, ex.z) : this.sigPos(sg.key, this._v1);
      if (!p) continue; // unresolved: no beacon — the seat is the server's until the third ping
      if (!ex && discovery.siteGone(sg.key)) continue; // space campaign F: a shared site taken / collapsed / faded shows no beacon
      live.add(sg.key);
      if (!this.sigBeacons.has(sg.key)) {
        const g = new THREE.Group();
        const core = new THREE.Mesh(
          new THREE.OctahedronGeometry(26),
          markMrtOverlay(new THREE.MeshBasicMaterial({ color: 0xc9a6ff, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false })),
        );
        const glow = this.spriteGlow(0xc9a6ff, 160);
        (glow.material as THREE.SpriteMaterial).opacity = 0.5;
        g.add(core, glow);
        this.root.add(g);
        this.sigBeacons.set(sg.key, g);
      }
      const b = this.sigBeacons.get(sg.key)!;
      b.position.copy(this.local(p.x, p.y, p.z, this._v2));
      b.rotation.y += 0.01;
    }
    for (const [k, obj] of [...this.sigBeacons]) {
      if (!live.has(k)) { this.root.remove(obj); this.sigBeacons.delete(k); }
    }
  }

  // ── Wormholes: temporary free crossings between systems ──────────────────
  private sysChatSub: { unsubscribe(): void } | null = null;
  private sysChatSystem = '';
  /** Keep the sys:<system> chat channel subscribed — ONE live handle per system.
   *  (bug sweep) the 30 s heartbeat re-subscribed the identical query with no
   *  handle: an hour of flight held ~120 live subscriptions, every system jump
   *  stranded the old channel for the session. Subscribe-new-then-unsubscribe-old. */
  private ensureSysChatSub(systemId: string): void {
    if (this.sysChatSub && this.sysChatSystem === systemId) return;
    const prev = this.sysChatSub;
    try {
      this.sysChatSub = (atlas.conn?.subscriptionBuilder()
        .onApplied(() => { try { prev?.unsubscribe(); } catch { /* gone */ } })
        .subscribe([`SELECT * FROM chat_message WHERE kind = 'sys:${systemId}'`]) ?? null) as { unsubscribe(): void } | null;
      this.sysChatSystem = systemId;
    } catch { /* fine */ }
  }

  private updateWormholes(dt: number): void {
    void dt;
    const root = this.root;
    if (!root) return;
    // The LOCAL roster heartbeat: report our system every 30 s (intel, not
    // authority) and keep the sys:<system> channel subscribed.
    if (performance.now() > this.presencePingAt && atlas.conn?.identity) {
      this.presencePingAt = performance.now() + 30_000;
      // Space Wave 1: the ping carries NOTHING — the server writes the roster
      // from the location row and the active hull (presence is a projection).
      (atlas.conn.reducers as unknown as { spacePresencePing(a: Record<string, never>): Promise<void> })
        .spacePresencePing({}).catch(() => { /* pre-publish */ });
      this.ensureSysChatSub(this.system.id);
    }
    // LIVING HOLES (tier-1): each has a birth + lifetime inside the epoch, a
    // mass budget (flux rows mirror the server ledger), and an effect.
    const fluxOf = (key: string): { massUsed: number; collapsed: boolean } | null => this.fluxByKey().get(key) ?? null;
    const mine = this.liveWormholes();
    const liveKeys = new Set(mine.map((w) => w.key));
    for (const w of mine) {
      let entry = this.wormholeObjs.find((o) => o.key === w.key);
      if (!entry) {
        // THE HIDDEN-POSITION LAW: the throat's seat is the server's — a mouth appears only once MY scanner resolved it.
        const seat = this.sigPos(whHiddenKey(w.key, this.system.id), this._v1);
        if (!seat) continue;
        const pos = seat.clone();
        const obj = new THREE.Group();
        const core = this.spriteGlow(0x8fd0ff, 420);
        (core.material as THREE.SpriteMaterial).opacity = 0.7;
        obj.add(core);
        for (let i = 0; i < 10; i++) {
          const sp = this.spriteGlow(0xbfe6ff, 60);
          (sp.material as THREE.SpriteMaterial).opacity = 0.5;
          sp.userData.ang = (i / 10) * Math.PI * 2;
          obj.add(sp);
        }
        root.add(obj);
        entry = { key: w.key, to: w.a === this.system.id ? w.b : w.a, pos, obj };
        this.wormholeObjs.push(entry);
      }
      entry.obj.position.copy(this.local(entry.pos.x, entry.pos.y, entry.pos.z, this._v1));
      { // The throat tightens as mass passes: full → shrunk (50 %) → critical (90 %).
        const at = whAttrsOf(entry.key);
        const used = fluxOf(entry.key)?.massUsed ?? 0;
        const pct = (used / at.massBudget) * 100;
        const scale = pct >= 90 ? 0.45 : pct >= 50 ? 0.72 : 1;
        entry.obj.scale.setScalar(scale);
        const core = entry.obj.children[0] as THREE.Sprite | undefined;
        if (core?.material) {
          (core.material as THREE.SpriteMaterial).color.setHex(
            pct >= 90 ? 0xff8866 : at.effect === 'magnetar' ? 0xffb090 : at.effect === 'pulsar' ? 0x9fd8ff : at.effect === 'cataclysm' ? 0xd0a8ff : 0x8fd0ff);
        }
      }
      const t = performance.now() / 1000;
      for (const child of entry.obj.children) {
        const ang = (child.userData.ang as number | undefined);
        if (ang === undefined) continue;
        child.position.set(Math.cos(ang + t * 0.7) * 320, Math.sin((ang + t * 0.7) * 2) * 90, Math.sin(ang + t * 0.7) * 320);
      }
      // Transit: fly the throat — the SERVER weighs the ship first (mass
      // ledger); a collapsed or evaporated hole refuses, and enough mass
      // slams the door behind the last jumper.
      if (!this.sysJump && !this.interior && this.pos.distanceTo(entry.pos) < 700 && this.whJumpAt < performance.now()) {
        this.whJumpAt = performance.now() + 5000; // one attempt per approach
        const target = this.galaxySystems().find((sy) => sy.id === entry.to);
        const conn2 = atlas.conn;
        if (target && conn2) {
          const key = entry.key;
          (conn2.reducers as unknown as { wormholeJump(a: { whKey: string }): Promise<void> })
            .wormholeJump({ whKey: key })
            .then(() => {
              const at = whAttrsOf(key);
              this.toast(`⨀ The wormhole takes you — ${target.name}${at.effect ? ` · ${at.effect.toUpperCase()} space` : ''}`);
              this.switchSystem(target);
            })
            .catch((e: Error) => this.toast(`⨀ ${String(e.message ?? e)}`));
          return;
        }
      }
    }
    for (let i = this.wormholeObjs.length - 1; i >= 0; i--) {
      const o = this.wormholeObjs[i];
      if (!liveKeys.has(o.key)) {
        root.remove(o.obj);
        for (const c of o.obj.children) { const mat = (c as THREE.Sprite).material as THREE.SpriteMaterial | undefined; if (mat) this.disposeLater(mat); } // drawn this tick — never a sync dispose (the teardown-flood law)
        this.wormholeObjs.splice(i, 1);
      }
    }
  }

  // ── Deployed platforms (depot / refinery beacons) ────────────────────────
  private updateSpaceStructures(): void {
    const root = this.root;
    const conn = atlas.conn;
    if (!root || !conn) return;
    const live = new Set<bigint>();
    try {
      for (const s of conn.db.spaceStructure.iter()) {
        if (s.systemId !== this.system.id) continue;
        live.add(s.structId);
        let obj = this.structObjs.get(s.structId);
        if (!obj) {
          const g = new THREE.Group();
          const tint = s.kind === 'refinery' ? 0x7de8c8 : 0xffd873;
          const core = new THREE.Mesh(
            new THREE.OctahedronGeometry(14),
            new THREE.MeshStandardMaterial({ color: 0x6e7480, roughness: 0.5, metalness: 0.5, emissive: tint, emissiveIntensity: 0.5 }),
          );
          const ring = new THREE.Mesh(
            new THREE.TorusGeometry(26, 1.6, 6, 28),
            markMrtOverlay(new THREE.MeshBasicMaterial({ color: tint, transparent: true, opacity: 0.7, blending: THREE.AdditiveBlending, depthWrite: false })),
          );
          ring.rotation.x = Math.PI / 2;
          const glow = this.spriteGlow(tint, 70, true);
          (glow.material as THREE.SpriteMaterial).opacity = 0.4;
          g.add(core, ring, glow);
          root.add(g);
          this.structObjs.set(s.structId, g);
          obj = g;
        }
        obj.position.copy(this.local(s.x, s.y, s.z, this._v1));
        obj.rotation.y += 0.005;
      }
    } catch { /* rows mid-churn */ }
    for (const [id, obj] of [...this.structObjs]) {
      if (!live.has(id)) { root.remove(obj); this.structObjs.delete(id); }
    }
  }

  /** My platform of a kind within range, or null. */
  private nearOwnStructure(kind: 'depot' | 'refinery', range: number): { structId: bigint; d: number } | null {
    const conn = atlas.conn;
    if (!conn?.identity) return null;
    try {
      for (const s of conn.db.spaceStructure.iter()) {
        if (!s.owner.isEqual(conn.identity) || s.kind !== kind || s.systemId !== this.system.id) continue;
        const d = Math.hypot(s.x - this.pos.x, s.y - this.pos.y, s.z - this.pos.z);
        if (d < range) return { structId: s.structId, d };
      }
    } catch { /* fine */ }
    return null;
  }

  // ── Incursions: raider armadas occupy rim systems for the epoch ──────────
  private incursionHere(): boolean {
    return incursionsOf(incursionEpoch(Date.now())).some((i) => i.systemId === this.system.id);
  }

  private updateIncursion(): void {
    const eph = incursionEpoch(Date.now());
    if (eph !== this.incursionSeenEpoch) {
      this.incursionSeenEpoch = eph;
      this.incursionKills = 0;
    }
    // The entry warning rides activate()/switchSystem via the toast there.
  }

  /** 0–100: the occupation's remaining grip (your kills push it back). */
  private incursionInfluence(): number {
    // Space Wave 4: the SHARED grip when the server's row is in (else the local tally until it lands).
    const conn = atlas.conn;
    if (conn) {
      const epoch = BigInt(incursionEpoch(Date.now()));
      try { for (const s of conn.db.incursionState.iter()) if (s.epoch === epoch && s.systemId === this.system.id) return Math.max(0, s.grip); } catch { /* pre-publish */ }
    }
    return Math.max(0, 100 - this.incursionKills * 2);
  }

  // ── Space Wave 4: the campaign ───────────────────────────────────────────
  /** The server's campaign for the current window (none for a forced window, or before the sweep opened it). */
  private campaignRowMemo: { cyc: number; stamp: number; row: CampaignRow | null } | null = null;
  /** Gunship serials never repeat within a campaign for this pilot (2026-09-23): each battle rebuild (masters landing,
   *  a hop out and back, a reconnect reload) restarted at sk0, and the record refused — silently — every serial this
   *  pilot had already claimed. Keys only need to be unique per pilot (claims are per identity): a rebuild in the same
   *  cycle continues the count; a fresh page starts from a clock-derived base (2 s steps, period 1,998 s > the war). */
  private skSerialCyc = -1;
  private skSerialNext = 0;
  private skSerialBase(cyc: number): number {
    if (this.skSerialCyc !== cyc) { this.skSerialCyc = cyc; this.skSerialNext = (Math.floor(Date.now() / 2000) % 999) * 1000; }
    return this.skSerialNext;
  }
  private campaignRow(): CampaignRow | null {
    const conn = atlas.conn;
    // A probe's SANDBOX campaign (space campaign slice C, `__warContract.override(id)`): the forced picture follows its rows.
    const ov = campaignOverride();
    if (conn && ov !== null) { try { return (conn.db.spaceCampaign.campaignId.find(ov) as CampaignRow | undefined) ?? null; } catch { return null; } }
    const win = this.battleWindow();
    if (!conn || !win || this.battleForce > 0) return null;
    // Memoised on the table's mutation stamp (lib/fast_find tableStamp; NaN when unpatched = never memoised):
    // called several times a 60 Hz tick, and each call walked every retained campaign row with a BigInt per row.
    const stamp = tableStamp(conn.db.spaceCampaign);
    const m = this.campaignRowMemo;
    if (m && m.cyc === win.cyc && m.stamp === stamp) return m.row;
    let row: CampaignRow | null = null;
    const cyc = BigInt(win.cyc);
    try { for (const c of conn.db.spaceCampaign.iter()) if (c.cyc === cyc) { row = c; break; } } catch { /* pre-publish */ }
    this.campaignRowMemo = { cyc: win.cyc, stamp, row };
    return row;
  }

  private campaignSides(campaignId: bigint): [CampaignSideRow | null, CampaignSideRow | null] {
    const out: [CampaignSideRow | null, CampaignSideRow | null] = [null, null];
    try { for (const s of atlas.conn!.db.campaignSide.iter()) if (s.campaignId === campaignId && (s.side === 0 || s.side === 1)) out[s.side] = s; } catch { /* pre-publish */ }
    return out;
  }

  /** The campaign's capital roster (THE ROSTER LAW) — through the index accessor, never a table spread. */
  private campaignRoster(campaignId: bigint): CampaignCapRow[] {
    try { return [...atlas.conn!.db.campaignCapital.campaignCapitalByCampaign.filter(campaignId)] as CampaignCapRow[]; } catch { return []; }
  }

  /** The campaign's escort roster (THE ESCORT LAW) — through the index accessor. */
  private campaignEscorts(campaignId: bigint): CampaignEscortRow[] {
    try { return [...atlas.conn!.db.campaignEscort.campaignEscortByCampaign.filter(campaignId)] as CampaignEscortRow[]; } catch { return []; }
  }

  /** THE ESCORT LAW on the client: the frigates and corvettes the campaign fields (its `campaign_escort` rows; a forced
   *  window fields the full ladder) warp in on their own stagger to STATIONS — frigates a line 5 km inside their own
   *  wall, corvettes the near band of the furball — as real FSM hulls of their class (THE ONE TARGET RULE picks their
   *  quarry). A row that dies kills its hull here; a hull that dies here never re-forms: they are a roster, not a pool. */
  private updateWarEscorts(win: { cyc: number; t: number }, b: NonNullable<typeof this.battle>, ending: boolean): void {
    if (ending || !this.root) return;
    // THE PENDING-DEATH LAW (space campaign slice C): every frame — a pending hull burns at its last, a restore ramps.
    this.tickEscortPendings();
    // Space campaign slice H: an EXTRACTING escort flies the server's declared course, a WITHDRAWN one holds its refuge.
    seatExtractions(this.pirates, atlas.conn, this.campaignRow()?.campaignId ?? null, performance.now());
    if ((globalThis as { __warContract?: unknown }).__warContract === undefined) this.installWarProbe();
    // Once a second (2026-09-23): this ran every 60 Hz tick — the row filters, eighteen key strings and a
    // pirate scan per slot each tick. The warp-in stagger is seconds long; a second of latency is invisible.
    const nowMs = performance.now();
    if (nowMs - b.escortSyncAt < 1000) return;
    b.escortSyncAt = nowMs;
    const camp = this.campaignRow();
    if (!camp && this.battleForce <= 0) return; // NO ROW, NO LINE: a real window fields no escort before the server's row
    const rows = camp ? this.campaignEscorts(camp.campaignId) : null;
    if (rows) {
      // An escort the committed ladder never fielded (spawned before the row, or a slot with no row) warps out.
      const named = new Set(rows.map((r) => `war${r.side}:${ESCORT_KEY[r.cls as 'frigate' | 'corvette'] ?? ''}${r.slot}`));
      let removed = false;
      for (let i2 = this.pirates.length - 1; i2 >= 0; i2--) {
        const p2 = this.pirates[i2];
        if (p2.warSide === undefined || p2.deadUntil > 0 || !/^war[01]:(fri|cor)\d+$/.test(p2.key) || named.has(p2.key)) continue;
        this.warFlash(p2.root.position.clone(), p2.warSide, 80);
        if (this.target === p2) this.target = null;
        this.root?.remove(p2.root);
        this.disposeSectorRoot(p2.root as THREE.Group);
        this.pirates.splice(i2, 1);
        removed = true;
      }
      if (removed) this.flushDisposeSoon();
    }
    const f = b.frame;
    const lineD = this.warLineD(win.t);
    for (const side of [0, 1] as const) {
      const sgn = side === 0 ? 1 : -1;
      for (const cls of ['frigate', 'corvette'] as const) {
        const max = cls === 'frigate' ? WAR_FRIGATES : WAR_CORVETTES;
        const slots: Array<{ slot: number; alive: boolean; fresh?: boolean }> = rows
          ? rows.filter((r) => r.side === side && r.cls === cls).map((r) => ({ slot: r.slot, alive: r.alive, fresh: r.killedBy === 'round' && Date.now() - Number(r.killedAt.microsSinceUnixEpoch / 1000n) < 8000 }))
          : Array.from({ length: max }, (_, k) => ({ slot: k, alive: true }));
        for (const { slot, alive, fresh } of slots) {
          if (slot >= 16) continue;
          const key = `war${side}:${ESCORT_KEY[cls]}${slot}`;
          const live = this.pirates.find((q) => q.key === key && q.deadUntil === 0);
          // Slice D, THE LETHAL SALVO: a fresh ROUND death of an escort is drawn from the killer line at that hull.
          if (!alive && live && fresh) this.drawLethalSalvo(b, win, side, new THREE.Vector3(live.root.position.x + this.off.x, live.root.position.y + this.off.y, live.root.position.z + this.off.z));
          if (!alive) { if (live) this.killPirate(live, false, true); b.escortDead.add(key); continue; } // the ROSTER's death — the only one under a roster (slice C)
          if (live || b.escortDead.has(key)) continue;
          const arr = 3 + warH(`${key}arr${win.cyc}`) * 9; // a warp-in stagger of its own, 3–12 s after the walls
          if (win.t < arr) continue;
          const along = cls === 'frigate' ? lineD - 5000 - slot * 500 : 3200 + warH(`${key}a`) * 1800;
          const across = cls === 'frigate' ? (slot - 1) * 2400 : (slot - 2.5) * 1500;
          const x = f.c.x + f.ax.x * sgn * along + f.az.x * across, z = f.c.z + f.ax.z * sgn * along + f.az.z * across;
          const y = f.c.y + (warH(`${key}y`) - 0.5) * 600;
          const pr = this.spawnHostile(key, 3, x, y, z, side, cls);
          if (pr) { pr.poiKey = `war${side}:${cls}`; this.warFlash(this.local(x, y, z, this._v4), side, cls === 'frigate' ? 260 : 80); }
        }
      }
    }
  }

  /** The escort roster row for a hull key (`war<side>:fri|cor<slot>`), or undefined. */
  private escortRowOf(rows: readonly CampaignEscortRow[] | null, key: string): CampaignEscortRow | undefined {
    const m = /^war([01]):(fri|cor)(\d+)$/.exec(key);
    if (!m || !rows) return undefined;
    const cls = m[2] === 'fri' ? 'frigate' : 'corvette', side = Number(m[1]), slot = Number(m[3]);
    return rows.find((r) => r.side === side && r.cls === cls && r.slot === slot);
  }

  /** THE PENDING-DEATH LAW (space campaign slice C, 2026-09-30) — the capitals' contract for the escorts: under a roster
   *  a lethal blow leaves a frigate / corvette PENDING (held at 1 hp, engines and guns out — its existing subsystem
   *  fields), sends ONE claim when the blow was mine, and the death arrives only when the roster row flips. Returns
   *  true when the hull is pending (the caller must not kill it); false = kill it now (a forced window, a dead row). */
  private escortPendingBlow(p: Pirate, byPlayer: boolean): boolean {
    const camp = this.campaignRow();
    const rows = camp ? this.campaignEscorts(camp.campaignId) : [];
    const row = this.escortRowOf(rows, p.key);
    const now = performance.now();
    // Slice D: a PINNED war takes no claim — the escort waits at 1 hp for the record (syncWarLives resolves it).
    const r = onLethal(pendingOf(p), { rosterMode: !!camp && rows.length > 0, rowAlive: row ? row.alive : null, byPlayer: byPlayer && !(camp && warPinned(camp.campaignId)), now });
    if (r.action === 'kill') return false;
    setPending(p, p.key, r.next);
    p.hp = 1;
    p.slowUntil = Number.MAX_SAFE_INTEGER; p.jamUntil = Number.MAX_SAFE_INTEGER;
    if (r.sendClaim && camp) {
      const kind = warHullKind(p.key);
      const maxHp = this.statsOf(p).hp;
      (atlas.conn?.reducers as unknown as { campaignKill(a: { campaignId: bigint; hullKey: string }): Promise<void> } | undefined)
        ?.campaignKill({ campaignId: camp.campaignId, hullKey: p.key })
        .then(() => { const s = pendingOf(p); if (s) setPending(p, p.key, onClaimResult(s, true, '', performance.now(), maxHp)); this.toast(claimToast('gunship', `⚑ Receipt: a ${kind} struck off the line (+${WAR_POINTS[kind]} pts this war) — it dies when the roster says so`)); }) // space campaign L (gap 29): an unpinned war's escort claim is the client's word
        .catch((e: Error) => {
          const why = String(e?.message ?? e);
          const s = pendingOf(p);
          if (s) setPending(p, p.key, onClaimResult(s, false, why, performance.now(), maxHp));
          this.toast(`The record refuses the kill: ${why} — the ${kind} fights on`);
        });
    }
    return true;
  }

  /** Every frame: pending escorts burn at 1 hp with their engines and guns out (embers on a time-based cadence); a
   *  restore ramps the hull back (smoothstep over RESTORE_MS) and re-lights it; a roster death is left to the once-a-
   *  second slot loop (rosterDeath); an expiry restores with a word to the pilot. */
  private tickEscortPendings(): void {
    let rows: CampaignEscortRow[] | null | undefined;
    const now = performance.now();
    for (const p of this.pirates) {
      if (p.deadUntil !== 0 || p.warSide === undefined || (p.cls !== 'frigate' && p.cls !== 'corvette')) continue;
      const s = pendingOf(p);
      if (!s) continue;
      if (rows === undefined) { const camp = this.campaignRow(); rows = camp ? this.campaignEscorts(camp.campaignId) : null; }
      const row = this.escortRowOf(rows, p.key);
      const r = tickPending(s, row ? row.alive : null, now, this.statsOf(p).hp);
      if (r.kill) continue; // the roster flipped: the slot loop ends it this second, as every client does
      if (r.hp !== null) p.hp = r.hp;
      if (r.disabled) {
        p.slowUntil = Number.MAX_SAFE_INTEGER; p.jamUntil = Number.MAX_SAFE_INTEGER;
        // Embers WALK the hull (a small burst at a random point inside its hit radius): one burst at the centre at the
        // hull's scale read as a white disc larger than the frigate (the probe's picture, 2026-09-30).
        if (now - (s.fxAt ?? 0) > 650) {
          const hr = this.hitRadiusOf(p) * 0.7;
          const at = p.root.position.clone().add(new THREE.Vector3((Math.random() - 0.5) * 2 * hr, (Math.random() - 0.5) * hr * 0.6, (Math.random() - 0.5) * 2 * hr));
          this.fxImpact(at, p.cls === 'frigate' ? 6 : 3, 0xff8a3d);
          setPending(p, p.key, { ...(r.next ?? s), fxAt: now }); continue;
        }
      } else if (p.slowUntil === Number.MAX_SAFE_INTEGER) { p.slowUntil = 0; p.jamUntil = 0; }
      if (s.phase === 'pending' && r.next?.phase === 'restoring' && r.next.outcome === 'expired') this.toast(`⚑ No death on the roster — the ${warHullKind(p.key)} fights on`);
      setPending(p, p.key, r.next);
    }
  }

  /** The probe's door (space campaign slice C): strike a named war escort as the player, list the escorts. */
  private installWarProbe(): void {
    installWarContractProbe({
      strike: (key: string, byPlayer = true): string => {
        const p = this.pirates.find((q) => q.key === key && q.deadUntil === 0);
        if (!p) return 'no such hull in the picture';
        p.hp = 0;
        this.killPirate(p, byPlayer);
        return p.deadUntil === 0 ? `pending (${pendingOf(p)?.outcome ?? '?'})` : 'dead';
      },
      escorts: () => this.pirates.filter((q) => q.warSide !== undefined && (q.cls === 'frigate' || q.cls === 'corvette')).map((q) => {
        const s = pendingOf(q);
        return { key: q.key, hp: q.hp, dead: q.deadUntil !== 0, phase: s?.phase ?? '', outcome: s?.outcome ?? '', reason: s?.reason ?? '', abs: [q.root.position.x + this.off.x, q.root.position.y + this.off.y, q.root.position.z + this.off.z] as [number, number, number], disabled: q.jamUntil === Number.MAX_SAFE_INTEGER };
      }),
    });
  }

  /** My receipts this campaign: points and the pay so far. */
  private myReceipts(campaignId: bigint): { points: number; paid: bigint; n: number } {
    let points = 0, paid = 0n, n = 0;
    const me = atlas.conn?.identity;
    try { for (const r of atlas.conn!.db.campaignReceipt.iter()) if (me && r.identity.isEqual(me) && r.campaignId === campaignId) { points += r.points; paid += r.paidSov; n++; } } catch { /* pre-publish */ }
    return { points, paid, n };
  }

  /** THE CAMPAIGN LAW: the row's ceilings cap the warp-in and the launches. THE ROSTER LAW: the deaths are the
   *  roster's — a `campaign_capital` row flipping to dead kills THAT slot here, credited to the pilot it names
   *  (the ace bounty when it is me) or to the line; the torpedo war is the picture, never the authority. A
   *  campaign with no roster rows (pre-publish) keeps the old count sync: the weakest local capitals die to match.
   *  Once a second. */
  private syncBattleToCampaign(b: NonNullable<typeof this.battle>, win: { cyc: number; t: number }, camp: CampaignRow): void {
    const nowS = performance.now() / 1000;
    if (nowS - b.syncAt < 1) return;
    b.syncAt = nowS;
    const sides = this.campaignSides(camp.campaignId);
    for (const side of [0, 1] as const) {
      const s = sides[side];
      if (!s) continue;
      b.capMax[side] = Math.min(BATTLE_CAPS, s.capsCommitted);
      b.skMax[side] = Math.min(WAR_SKIRMISHERS, s.skirmAlive);
      // A capital seated at or past the committed count is one the roster never names: it warps back out.
      for (let k = b.capMax[side]; k < b.capN[side]; k++) {
        if (b.capDead[side][k] || b.capGone[side][k]) continue;
        b.capGone[side][k] = true;
        this.warFlash(this.local(b.capKin[side][k]?.p.x ?? b.frame.c.x, b.capKin[side][k]?.p.y ?? b.frame.c.y, b.capKin[side][k]?.p.z ?? b.frame.c.z, this._v4), side, 900);
      }
    }
    const roster = this.campaignRoster(camp.campaignId);
    b.rosterMode = roster.length > 0;
    if (b.rosterMode) {
      const me = atlas.conn?.identity?.toHexString() ?? '';
      const name = (sd: number): string => (sd === 0 ? 'Concord' : 'Compact');
      let newest: CampaignCapRow | null = null;
      for (const r of roster) {
        if (r.alive || (r.side !== 0 && r.side !== 1) || r.slot >= BATTLE_CAPS) continue;
        if (!newest || r.killedAt.microsSinceUnixEpoch > newest.killedAt.microsSinceUnixEpoch) newest = r;
        const side = r.side as 0 | 1, k = r.slot;
        if (b.capDead[side][k] || b.capGone[side][k]) continue;
        if (k >= b.capN[side]) { b.capDead[side][k] = true; continue; } // fell before it warped in here — never seen, never drawn
        const mine = me !== '' && r.killedBy === me;
        const why = r.killedBy === 'round' ? `☄ A ${name(side)} capital breaks — the line thins (round ${r.round})`
          : r.killedBy === 'debug' ? `☄ A ${name(side)} capital is struck from the roster`
          : mine ? undefined : `☄ A ${name(side)} capital falls to another pilot's guns`;
        // Slice D, THE LETHAL SALVO: a FRESH round death is drawn from the killer line (an old one a late joiner reads is not).
        const ageS = (Date.now() - Number(r.killedAt.microsSinceUnixEpoch / 1000n)) / 1000;
        if (r.killedBy === 'round' && ageS < 8) this.drawLethalSalvo(b, win, side, this.capPosNow(side, k, win.t, new THREE.Vector3()));
        this.warCapitalKilled(side, k, win, mine, why, ageS);
      }
      if (newest && (!b.lastLoss || b.lastLoss.side !== newest.side || b.lastLoss.k !== newest.slot)) b.lastLoss = { side: newest.side, k: newest.slot, by: newest.killedBy, at: performance.now() };
      return;
    }
    for (const side of [0, 1] as const) {
      const s = sides[side];
      if (!s) continue;
      const living: number[] = [];
      for (let k = 0; k < b.capN[side]; k++) if (!b.capDead[side][k] && !b.capGone[side][k]) living.push(k);
      let excess = living.length - s.capsAlive;
      if (excess <= 0) continue;
      living.sort((x, y) => (b.capHp[side][x] - b.capHp[side][y]) || (warH(`c${win.cyc}s${side}k${x}`) - warH(`c${win.cyc}s${side}k${y}`)));
      for (const k of living) {
        if (excess-- <= 0) break;
        this.warCapitalKilled(side, k, win, false, `☄ A ${side === 0 ? 'Concord' : 'Compact'} capital breaks — the line thins`);
      }
    }
  }

  /** The HUD's campaign line: the sides' living capitals, a retreat, my points. */
  private campaignLine(win: { cyc: number; t: number; poi: { name: string } }, mmss: (s: number) => string): string | null {
    const camp = this.campaignRow();
    if (!camp) return null;
    const [a, bb] = this.campaignSides(camp.campaignId);
    if (!a || !bb) return null;
    const mine = this.myReceipts(camp.campaignId);
    const endsS = Number(camp.endsAt.microsSinceUnixEpoch / 1_000_000n);
    const left = Math.max(0, endsS - Date.now() / 1000);
    const state = camp.state === 'retreat' ? ` · the ${camp.retreatSide === 0 ? 'Concord' : 'Compact'} RETREATS` : camp.state === 'resolved' ? ` · ${camp.winner === 'draw' ? 'a draw' : `the ${camp.winner === 'aurelian' ? 'Concord' : 'Compact'} holds the field`}` : '';
    const ll = this.battle?.lastLoss;
    const esc = this.campaignEscorts(camp.campaignId);
    const escN = (sd: number, c: string): number => esc.filter((r) => r.side === sd && r.cls === c && r.alive).length;
    const escorts = esc.length ? ` · escorts ${escN(0, 'frigate')}f/${escN(0, 'corvette')}c v ${escN(1, 'frigate')}f/${escN(1, 'corvette')}c` : '';
    const me = atlas.conn?.identity?.toHexString() ?? '';
    const cause = ll ? ` · last loss: ${ll.side === 0 ? 'Concord' : 'Compact'} #${ll.k + 1} ${ll.by === 'round' ? 'to the line' : ll.by === 'debug' ? '(lever)' : ll.by === me ? 'to YOU' : 'to a pilot'}` : '';
    // THE HONEST LATE WINDOW (space campaign slice C): a campaign the sweep opened late keeps its real clock — say so,
    // and say when its 15-minute retreat floor cannot be reached. Pending escorts name what the record said.
    const lw = lateWindow(Number(camp.openedAt.microsSinceUnixEpoch / 1_000_000n), endsS);
    const late = camp.state === 'engaged' && lw.lateS >= LATE_NOTICE_S ? ` · opened ${Math.round(lw.lateS / 60)} min late: a ${Math.round(lw.warS / 60)}-min war${lw.floorReachable ? '' : ', too short for a retreat (only a line with no capitals leaves)'}` : '';
    const pend = pendingHud(pendingList());
    return `⚔ CAMPAIGN at ${win.poi.name} — Concord ${a.capsAlive}/${a.capsCommitted} · Compact ${bb.capsAlive}/${bb.capsCommitted} capitals${state}${escorts}${cause} · decides ${camp.targetSystem}${mine.n ? ` · your receipts ${mine.points} pts` : ''}${late}${pend}${this.warAuthorityLine(camp)} — ${mmss(left)} left`;
  }

  // ── Sovereignty ──────────────────────────────────────────────────────────
  /** The system this war cycle decides (the front line on the star chart). */
  private warTargetOf(cyc: number): string {
    return `sys${1 + (Math.floor(warH(`wt${cyc}`) * 100) % 100)}`;
  }

  // ── THE RAIDER LAW: real hulls on the lane ─────────────────────────────────
  private ambushSyncAt = 0;
  /** The live fight on a run, if any. */
  private ambushFor(shipmentId: bigint): AmbushRow | null {
    try { for (const a of atlas.conn!.db.spaceAmbush.iter()) if (a.shipmentId === shipmentId && a.state === 'fighting') return a; } catch { /* pre-publish */ }
    return null;
  }

  /** Every second: each fight's raiders are REAL FSM hulls (`raid<id>:<i>`) that chase the run — the first
   *  `raidersAlive` indices stand, the rest park; a fight that ended takes its hulls with it. */
  private syncAmbushes(): void {
    const conn = atlas.conn;
    if (!conn || !this.root || this.system.id !== 'sys0') return;
    const nowP = performance.now();
    if (nowP - this.ambushSyncAt < 1000) return;
    this.ambushSyncAt = nowP;
    let fights: AmbushRow[] = [];
    try { fights = [...conn.db.spaceAmbush.iter()].filter((a) => a.state === 'fighting'); } catch { return; }
    const live = new Set<string>();
    const runs = this.transitRuns();
    for (const a of fights) {
      const run = runs.find((r) => r.row.shipmentId === a.shipmentId);
      const at = run ? run.pos : [a.x, a.y, a.z] as [number, number, number];
      const tier = SYS0_DENS.find((d) => d.key === a.denKey)?.tier ?? 2;
      for (let i = 0; i < a.raiders; i++) {
        const key = `raid${a.ambushId}:${i}`;
        live.add(key);
        let p = this.pirates.find((q) => q.key === key);
        const ang = (i / Math.max(1, a.raiders)) * Math.PI * 2;
        const hx = at[0] + Math.cos(ang) * 900, hy = at[1] + 120 + i * 40, hz = at[2] + Math.sin(ang) * 900;
        if (!p) {
          if (i >= a.raidersAlive) continue; // already dead — never spawned here
          p = this.spawnHostile(key, tier, hx, hy, hz) ?? undefined as unknown as Pirate;
          if (!p) continue;
          this.warpFlash(this.local(hx, hy, hz, this._v1).clone());
          continue;
        }
        p.home.set(hx, hy, hz); // the party chases the run
        if (i >= a.raidersAlive && p.deadUntil === 0) { p.deadUntil = Number.MAX_SAFE_INTEGER; p.root.visible = false; if (this.target === p) this.target = null; }
      }
    }
    // Fights that ended: their hulls leave (a beaten party is dead; the rest warp home).
    for (const p of [...this.pirates]) {
      if (!p.key.startsWith('raid') || live.has(p.key)) continue;
      if (p.deadUntil === 0) this.warpFlash(p.root.position.clone());
      this.root.remove(p.root);
      this.pirates = this.pirates.filter((q) => q !== p);
      if (this.target === p) this.target = null;
    }
  }

  // ── Space Wave 6: one state at four resolutions ───────────────────────────
  private denSyncAt = 0;
  private readonly denEpoch = new Map<string, bigint>();
  /** The shared den row a hull belongs to (its key's den, slot below the capacity), or null. */
  private denRowFor(p: Pirate): DenRow | null {
    const conn = atlas.conn;
    if (!conn || p.ally || p.warSide !== undefined) return null;
    const denKey = p.key.split(':')[0];
    const slot = slotOf(p.key);
    try { for (const d of conn.db.denState.iter()) if (d.systemId === this.system.id && d.denKey === denKey && slot >= 0 && slot < d.capacity) return d; } catch { /* pre-publish */ }
    return null;
  }

  /** This system's resolution row. */
  private lodRow(): { lod: number; observers: number; authorityEpoch: bigint } | null {
    try { return atlas.conn?.db.simLod.systemId.find(this.system.id) ?? null; } catch { return null; }
  }

  /** THE SHARED-DEN LAW: every ~2 s the den rows decide which den hulls stand — the first `alive` slots
   *  live, the rest park (no private respawn); a changed authority epoch re-syncs the whole den. */
  /** THE DEN SLOT LAW: this system's engaged slots by pirate key (refreshed with the dens). */
  private slotRows = new Map<string, SlotRow>();
  /** Blows on den slots waiting for the server (coalesced per hull). */
  private denHits = new Map<string, { dmg: number; inFlight: boolean }>();
  private slotRowFor(p: Pirate): SlotRow | null { return this.slotRows.get(p.key) ?? null; }

  // THE CREW LAW: a Sovereign's dorsal turret is a second seat — the crew's client rides the captain's hull.
  private crew: { captainHex: string; captainName: string; seat: string } | null = null; // G: `seat` = turret | engineer
  private manned: string | null = null;
  private crewHexes = new Set<string>();
  private crewSyncAt = 0;
  private crewSeatShown = false;
  private syncCrew(nowP: number): void {
    if (nowP - this.crewSyncAt < 1000) return;
    this.crewSyncAt = nowP;
    const conn = atlas.conn;
    if (!conn?.identity) return;
    const me = conn.identity;
    let crew: { captainHex: string; captainName: string; seat: string } | null = null, manned: string | null = null;
    this.crewHexes.clear();
    try {
      for (const r of conn.db.spaceCrew.iter()) {
        this.crewHexes.add(r.crew.toHexString());
        if (r.crew.isEqual(me)) crew = { captainHex: r.captain.toHexString(), captainName: conn.db.account.identity.find(r.captain)?.username ?? 'the captain', seat: r.seat };
        if (r.captain.isEqual(me)) manned = conn.db.account.identity.find(r.crew)?.username ?? 'a pilot';
      }
    } catch { /* pre-publish */ }
    if (!!crew !== !!this.crew) {
      if (crew) { this.toast(crew.seat === 'engineer' ? `⚙ ENGINEER STATION — ${crew.captainName}'s Sovereign: ${keyHint('spaceEngineering')} the panel, ${keyHint('spacePatch')} patch, 1–4 route power; ${keyHint('spaceTurret')} to leave` : `🎯 TURRET SEAT — ${crew.captainName}'s Sovereign carries you; aim with the mouse, fire as usual; ${keyHint('spaceTurret')} to leave`); this.camBack = ZOOM_MIN; }
      else { this.toast('You are back in your own hull'); if (this.shipMesh) this.shipMesh.visible = !this.cockpitView; this.hud?.prompt(null); this.crewSeatShown = false; }
    }
    if (manned !== this.manned) this.toast(manned ? `👥 ${manned} mans your dorsal turret` : (this.manned ? `${this.manned} left your turret` : ''));
    this.crew = crew; this.manned = manned;
  }
  /** The crew's frame: the ship IS the captain's hull — its seat, its motion; no flight of our own. */
  private rideCaptain(dt: number): void {
    if (!this.crew) return;
    const r = this.remoteRows.get(this.crew.captainHex);
    if (!r) return;
    const sy = Math.sin(r.yaw), cy = Math.cos(r.yaw);
    const [ox, oy, oz] = this.crew.seat === 'engineer' ? ENGINEER_SEAT : CREW_TURRET_SEAT; // G: the engineer sits aft of the bridge
    const tx = r.x + ox * cy + oz * sy, ty = r.y + oy, tz = r.z - ox * sy + oz * cy;
    this._v1.set(tx, ty, tz);
    this.vel.copy(this._v1).sub(this.pos).divideScalar(Math.max(dt, 1 / 120)); // the hull's motion, for bolts and the readout
    if (this.vel.length() > 400) this.vel.set(0, 0, 0); // a teleport is not a velocity
    this.pos.copy(this._v1);
    this.throttle = 0;
    this.cruise = 1;
    if (this.shipMesh) this.shipMesh.visible = false; // we sit on THEIR hull
    if (!this.crewSeatShown) { this.hud?.prompt(`${this.crew.seat === 'engineer' ? 'ENGINEER STATION' : 'TURRET SEAT'} · ${this.crew.captainName}'s Sovereign · ${keyHint('spaceTurret')} to leave`); this.crewSeatShown = true; }
  }
  /** Space campaign G: the narrow host the engineering controller reads (it never reaches into the module). */
  private engAttached = false;
  private attachEngineering(): void {
    if (this.engAttached) return;
    this.engAttached = true;
    shipEngineering.attach({
      active: () => this.active,
      seat: () => (this.crew ? { captainHex: this.crew.captainHex, seat: this.crew.seat } : null),
      hull: () => ({ hp: this.hull, max: this.stats.hp }),
      addHull: (n) => { this.hull = Math.min(this.stats.hp, this.hull + n); },
      safeDock: () => !!this.dock || !!this.nearStation() || !!this.nearOwnStructure('depot', 600),
      nearbyPilots: () => {
        const conn = atlas.conn, out: Array<{ hex: string; name: string; d: number; chassis: string }> = [];
        if (!conn?.identity) return out;
        for (const [hex, r] of this.remoteRows) {
          const d = Math.hypot(r.x - this.pos.x, r.y - this.pos.y, r.z - this.pos.z);
          if (d > CREW_BOARD_RANGE_M || this.crewHexes.has(hex)) continue;
          let name = hex.slice(0, 6), chassis = '';
          try { const id = Identity.fromString(hex); name = conn.db.account.identity.find(id)?.username ?? name; chassis = conn.db.spacePresence.identity.find(id)?.chassisKey ?? ''; } catch { /* */ }
          out.push({ hex, name, d, chassis });
        }
        return out.sort((a, b) => a.d - b.d);
      },
      applyPower: (preset, by) => {
        if (preset !== 'balanced' && preset !== 'weapons' && preset !== 'shields' && preset !== 'engines') return;
        this.power = preset;
        this.toast(`⚡ Power → ${preset.toUpperCase()} — routed by ${by} at the engineer station (your ${keyHint('spacePowerWeapons')}–${keyHint('spacePowerBalanced')} takes it back)`);
      },
      power: () => this.power,
      droidLevel: () => this.stats.droid,
      toast: (t) => this.toast(t),
    });
  }
  private toggleTurretSeat(): void {
    const conn = atlas.conn;
    if (!conn?.identity || this.interior || this.sysJump) return;
    if (this.crew) {
      (conn.reducers as unknown as { leaveTurret(a: Record<string, never>): Promise<void> }).leaveTurret({}).catch((e: Error) => this.toast(`⚠ ${String(e.message ?? e)}`));
      return;
    }
    // Space campaign G: a captain's invite (gunner or engineer) is answered here first — the seat is the captain's to offer.
    const invite = shipEngineering.view().invitesToMe[0];
    if (invite) { shipEngineering.accept(invite.leaseId); return; }
    // The nearest Sovereign in flight within the boarding range (its roster row names the hull).
    let best: { hex: string; d: number } | null = null;
    try {
      for (const pr of conn.db.spacePresence.iter()) {
        if (pr.chassisKey !== 'ship_sovereign' || pr.identity.isEqual(conn.identity)) continue;
        const row = this.remoteRows.get(pr.identity.toHexString());
        if (!row) continue;
        const d = Math.hypot(row.x - this.pos.x, row.y - this.pos.y, row.z - this.pos.z);
        if (d <= CREW_BOARD_RANGE_M && (!best || d < best.d)) best = { hex: pr.identity.toHexString(), d };
      }
    } catch { /* pre-publish */ }
    if (!best) { this.toast(`No Sovereign within 3 km to board (${keyHint('spaceTurret')} takes a friend's turret seat)`); return; }
    const hex = best.hex;
    (conn.reducers as unknown as { boardTurret(a: { captain: Identity }): Promise<void> }).boardTurret({ captain: new Identity(hex) })
      .catch((e: Error) => this.toast(`⚠ ${String(e.message ?? e)}`));
  }

  // The lit-material triage (dev): three cubes ahead of the ship — the interior no-draw investigation.
  private litProbeObjs: THREE.Object3D[] = [];
  private litProbe(kind: 'basic' | 'emissive' | 'standard' | 'clear'): boolean {
    if (!this.root) return false;
    for (const o of this.litProbeObjs) this.root.remove(o);
    this.litProbeObjs = [];
    if (kind === 'clear') return true;
    const mat = kind === 'basic' ? new THREE.MeshBasicMaterial({ color: 0xff2040 })
      : kind === 'emissive' ? new THREE.MeshStandardMaterial({ color: 0x000000, emissive: 0x20ff40, emissiveIntensity: 1 })
        : new THREE.MeshStandardMaterial({ color: 0x4080ff, roughness: 0.6, metalness: 0.1 });
    const cube = new THREE.Mesh(new THREE.BoxGeometry(30, 30, 30), mat);
    const fwd = this._v1.set(0, 0, -1).applyQuaternion(this.quat);
    cube.position.copy(this._ship).addScaledVector(fwd, 70);
    this.root.add(cube);
    this.litProbeObjs.push(cube);
    return true;
  }
  // THE DEN POSITION LAW: seats streamed by the pilots the hulls hunt; my own engaged hulls go out on the stream.
  private denHullStreamAt = 0;
  private denHullsStreamed = 0;
  private syncDenHulls(nowP: number): void {
    const conn = atlas.conn;
    if (!conn?.identity || !this.root) return;
    const me = conn.identity;
    const seated = new Set<string>();
    try {
      for (const h of conn.db.denHull.iter()) {
        if (h.systemId !== this.system.id || h.writer.isEqual(me)) continue;
        const key = `${h.denKey}:pirate${h.slot}`;
        const p = this.pirates.find((q) => q.key === key && !q.ally && q.warSide === undefined);
        if (!p) continue;
        const stamp = h.updatedAt.microsSinceUnixEpoch;
        if (!p.seat || p.seat.stamp !== stamp) {
          p.seat = { x: h.x, y: h.y, z: h.z, yaw: h.yaw, at: nowP, stamp };
          // the arrival (this frame) and the server stamp feed the playback; a stale seat restarts it
          p.seatTrack ??= new RemoteTrack();
          p.seatTrack.push(nowP, Number(stamp / 1000n), h.x, h.y, h.z, h.yaw);
        }
        seated.add(key);
      }
    } catch { /* pre-publish */ }
    for (const p of this.pirates) if (p.seat && !seated.has(p.key) && nowP - p.seat.at > DEN_HULL_STALE_S * 1000) { p.seat = undefined; p.seatTrack = undefined; }
    if (nowP - this.denHullStreamAt < DEN_HULL_STREAM_MS) return;
    this.denHullStreamAt = nowP;
    const hulls: Array<{ denKey: string; slot: number; x: number; y: number; z: number; yaw: number; ai: string }> = [];
    for (const p of this.pirates) {
      if (p.deadUntil > 0 || p.ally || p.warSide !== undefined || p.seat) continue;
      const row = this.slotRowFor(p);
      if (!row || !row.target.isEqual(me)) continue;
      const slot = slotOf(p.key);
      if (slot < 0) continue;
      hulls.push({ denKey: row.denKey, slot, x: p.root.position.x + this.off.x, y: p.root.position.y + this.off.y, z: p.root.position.z + this.off.z, yaw: p.root.rotation.y, ai: p.ai });
      if (hulls.length >= 12) break;
    }
    if (!hulls.length) return;
    this.denHullsStreamed = hulls.length;
    (conn.reducers as unknown as { denHullSync(a: { systemId: string; hulls: typeof hulls }): Promise<void> })
      .denHullSync({ systemId: this.system.id, hulls }).catch(() => { /* the fight ended between syncs */ });
  }

  // THE PVP LAW: my bolts land on pilots my guild is at war with; the server judges each blow; theirs land on me through rows.
  private warTargets = new Set<string>();
  private warTargetsAt = 0;
  private pvpQueue = new Map<string, { dmg: number; inFlight: boolean }>();
  private pvpSeenHit = 0n;
  private pvpPrimed = false;
  private pvpHitsTaken = 0;
  private pvpToastAt = 0;
  private refreshWarTargets(nowP: number): void {
    if (nowP - this.warTargetsAt < 2000) return;
    this.warTargetsAt = nowP;
    this.warTargets.clear();
    const conn = atlas.conn;
    if (!conn?.identity) return;
    try {
      let myGuild: bigint | null = null;
      for (const m of conn.db.guildMember.iter()) if (m.identity.isEqual(conn.identity)) { myGuild = m.guildId; break; }
      if (myGuild === null) return;
      const nowUs = BigInt(Date.now()) * 1000n;
      const enemies = new Set<bigint>();
      for (const w of conn.db.guildWar.iter()) {
        if (w.state !== 'declared' || w.endsAt.microsSinceUnixEpoch <= nowUs) continue;
        if (w.guildA === myGuild) enemies.add(w.guildB); else if (w.guildB === myGuild) enemies.add(w.guildA);
      }
      if (!enemies.size) return;
      for (const m of conn.db.guildMember.iter()) if (enemies.has(m.guildId)) this.warTargets.add(m.identity.toHexString());
    } catch { /* pre-publish */ }
  }
  private pvpHit(hex: string, dmg: number): void {
    const q = this.pvpQueue.get(hex) ?? { dmg: 0, inFlight: false };
    q.dmg += dmg;
    this.pvpQueue.set(hex, q);
    this.flushPvp(hex, q);
  }
  private flushPvp(hex: string, q: { dmg: number; inFlight: boolean }): void {
    if (q.inFlight || q.dmg <= 0) return;
    const dmg = Math.min(PVP_HIT_MAX, q.dmg);
    q.dmg = Math.max(0, q.dmg - dmg);
    q.inFlight = true;
    const reducers = atlas.conn?.reducers as unknown as { spacePvpStrike(a: { target: Identity; dmg: number }): Promise<void> } | undefined;
    if (!reducers) { q.inFlight = false; return; }
    reducers.spacePvpStrike({ target: new Identity(hex), dmg })
      .then(() => { q.inFlight = false; if (q.dmg > 0) this.flushPvp(hex, q); })
      .catch((e: Error) => {
        q.inFlight = false; q.dmg = 0;
        this.lastError = `spacePvpStrike: ${String(e.message ?? e)}`;
        if (performance.now() - this.pvpToastAt > 3000) { this.pvpToastAt = performance.now(); this.toast(`⚔ ${String(e.message ?? e)}`); }
      });
  }
  /** Blows the server judged against ME: applied to my hull once each, the attacker named for the killmail. */
  private applyPvpHits(): void {
    const conn = atlas.conn;
    if (!conn?.identity) return;
    const me = conn.identity;
    try {
      if (!this.pvpPrimed) { // never replay the history a fresh subscription hands over
        for (const h of conn.db.spacePvpHit.iter()) if (h.target.isEqual(me) && h.hitId > this.pvpSeenHit) this.pvpSeenHit = h.hitId;
        this.pvpPrimed = true;
        return;
      }
      for (const h of conn.db.spacePvpHit.iter()) {
        if (!h.target.isEqual(me) || h.hitId <= this.pvpSeenHit) continue;
        this.pvpSeenHit = h.hitId;
        const dir = this._v6.copy(this.shipLocal()).sub(this.local(h.x, h.y, h.z, this._v1)).normalize(); // the bolt flew attacker → me
        this.damagePlayer(h.dmg, dir, { kind: 'player', key: h.attacker.toHexString(), hitId: h.hitId }); // G: the hit id lets the server judge it
        this.pvpHitsTaken++;
        if (performance.now() - this.pvpToastAt > 3000) {
          this.pvpToastAt = performance.now();
          const who = conn.db.account.identity.find(h.attacker)?.username ?? 'a pilot at war with you';
          this.toast(`⚔ ${who} lands a hit — the wardec is live here`);
        }
      }
    } catch { /* pre-publish */ }
  }
  private syncDens(): void {
    const conn = atlas.conn;
    if (!conn || !this.root) return;
    const nowP = performance.now();
    if (nowP - this.denSyncAt < 2000) return;
    this.denSyncAt = nowP;
    let rows: DenRow[] = [];
    try { rows = [...conn.db.denState.iter()].filter((d) => d.systemId === this.system.id); } catch { return; }
    // The engaged hulls' shared hp: one truth for every pilot here (a blow of mine still in flight keeps my prediction).
    this.slotRows.clear();
    try {
      for (const sl of conn.db.denSlot.iter()) {
        if (sl.systemId !== this.system.id) continue;
        const key = `${sl.denKey}:pirate${sl.slot}`;
        this.slotRows.set(key, sl as SlotRow);
        const p = this.pirates.find((q) => q.key === key && !q.ally && q.warSide === undefined && q.deadUntil === 0);
        if (p && !this.denHits.get(key)?.inFlight) p.hp = sl.hp;
      }
    } catch { /* pre-publish */ }
    for (const row of rows) {
      const hulls = this.pirates
        .filter((p) => !p.ally && p.warSide === undefined && p.key.startsWith(`${row.denKey}:`) && slotOf(p.key) >= 0 && slotOf(p.key) < row.capacity)
        .sort((a, b) => slotOf(a.key) - slotOf(b.key));
      if (!hulls.length) continue;
      this.denEpoch.set(row.denKey, row.authorityEpoch);
      hulls.forEach((p, i) => {
        const shouldLive = i < row.alive;
        const lives = p.deadUntil === 0;
        if (shouldLive && !lives && !p.escaped) {
          // The server refilled the slot: back at home, whole, patrolling.
          p.deadUntil = 0;
          p.hp = this.statsOf(p).hp * (this.aceName(p) ? ACE_HP_MULT : 1);
          p.root.position.copy(this.local(p.home.x, p.home.y, p.home.z, this._v1));
          p.root.visible = true;
          p.ai = 'patrol';
        } else if (!shouldLive && lives) {
          // Cleared by someone else: the hull is not here.
          p.deadUntil = Number.MAX_SAFE_INTEGER;
          p.root.visible = false;
          if (this.target === p) this.target = null;
        }
      });
    }
  }

  // ── Space Wave 5: what I know ────────────────────────────────────────────
  private intelSubFaction: string | null = null;
  /** The civ's observations and bookmarks arrive once the banner is known (never a '' banner — private rows stay private). */
  private subscribeIntel(): void {
    const conn = atlas.conn;
    if (!conn || this.intelSubFaction === this.faction) return;
    this.intelSubFaction = this.faction;
    if (this.faction !== 'aurelian' && this.faction !== 'borean') return;
    try {
      conn.subscriptionBuilder().subscribe([`SELECT * FROM space_intel WHERE civ = '${this.faction}'`, `SELECT * FROM space_bookmark WHERE civ = '${this.faction}'`]);
    } catch { /* pre-publish */ }
  }

  /** The newest observation per system among mine and my civ's (the chart's view). */
  private intelView(): Map<string, IntelRow> {
    const conn = atlas.conn;
    if (!conn) return new Map();
    const me = conn.identity;
    const rows: IntelRow[] = [];
    try { for (const r of conn.db.spaceIntel.iter()) if ((me && r.observer.isEqual(me)) || (this.faction && r.civ === this.faction)) rows.push(r); } catch { /* pre-publish */ }
    return newestPerSystem(rows);
  }

  /** This system's bookmarks: mine and my civ's. */
  private bookmarksHere(): BookmarkRow[] {
    const conn = atlas.conn;
    if (!conn) return [];
    const me = conn.identity;
    const out: BookmarkRow[] = [];
    try { for (const b of conn.db.spaceBookmark.iter()) if (b.systemId === this.system.id && ((me && b.owner.isEqual(me)) || (this.faction && b.civ === this.faction))) out.push(b); } catch { /* pre-publish */ }
    return out;
  }

  private controllerOf(systemId: string): 'aurelian' | 'borean' | 'commons' {
    let score = 0;
    try {
      const row = atlas.conn?.db.sectorControl.systemId.find(systemId);
      if (row) score = row.score;
    } catch { /* fine */ }
    return controlFromScore(systemId, score);
  }

  // ── K: the OPS panel — squadron + platforms ──────────────────────────────
  private myGroups(): Array<{ groupId: bigint; name: string }> {
    const conn = atlas.conn;
    const out: Array<{ groupId: bigint; name: string }> = [];
    try {
      if (conn?.identity) {
        for (const gm of conn.db.groupMember.iter()) {
          if (!gm.identity.isEqual(conn.identity)) continue;
          for (const g of conn.db.group.iter()) {
            // Any membership makes a squadron — charter economics are P9's.
            if (g.groupId === gm.groupId) out.push({ groupId: g.groupId, name: g.name });
          }
        }
      }
    } catch { /* fine */ }
    return out;
  }

  private opsSub: { unsubscribe(): void } | null = null;
  private toggleOpsPanel(): void {
    if (this.opsPanel) {
      window.clearInterval(this.opsTimer);
      this.opsPanel.remove();
      this.opsPanel = null;
      // (bug sweep) the squadron channels subscribed on open were never released — every toggle stacked N more.
      try { this.opsSub?.unsubscribe(); } catch { /* gone */ }
      this.opsSub = null;
      return;
    }
    if (!this.active) return;
    const root = document.createElement('div');
    root.id = 'space-ops';
    root.style.cssText =
      'position:fixed;right:16px;top:64px;z-index:37;width:340px;max-height:74vh;overflow-y:auto;' +
      'background:color-mix(in srgb, var(--ax-bg) 94%, transparent);border:1px solid var(--ax-line);border-radius:var(--ax-win-radius);padding:12px;' +
      'font:12px var(--ax-font);color:var(--ax-text);pointer-events:auto';
    document.body.appendChild(root);
    this.opsPanel = root;
    // Squadron chat rides an exact-kind sub per group (SQL has no LIKE).
    try {
      const subs = this.myGroups().map((g) => `SELECT * FROM chat_message WHERE kind = 'fleet:${g.groupId}'`);
      // THE SQUAD LAW (space campaign B): the mates' own player_state rows — the AOI subscription holds only pilots
      // within a few cells, so a mate 40 km out in the same sky read "off-grid". One row per mate, released with the
      // panel (THE AOI GATE keeps them from becoming remote avatars).
      const me = atlas.conn?.identity;
      const groups = new Set(this.myGroups().map((g) => g.groupId));
      const mates = new Set<string>();
      try { for (const gm of atlas.conn!.db.groupMember.iter()) if (groups.has(gm.groupId) && !(me && gm.identity.isEqual(me))) mates.add(gm.identity.toHexString()); } catch { /* mid-churn */ }
      for (const hx of [...mates].slice(0, 32)) subs.push(`SELECT * FROM player_state WHERE identity = 0x${hx.replace(/^0x/, '')}`);
      if (subs.length) this.opsSub = (atlas.conn?.subscriptionBuilder().subscribe(subs) ?? null) as { unsubscribe(): void } | null;
    } catch { /* fine */ }
    this.renderOps();
    this.opsTimer = window.setInterval(() => this.renderOpsKeepingFocus(), 1200);
  }

  /**
   * The 1.2 s re-render rebuilt the panel with `replaceChildren()` and destroyed the focused chat field — focus fell to
   * the page and the next letters flew the ship (space campaign B, the text-entry law). The typed text, the caret and
   * the focus now carry over to the rebuilt field with the same `data-k`, inside the same task (no key can land between).
   */
  private renderOpsKeepingFocus(): void {
    const root = this.opsPanel;
    const a = document.activeElement as HTMLInputElement | null;
    const keep = root && a && a.tagName === 'INPUT' && root.contains(a) && a.dataset.k
      ? { k: a.dataset.k, v: a.value, s: a.selectionStart, e: a.selectionEnd } : null;
    this.renderOps();
    if (!keep || !this.opsPanel) return;
    const fresh = this.opsPanel.querySelector<HTMLInputElement>(`input[data-k="${keep.k}"]`);
    if (!fresh) return;
    fresh.value = keep.v;
    fresh.focus();
    try { fresh.setSelectionRange(keep.s ?? keep.v.length, keep.e ?? keep.v.length); } catch { /* not a text field */ }
  }

  private renderOps(): void {
    const root = this.opsPanel;
    const conn = atlas.conn;
    if (!root || !conn) return;
    root.replaceChildren();
    const h = (txt: string): HTMLDivElement => {
      const d = document.createElement('div');
      d.textContent = txt;
      d.style.cssText = 'font:700 11px var(--ax-mono);color:var(--ax-muted);letter-spacing:1.4px;margin:8px 0 4px';
      return d;
    };
    const btnCss = 'background:var(--ax-bg3);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-text);padding:3px 10px;cursor:pointer;font:11px var(--ax-mono);margin:2px';
    root.appendChild(h(`SQUADRON — ${keyHint('spaceOps')} / Esc closes`));
    const groups = this.myGroups();
    if (!groups.length) {
      const d = document.createElement('div');
      d.textContent = 'No squadron — found or join a Group (Passport → Groups). Grouped pilots share duty progress, warp to each other, and talk on a fleet channel.';
      d.style.color = 'var(--ax-soft)';
      root.appendChild(d);
    }
    const me = conn.identity;
    for (const g of groups.slice(0, 2)) {
      const gname = document.createElement('div');
      gname.textContent = `▸ ${g.name}`;
      gname.style.cssText = 'font-weight:600;margin-top:4px';
      root.appendChild(gname);
      // Roster — THE SQUAD LAW (space campaign B, lib/space_squad_nav): each mate's location resolves into exactly ONE
      // offer — intercept (same sky, flying, freshly seen), travel (another charted sky: its own confirm, then the
      // server's travel order), or a status line with no warp button. The old `Warp to` flew to a mate's raw position
      // in THIS system whatever sky they were in, or the ground.
      try {
        const systems = this.galaxySystems();
        const sysName = (id: string): string | null => systems.find((sy) => sy.id === id)?.name ?? null;
        const stName = (key: string): string | null => stationByKey(systems, key)?.name ?? null;
        for (const gm of conn.db.groupMember.iter()) {
          if (gm.groupId !== g.groupId) continue;
          if (me && gm.identity.isEqual(me)) continue;
          let uname = gm.identity.toHexString().slice(0, 8);
          for (const a of conn.db.account.iter()) if (a.identity.isEqual(gm.identity)) { uname = a.username; break; }
          const hex = gm.identity.toHexString();
          const offer = squadOffer({ systemId: this.system.id, x: this.pos.x, y: this.pos.y, z: this.pos.z }, this.mateFacts(gm.identity, uname), sysName, stName);
          const row = document.createElement('div');
          row.style.cssText = 'display:flex;align-items:center;gap:6px;padding:2px 0;flex-wrap:wrap';
          row.dataset.squadMate = uname;
          row.dataset.squadOffer = offer.kind;
          const nm = document.createElement('span');
          nm.textContent = uname;
          nm.style.cssText = 'font-weight:600';
          row.appendChild(nm);
          const st = document.createElement('span');
          st.textContent = offer.text;
          st.style.cssText = 'color:var(--ax-muted);font:10px var(--ax-mono);flex:1 1 140px';
          row.appendChild(st);
          if (offer.kind === 'intercept') {
            const warp = document.createElement('button');
            warp.textContent = '⇢ Intercept';
            warp.title = `An in-system hyper jump to ${uname} (${offer.distKm} km) — the drive spools as for any nav point`;
            warp.style.cssText = btnCss;
            warp.onclick = () => this.warpToMate(uname, offer.x, offer.y, offer.z);
            row.appendChild(warp);
          } else if (offer.kind === 'travel') {
            const go = document.createElement('button');
            go.textContent = `⚡ Travel to ${offer.systemName}`;
            go.style.cssText = btnCss;
            go.onclick = () => { this.squadConfirm = this.squadConfirm === hex ? null : hex; this.renderOps(); };
            row.appendChild(go);
          }
          root.appendChild(row);
          if (offer.kind === 'travel' && this.squadConfirm === hex) {
            // The travel order's OWN confirm (report §22.1: moving focus never commits a jump; the destination, the
            // system and the mode are stated before the order is sent).
            const cf = document.createElement('div');
            cf.dataset.squadConfirm = uname;
            cf.style.cssText = 'background:var(--ax-bg2);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:6px;margin:2px 0 4px;font:11px var(--ax-font);color:var(--ax-soft)';
            cf.textContent = offer.confirm;
            const yes = document.createElement('button');
            yes.textContent = `Confirm hyper-warp to ${offer.systemName}`;
            yes.style.cssText = btnCss;
            yes.onclick = () => { this.squadConfirm = null; this.warpToSystem(offer.systemId); this.renderOps(); };
            const no = document.createElement('button');
            no.textContent = 'Cancel';
            no.style.cssText = btnCss;
            no.onclick = () => { this.squadConfirm = null; this.renderOps(); };
            const line = document.createElement('div');
            line.append(yes, no);
            cf.appendChild(line);
            root.appendChild(cf);
          }
        }
      } catch { /* rows mid-churn */ }
      // Fleet chat: last 6 lines + the send box.
      const log = document.createElement('div');
      log.style.cssText = 'background:var(--ax-bg2);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:6px;margin:4px 0;font:11px var(--ax-mono);max-height:110px;overflow-y:auto';
      const lines: Array<{ at: bigint; text: string }> = [];
      try {
        for (const m of conn.db.chatMessage.iter()) {
          if (m.kind !== `fleet:${g.groupId}`) continue;
          lines.push({ at: m.at.microsSinceUnixEpoch, text: `${m.senderName}: ${m.text}` });
        }
      } catch { /* fine */ }
      lines.sort((a, z) => (a.at < z.at ? -1 : 1));
      for (const l of lines.slice(-6)) {
        const d = document.createElement('div');
        d.textContent = l.text;
        log.appendChild(d);
      }
      if (!lines.length) {
        const d = document.createElement('div');
        d.textContent = '(the squadron channel is quiet)';
        d.style.color = 'var(--ax-muted)';
        log.appendChild(d);
      }
      root.appendChild(log);
      const form = document.createElement('div');
      form.style.cssText = 'display:flex;gap:4px';
      const inp = document.createElement('input');
      inp.type = 'text';
      inp.maxLength = 240;
      inp.placeholder = 'fleet channel…';
      inp.dataset.k = `fleet:${g.groupId}`; // space campaign B: the re-render hands the typing back (renderOpsKeepingFocus)
      inp.style.cssText = 'flex:1;background:var(--ax-bg2);border:1px solid var(--ax-line);border-radius:var(--ax-radius);color:var(--ax-text);padding:3px 8px;font:11px var(--ax-mono)';
      inp.onkeydown = (e) => { e.stopPropagation(); if (e.key === 'Enter') send(); };
      const sendB = document.createElement('button');
      sendB.textContent = 'Send';
      sendB.style.cssText = btnCss;
      const send = (): void => {
        const text = inp.value.trim();
        if (!text) return;
        inp.value = '';
        conn.reducers.sendFleetChat({ groupId: g.groupId, text })
          .catch((e: Error) => this.toast(`Channel refused: ${e.message ?? e}`));
      };
      sendB.onclick = send;
      form.append(inp, sendB);
      root.appendChild(form);
      const note = document.createElement('div');
      note.textContent = 'Squadron kills at your contract dens advance YOUR duty missions too.';
      note.style.cssText = 'color:var(--ax-muted);font:10px var(--ax-font);margin-top:3px';
      root.appendChild(note);
    }
    // ── LOCAL: the system roster + channel (presence is client-claimed intel) ──
    root.appendChild(h(`LOCAL — ${this.system.name}`));
    try {
      const cutoff = BigInt(Date.now() - 120_000) * 1000n;
      const locals = [...(conn.db as unknown as { spacePresence: { iter(): Iterable<{ identity: { isEqual(o: unknown): boolean; toHexString(): string }; systemKey: string; chassisKey: string; updatedAt: { microsSinceUnixEpoch: bigint } }> } }).spacePresence.iter()]
        .filter((pr) => pr.systemKey === this.system.id && pr.updatedAt.microsSinceUnixEpoch > cutoff);
      const rosterD = document.createElement('div');
      rosterD.style.cssText = 'font:11px var(--ax-mono);color:var(--ax-hi)';
      const names = locals.map((pr) => {
        let uname = pr.identity.toHexString().slice(0, 8);
        for (const a2 of conn.db.account.iter()) if (a2.identity.toHexString() === pr.identity.toHexString()) { uname = a2.username; break; }
        return `${uname}${me && pr.identity.isEqual(me) ? ' (you)' : ''}`;
      });
      rosterD.textContent = names.length ? `${names.length} in local: ${names.join(', ')}` : 'local is empty — or nobody is broadcasting';
      root.appendChild(rosterD);
      // Scramble a local (tier-2 tackle: presence-verified, 15 s).
      for (const pr of locals) {
        if (me && pr.identity.isEqual(me)) continue;
        let uname = pr.identity.toHexString().slice(0, 8);
        for (const a2 of conn.db.account.iter()) if (a2.identity.toHexString() === pr.identity.toHexString()) { uname = a2.username; break; }
        const row = document.createElement('div');
        row.style.cssText = 'display:flex;align-items:center;gap:6px;padding:1px 0';
        const nm = document.createElement('span');
        nm.textContent = `⌖ ${uname} (${pr.chassisKey.replace('ship_', '')})`;
        nm.style.cssText = 'font:11px var(--ax-mono);color:var(--ax-hi)';
        const tk = document.createElement('button');
        tk.textContent = 'Scramble';
        tk.style.cssText = btnCss;
        tk.onclick = () => {
          (conn.reducers as unknown as { tacklePilot(a3: { target: unknown }): Promise<void> })
            .tacklePilot({ target: pr.identity })
            .then(() => this.toast(`⌖ ${uname} is SCRAMBLED — 15 s`))
            .catch((e: Error) => this.toast(`Scramble failed: ${e.message ?? e}`));
        };
        row.append(nm, tk);
        root.appendChild(row);
      }
      // The system's ESS bank (tier-2): rob it in person, loudly.
      try {
        interface BankRow { systemKey: string; bankSov: bigint }
        const bdb = conn.db as unknown as { essBank: { iter(): Iterable<BankRow> } };
        const bank = [...bdb.essBank.iter()].find((b2) => b2.systemKey === this.system.id);
        if (bank && bank.bankSov > 0n) {
          const row = document.createElement('div');
          row.style.cssText = 'display:flex;align-items:center;gap:6px;padding:2px 0';
          const lbl = document.createElement('span');
          lbl.textContent = `🏦 reserve bank: ${bank.bankSov} ⓒ (burns in 3 h)`;
          lbl.style.cssText = 'font:11px var(--ax-mono);color:var(--ax-hi)';
          const rob = document.createElement('button');
          rob.textContent = 'Rob the vault';
          rob.style.cssText = btnCss;
          rob.onclick = () => {
            (conn.reducers as unknown as { raidEssBank(a3: { systemKey: string }): Promise<void> })
              .raidEssBank({ systemKey: this.system.id })
              .then(() => this.toast('🚨 The vault is yours — and the whole system knows'))
              .catch((e: Error) => this.toast(`The vault refuses: ${e.message ?? e}`));
          };
          row.append(lbl, rob);
          root.appendChild(row);
        }
      } catch { /* pre-publish */ }
      const log = document.createElement('div');
      log.style.cssText = 'background:var(--ax-bg2);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:6px;margin:4px 0;font:11px var(--ax-mono);max-height:90px;overflow-y:auto';
      const lines: Array<{ at: bigint; text: string }> = [];
      for (const m of conn.db.chatMessage.iter()) {
        if (m.kind !== `sys:${this.system.id}`) continue;
        lines.push({ at: m.at.microsSinceUnixEpoch, text: `${m.senderName}: ${m.text}` });
      }
      lines.sort((x, z) => (x.at < z.at ? -1 : 1));
      for (const l of lines.slice(-5)) { const d = document.createElement('div'); d.textContent = l.text; log.appendChild(d); }
      if (!lines.length) { const d = document.createElement('div'); d.textContent = '(local is quiet)'; d.style.color = 'var(--ax-muted)'; log.appendChild(d); }
      root.appendChild(log);
      const form = document.createElement('div');
      form.style.cssText = 'display:flex;gap:4px';
      const inp = document.createElement('input');
      inp.type = 'text'; inp.maxLength = 240; inp.placeholder = 'local channel…';
      inp.dataset.k = 'local'; // space campaign B: the re-render hands the typing back (renderOpsKeepingFocus)
      inp.style.cssText = 'flex:1;background:var(--ax-bg2);border:1px solid var(--ax-line);border-radius:var(--ax-radius);color:var(--ax-text);padding:3px 8px;font:11px var(--ax-mono)';
      const sendLocal = (): void => {
        const text = inp.value.trim();
        if (!text) return;
        inp.value = '';
        (conn.reducers as unknown as { sendLocalChat(a: { systemKey: string; text: string }): Promise<void> })
          .sendLocalChat({ systemKey: this.system.id, text }).catch((e: Error) => this.toast(`Local refused: ${e.message ?? e}`));
      };
      inp.onkeydown = (e) => { e.stopPropagation(); if (e.key === 'Enter') sendLocal(); };
      const sb = document.createElement('button'); sb.textContent = 'Send'; sb.style.cssText = btnCss; sb.onclick = sendLocal;
      form.append(inp, sb);
      root.appendChild(form);
    } catch { /* pre-publish server */ }

    // ── D-SCAN: a long-range snapshot of everything on grid (100 km sphere) ──
    root.appendChild(h('D-SCAN'));
    {
      const scanB = document.createElement('button');
      scanB.textContent = 'Scan (100 km sphere)';
      scanB.style.cssText = btnCss;
      const out = document.createElement('div');
      out.style.cssText = 'font:11px var(--ax-mono);color:var(--ax-hi);max-height:120px;overflow-y:auto';
      scanB.onclick = () => {
        const hits: string[] = [];
        const R = 100_000;
        for (const pr of this.pirates) {
          if (pr.deadUntil !== 0) continue;
          const d = pr.root.position.length();
          if (d < R) hits.push(`raider (t${pr.tier}) — ${Math.round(d / 1000)} km`);
        }
        for (const [hex, rr] of this.remoteRows) {
          void hex;
          const d = Math.hypot(rr.x - this.pos.x, rr.y - this.pos.y, rr.z - this.pos.z);
          if (d < R) hits.push(`pilot ship — ${Math.round(d / 1000)} km`);
        }
        for (const o of this.wormholeObjs) {
          const d = this.pos.distanceTo(o.pos);
          if (d < R) hits.push(`wormhole → ${o.to} — ${Math.round(d / 1000)} km`);
        }
        try {
          for (const st of conn.db.spaceStructure.iter()) {
            if (st.systemId !== this.system.id) continue;
            const d = Math.hypot(st.x - this.pos.x, st.z - this.pos.z);
            if (d < R) hits.push(`${st.kind} platform — ${Math.round(d / 1000)} km`);
          }
        } catch { /* fine */ }
        out.replaceChildren();
        if (!hits.length) { const d = document.createElement('div'); d.textContent = 'clear grid — nothing within 100 km'; d.style.color = 'var(--ax-muted)'; out.appendChild(d); }
        for (const line of hits.slice(0, 18)) { const d = document.createElement('div'); d.textContent = line; out.appendChild(d); }
        sfx('sfx_scan_ping', { gain: 0.3, cooldownMs: 800 });
      };
      root.appendChild(scanB);
      root.appendChild(out);
    }

    // ── Platforms ──
    root.appendChild(h('PLATFORMS'));
    try {
      const deeds: Array<{ deedId: bigint; structureType: string }> = [];
      if (me) {
        for (const d of conn.db.structureDeed.iter()) {
          if (d.owner.isEqual(me) && d.packed) deeds.push({ deedId: d.deedId, structureType: d.structureType });
        }
      }
      if (deeds.length) {
        const d0 = deeds[0];
        const row = document.createElement('div');
        row.style.cssText = 'display:flex;align-items:center;gap:4px;flex-wrap:wrap';
        const lbl = document.createElement('span');
        lbl.textContent = `packed deed: ${d0.structureType.replace(/_/g, ' ')} (${deeds.length} held)`;
        row.appendChild(lbl);
        for (const kind of ['depot', 'refinery'] as const) {
          const b = document.createElement('button');
          b.textContent = `Deploy ${kind} here`;
          b.style.cssText = btnCss;
          b.onclick = () => {
            const fwd = this._v1.set(0, 0, -1).applyQuaternion(this.quat);
            conn.reducers.deploySpaceStructure({
              deedId: d0.deedId, kind, systemId: this.system.id,
              x: this.pos.x + fwd.x * 140, y: this.pos.y + fwd.y * 140, z: this.pos.z + fwd.z * 140,
            })
              .then(() => this.toast(`${kind === 'depot' ? 'Depot' : 'Refinery beacon'} deployed — it holds station here`))
              .catch((e: Error) => this.toast(`Deploy refused: ${e.message ?? e}`));
          };
          row.appendChild(b);
        }
        root.appendChild(row);
      } else {
        const d = document.createElement('div');
        d.textContent = 'No packed deeds aboard — redeed a structure (or buy a deed at a bazaar terminal) to deploy a depot or refinery beacon.';
        d.style.cssText = 'color:var(--ax-soft)';
        root.appendChild(d);
      }
      if (me) {
        for (const s of conn.db.spaceStructure.iter()) {
          if (!s.owner.isEqual(me)) continue;
          const row = document.createElement('div');
          row.style.cssText = 'display:flex;align-items:center;gap:6px;padding:2px 0';
          const d = Math.round(Math.hypot(s.x - this.pos.x, s.z - this.pos.z) / 1000);
          const lbl = document.createElement('span');
          lbl.textContent = `${s.kind === 'refinery' ? '⚗' : '📦'} ${s.kind} · ${s.systemId}${s.systemId === this.system.id ? ` · ${d} km` : ''}`;
          row.appendChild(lbl);
          if (s.kind === 'refinery') {
            const mc = document.createElement('button');
            mc.textContent = 'Crack moon chunk';
            mc.style.cssText = btnCss;
            mc.onclick = () => {
              (conn.reducers as unknown as { collectMoonChunk(a3: { structureId: bigint }): Promise<void> })
                .collectMoonChunk({ structureId: s.structId })
                .then(() => this.toast('⛏ The chunk cracks — ferrite, stone, a crystal seam'))
                .catch((e: Error) => this.toast(`${e.message ?? e}`));
            };
            row.appendChild(mc);
          }
          if (s.systemId === this.system.id && Math.hypot(s.x - this.pos.x, s.z - this.pos.z) < 800) {
            const b = document.createElement('button');
            b.textContent = 'Scoop';
            b.style.cssText = btnCss;
            b.onclick = () => {
              conn.reducers.scoopSpaceStructure({ structId: s.structId })
                .then(() => this.toast('Platform folded back into a deed'))
                .catch((e: Error) => this.toast(`Scoop refused: ${e.message ?? e}`));
            };
            row.appendChild(b);
          }
          root.appendChild(row);
        }
      }
    } catch { /* rows mid-churn */ }
    // ── The front line ──
    // THE FRONT FROM THE ROW (space campaign B, lib/space_front): TWO places, both named — the physical battlefield
    // (the fort in the Commons Reach, where the lines fight) and the strategic front (the system the war decides: the
    // campaign row's target, the council's press before it opens, the cycle's rotation only as a labelled forecast).
    // The line printed `warTargetOf(cycle)` — the hash — even when a council had pressed another system.
    root.appendChild(h('THE FRONT'));
    {
      const systems = this.galaxySystems();
      const nameOfSys = (id: string): string | null => systems.find((sy) => sy.id === id)?.name ?? null;
      const campaigns: Array<{ cyc: number; fortKey: string; targetSystem: string; state: string; winner: string }> = [];
      const presses: FrontPress[] = [];
      try { for (const c of (conn.db as unknown as { spaceCampaign: { iter(): Iterable<CampaignRow> } }).spaceCampaign.iter()) campaigns.push({ cyc: Number(c.cyc), fortKey: c.fortKey, targetSystem: c.targetSystem, state: c.state, winner: c.winner }); } catch { /* pre-publish */ }
      try {
        for (const o of (conn.db as unknown as { civOperation: { iter(): Iterable<{ civ: string; cyc: bigint; operation: string; targetSystem: string; utilityJson: string }> } }).civOperation.iter()) {
          if (o.operation !== 'press' || !o.targetSystem) continue;
          let u = 0;
          try { u = (JSON.parse(o.utilityJson) as Record<string, number>).press ?? 0; } catch { /* malformed */ }
          presses.push({ civ: o.civ, cyc: Number(o.cyc), targetSystem: o.targetSystem, press: u });
        }
      } catch { /* pre-publish */ }
      const fl = frontLabels({ nowS: Date.now() / 1000, campaigns, presses, ceasefireFor: (c) => this.ceasefireStands(c), systemName: nameOfSys });
      const line = (label: string, text: string, key: string): void => {
        const d = document.createElement('div');
        d.dataset.front = key;
        d.style.cssText = 'color:var(--ax-soft);margin:1px 0';
        const b = document.createElement('b');
        b.textContent = `${label}: `;
        b.style.color = 'var(--ax-text)';
        d.append(b, document.createTextNode(text));
        root.appendChild(d);
      };
      line('⚔ Battlefield', `${fl.battlefield} — ${fl.when}`, 'battlefield');
      line('⚑ Strategic front', fl.front, 'strategic');
      line('What changes', fl.stakes, 'stakes');
      const ctl = this.controllerOf(this.system.id);
      line('This sky', `${systemLabel(this.system.id, this.system.name)}: ${ctl === 'commons' ? 'Commons space (uncontested)' : ctl === 'aurelian' ? 'the Aurelian Concord holds it' : 'the Borean Compact holds it'}`, 'here');
      line('You can', this.system.id === 'sys0' ? `fly to the battlefield (${keyHint('spaceSystemMap')} charts it) — kills pay bounties and campaign receipts` : `hyper-warp home to the Commons Reach to join the battle (${keyHint('spaceGalaxy')} — the galaxy chart)`, 'can');
    }
    // Space Wave 3: THE LANE — the firms, the belt, the run.
    root.appendChild(h('THE LANES — each civ\'s miner hauls ore to its own yard'));
    const laneLine = (txt: string): void => {
      const d = document.createElement('div');
      d.style.cssText = 'font:11px var(--ax-mono);color:var(--ax-text);margin:2px 0';
      d.textContent = txt;
      root.appendChild(d);
    };
    for (const f of conn.db.spaceFirm.iter()) {
      const w = conn.db.wallet.identity.find(new Identity(firmIdentityBits(f.firmId)));
      laneLine(`${f.name} (${f.stationKey}) — ${w ? `${w.sov} ⓒ` : '—'}${f.kind === 'yard' ? ` · hulls ${f.hulls}/${f.hullsTarget} · short ${f.shortHours} h` : ''}`);
    }
    for (const dep of conn.db.spaceDeposit.iter()) {
      if (dep.systemId !== this.system.id) continue;
      const belt = SYS0_BELTS.find((b) => b.key === dep.beltKey);
      laneLine(`${belt?.name ?? dep.beltKey}: ${dep.remaining}/${dep.capacity} u of ${dep.oreKey.replace(/_/g, ' ')} left`);
    }
    const runs = this.transitRuns();
    if (!runs.length) laneLine('No run in transit — the miner sends one each hour it holds 60 u.');
    for (const r of runs) {
      const escorted = !r.row.escortedBy.isEqual(Identity.zero());
      const km = Math.round(this.pos.distanceTo(this._v1.set(r.pos[0], r.pos[1], r.pos[2])) / 1000);
      const fight = this.ambushFor(r.row.shipmentId);
      laneLine(`Run #${r.id}: ${r.row.qty} u → ${r.toName} · ${escorted ? 'escorted' : `UNESCORTED — ${keyHint('spaceEscort')} to ride with it`} · ${km} km from you${fight ? ` · ⚔ ${fight.raidersAlive}/${fight.raiders} raiders from ${fight.denKey}` : ''}`);
    }
    // Space Wave 4: THE CAMPAIGN — the server's war, its sides, the reserves, my receipts.
    root.appendChild(h('THE CAMPAIGN — the fleet war is a server-owned operation'));
    const camp = this.campaignRow();
    if (!camp) laneLine(this.battleWindow() ? 'The sweep has not opened this cycle\'s campaign yet.' : 'No war this half-cycle — the next campaign opens on the clock.');
    else {
      const [a, bb] = this.campaignSides(camp.campaignId);
      laneLine(`#${camp.campaignId} at ${camp.fortKey} · ${camp.state}${camp.retreatSide >= 0 ? ` (side ${camp.retreatSide} retreats)` : ''}${camp.winner ? ` · winner ${camp.winner}` : ''} · decides ${camp.targetSystem} · ${camp.rounds} rounds`);
      for (const s of [a, bb]) if (s) laneLine(`${s.civ}: capitals ${s.capsAlive}/${s.capsCommitted} (lost ${s.capLost}) · gunships ${s.skirmAlive}/${s.skirmCommitted} (lost ${s.skLost}) · ${s.points} pts from pilots`);
      const mine = this.myReceipts(camp.campaignId);
      laneLine(mine.n ? `Your receipts: ${mine.n} (${mine.points} pts · ${mine.paid} ⓒ paid)` : 'Your receipts: none yet — kill enemy hulls inside the war zone.');
    }
    try { for (const r of conn.db.civReserve.iter()) laneLine(`${r.civ} reserve: ${r.hulls} hulls · ${r.capitals} capitals`); } catch { /* pre-publish */ }
    // Space campaign slice C: the live war's honest window + THE AFTER-ACTION RECORDS of the recent wars (lib/space_aar).
    // Space campaign slice H: THE OPERATIONS BOARD — guild-funded objectives for this war (lib/space_ops_board).
    renderOperationsBoard(root, { conn, camp: camp ? { campaignId: camp.campaignId, cyc: camp.cyc, state: camp.state } : null, toast: (t) => this.toast(t), rerender: () => this.renderOpsKeepingFocus() }, h, btnCss);
    renderCampaignRecord(root, conn, camp, h);
    // Space Wave 7: THE COUNCILS — what each civ decided this cycle and why, the treaty, the fleet stances, the firms' policies.
    root.appendChild(h('THE COUNCILS — organisations plan, individuals enact'));
    try {
      const cyc = decisionCycle(Date.now() / 1000);
      const ops = [...conn.db.civOperation.iter()].filter((o) => Number(o.cyc) === cyc);
      if (!ops.length) laneLine('No decision this cycle yet — the next sim tick asks the councils.');
      for (const o of ops) {
        const u = JSON.parse(o.utilityJson) as Record<CivOperation, number>;
        const table = (Object.keys(u) as CivOperation[]).map((k) => `${k} ${u[k].toFixed(2)}`).join(' · ');
        laneLine(`${o.civ === 'aurelian' ? 'Concord' : 'Compact'}: ${OPERATION_LABEL[o.operation as CivOperation] ?? o.operation}${o.targetSystem ? ` → ${o.targetSystem}` : ''}${o.capsReserved ? ` · ${o.capsReserved} capitals reserved` : ''}`);
        laneLine(`  why: ${o.reason}`);
        laneLine(`  utilities: ${table}`);
      }
      for (const a of conn.db.spaceAdvice.iter()) if (Number(a.cyc) === cyc) laneLine(`  the ${a.civ === 'aurelian' ? 'Concord' : 'Compact'}'s council (${a.source}) urged ${a.operation}${a.say ? ` — “${a.say}”` : ''}`);
      const tr = [...conn.db.civTreaty.iter()].filter((x) => x.state === 'standing' || x.state === 'proposed' || (x.state === 'breached' && cyc - Number(x.breachedCyc) < 6));
      if (!tr.length) laneLine('Treaties: none stand.');
      for (const x of tr) laneLine(`Treaty: ${x.kind} ${x.state}${x.state === 'proposed' ? ` (by the ${x.proposedBy === 'aurelian' ? 'Concord' : 'Compact'})` : x.state === 'standing' ? ` — cycles ${x.fromCyc}…${x.untilCyc}` : x.state === 'breached' ? ` — broken by the ${x.breachedBy === 'aurelian' ? 'Concord' : 'Compact'} at cycle ${x.breachedCyc}` : ''}`);
      const camp = this.campaignRow();
      if (camp) for (const st of conn.db.campaignStance.iter()) if (st.campaignId === camp.campaignId) laneLine(`Fleet command ${st.side === 0 ? 'Concord' : 'Compact'}: ${st.stance.toUpperCase()} since round ${st.sinceRound} (${st.reason})`);
      for (const fp of conn.db.firmPolicy.iter()) { const f = conn.db.spaceFirm.firmId.find(fp.firmId); laneLine(`${f?.name ?? `firm ${fp.firmId}`}: ${fp.runs ? 'runs' : 'PAUSED'} · escort reward ×${fp.escortMult} — ${fp.reason}`); }
    } catch { /* pre-publish */ }
    // Space Wave 5: INTEL & BOOKMARKS — what I (and my civ) know, the scout's pay, the marks.
    root.appendChild(h('INTEL & BOOKMARKS — the chart shows what was observed'));
    { // space campaign F: the survey desk — sites, tenders, the expedition log, intel licences, The Silent Relay
      const sd = document.createElement('button');
      sd.textContent = `Survey desk (${keyHint('spaceSurvey')}) — sites, tenders, licences, the log`;
      sd.style.cssText = btnCss;
      sd.onclick = () => surveyDesk.open();
      root.appendChild(sd);
    }
    const view = this.intelView();
    const nowUs = BigInt(Date.now()) * 1000n;
    let fresh = 0, stale = 0;
    for (const [, r] of view) { const f = intelFreshness(r.observedAt.microsSinceUnixEpoch, nowUs); if (f === 'fresh') fresh++; else if (f === 'stale') stale++; }
    laneLine(`${view.size} systems observed (${fresh} fresh · ${stale} stale) — ${this.faction ? `shared with the ${this.faction === 'aurelian' ? 'Concord' : 'Compact'}` : 'private (swear a civ to share)'}`);
    const here = view.get(this.system.id);
    laneLine(here ? `${this.system.name}: last observed ${ageLabel(Number(nowUs - here.observedAt.microsSinceUnixEpoch) / 1_000_000)} by ${here.sensor}` : `${this.system.name}: no observation on file yet`);
    {
      const civRows: IntelRow[] = [];
      try { for (const r of conn.db.spaceIntel.iter()) if (this.faction && r.civ === this.faction) civRows.push(r); } catch { /* pre-publish */ }
      const pays = this.faction !== '' && scoutPays(civRows, this.system.id, nowUs);
      const tier = this.system.tier;
      const b = document.createElement('button');
      b.textContent = pays ? `File a scout report — fresh news pays ${SCOUT_PAY_SOV * BigInt(tier)} ⓒ` : this.faction ? 'File a scout report (already fresh — no pay)' : 'File a scout report (private, unpaid)';
      b.style.cssText = btnCss;
      b.onclick = () => {
        (conn.reducers as unknown as { fileScoutReport(a: Record<string, never>): Promise<void> }).fileScoutReport({})
          .then(() => { this.toast(pays ? '📡 Scout report filed — the civ pays for fresh news' : '📡 Scout report filed'); this.renderOps(); })
          .catch((e: Error) => this.toast(`Report refused — ${String(e.message ?? e)}`));
      };
      root.appendChild(b);
    }
    {
      const form = document.createElement('div');
      form.style.cssText = 'display:flex;gap:4px;flex-wrap:wrap;align-items:center;margin:4px 0';
      const label = document.createElement('input'); label.placeholder = 'bookmark label'; label.maxLength = 40;
      label.style.cssText = 'width:140px;background:var(--ax-bg2);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-text);padding:2px 6px;font:11px var(--ax-mono)';
      const kind = document.createElement('select');
      for (const k of BOOKMARK_KINDS) { const o = document.createElement('option'); o.value = k; o.textContent = k; kind.appendChild(o); }
      kind.style.cssText = 'background:var(--ax-bg2);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-text);padding:2px 4px;font:11px var(--ax-mono)';
      const shared = document.createElement('label'); shared.style.cssText = 'font:11px var(--ax-mono);color:var(--ax-text)';
      const cb = document.createElement('input'); cb.type = 'checkbox'; cb.checked = this.faction !== '';
      shared.append(cb, document.createTextNode(' share with my civ'));
      const add = document.createElement('button'); add.textContent = 'Bookmark here'; add.style.cssText = btnCss;
      add.onclick = () => {
        (conn.reducers as unknown as { addBookmark(a: { label: string; kind: string; shared: boolean }): Promise<void> })
          .addBookmark({ label: label.value.trim(), kind: kind.value, shared: cb.checked })
          .then(() => { this.toast(`⚑ Bookmarked — ${keyHint('spaceNav')} cycles it`); this.nav = []; this.renderOps(); })
          .catch((e: Error) => this.toast(`Bookmark refused — ${String(e.message ?? e)}`));
      };
      form.append(label, kind, shared, add);
      root.appendChild(form);
      const me = conn.identity;
      const marks: BookmarkRow[] = [];
      try { for (const bm of conn.db.spaceBookmark.iter()) if ((me && bm.owner.isEqual(me)) || (this.faction && bm.civ === this.faction)) marks.push(bm); } catch { /* pre-publish */ }
      if (!marks.length) laneLine('No bookmarks yet.');
      for (const bm of marks.slice(0, 24)) {
        const r = document.createElement('div');
        r.style.cssText = 'display:flex;gap:6px;align-items:center;font:11px var(--ax-mono);color:var(--ax-text);margin:2px 0';
        const txt = document.createElement('span'); txt.style.cssText = 'flex:1';
        txt.textContent = `⚑ ${bm.label} · ${bm.kind} · ${bm.systemId}${bm.civ ? ' · shared' : ''}${me && bm.owner.isEqual(me) ? '' : ' · civ'}`;
        r.appendChild(txt);
        if (bm.systemId === this.system.id) {
          const go = document.createElement('button'); go.textContent = 'Nav'; go.style.cssText = btnCss;
          go.onclick = () => { this.nav = this.navFor(); const i = this.nav.findIndex((n) => n.name === `⚑ ${bm.label}`); if (i >= 0) { this.navIdx = i - 1; this.cycleNav(); } };
          r.appendChild(go);
        }
        if (me && bm.owner.isEqual(me)) {
          const rm = document.createElement('button'); rm.textContent = 'Remove'; rm.style.cssText = btnCss;
          rm.onclick = () => { (conn.reducers as unknown as { removeBookmark(a: { bookmarkId: bigint }): Promise<void> }).removeBookmark({ bookmarkId: bm.bookmarkId }).then(() => { this.nav = []; this.renderOps(); }).catch(() => { /* gone */ }); };
          r.appendChild(rm);
        }
        root.appendChild(r);
      }
    }
    // Space Wave 6: SIMULATION — the system's resolution and the shared dens.
    root.appendChild(h('SIMULATION — one state at four resolutions'));
    const lod = this.lodRow();
    laneLine(lod ? `${this.system.name}: ${LOD_NAME[lod.lod as Lod]} · ${lod.observers} observer${lod.observers === 1 ? '' : 's'} · authority epoch ${lod.authorityEpoch}` : `${this.system.name}: not on the resolution ledger yet (the next sim tick files it)`);
    try {
      const dens = [...conn.db.denState.iter()].filter((d) => d.systemId === this.system.id);
      if (!dens.length) laneLine('No den rows yet — the first sim tick with a pilot here creates them at capacity.');
      for (const d of dens) laneLine(`den ${d.denKey}: ${d.alive}/${d.capacity} raiders (tier ${d.tier})${d.alive === 0 ? ' — CLEARED, refilling one per 2 min' : ''}`);
      // THE DEN SLOT LAW: the engaged hulls' shared hp and who they hunt (the same for every pilot here).
      for (const sl of this.slotRows.values()) {
        const who = conn.identity && sl.target.isEqual(conn.identity) ? 'YOU' : (conn.db.account.identity.find(sl.target)?.username ?? sl.target.toHexString().slice(0, 8));
        laneLine(`  ${sl.denKey}:pirate${sl.slot} engaged — hp ${sl.hp.toFixed(1)}/${sl.hpMax} · hunting ${who}`);
      }
    } catch { /* pre-publish */ }
  }

  /** Warp to a squadmate: a synthetic nav point + the standard hyper spool. */
  private warpToMate(name: string, x: number, y: number, z: number): void {
    this.nav = this.nav.filter((n) => !n.name.startsWith('Squad:'));
    this.nav.push({ name: `Squad: ${name}`, pos: [x, y, z], arrive: 900 } as NavPoint);
    this.navIdx = this.nav.length - 1;
    this.toggleHyper();
  }

  /** The mate whose "Travel to" confirm row is open (hex), across the ops panel's 1.2 s re-renders. */
  private squadConfirm: string | null = null;

  /** THE SQUAD LAW's inputs for one mate, from the replicated rows (lib/space_squad_nav squadOffer). */
  private mateFacts(id: Identity, name: string): MateFacts {
    const conn = atlas.conn!;
    const nowUs = BigInt(Date.now()) * 1000n;
    let online = false, inSpace = false;
    let loc: MateFacts['loc'] = null;
    let newest: bigint | null = null;
    try { online = !!conn.db.presence.identity.find(id)?.online; } catch { /* not subscribed yet */ }
    try { inSpace = !!(conn.db as unknown as { spaceSession: { identity: { find(i: Identity): unknown } } }).spaceSession.identity.find(id); } catch { /* pre-publish */ }
    try {
      const l = (conn.db as unknown as { spaceLocation: { identity: { find(i: Identity): { systemId: string; mode: string; stationKey: string } | undefined } } }).spaceLocation.identity.find(id);
      if (l) loc = { systemId: l.systemId, mode: l.mode, stationKey: l.stationKey };
    } catch { /* pre-publish */ }
    const ps = conn.db.playerState.identity.find(id);
    if (ps) newest = ps.updatedAt.microsSinceUnixEpoch;
    try {
      const pr = (conn.db as unknown as { spacePresence: { identity: { find(i: Identity): { systemKey: string; updatedAt: { microsSinceUnixEpoch: bigint } } | undefined } } }).spacePresence.identity.find(id);
      // A presence ping only vouches for the system it names.
      if (pr && pr.systemKey === (loc?.systemId ?? 'sys0') && (newest === null || pr.updatedAt.microsSinceUnixEpoch > newest)) newest = pr.updatedAt.microsSinceUnixEpoch;
    } catch { /* pre-publish */ }
    return {
      name, online, inSpace, loc, transitTo: null,
      pos: ps ? { x: ps.posX, y: ps.posY, z: ps.posZ } : null,
      observedAgeS: newest === null ? null : Math.max(0, Number(nowUs - newest) / 1e6),
    };
  }

  // ── Fleet: active chassis + loadout → derived stats (lib/fleet.ts) ───────
  /** Per-arc shield max — the chassis pool split front/rear at the classic
   *  40-of-60 Lancer ratio, so component upgrades scale both arcs. */
  private arcMax(): number {
    return Math.round(this.stats.shield * 2 / 3);
  }

  /** Mirror the active player_ship + its ship_component rows into stats.
   *  Pools CLAMP to the new maxes (an unequip mid-flight can't leave
   *  phantom hull); a chassis change re-skins the flown hull. */
  private refreshFleet(): void {
    const conn = atlas.conn;
    let chassis = DEFAULT_CHASSIS;
    const comps: Array<{ slot: string; itemKey: string; quality: number }> = [];
    try {
      if (conn?.identity) {
        let row: { shipId: bigint; chassisKey: string; active: boolean } | null = null;
        for (const s of conn.db.playerShip.iter()) {
          if (!s.owner.isEqual(conn.identity)) continue;
          if (s.active) { row = s; break; }
          row ??= s;
        }
        if (row) {
          if (CHASSIS[row.chassisKey]) chassis = row.chassisKey;
          for (const c of conn.db.shipComponent.iter()) {
            if (c.shipId === row.shipId) comps.push({ slot: c.slot, itemKey: c.itemKey, quality: c.quality });
          }
        }
      }
    } catch { /* row cache mid-churn — keep the last stats */ }
    try {
      const pilot = conn?.identity ? conn.db.pilotState.identity.find(conn.identity) : null;
      this.faction = pilot?.factionKey ?? '';
      this.subscribeIntel(); // Space Wave 5: the civ's intel follows the banner
    } catch { /* mid-churn */ }
    const changed = chassis !== this.chassisKey;
    const hadMissiles = this.stats.missiles, hadCounters = this.stats.counters;
    this.chassisKey = chassis;
    // WHY: the hull row lands after the flight began (and on a hull swap) — the chase must be the hull's actually flown, or a long hull starts with the camera inside it
    if (changed && !this.cockpitView) { this.camBack = chaseFor(CHASSIS[chassis]); if (this.active && !this.interior) this.applyCamZoom(); }
    // Space Wave 1: the three flight-stat skills and the system's wormhole
    // effect are part of the flown numbers (the catalogue's promises, kept).
    const skills: SkillLevels = { ...NO_SKILLS };
    try {
      if (conn?.identity) {
        for (const sk of conn.db.pilotSkill.iter()) {
          if (!sk.identity.isEqual(conn.identity)) continue;
          if (sk.skillKey === 'gunnery' || sk.skillKey === 'shields' || sk.skillKey === 'navigation') skills[sk.skillKey] = sk.level;
        }
      }
    } catch { /* mid-churn */ }
    this.stats = shipStats(chassis, comps, skills, { effect: systemEffectOf(this.system.id, Date.now()) });
    this.hull = Math.min(this.hull, this.stats.hp);
    this.shieldF = Math.min(this.shieldF, this.arcMax());
    this.shieldR = Math.min(this.shieldR, this.arcMax());
    this.cap = Math.min(this.cap, this.stats.capacitor);
    // A freshly-fitted rack/pod arrives loaded; unequip clamps ammo to zero.
    if (this.stats.missiles > hadMissiles) this.missileAmmo = this.stats.missiles;
    if (this.stats.counters > hadCounters) this.counterAmmo = this.stats.counters;
    this.missileAmmo = Math.min(this.missileAmmo, this.stats.missiles);
    this.counterAmmo = Math.min(this.counterAmmo, this.stats.counters);
    if (changed && this.active) this.swapPlayerHull();
  }

  /** Re-skin the flown hull for the active chassis (stock fighter fallback
   *  until the Higgsfield GLBs cook — the prefab-key self-heal). */
  private swapPlayerHull(): void {
    const spec = CHASSIS[this.chassisKey] ?? CHASSIS[DEFAULT_CHASSIS];
    void (async () => {
      const skin = (await this.master(spec.prefab)) ?? (await this.master('prop:ship_fighter'));
      if (!this.active || !this.shipMesh || !skin) return;
      if (spec !== (CHASSIS[this.chassisKey] ?? CHASSIS[DEFAULT_CHASSIS])) return; // WHY: a later hull swap landed first — this slower load would paint the old chassis over the new hull
      this.shipMesh.clear();
      this.shipMesh.add(this.wrapHull(skin.clone(), spec.hullSize));
    })();
  }

  /** P near a station: the Shipyard / Hangar / Loadout dock window. */
  /** THE REGISTRY LAW: P docks server-side first (the range check is the
   *  server's), then opens the dock on the REAL station key; closing the
   *  dock undocks unless we are walking the promenade. */
  private toggleDock(): void {
    if (this.dock) { this.dock.close(); return; }
    const st = this.nearStation();
    if (!st) { this.toast('No shipyard in range — pull up to a station'); return; }
    const conn = atlas.conn;
    if (!conn) return;
    const stationKey = st.key;
    const open = (): void => {
      document.exitPointerLock?.();
      this.releaseHeld('ui');
      // space campaign K: the Station tab quotes against these pools; a completed repair job lands here ONCE.
      bindFlightPools(() => ({ hull: this.hull, hpMax: this.stats.hp, missiles: this.missileAmmo, missilesMax: this.stats.missiles, counters: this.counterAmmo, countersMax: this.stats.counters }));
      bindRepairSink((job) => {
        this.hull = Math.min(this.stats.hp, this.hull + job.restoreHp);
        this.missileAmmo = Math.min(this.stats.missiles, this.missileAmmo + job.missiles);
        this.counterAmmo = Math.min(this.stats.counters, this.counterAmmo + job.counters);
        this.shieldF = this.arcMax(); this.shieldR = this.arcMax();
        this.toast(`Repair #${job.jobId} complete — +${job.restoreHp} hull${job.missiles + job.counters > 0 ? `, ${job.missiles} missiles, ${job.counters} counters` : ''} (receipt in the Station tab)`);
      }); // space campaign B: the dock owns the keys now — nothing held may fly the docked hull
      this.dock = new FleetDock(
        () => {
          this.dock = null;
          this.refreshFleet();
          if (!this.interior) (conn.reducers as unknown as { undock(a: Record<string, never>): Promise<void> }).undock({}).catch(() => { /* already flying */ });
        },
        () => ({ systemId: this.system.id, stationKey }),
      );
      this.dock.open();
    };
    if (this.myLocation()?.mode === 'docked') { open(); return; }
    this.vel.set(0, 0, 0);
    this.throttle = 0;
    (conn.reducers as unknown as { dockAtStation(a: { stationKey: string }): Promise<void> })
      .dockAtStation({ stationKey })
      .then(open)
      .catch((e: Error) => this.toast(`Docking refused — ${String(e.message ?? e)}`));
  }
}
